/**
 * Lightweight i18n provider for Zcardon.
 *
 * Two languages today: `en` (default) and `vi` (Vietnamese).
 * The user's choice persists in `localStorage` under `zcardon_lang` so a
 * returning visitor picks up where they left off.
 *
 * Usage:
 *   import { useT } from "../lib/i18n";
 *   const t = useT();
 *   <h1>{t("landing.hero.title_a")}</h1>
 *
 * Interpolation:
 *   t("greeting", { name: "Alex" })   // "Hi, {name}" -> "Hi, Alex"
 *
 * Falls back to English when a key is missing in Vietnamese, and finally to
 * the raw key when missing in both — so nothing crashes if a translator
 * forgets a string.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "zcardon_lang";

// Display metadata for each supported language. Native names are used in the
// picker so users find their language without translating the label.
// EN + VI are hand-curated; the rest are batch-translated via Claude and
// spliced into DICT below. See backend/scripts/translate_dict.py.
export const LANGUAGES = [
  { code: "en", native: "English",     english: "English",                 rtl: false },
  { code: "es", native: "Español",     english: "Spanish",                 rtl: false },
  { code: "fr", native: "Français",    english: "French",                  rtl: false },
  { code: "it", native: "Italiano",    english: "Italian",                 rtl: false },
  { code: "pt", native: "Português",   english: "Portuguese (Brazil)",     rtl: false },
  { code: "ru", native: "Русский",     english: "Russian",                 rtl: false },
  { code: "ar", native: "العربية",      english: "Arabic",                  rtl: true  },
  { code: "hi", native: "हिन्दी",       english: "Hindi",                   rtl: false },
  { code: "ja", native: "日本語",       english: "Japanese",                rtl: false },
  { code: "ko", native: "한국어",        english: "Korean",                  rtl: false },
  { code: "vi", native: "Tiếng Việt",  english: "Vietnamese",              rtl: false },
  { code: "th", native: "ไทย",         english: "Thai",                    rtl: false },
];
const SUPPORTED = LANGUAGES.map((l) => l.code);

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

function detectInitialLang() {
  try {
    const saved = (localStorage.getItem(STORAGE_KEY) || "").toLowerCase();
    if (SUPPORTED.includes(saved)) return saved;
  } catch (e) { /* ignore */ }
  // Walk the visitor's full preference list (most-preferred first) and pick
  // the first language we support. Falls back to English. This means a
  // Japanese visitor with `navigator.languages = ["ja-JP", "ja", "en-US"]`
  // lands directly in Japanese on first paint — no flicker.
  try {
    const prefs = Array.isArray(navigator?.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator?.language || ""];
    for (const raw of prefs) {
      if (!raw) continue;
      const code = String(raw).toLowerCase().split("-")[0];
      if (SUPPORTED.includes(code)) return code;
    }
  } catch (e) { /* ignore */ }
  return "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    try {
      document.documentElement.lang = lang;
      const meta = LANGUAGES.find((l) => l.code === lang);
      document.documentElement.dir = meta?.rtl ? "rtl" : "ltr";
    } catch (e) { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, vars) => {
    // Look up in the user's chosen language; fall back to EN; finally fall
    // back to the raw key so a missing translation never blows up the UI.
    const dict = DICT[lang] || DICT.en;
    let raw = dict[key] ?? DICT.en[key] ?? key;
    if (vars && typeof raw === "string") {
      raw = raw.replace(/\{(\w+)\}/g, (_, name) => (vars[name] ?? `{${name}}`));
    }
    return raw;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }
export function useT() { return useContext(LangCtx).t; }

// ---------------------------------------------------------------------------
// Dictionary
// ---------------------------------------------------------------------------
// Keys are flat with dot-namespacing (e.g. `landing.hero.title`). Keep
// English as the source of truth — Vietnamese should mirror the same key set.
// When you add a new string anywhere in the app, add it to BOTH dicts here.
// ---------------------------------------------------------------------------
export const DICT = {
  en: {
    // ─── Header / global nav
    "header.nav.home": "Home",
    "header.nav.rates": "Rates",
    "header.nav.support": "Support",
    "header.nav.admin": "Admin",
    "header.nav.signin": "Sign in",
    "header.nav.signout": "Sign out",
    "header.nav.get_started": "Get started",
    "header.nav.account": "Account",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Switch language",

    // ─── Landing — hero
    "landing.hero.eyebrow": "A new rail for digital spending",
    "landing.hero.title_a": "Fund a virtual card.",
    "landing.hero.title_b_ship": "Ship",
    "landing.hero.title_b_rest": "it to anyone,",
    "landing.hero.title_c": "spendable in",
    "landing.hero.subtitle": "Issue a tap-and-pay virtual debit card with a scannable QR. Stripe-powered checkout, USD-denominated balance, transparent service & gateway fees — delivered to any receiver by email or download.",
    "landing.hero.cta_start": "Start issuing cards",
    "landing.hero.cta_request_cash": "Request a cash card",
    "landing.hero.cta_rates": "See rates",
    "landing.hero.cta_find_card": "Find my existing card",
    "landing.hero.stat_currency": "Currency",
    "landing.hero.stat_settle": "Settle time",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "Service fee",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "Built for the modern card economy",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "Settles in USD",
    "landing.hero.marquee": "Issue · Fund · Spend in USD",

    // ─── Landing — demo preview
    "landing.demo.eyebrow": "See it in 20 seconds",
    "landing.demo.title_a": "From issue to",
    "landing.demo.title_b_spend": "spend",
    "landing.demo.title_c": ". In four taps.",
    "landing.demo.step_issue.label": "Issue a card",
    "landing.demo.step_issue.detail": "Pick an amount. Card ready in ~2 minutes.",
    "landing.demo.step_share.label": "Share it",
    "landing.demo.step_share.detail": "Email, text, WhatsApp, or download as PNG.",
    "landing.demo.step_scan.label": "Receiver scans",
    "landing.demo.step_scan.detail": "Phone camera opens the Track page instantly.",
    "landing.demo.step_spend.label": "Spend in USD",
    "landing.demo.step_spend.detail": "Tap-to-pay, withdraw cash, spend at merchants.",
    "landing.demo.cta_try": "Try it now",
    "landing.demo.live_pill": "Live demo",
    "landing.demo.scene1_caption": "Generating card · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Sending to receiver via WhatsApp",
    "landing.demo.scene3_caption": "Scanning QR · match found",
    "landing.demo.scene4_eyebrow": "Balance credited",
    "landing.demo.scene4_status": "✓ approved · tap-to-pay ready",

    // ─── Landing — footer
    "landing.footer.rates": "Rates",
    "landing.footer.support": "Support",
    "landing.footer.admin_portal": "Admin portal",
    "landing.footer.copy": "© 2026 Zcardon · A demo fintech product",

    // ─── Landing — Request Cash dialog
    "landing.cash.dialog_title": "Request a cash card",
    "landing.cash.dialog_desc": "We'll text you a Stripe checkout link. Pay with any card; the cash card is funded the instant Stripe confirms.",
    "landing.cash.phone_label": "Mobile number",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Amount (USD)",
    "landing.cash.name_label": "Your name (optional)",
    "landing.cash.submit": "Send checkout link",
    "landing.cash.submitting": "Sending…",
    "landing.cash.success_title": "Checkout link on the way",
    "landing.cash.success_body": "We sent a Stripe checkout link to {phone}. Tap it to pay and your virtual card will activate immediately.",
    "landing.cash.success_open_checkout": "Open checkout now",

    // ─── Landing — Find My Card
    "landing.find.cta": "Already paid? Find my card",
    "landing.find.dialog_title": "Find my existing card",
    "landing.find.dialog_desc": "Enter the phone number you used. We'll text you a 6-digit code to recover any active cash card on file.",
    "landing.find.phone_label": "Mobile number",
    "landing.find.code_label": "6-digit code",
    "landing.find.send_code": "Send code",
    "landing.find.verify": "Verify and find",
    "landing.find.sending": "Sending…",
    "landing.find.verifying": "Verifying…",
    "landing.find.no_cards": "No active cards found for that number.",

    // ─── Login
    "login.eyebrow.welcome": "Welcome",
    "login.eyebrow.admin": "Administration",
    "login.title": "Sign in",
    "login.title_admin": "Admin sign in",
    "login.admin_badge": "Admin portal",
    "login.email_label": "Email",
    "login.password_label": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",
    "login.toast_welcome": "Welcome back, {name}",
    "login.toast_failed": "Login failed",
    "login.gate_title": "Customer sign-ins are temporarily paused",
    "login.gate_default_message": "We're making improvements to the platform. Please check back soon.",
    "login.gate_admin_note": "Admins and managers can still sign in below.",
    "login.new_here": "New here?",
    "login.create_account": "Create an account",
    "login.customer_q": "Customer?",
    "login.regular_signin": "Regular sign in",
    "login.demo_title": "Demo accounts",
    "login.demo_customers_note": "Customers: register via /register or create from Admin panel",
    "login.forgot_password": "Forgot your password?",

    // ─── Forgot Password
    "forgot.eyebrow": "Account recovery",
    "forgot.title": "Reset your password",
    "forgot.subtitle": "Enter the email tied to your Zcardon account and we'll send you a secure link to choose a new password.",
    "forgot.email_label": "Email",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "Send reset link",
    "forgot.submitting": "Sending…",
    "forgot.toast_failed": "Could not send reset link. Please try again.",
    "forgot.sent_title": "Check your inbox",
    "forgot.sent_message": "If an account exists for {email}, we've sent a password-reset link.",
    "forgot.sent_expiry_note": "The link is valid for 15 minutes. Didn't get it? Check spam, or send another.",
    "forgot.back_to_login": "Back to sign in",
    "forgot.resend_link": "Send another link",
    "forgot.remember_q": "Remember your password?",
    "forgot.signin_link": "Sign in",

    // ─── Reset Password
    "reset.eyebrow": "Account recovery",
    "reset.title": "Choose a new password",
    "reset.subtitle": "Pick a strong password you haven't used before. You'll be signed in automatically once it's set.",
    "reset.verifying": "Verifying your reset link…",
    "reset.invalid_title": "This reset link is invalid or expired",
    "reset.invalid_message": "Reset links are valid for 15 minutes and can be used once. Request a fresh one to continue.",
    "reset.request_new_link": "Request a new reset link",
    "reset.new_password_label": "New password",
    "reset.confirm_password_label": "Confirm password",
    "reset.password_hint": "At least 8 characters. Avoid passwords used elsewhere.",
    "reset.submit": "Update password",
    "reset.submitting": "Updating…",
    "reset.toast_success": "Password updated. You're signed in.",
    "reset.toast_failed": "Could not reset password. Please try again.",
    "reset.error_min_length": "Password must be at least 8 characters.",
    "reset.error_mismatch": "Passwords don't match.",

    // ─── Register
    "register.eyebrow": "Join",
    "register.title": "Create account",
    "register.full_name_label": "Full name",
    "register.email_label": "Email",
    "register.password_label": "Password",
    "register.consent.label": "I have read and agree to Zcardon's",
    "register.consent.terms_link": "Terms of Service",
    "register.consent.privacy_link": "Privacy Policy",
    "register.consent.sms_link": "SMS Policy",
    "register.consent.and": "and",
    "register.consent.fineprint": "If you later add a phone number to your account, you may receive transactional SMS (verification codes and account alerts). Msg & data rates may apply. Frequency varies. Reply STOP to opt out, HELP for help. Consent to receive SMS is not a condition of using Zcardon.",
    "register.consent_required": "Please confirm you've read the Terms, Privacy Policy, and SMS Policy.",
    "register.submit": "Create account",
    "register.submitting": "Creating…",
    "register.gate_title": "Sign-ups are temporarily paused",
    "register.gate_default_message": "We're making improvements to the platform. Please check back soon.",
    "register.gate_already_member": "Already have an account?",
    "register.toast_created": "Account created",
    "register.toast_failed": "Registration failed",
    "register.already_member": "Already a member?",
    "register.signin_link": "Sign in",

    // ─── Customer Dashboard (top-level)
    "dash.eyebrow": "Dashboard",
    "dash.greeting": "Welcome back, {name}",
    "dash.subtitle": "Issue a virtual card or send a wire deposit — your control center is below.",
    "dash.action.issue_card": "Issue a card",
    "dash.action.direct_deposit": "Direct deposit (wire)",
    "dash.action.support": "Support",
    "dash.section.cards": "Your cards",
    "dash.section.activity": "Recent activity",
    "dash.section.deposits": "Direct deposits",
    "dash.empty.cards": "No cards yet — issue your first to get started.",
    "dash.empty.activity": "Once you start issuing cards or sending wires, recent activity will appear here.",

    // ─── Referral Landing
    "ref.eyebrow": "Welcome to Zcardon",
    "ref.greeting": "{name} sent you.",
    "ref.loading": "Loading your invite…",
    "ref.resolved_redirect": "One moment — sending you in.",
    "ref.resolving": "Resolving your invite link…",

    // ─── Support page
    "support.eyebrow": "Here for you",
    "support.title": "Support, not tickets.",
    "support.subtitle": "Talk to a human. Email replies within one business hour on weekdays.",
    "support.card.email": "Email",
    "support.card.phone": "Phone",
    "support.card.live_chat": "Live chat",
    "support.card.live_chat_value": "In your dashboard",
    "support.card.hours": "Hours",
    "support.faq.title": "Common questions",
    "support.faq.q1": "How long until the receiver gets the card?",
    "support.faq.a1": "Typically within 2 minutes of successful funding. Delivery via email (Resend) and MMS (Twilio) happens automatically.",
    "support.faq.q2": "Can I cancel or freeze a card after sending?",
    "support.faq.a2": "Yes — open the card detail page and tap Freeze. You can also freeze directly from the receiver alert email.",
    "support.faq.q3": "Which currencies can the card spend in?",
    "support.faq.a3": "All cards settle in USD. The receiver can spend anywhere Visa/Mastercard is accepted — FX is handled by the card network.",
    "support.faq.q4": "What fees apply?",
    "support.faq.a4": "A transparent service fee plus the Stripe gateway cost. See the live quote on the Rates page before you pay.",

    // ─── Rates page
    "rates.eyebrow": "Transparent",
    "rates.title_a": "No hidden",
    "rates.title_b": "spreads",
    "rates.subtitle": "You see the service fee and gateway cost before paying. The principal always credits the virtual card balance in USD.",
    "rates.row.service_fee": "Service fee",
    "rates.row.service_fee_value": "{pct} of sent amount",
    "rates.row.gateway": "Gateway (Stripe)",
    "rates.row.settlement": "Settlement",
    "rates.row.settlement_value": "USD balance on virtual card",
    "rates.row.topup": "Top-up methods",
    "rates.row.topup_value": "Card · Debit · Bank (ACH)",
    "rates.sim.eyebrow": "Live quote",
    "rates.sim.title_a": "Ship any amount,",
    "rates.sim.title_b": "see the breakdown instantly.",
    "rates.sim.amount_label": "Amount to send",
    "rates.sim.line.principal": "On card (principal)",
    "rates.sim.line.service": "Service fee · {pct}",
    "rates.sim.line.gateway": "Gateway · {pct} + {fixed}",
    "rates.sim.line.you_pay": "You pay",
    "rates.sim.cta": "Start with ${amount} →",
    "rates.sim.note": "Rates applied above are admin-configured. Recipient always gets the full principal.",

    // ─── Direct Deposits
    "dd.title": "Direct Deposit",
    "dd.intro": "Send money to a receiver's bank account, anywhere in the world. Pay with card (Stripe Checkout) or your Zcardon balance — we handle the wire to the receiver's bank automatically where supported, or manually for countries we don't yet auto-route. Every transaction is logged with a printable receipt.",
    "dd.new_btn": "Log a new direct deposit",
    "dd.close": "Close",
    "dd.history_eyebrow": "Your deposits",
    "dd.loading": "Loading…",
    "dd.empty": "No direct deposits yet.",
    "dd.status.approved": "approved",
    "dd.status.pending": "pending approval",
    "dd.status.rejected": "rejected",
    "dd.row.confirmed": "✓ Receiver confirmed receipt",
    "dd.row.disputed": "⚠ Receiver flagged a discrepancy",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Could not load deposits",
    "dd.error.pdf": "Could not download receipt",

    // ─── Pay (public charge / receiver tap-to-pay) page
    "pay.mode.view": "Your Zcardon card",
    "pay.mode.charge": "Pay with QR",
    "pay.heading.view": "Hello, {name}.",
    "pay.heading.charge": "Charge this card.",
    "pay.cardholder": "Cardholder",
    "pay.paused.label": "Card is paused",
    "pay.paused.body": "This card has been frozen and cannot accept new charges until the owner resumes it.",
    "pay.balance.label": "Available balance",
    "pay.charges_count": "{count} charge",
    "pay.charges_count_plural": "{count} charges",
    "pay.topups_count": "{count} top-up",
    "pay.topups_count_plural": "{count} top-ups",
    "pay.activity.label": "Recent activity",
    "pay.activity.empty": "No activity yet",
    "pay.cta.charge": "Charge this card (merchant)",
    "pay.cta.charge_paused": "Card is paused",
    "pay.cta.note_active": "Tap above only if you are the merchant accepting payment from this card.",
    "pay.cta.note_paused": "This card has been paused by the sender. Ask them to resume it.",
    "pay.back_to_balance": "Back to balance",
    "pay.form.amount_label": "Amount (USD)",
    "pay.form.amount_max": "Max {amount}",
    "pay.form.merchant_label": "Merchant / reason",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Memo (optional)",
    "pay.form.memo_placeholder": "Two lattes",
    "pay.form.submit_processing": "Processing…",
    "pay.form.submit_paused": "Card is paused",
    "pay.form.submit_charge": "Charge {amount}",
    "pay.form.submit_charge_card": "Charge card",
    "pay.form.disclaimer": "By charging this card you confirm you are authorized by the cardholder to spend the amount above.",
    "pay.receipt.title": "Payment complete",
    "pay.receipt.to": "to",
    "pay.receipt.id": "receipt",
    "pay.receipt.time": "time",
    "pay.receipt.new_balance": "new balance",
    "pay.receipt.done": "Done · back to balance",
    "pay.error.cant_load": "Can't load this card",
    "pay.error.back_home": "Back home",
    "pay.error.loading": "Loading card…",
    "pay.error.charge_failed": "Charge failed",
    "pay.tap.eyebrow": "Tap to pay",
    "pay.tap.title": "Show this code",
    "pay.tap.subtitle": "to the merchant's scanner",
    "pay.tap.balance": "Balance",
    "pay.tap.paused": "Paused",
    "pay.tap.qr_unavailable": "QR unavailable for this card",
    "pay.tap.refresh_in": "Refreshes in {secs}s",
    "pay.tap.show_digits": "Show digits",
    "pay.tap.hide_digits": "Hide digits",
    "pay.tap.card_label": "Card · ••••",
    "pay.tap.auto_hides": "Auto-hides in 60s",
    "pay.tap.full_only_image": "Full number is shown only on the original card image",
    "pay.tap.disclaimer": "Closed-loop card · spendable at any Zcardon-enabled merchant. QR rotates every 60s for anti-replay protection.",
    "pay.subscribe.eyebrow": "Stay in the loop",
    "pay.subscribe.title": "Get an email on every transaction",
    "pay.subscribe.desc": "Free. One email per charge or top-up. Unsubscribe any time. We never share your address.",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "Notify me",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "You're subscribed",
    "pay.subscribe.done.body": "We'll email {email} instantly on every charge or top-up to card •••• {last4}.",
    "pay.subscribe.toast.already": "You were already subscribed — no duplicate sent.",
    "pay.subscribe.toast.success": "You'll get an email on every transaction. Welcome!",
    "pay.subscribe.toast.failed": "Subscribe failed",
    "pay.save.eyebrow": "Carry your card",
    "pay.save.title": "Save this card to your phone",
    "pay.save.desc": "Save the card image to your photos for offline access — show the QR to merchants anytime, no app required.",
    "pay.save.cta": "Save card image",
    "pay.save.preparing": "Preparing…",
    "pay.save.add_home": "Add to Home Screen",
    "pay.save.toast.opened": "Card image opened — long-press to save to Photos",
    "pay.save.toast.failed": "Could not save card",
    "pay.save.ios.title": "To add Zcardon to your iPhone home screen:",
    "pay.save.ios.step1": "Tap the Share button at the bottom of Safari",
    "pay.save.ios.step2": "Scroll down and tap Add to Home Screen",
    "pay.save.ios.step3": "Tap Add to confirm",
    "pay.save.ios.note": "Zcardon will appear like any other app — tap the icon to open this card instantly.",
    "pay.thanks.eyebrow": "Send a thank-you",
    "pay.thanks.body": "${amount} just landed — let the person who funded this card know it helped.",
    "pay.thanks.placeholder": "Optional — say a little something…",
    "pay.thanks.cta": "Send {emoji} thank-you",
    "pay.thanks.sending": "Sending…",
    "pay.thanks.done": "Thank-you sent — they'll see it in seconds.",
    "pay.thanks.toast.sent": "Thank-you sent",
    "pay.thanks.toast.failed": "Could not send thank-you",
    "pay.activity.topup_bank": "Bank transfer top-up",
    "pay.activity.topup_debit": "Debit card top-up",
    "pay.activity.topup_credit": "Credit card top-up",
    "pay.activity.topup_crypto": "Crypto top-up",
    "pay.activity.topup_received": "Top-up received",
    "pay.activity.merchant_charge": "Merchant charge",
    "pay.toast.payment_received": "Payment received · −{amount}",

    // ─── Cash request status (post Stripe checkout)
    "cash.confirming": "Confirming payment…",
    "cash.confirming.sub": "Stripe is finalizing the charge.",
    "cash.notfound.title": "Link expired",
    "cash.notfound.body": "We can't find this cash request. The link may be from an older session, or the card was never finalized. Start a new request — it only takes a minute, and any payment you already made will be auto-credited as soon as the background reconciler picks it up.",
    "cash.notfound.ref": "ref:",
    "cash.notfound.start": "Start a new request",
    "cash.notfound.contact": "Contact support",
    "cash.error.title": "Couldn't check status",
    "cash.error.return": "Return home",
    "cash.autopay.opening": "Opening Tap-to-Pay…",
    "cash.autopay.redirect": "Redirecting in {secs}s.",
    "cash.autopay.stay": "Stay here instead",
    "cash.ready.title": "Your card is ready",
    "cash.ready.subtitle": "Funded ${amount} USD · also texted to {phone}",
    "cash.card.label": "Card",
    "cash.card.exp": "Exp",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Balance",
    "cash.cta.open_pay": "Open Tap-to-Pay screen",
    "cash.toast.funded": "Your card is funded — check your phone for the SMS!",
    "cash.pending.title": "Still waiting on Stripe",
    "cash.pending.body": "We're polling for confirmation. This usually takes 5-15 seconds. If you closed the checkout without paying, you can start over from the homepage.",
    "cash.pending.back": "Back to home",
    "cash.timeout.title": "Couldn't confirm payment",
    "cash.timeout.body": "We waited 3 minutes for Stripe to confirm. Most often this means the checkout was closed before paying — but if you DID pay, your card will still be activated automatically within the next few minutes (our background reconciler runs every 5 min) and we'll text it to {phone} as soon as it does.",
    "cash.timeout.start_over": "Start over",
    "cash.timeout.try_again": "Try again",
    "cash.resend.title": "Didn't get it? Send again",
    "cash.resend.degraded.lead": "Delivery to US numbers is degraded right now.",
    "cash.resend.degraded.sms": "Texts to US phones may be silently blocked by the carrier (toll-free verification still pending).",
    "cash.resend.degraded.email": "Email is in test mode and only delivers to the account-owner address while DNS is being validated.",
    "cash.resend.degraded.tail": "If a channel doesn't work, please try the other — or open this page on the receiver's device to grab the card directly.",
    "cash.resend.text": "Text",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "Sending…",
    "cash.resend.email_sandbox": "Email is in test mode. While the zcardon.com domain is being verified at Resend, we can only send to the account-owner address. If your email isn't accepted, use Text or WhatsApp instead.",
    "cash.resend.email_label": "Email address",
    "cash.resend.email_send": "Send to email",
    "cash.resend.cancel": "Cancel",
    "cash.resend.limit": "Limit 5 resends per card. Phone on file: {phone}",
    "cash.resend.no_phone": "(none)",
    "cash.wallet.title": "Add to Wallet",
    "cash.wallet.apple": "Add to Apple Wallet",
    "cash.wallet.google": "Add to Google Wallet",
    "cash.wallet.generating": "Generating pass…",
    "cash.wallet.note": "Stays on your lock screen · Updates automatically when balance changes",
    "cash.wallet.toast.apple": "Pass downloaded — open it on your iPhone to add to Apple Wallet",
    "cash.wallet.toast.apple_failed": "Could not generate Apple Wallet pass",
    "cash.wallet.toast.google_failed": "Could not generate Google Wallet pass",
    "cash.delivery.fallback_title": "Sent via {fallback}",
    "cash.delivery.fallback_body": "Your carrier blocked the original SMS, so we re-sent your card via {fallback} to {phone}. Check that channel.",
    "cash.delivery.toll_blocked": "Carrier blocked the SMS",
    "cash.delivery.failed_title": "We couldn't text your card",
    "cash.delivery.toll_body": "Your mobile carrier rejected the message (this happens with new senders pending verification). Save your card now: tap",
    "cash.delivery.failed_body": "Your card is funded — please save it now since the SMS didn't go through. Tap",
    "cash.delivery.cta_label": "Open Tap-to-Pay screen",
    "cash.delivery.body_tail": "above, take a screenshot, or use Email below to send a copy to yourself.",
    "cash.handoff.eyebrow": "Hand off in person",
    "cash.handoff.body": "Have the receiver scan this with their camera — the card opens directly on their phone, no SMS or email needed.",
    "cash.handoff.share_label": "Or share this link",
    "cash.handoff.copy": "Copy link",
    "cash.handoff.copied": "✓ Copied",
    "cash.handoff.share": "Share",
    "cash.handoff.toast.copied": "Link copied — paste it anywhere",
    "cash.handoff.toast.copy_failed": "Could not copy. Long-press the link to copy manually.",

    // ─── DepositPortal (public receiver receipt page)
    "portal.loading": "Loading your receipt…",
    "portal.unavailable.title": "Link unavailable",
    "portal.unavailable.body": "This link is invalid or has expired.",
    "portal.unavailable.hint": "If the sender shared this link with you, ask them to resend the receipt email.",
    "portal.brand": "Receipt portal",
    "portal.eyebrow": "{sender} sent you",
    "portal.reference": "Reference",
    "portal.pending.title": "Pending approval",
    "portal.pending.body": "The sender has logged this transfer but it's still being reviewed by Zcardon. Please check back shortly — the receipt will become available once approved.",
    "portal.row.bank": "Bank",
    "portal.row.account": "Account",
    "portal.row.routing": "Routing",
    "portal.row.memo": "Memo",
    "portal.confirmed.title": "You confirmed receipt",
    "portal.confirmed.body": "Confirmed on {when}. The sender can see this.",
    "portal.disputed.title": "You flagged a discrepancy",
    "portal.disputed.body": "Reported on {when}. The sender has been emailed.",
    "portal.actions.body": "Has this amount landed in your account? Confirming closes the loop with the sender — no money moves through Zcardon, this is just a record.",
    "portal.actions.confirm": "Yes, I received it",
    "portal.actions.flag": "Flag a problem",
    "portal.dispute.placeholder": "Wrong amount, didn't arrive, wrong account number…",
    "portal.dispute.submit": "Submit report",
    "portal.dispute.too_short": "Add a short reason (at least 4 characters)",
    "portal.toast.confirmed": "Thanks — receipt confirmed.",
    "portal.toast.confirm_failed": "Could not confirm receipt",
    "portal.toast.reported": "Reported. The sender has been notified.",
    "portal.toast.report_failed": "Could not submit dispute",
    "portal.toast.pdf_failed": "Could not download receipt",
    "portal.download_pdf": "Download PDF receipt",
    "portal.privacy": "This page is private to you. Anyone with the link can view this receipt — don't share it publicly. Questions:",

    // ─── Card detail (owner-facing)
    "detail.back": "Back to dashboard",
    "detail.eyebrow": "Your card",
    "detail.confirming_payment": "Confirming your payment…",
    "detail.active_ready": "Card is active & ready to share.",
    "detail.balance.label": "Current balance",
    "detail.toast.notfound": "Card not found",
    "detail.toast.loaded": "Funds loaded to card.",
    "detail.toast.confirmed": "Payment confirmed.",
    "detail.toast.expired": "Payment session expired",
    // ─── Delivery confirmation (sender-facing)
    "detail.delivery.receiver_fallback": "your recipient",
    "detail.delivery.received_title": "{name} opened the card",
    "detail.delivery.received_when": "Confirmed delivery · {when}",
    "detail.delivery.waiting_title_on": "You'll get an email the moment it's received",
    "detail.delivery.waiting_title_off": "Delivery notifications are off",
    "detail.delivery.waiting_body_on": "As soon as your recipient scans the QR on this card, we'll ping you by email — just once.",
    "detail.delivery.waiting_body_off": "Turn this on to get a one-time email the moment your recipient first opens the card.",
    "detail.delivery.toggle_on": "On",
    "detail.delivery.toggle_off": "Turn on",
    "detail.delivery.toast_enabled": "You'll be notified when the card is opened.",
    "detail.delivery.toast_disabled": "Delivery notifications turned off.",
    "detail.delivery.toast_failed": "Couldn't update delivery notifications.",
    // ─── Tell-your-recipient nudge panel
    "nudge.eyebrow": "Tell your recipient",
    "nudge.title": "Send {receiver} a heads-up",
    "nudge.subtitle": "We drafted a short message so {receiver} knows the card is live and how to tap it.",
    "nudge.receiver_fallback": "your recipient",
    "nudge.sender_fallback": "a friend",
    "nudge.template": "Hi {receiver} — {sender} just sent you a Zcardon virtual card loaded with {amount}. Tap to pay here: {url}\n\nNo app required. The link works on any phone.",
    "nudge.email_subject": "{sender} sent you a {amount} Zcardon card",
    "nudge.action_copy": "Copy message",
    "nudge.action_sms": "Open SMS",
    "nudge.action_email": "Open email",
    "nudge.toast_copied": "Message copied to clipboard.",
    "nudge.toast_copy_failed": "Couldn't copy. Select and copy manually.",
    // ─── iOS install prompt
    "ios_install.title": "Install Zcardon on your iPhone",
    "ios_install.step_1": "Tap",
    "ios_install.share_label": "Share",
    "ios_install.step_2": " then choose \"Add to Home Screen\" — Zcardon will open as a full-screen app.",
    // ─── Smart app banner (open in installed app)
    "smart_banner.title": "Open in the Zcardon app",
    "smart_banner.subtitle": "Faster checkout. Tap to pay without leaving the app.",
    "smart_banner.cta": "Open",
    "smart_banner.dismiss": "Dismiss",
    // ─── Live social-proof carousel
    "social_proof.eyebrow": "Live · last 48 hours",
    "social_proof.empty": "Waiting for the next card to arrive…",
    "social_proof.line_opened": "{name} just opened a {amount} card.",
    "social_proof.line_issued": "{name} got a {amount} card from a friend.",
    "social_proof.line_deposit": "{name} received a {amount} direct deposit.",
    "social_proof.stat_funded": "Cards funded today",
    "social_proof.stat_delivered": "Cards delivered today",
    "social_proof.stat_recent": "Recent activity",
    "social_proof.ago_seconds": "{n}s ago",
    "social_proof.ago_minutes": "{n}m ago",
    "social_proof.ago_hours": "{n}h ago",
    // ─── Cards-in-flight widget
    "flight.eyebrow": "Cards in flight · last 48h",
    "flight.title": "Where Zcardon lands",
    "flight.subtitle": "Real receiver locations, anonymized to region. We only show countries and US states — never names, addresses, or amounts.",
    "flight.row_template": "Card opened in {label}",
    "flight.more_count": "+ {n} more regions",
    "flight.ago_seconds": "{n}s ago",
    "flight.ago_minutes": "{n}m ago",
    "flight.ago_hours": "{n}h ago",
    // ─── Trust strip (unified social-proof section)
    "trust_strip.eyebrow": "Real signal · live from the database",
    "trust_strip.title_a": "Not testimonials.",
    "trust_strip.title_b": "Live database reads.",
    "trust_strip.how_link": "How we collect this",
    "trust_strip.modal_eyebrow": "Data sources",
    "trust_strip.modal_title": "All real, all anonymized",
    "trust_strip.modal_close": "Close",
    "trust_strip.modal_intro": "Every number on this strip comes from our own database — never a third-party feed, never made-up. Here's where each section pulls from and how we anonymize it before showing it to you.",
    "trust_strip.src_carousel_title": "Live activity carousel",
    "trust_strip.src_carousel_body": "Pulls from the cards and direct_deposits collections, last 48h. Names show as first-name + last-initial; amounts round to the nearest $5 to defeat triangulation. Refreshed every 60 seconds.",
    "trust_strip.src_flight_title": "Where Zcardon lands",
    "trust_strip.src_flight_body": "Reads QR-scan landings from card_scans, geo-resolved at scan time. Collapsed to country (or US state). No city precision below state level, no IPs displayed, no card identifiers. Refreshed every 90 seconds.",
    "trust_strip.src_badges_title": "30-day trust numbers",
    "trust_strip.src_badges_body": "Pure aggregate counts — never per-row data. Each metric only appears once it crosses a minimum sample size, so a 100% rate from 2 confirmations would be hidden. Refreshed every minute.",
    "trust_strip.modal_promise": "We will never publish names, addresses, exact amounts, IPs, or card numbers in public-facing widgets. Promise.",
    // ─── TrustBadge (extracted)
    "trust_badge.eyebrow": "Real numbers · last 30 days",
    "trust_badge.title": "People are actually using this.",
    "trust_badge.subtitle": "Not a testimonial — a live count, refreshed from the database every minute.",
    "trust_badge.confirmed_label": "recipients confirm receipt within 24h",
    "trust_badge.confirmed_sub": "from {n} responses last 30 days",
    "trust_badge.thanks_label": "thank-you notes sent",
    "trust_badge.thanks_sub": "from receivers in the last 30 days",
    "trust_badge.cards_label": "cards funded",
    "trust_badge.cards_sub": "last 30 days · live count",
    // ─── Live pulse rail (vertical timeline)
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} opened a {amount} card",
    "pulse.tooltip_issued": "{name} got a {amount} card",
    "pulse.tooltip_deposit": "{name} received {amount} deposit",
    "pulse.ago_seconds": "{n}s ago",
    "pulse.ago_minutes": "{n}m ago",
    "pulse.ago_hours": "{n}h ago",
    // ─── Note word cloud ("what people use it for")
    "note_cloud.eyebrow": "What people use it for",
    "note_cloud.title": "Real reasons. Real people.",
    "note_cloud.subtitle": "Aggregated from {n} card notes in the last 90 days. We only show words that appear in at least {min} different notes — your message stays yours.",
    "note_cloud.footnote": "Names, amounts, and contact info filtered out before display.",
    "note_cloud.lang_eyebrow": "What language are these notes in?",
    "note_cloud.lang_other": "Other",
    // ─── Download app button + modal
    "download.button": "Download app",
    "download.close": "Close",
    "download.modal_eyebrow": "Get Zcardon",
    "download.modal_title": "Install Zcardon on your phone",
    "download.footnote": "No App Store yet — you're installing the secure web app directly. Updates instantly.",
    "download.installed_title": "Zcardon installed",
    "download.installed_body": "Look for the icon on your home screen.",
    "download.webview_warning": "Open this page in your phone's browser (Safari on iPhone, Chrome on Android) to install — in-app browsers can't add to your home screen.",
    "download.ios_step1_title": "On Safari",
    "download.ios_step1_body": "If you're not already in Safari, copy this link and open it there. (Chrome and other iOS browsers can't install apps.)",
    "download.ios_step2_title": "Tap Share",
    "download.ios_step2_body": "The Share icon is at the bottom-center on iPhone, top-right on iPad.",
    "download.ios_step3_title": "Add to Home Screen",
    "download.ios_step3_body": "Scroll down in the share menu and choose \"Add to Home Screen\". Zcardon installs as a full-screen app.",
    "download.android_ready_body": "Chrome detected Zcardon and is ready to install. Tap below.",
    "download.android_install": "Install Zcardon",
    "download.android_installing": "Installing…",
    "download.android_step1_title": "Open Chrome menu",
    "download.android_step1_body": "Tap the three dots in the top-right of Chrome.",
    "download.android_step2_title": "Add to Home screen",
    "download.android_step2_body": "Choose \"Install app\" or \"Add to Home screen\". Zcardon installs as a full-screen app.",
    "download.desktop_body": "Scan this QR code with your phone's camera to open Zcardon there, then install it from your phone's browser.",
    "download.steps_hint": "These steps happen in your browser, not on this page. None of the rows below are tappable — they're describing what to look for.",
    "download.ios_arrow_label": "Tap this in Safari",
    "download.android_arrow_label": "Look up here in Chrome",
    // ─── /install page (where the homepage QR points)
    "install.eyebrow": "Install Zcardon",
    "install.title": "Add Zcardon to your home screen",
    "install.subtitle": "Two taps and you'll have Zcardon as a real app icon — no App Store needed.",
    "install.back_home": "Open Zcardon in browser",
    "install.continue": "Continue to Zcardon →",
    "install.copy": "Copy",
    "install.copied": "Copied",
    "install.ios_step1_title": "Open this page in Safari",
    "install.ios_step1_body": "If you scanned from another browser, copy the URL and open it in Safari first — Chrome on iOS can't install apps.",
    "install.ios_step2_title": "Tap the Share button",
    "install.ios_step2_body": "It's the square with an arrow pointing up — at the bottom of Safari (or top-right on iPad).",
    "install.ios_step3_title": "Tap \"Add to Home Screen\"",
    "install.ios_step3_body": "Scroll down in the share sheet and choose \"Add to Home Screen\", then confirm. Zcardon appears as an app icon.",
    "install.ios_arrow_label": "Look for this icon",
    "install.android_ready_title": "Ready to install",
    "install.android_ready_body": "Chrome detected Zcardon as installable. Tap below and we'll appear as an app icon on your home screen.",
    "install.android_install": "Install Zcardon",
    "install.android_installing": "Installing…",
    "install.android_step1_title": "Tap Chrome's menu",
    "install.android_step1_body": "It's the three dots in the top-right corner of Chrome.",
    "install.android_step2_title": "Tap \"Install app\" or \"Add to Home screen\"",
    "install.android_step2_body": "Confirm to add the Zcardon icon to your phone. Tap it any time to launch.",
    "install.android_hint": "If you don't see \"Install app\" yet, browse the home page once — Chrome unlocks it after a brief visit.",
    "install.desktop_title": "Scan with your phone",
    "install.desktop_body": "Zcardon is a mobile-first app. Open this URL on your phone to install:",
    "install.webview_title": "Open in your browser",
    "install.webview_body": "In-app browsers (Instagram, Facebook, TikTok) can't install apps. Tap the \"…\" or share menu, then choose \"Open in Safari\" / \"Open in Chrome\". Or copy this link:",
    "install.already_title": "You're already in the app",
    "install.already_body": "Zcardon is installed on this device. Tap the icon on your home screen to open it any time.",
    "install.installed_title": "Installed",
    "install.installed_body": "Look for the Zcardon icon on your home screen — that's your app.",
    "install.steps_hint": "These steps happen in your browser, not on this page. None of the rows below are tappable — they're describing what to look for in the browser's menu.",
    "install.desktop_body_qr": "Zcardon installs as an app icon on iPhone or Android — not on desktop. Point your phone camera at this QR code:",
    "install.desktop_or": "Or copy the link",
    "install.android_arrow_label": "Look up here in Chrome",
    "install.ios_arrow_caption": "It's at the very bottom of Safari",

    // ─── VietQR (Accept VND via VietQR card)
    "vietqr.disabled.title": "Accept VND via VietQR",
    "vietqr.disabled.badge": "VIETNAM ONLY",
    "vietqr.disabled.desc": "Attach a Vietnamese bank account and the QR on this card becomes a real VietQR — any Vietnamese banking app (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) can scan it and pay you VND directly via the NAPAS network. No Stripe Issuing approval needed.",
    "vietqr.disabled.btn_enter": "Enter bank details",
    "vietqr.disabled.or": "or",
    "vietqr.disabled.btn_send_link": "Send setup link to receiver",
    "vietqr.disabled.btn_send_link_tip": "Mint a 7-day public link the receiver can fill out themselves",
    "vietqr.form.warn_title": "Read this before enabling",
    "vietqr.form.warn_body_a": "Scans no longer route to your Zcardon tracking page. Funds flow",
    "vietqr.form.warn_body_b": "directly to your Vietnamese bank account",
    "vietqr.form.warn_body_c": "via NAPAS interbank — Zcardon never sees the money. VietQR works only inside Vietnam for VND transfers.",
    "vietqr.form.bank": "Bank",
    "vietqr.form.account_number": "Account number",
    "vietqr.form.account_name": "Account name (25 chars)",
    "vietqr.form.city": "City (15 chars)",
    "vietqr.form.advanced": "Advanced — fixed amount / reference (optional)",
    "vietqr.form.amount_vnd": "Amount (VND)",
    "vietqr.form.amount_ph": "leave blank for static",
    "vietqr.form.reference": "Reference (25 chars)",
    "vietqr.form.confirm": "I understand that scanning this card will move VND directly into the bank account above. Zcardon does not process or hold these funds.",
    "vietqr.form.btn_attach": "Attach VietQR",
    "vietqr.form.btn_attaching": "Attaching…",
    "vietqr.form.btn_cancel": "Cancel",
    "vietqr.enabled.title": "VietQR active",
    "vietqr.enabled.desc": "When a Vietnamese banking app scans this card it will transfer VND directly to the bank account below — bypassing Zcardon entirely.",
    "vietqr.enabled.bank_label": "Bank",
    "vietqr.enabled.account_label": "Account",
    "vietqr.enabled.fixed_amount": "Fixed amount",
    "vietqr.enabled.reference": "Reference",
    "vietqr.enabled.btn_detach": "Detach VietQR",
    "vietqr.enabled.btn_detaching": "Detaching…",
    "vietqr.enabled.attached_at": "Attached",
    "vietqr.analytics.live_activity": "Live activity",
    "vietqr.analytics.today": "today",
    "vietqr.analytics.days_live": "{n}d live",
    "vietqr.analytics.phantom_views": "Phantom views",
    "vietqr.analytics.confirmed_pays": "Confirmed pays",
    "vietqr.analytics.total_vnd": "Total VND",
    "vietqr.analytics.recent_confirmed": "Recent confirmed",
    "vietqr.analytics.phantom_explainer": "Phantom views ≈ PNG fetches since you went live. Real VietQR scans bypass our servers, so we estimate from \"card was shown\" activity. Ask the receiver to confirm payments via their setup link for ground-truth data.",
    "vietqr.toast.required": "Bank account number and name are required.",
    "vietqr.toast.confirm_first": "Please confirm you understand VietQR routes VND to your bank, bypassing Zcardon.",
    "vietqr.toast.attached_default": "VietQR attached.",
    "vietqr.toast.attach_failed": "Failed to attach VietQR.",
    "vietqr.toast.detach_confirm": "Detach VietQR? The QR on the card will revert to the Zcardon tracking URL.",
    "vietqr.toast.detached": "VietQR detached — QR reverts to Zcardon tracking URL.",
    "vietqr.toast.detach_failed": "Failed to detach.",
    "vietqr.toast.link_copied": "Setup link copied · expires in {h}h",
    "vietqr.toast.link_generated": "Setup link generated · copy from below",
    "vietqr.toast.link_failed": "Failed to mint setup link.",

    // ─── Set Password (Agent Welcome)
    "setpw.toast.too_short": "Password must be at least 8 characters",
    "setpw.toast.no_match": "Passwords don't match",
    "setpw.toast.success_signin_again": "Password set — please sign in",
    "setpw.toast.welcome": "Welcome to the Zcardon agent program",
    "setpw.toast.expired": "Link expired or invalid",
    "setpw.verifying": "Verifying your invite…",
    "setpw.invalid.title": "Link expired",
    "setpw.invalid.desc": "This welcome link has expired or already been used. Please contact your Zcardon admin to receive a fresh invite.",
    "setpw.invalid.home_btn": "Home",
    "setpw.invalid.support_btn": "Contact support",
    "setpw.fallback_name": "agent",
    "setpw.eyebrow": "Agent welcome",
    "setpw.heading": "Welcome, {name}",
    "setpw.subheading": "Set your password to activate your agent account. You'll be signed in immediately.",
    "setpw.code_label": "Your agent code",
    "setpw.code_tag": "Share this when onboarding customers",
    "setpw.form.title": "Choose a password",
    "setpw.form.new_label": "New password",
    "setpw.form.placeholder": "8 characters minimum",
    "setpw.form.confirm_label": "Confirm password",
    "setpw.form.btn_busy": "Activating…",
    "setpw.form.btn_submit": "Activate my agent account",

    // ─── Transactions
    "tx.heading": "Transactions",
    "tx.kind.direct_deposit": "Direct deposit",
    "tx.kind.card_topup": "Card top-up",
    "tx.kind.outbound_payout": "Outbound payout",
    "tx.toast.load_failed": "Failed to load transactions",
    "tx.summary.deposits": "Deposits",
    "tx.summary.topups": "Top-ups",
    "tx.summary.topups_sub": "card",
    "tx.summary.payouts": "Payouts",
    "tx.summary.payouts_sub": "outbound",
    "tx.filter.all": "All",
    "tx.filter.deposits": "Deposits",
    "tx.filter.topups": "Top-ups",
    "tx.filter.payouts": "Payouts",
    "tx.loading": "Loading transactions…",
    "tx.empty.filtered": "No transactions match this filter",
    "tx.empty.none": "No transactions yet",
    "tx.empty.cta": "Start with a direct deposit →",
    "tx.open_title": "Open",

    // ─── Issuing Terms Accept
    "terms.load_error_default": "Failed to load terms",
    "terms.toast.sign_and_check": "Please sign and confirm to accept the agreement",
    "terms.toast.recorded": "Agreement recorded",
    "terms.toast.failed": "Submission failed",
    "terms.error.title": "Link not valid",
    "terms.back_home": "Back to home",
    "terms.loading": "Loading agreement…",
    "terms.done.title": "Agreement accepted",
    "terms.done.meta": "Version {v} · {ts}",
    "terms.done.next": "You can close this page. Your card will be ready to use as soon as it's issued.",
    "terms.eyebrow": "Cardholder agreement · v{v}",
    "terms.title_reaccept": "Re-accept your cardholder agreement",
    "terms.title_accept": "Accept your cardholder agreement",
    "terms.desc.sender_sent": "sent you a Zcardon card",
    "terms.desc.you_were_sent": "You've been sent a Zcardon card",
    "terms.desc.ending": "ending",
    "terms.desc.read_before": "Read the agreement below, then sign and confirm to activate the card.",
    "terms.already_accepted_note": "You already accepted a previous version. Re-accept to update to the latest.",
    "terms.scroll_hint": "Scroll inside the box to read the full agreement.",
    "terms.view_json": "Open as JSON",
    "terms.sign_label": "Sign with your full legal name",
    "terms.sign_placeholder": "As shown on your government ID",
    "terms.consent": "I have read and agree to the Zcardon Cardholder Agreement v{v}.",
    "terms.btn_recording": "Recording…",
    "terms.btn_accept": "Accept agreement",

    // ─── Receive Hub
    "hub.eyebrow": "Your receive hub",
    "hub.heading.all_done": "You're all set 🎉",
    "hub.heading.greet": "Hi {name} 👋",
    "hub.fallback_name": "there",
    "hub.desc.sender_sent": "sent you a Zcardon card",
    "hub.desc.you_were_sent": "You've been sent a Zcardon card",
    "hub.desc.ending": "ending",
    "hub.desc.complete_steps": "Complete the steps below to activate everything.",
    "hub.progress.label": "{done} of {total} steps complete",
    "hub.blocker.title_one": "1 required step remaining",
    "hub.blocker.title_many": "{n} required steps remaining",
    "hub.blocker.subtext": "Your card won't fully activate until these are complete.",
    "hub.required_pill": "Required",
    "hub.done_pill": "Done",
    "hub.setup_btn": "Set up",
    "hub.refresh": "Refresh",
    "hub.loading": "Loading…",
    "hub.error.title": "Link issue",
    "hub.error.default": "Could not load your receive hub.",
    "hub.back_home": "Back to home",
    "hub.footer": "Bookmark this page · stays active for 90 days · only people with this link can see it.",

    // ─── Payout Onboarding
    "onboarding.eyebrow": "Receive your transfer",
    "onboarding.title": "Hi {name}",
    "onboarding.fallback_name": "there",
    "onboarding.intro_via": "via Zcardon",
    "onboarding.intro_ref_open": "(reference",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Add your {country} bank account below to receive the funds. Your details go directly to Stripe (our payments partner) — the sender never sees them.",
    "onboarding.invalid.title": "Link not valid",
    "onboarding.invalid.hint": "Please ask the sender to send a fresh link.",
    "onboarding.loading": "Loading…",
    "onboarding.complete.title": "Bank details already on file",
    "onboarding.complete.desc": "You've already submitted your bank account. The sender will release the funds shortly.",
    "onboarding.success.title": "Bank details received ✓",
    "onboarding.success.auto_paid": "Thanks — your bank account is verified and the funds have been released to your bank now. They should land within 1-2 business days.",
    "onboarding.success.pending": "Thanks — your bank account is verified. The sender will be notified, and the funds will land in your account within 1-2 business days.",
    "onboarding.success.ref": "Reference: {id}",
    "onboarding.beta.title": "Beta preview.",
    "onboarding.beta.body": "This page is shipped but the live payouts pipeline is awaiting Stripe approval. Submitting will return an error until the platform is enabled. Please check back, or contact support if urgent.",
    "onboarding.select_placeholder": "Select…",
    "onboarding.submit_error_default": "Submission failed. Please try again.",
    "onboarding.submit_busy": "Submitting…",
    "onboarding.submit": "Submit bank details →",
    "onboarding.secure_note": "Secured via Stripe. This link expires in 7 days.",
    "onboarding.validate.required": "{field} is required",
    "onboarding.validate.format": "{field} format looks wrong (expected {pattern})",
    "onboarding.validate.no_schema": "No form schema available",

    // ─── Agent Apply
    "apply.toast.invalid_image": "Please choose a JPEG, PNG, or WEBP image",
    "apply.toast.too_large": "Image must be 6 MB or smaller",
    "apply.toast.upload_both": "Please upload both sides of your driver license",
    "apply.toast.invalid_ssn": "Please enter a valid 9-digit Social Security Number",
    "apply.toast.password_too_short": "Password must be at least 8 characters",
    "apply.toast.password_mismatch": "The two passwords don't match",
    "apply.toast.failed": "Submission failed",
    "apply.toast.submitted": "Application submitted",
    "apply.success.title": "Application received",
    "apply.success.desc": "Thank you, {name}. We'll review your application within 2–3 business days and email you at {email} once approved. After approval you can sign in immediately with the password you just chose.",
    "apply.success.back_home": "Back to home",
    "apply.success.contact_support": "Contact support",
    "apply.eyebrow": "Agent program",
    "apply.heading": "Apply to become a Zcardon agent",
    "apply.intro": "Earn 0.5% commission on every card you help issue, every top-up you collect, and every cash deposit you accept on behalf of customers. We onboard agents who are trusted in their community and can verify their identity.",
    "apply.bullet1": "Get your own unique agent code · trackable across all transactions",
    "apply.bullet2": "Dashboard with weekly/monthly/yearly earnings summaries",
    "apply.bullet3": "Direct bank deposits — paid out weekly",
    "apply.section.your_info": "Your information",
    "apply.section.choose_password": "Choose your sign-in password",
    "apply.section.choose_password_helper": "You'll use this email + password to sign in once an admin approves your application (usually within 2–3 business days). Minimum 8 characters · stored hashed (bcrypt) — admins never see it.",
    "apply.field.full_name": "Full legal name",
    "apply.field.full_name_ph": "As shown on your driver license",
    "apply.field.email": "Email",
    "apply.field.phone": "Phone",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Driver license number",
    "apply.field.ssn": "Social Security Number",
    "apply.field.ssn_ph": "XXX-XX-XXXX",
    "apply.field.ssn_helper": "Required by US treasury rules for agents handling cash · stored encrypted · only the last 4 digits are visible in admin views.",
    "apply.field.address": "Address",
    "apply.field.address_ph": "Street, City, State, ZIP",
    "apply.field.why_join_label": "Why do you want to become an agent? (optional)",
    "apply.field.why_join_ph": "Tell us a bit about your community ties or remittance experience",
    "apply.field.password": "Password",
    "apply.field.password_ph": "Choose 8+ characters",
    "apply.field.password_confirm": "Confirm password",
    "apply.field.password_confirm_ph": "Type the password again",
    "apply.dl.section": "Driver license verification",
    "apply.dl.helper": "Upload clear, well-lit photos of both sides. JPEG, PNG, or WEBP · 6 MB max each. Images are stored privately and only viewed by Zcardon admins for verification.",
    "apply.dl.front": "Front of driver license",
    "apply.dl.back": "Back of driver license",
    "apply.dl.tap_upload": "Tap to upload",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.ssn_card.section": "Social Security card",
    "apply.ssn_card.helper": "Optional — upload a clear photo of your Social Security card if you have one available. Helps speed up identity verification. JPEG, PNG, or WEBP · 6 MB max. Stored encrypted and visible only to Zcardon admins.",
    "apply.ssn_card.label": "Social Security card",
    "apply.sms_consent.label": "I agree to receive transactional SMS from Zcardon (a Zeborg Water Solution Corp. product) at the phone number above — including one-time verification codes, application status updates, and account alerts. Msg & data rates may apply. Frequency varies. Reply STOP to opt out, HELP for help. By checking this box I also agree to Zcardon's",
    "apply.sms_consent.policy_link": "SMS Policy",
    "apply.sms_consent.privacy_link": "Privacy Policy",
    "apply.sms_consent.terms_link": "Terms of Service",
    "apply.sms_consent.and": "and",
    "apply.sms_consent.fineprint": "Consent to receive SMS is not a condition of applying to become a Zcardon agent.",
    "apply.toast.sms_consent_required": "Please confirm the SMS consent checkbox to continue.",
    "apply.cancel": "Cancel",
    "apply.submit_busy": "Submitting…",
    "apply.submit": "Submit application",

    // ─── Agent Portal
    "agent.toast.load_failed": "Failed to load agent data",
    "agent.toast.cust_created": "Customer {name} created",
    "agent.toast.cust_failed": "Failed to create customer",
    "agent.toast.card_issued": "Card issued · you earned {amount}",
    "agent.toast.card_issue_failed": "Failed to issue card",
    "agent.toast.cash_accepted": "Cash deposit accepted · you earned {amount}",
    "agent.toast.cash_failed": "Failed to record deposit",
    "agent.loading": "Loading…",
    "agent.eyebrow": "Agent portal",
    "agent.fallback_name": "Agent",
    "agent.code_line.code_label": "Agent code",
    "agent.code_line.commission_label": "commission",
    "agent.earnings.label": "Unpaid balance",
    "agent.earnings.sub": "{n} unpaid transactions",
    "agent.stat.week": "This week",
    "agent.stat.month": "This month",
    "agent.stat.year": "This year",
    "agent.stat.txns": "{n} txns",
    "agent.stat.customers": "Your customers",
    "agent.stat.active": "active",
    "agent.customers.title": "Your customers",
    "agent.customers.empty": "No customers yet. Tap \"Register customer\" to onboard your first user.",
    "agent.customers.col_customer": "Customer",
    "agent.customers.col_phone": "Phone",
    "agent.customers.col_cards": "Cards",
    "agent.customers.col_commissions": "Commissions earned",
    "agent.customers.col_joined": "Joined",
    "agent.customers.col_actions": "Actions",
    "agent.customers.txns": "{n} txns",
    "agent.customers.no_cards": "No cards yet",
    "agent.customers.cards_more": "+{n} more",
    "agent.customers.card_open": "Click to open full image",
    "agent.customers.card_status.pending_payment": "Awaiting Stripe payment",
    "agent.customers.card_status.suspended": "Suspended",
    "agent.customers.card_status.expired": "Expired",
    "agent.customers.card_status.cancelled": "Cancelled",
    "agent.customers.card_status_short.pending": "Pending",
    "agent.customers.card_status_short.other": "—",
    "agent.toast.stripe_cancelled": "Stripe payment cancelled — card was not issued.",
    "agent.toast.stripe_card_active": "Card activated! The receiver has been notified.",
    "agent.toast.stripe_card_pending": "Payment received — Stripe is finalizing. The card will activate momentarily.",
    "agent.commissions.title": "Recent commissions",
    "agent.commissions.empty": "No commissions yet. Issue your first card to earn.",
    "agent.commissions.col_when": "When",
    "agent.commissions.col_receiver": "Receiver",
    "agent.commissions.search_placeholder": "Search receiver name, email or phone…",
    "agent.commissions.search_clear": "Clear search",
    "agent.commissions.empty_search": "No commission rows match \u201c{q}\u201d.",
    "agent.commissions.col_customer": "Customer",
    "agent.commissions.col_type": "Type",
    "agent.commissions.col_txn": "Transaction",
    "agent.commissions.col_commission": "Commission",
    "agent.commissions.col_status": "Status",
    "agent.commissions.col_actions": "Actions",
    "agent.commissions.paid": "paid",
    "agent.commissions.unpaid": "unpaid",
    "agent.commissions.total_label": "Total · {n} rows",
    "agent.commissions.delete_title": "Delete this commission row (hides from reports; kept for audit)",
    "agent.commissions.delete_confirm": "Delete this commission of {amount}? This hides the row from your reports.",
    "agent.commissions.delete_success": "Commission row deleted.",
    "agent.commissions.delete_failed": "Failed to delete commission.",
    "agent.commissions.cannot_delete_paid": "Paid commissions can only be deleted by an admin.",
    "agent.commissions.cannot_delete_paid_short": "Paid rows can only be deleted by an admin",
    "agent.type.issuance": "Issuance",
    "agent.type.topup": "Top-up",
    "agent.type.cash_deposit": "Cash deposit",
    "agent.register_btn": "Register customer",
    "agent.dialog.new_cust.title": "New customer",
    "agent.dialog.new_cust.desc": "They'll be linked to your agent code. Every card you issue on their behalf earns you 0.5% commission.",
    "agent.dialog.field.full_name": "Full name",
    "agent.dialog.field.email": "Email",
    "agent.dialog.field.phone": "Phone",
    "agent.dialog.field.password": "Initial password (share with customer)",
    "agent.dialog.cancel": "Cancel",
    "agent.dialog.creating": "Creating…",
    "agent.dialog.create_customer": "Create customer",
    "agent.dialog.issue_card.title": "Issue card for {name}",
    "agent.dialog.issue_card.title_no_customer": "Issue a virtual card",
    "agent.issue.title": "Issue a card",
    "agent.issue.subtitle": "Enter the receiver's name, email, phone and amount. Pay by cash collected from the receiver or by credit/debit card via Stripe.",
    "agent.issue.cta": "Issue card",
    "agent.dialog.issue_card.desc": "Funded from cash collected in hand. Customer pays a 3.75% fee — you keep 0.5%.",
    "agent.dialog.field.receiver_name": "Receiver name",
    "agent.dialog.field.receiver_email": "Receiver email",
    "agent.dialog.field.receiver_phone": "Receiver phone",
    "agent.dialog.field.amount_usd": "Amount (USD)",
    "agent.dialog.field.note": "Note (optional)",
    "agent.dialog.issuing": "Issuing…",
    "agent.dialog.issue_card_btn": "Issue card",
    "agent.dialog.redirecting": "Redirecting to Stripe…",
    "agent.dialog.continue_to_stripe": "Continue to Stripe Checkout",
    "agent.dialog.field.payment_mode": "How is the customer paying?",
    "agent.dialog.mode.cash": "Cash collected",
    "agent.dialog.mode.cash_help": "I already received cash from the customer. Activate the card immediately.",
    "agent.dialog.mode.card": "Pay by card",
    "agent.dialog.mode.card_help": "Redirect to Stripe Checkout so the customer can enter their credit / debit card. The card activates after payment.",
    "agent.toast.redirecting_to_stripe": "Redirecting to Stripe Checkout…",
    "agent.commissions.print_btn": "Print",
    "agent.commissions.print_title": "Print the currently visible commission rows (respects the search filter)",
    "agent.commissions.open_receiver_title": "Click to view the full receiver profile (cards, commissions, contact)",
    "agent.receiver.loading": "Loading receiver…",
    "agent.receiver.desc": "Full receiver profile",
    "agent.receiver.load_failed": "Couldn't load the receiver profile.",
    "agent.receiver.field.email": "Email",
    "agent.receiver.field.phone": "Phone",
    "agent.receiver.field.first_seen": "First seen",
    "agent.receiver.kpi.cards": "Cards",
    "agent.receiver.kpi.active": "active",
    "agent.receiver.kpi.balance": "Total balance",
    "agent.receiver.kpi.funded": "Funded total",
    "agent.receiver.kpi.commission": "Commission earned",
    "agent.receiver.cards_h": "Cards ({n})",
    "agent.receiver.commissions_h": "Commissions ({n})",
    "agent.receiver.no_cards": "No cards yet for this receiver.",
    "agent.receiver.no_commissions": "No commissions yet for this receiver.",
    "agent.receiver.open_full": "Open full card image",
    "agent.receiver.close": "Close",
    "agent.receiver.print": "Print receiver report",
    "agent.receiver.col_when": "When",
    "agent.receiver.col_type": "Type",
    "agent.receiver.col_amount": "Txn $",
    "agent.receiver.col_commission": "Commission",
    "agent.receiver.col_status": "Status",
    "agent.commissions.print_popup_blocked": "Popup blocked — the print view opened in a new tab via fallback link.",
    "agent.actions.issue_card": "Issue card",
    "agent.dialog.cash_deposit.title": "Cash deposit for {name}",
    "agent.dialog.cash_deposit.desc": "Cash collected from customer · creates a card delivered to the receiver.",
    "agent.dialog.field.receiver_name_opt": "Receiver name (optional)",
    "agent.dialog.recording": "Recording…",
    "agent.dialog.record_deposit": "Record deposit",
    "agent.actions.cash_deposit": "Cash deposit",

  },

  vi: {
    // ─── Header / global nav
    "header.nav.home": "Trang chủ",
    "header.nav.rates": "Phí & Tỷ giá",
    "header.nav.support": "Hỗ trợ",
    "header.nav.admin": "Quản trị",
    "header.nav.signin": "Đăng nhập",
    "header.nav.signout": "Đăng xuất",
    "header.nav.get_started": "Bắt đầu",
    "header.nav.account": "Tài khoản",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Chuyển ngôn ngữ",

    // ─── Landing — hero
    "landing.hero.eyebrow": "Một chuẩn mới cho chi tiêu số",
    "landing.hero.title_a": "Nạp tiền vào thẻ ảo.",
    "landing.hero.title_b_ship": "Gửi",
    "landing.hero.title_b_rest": "đến bất kỳ ai,",
    "landing.hero.title_c": "chi tiêu bằng",
    "landing.hero.subtitle": "Phát hành thẻ ghi nợ ảo có thể chạm để thanh toán kèm mã QR quét được. Thanh toán qua Stripe, số dư bằng USD, phí dịch vụ và phí cổng minh bạch — gửi đến người nhận qua email hoặc tải xuống.",
    "landing.hero.cta_start": "Bắt đầu phát hành thẻ",
    "landing.hero.cta_request_cash": "Yêu cầu thẻ tiền mặt",
    "landing.hero.cta_rates": "Xem biểu phí",
    "landing.hero.cta_find_card": "Tìm thẻ hiện có của tôi",
    "landing.hero.stat_currency": "Tiền tệ",
    "landing.hero.stat_settle": "Thời gian xử lý",
    "landing.hero.stat_settle_value": "~2 phút",
    "landing.hero.stat_service_fee": "Phí dịch vụ",
    "landing.hero.image_eyebrow": "ĐƯỜNG CHÂN TRỜI · PHẠM VI TOÀN CẦU",
    "landing.hero.image_title": "Xây dựng cho nền kinh tế thẻ hiện đại",
    "landing.hero.live_pill": "TRỰC TIẾP",
    "landing.hero.live_pill_text": "Quy đổi sang USD",
    "landing.hero.marquee": "Phát hành · Nạp · Chi tiêu bằng USD",

    // ─── Landing — demo preview
    "landing.demo.eyebrow": "Xem trong 20 giây",
    "landing.demo.title_a": "Từ phát hành đến",
    "landing.demo.title_b_spend": "chi tiêu",
    "landing.demo.title_c": ". Chỉ trong bốn bước.",
    "landing.demo.step_issue.label": "Phát hành thẻ",
    "landing.demo.step_issue.detail": "Chọn số tiền. Thẻ sẵn sàng trong khoảng 2 phút.",
    "landing.demo.step_share.label": "Chia sẻ",
    "landing.demo.step_share.detail": "Email, tin nhắn, WhatsApp hoặc tải về dạng PNG.",
    "landing.demo.step_scan.label": "Người nhận quét",
    "landing.demo.step_scan.detail": "Camera điện thoại mở trang Theo dõi ngay lập tức.",
    "landing.demo.step_spend.label": "Chi tiêu bằng USD",
    "landing.demo.step_spend.detail": "Chạm để thanh toán, rút tiền mặt, chi tiêu tại cửa hàng.",
    "landing.demo.cta_try": "Thử ngay",
    "landing.demo.live_pill": "Demo trực tiếp",
    "landing.demo.scene1_caption": "Đang tạo thẻ · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Đang gửi cho người nhận qua WhatsApp",
    "landing.demo.scene3_caption": "Đang quét QR · tìm thấy",
    "landing.demo.scene4_eyebrow": "Đã ghi có số dư",
    "landing.demo.scene4_status": "✓ đã duyệt · sẵn sàng chạm để thanh toán",

    // ─── Landing — footer
    "landing.footer.rates": "Biểu phí",
    "landing.footer.support": "Hỗ trợ",
    "landing.footer.admin_portal": "Cổng quản trị",
    "landing.footer.copy": "© 2026 Zcardon · Sản phẩm fintech demo",

    // ─── Landing — Request Cash dialog
    "landing.cash.dialog_title": "Yêu cầu thẻ tiền mặt",
    "landing.cash.dialog_desc": "Chúng tôi sẽ gửi tin nhắn kèm liên kết thanh toán Stripe. Thanh toán bằng bất kỳ thẻ nào; thẻ tiền mặt được nạp ngay khi Stripe xác nhận.",
    "landing.cash.phone_label": "Số điện thoại di động",
    "landing.cash.phone_placeholder": "+84 90 123 4567",
    "landing.cash.amount_label": "Số tiền (USD)",
    "landing.cash.name_label": "Tên của bạn (tuỳ chọn)",
    "landing.cash.submit": "Gửi liên kết thanh toán",
    "landing.cash.submitting": "Đang gửi…",
    "landing.cash.success_title": "Liên kết thanh toán đang trên đường tới",
    "landing.cash.success_body": "Chúng tôi đã gửi liên kết thanh toán Stripe đến {phone}. Chạm vào để thanh toán và thẻ ảo của bạn sẽ kích hoạt ngay lập tức.",
    "landing.cash.success_open_checkout": "Mở trang thanh toán ngay",

    // ─── Landing — Find My Card
    "landing.find.cta": "Đã thanh toán? Tìm thẻ của tôi",
    "landing.find.dialog_title": "Tìm thẻ hiện có",
    "landing.find.dialog_desc": "Nhập số điện thoại bạn đã dùng. Chúng tôi sẽ gửi mã 6 chữ số để khôi phục bất kỳ thẻ tiền mặt đang hoạt động nào.",
    "landing.find.phone_label": "Số điện thoại di động",
    "landing.find.code_label": "Mã 6 chữ số",
    "landing.find.send_code": "Gửi mã",
    "landing.find.verify": "Xác minh và tìm",
    "landing.find.sending": "Đang gửi…",
    "landing.find.verifying": "Đang xác minh…",
    "landing.find.no_cards": "Không tìm thấy thẻ đang hoạt động cho số đó.",

    // ─── Login
    "login.eyebrow.welcome": "Chào mừng",
    "login.eyebrow.admin": "Quản trị",
    "login.title": "Đăng nhập",
    "login.title_admin": "Đăng nhập quản trị",
    "login.admin_badge": "Cổng quản trị",
    "login.email_label": "Email",
    "login.password_label": "Mật khẩu",
    "login.submit": "Đăng nhập",
    "login.submitting": "Đang đăng nhập…",
    "login.toast_welcome": "Chào mừng trở lại, {name}",
    "login.toast_failed": "Đăng nhập thất bại",
    "login.gate_title": "Đăng nhập của khách hàng tạm dừng",
    "login.gate_default_message": "Chúng tôi đang nâng cấp nền tảng. Vui lòng quay lại sau.",
    "login.gate_admin_note": "Quản trị viên và quản lý vẫn có thể đăng nhập bên dưới.",
    "login.new_here": "Bạn mới đến?",
    "login.create_account": "Tạo tài khoản",
    "login.customer_q": "Là khách hàng?",
    "login.regular_signin": "Đăng nhập thường",
    "login.demo_title": "Tài khoản demo",
    "login.demo_customers_note": "Khách hàng: đăng ký tại /register hoặc tạo từ Cổng quản trị",
    "login.forgot_password": "Quên mật khẩu?",

    // ─── Forgot Password
    "forgot.eyebrow": "Khôi phục tài khoản",
    "forgot.title": "Đặt lại mật khẩu",
    "forgot.subtitle": "Nhập email gắn với tài khoản Zcardon của bạn và chúng tôi sẽ gửi liên kết bảo mật để chọn mật khẩu mới.",
    "forgot.email_label": "Email",
    "forgot.email_placeholder": "ban@vidu.com",
    "forgot.submit": "Gửi liên kết đặt lại",
    "forgot.submitting": "Đang gửi…",
    "forgot.toast_failed": "Không gửi được liên kết. Vui lòng thử lại.",
    "forgot.sent_title": "Kiểm tra hộp thư của bạn",
    "forgot.sent_message": "Nếu {email} có tài khoản, chúng tôi đã gửi liên kết đặt lại mật khẩu.",
    "forgot.sent_expiry_note": "Liên kết có hiệu lực trong 15 phút. Không thấy email? Kiểm tra thư rác hoặc gửi lại.",
    "forgot.back_to_login": "Quay lại đăng nhập",
    "forgot.resend_link": "Gửi liên kết khác",
    "forgot.remember_q": "Nhớ mật khẩu rồi?",
    "forgot.signin_link": "Đăng nhập",

    // ─── Reset Password
    "reset.eyebrow": "Khôi phục tài khoản",
    "reset.title": "Chọn mật khẩu mới",
    "reset.subtitle": "Chọn mật khẩu mạnh chưa từng dùng. Bạn sẽ được đăng nhập tự động sau khi cập nhật.",
    "reset.verifying": "Đang xác minh liên kết đặt lại…",
    "reset.invalid_title": "Liên kết đặt lại không hợp lệ hoặc đã hết hạn",
    "reset.invalid_message": "Liên kết đặt lại có hiệu lực 15 phút và chỉ dùng được một lần. Hãy yêu cầu liên kết mới để tiếp tục.",
    "reset.request_new_link": "Yêu cầu liên kết đặt lại mới",
    "reset.new_password_label": "Mật khẩu mới",
    "reset.confirm_password_label": "Xác nhận mật khẩu",
    "reset.password_hint": "Tối thiểu 8 ký tự. Tránh dùng mật khẩu đã sử dụng ở nơi khác.",
    "reset.submit": "Cập nhật mật khẩu",
    "reset.submitting": "Đang cập nhật…",
    "reset.toast_success": "Đã cập nhật mật khẩu. Bạn đã đăng nhập.",
    "reset.toast_failed": "Không đặt lại được mật khẩu. Vui lòng thử lại.",
    "reset.error_min_length": "Mật khẩu phải có ít nhất 8 ký tự.",
    "reset.error_mismatch": "Mật khẩu không khớp.",

    // ─── Register
    "register.eyebrow": "Tham gia",
    "register.title": "Tạo tài khoản",
    "register.full_name_label": "Họ và tên",
    "register.email_label": "Email",
    "register.password_label": "Mật khẩu",
    "register.consent.label": "Tôi đã đọc và đồng ý với",
    "register.consent.terms_link": "Điều khoản dịch vụ",
    "register.consent.privacy_link": "Chính sách bảo mật",
    "register.consent.sms_link": "Chính sách SMS",
    "register.consent.and": "và",
    "register.consent.fineprint": "Nếu sau này bạn thêm số điện thoại vào tài khoản, bạn có thể nhận được tin nhắn giao dịch (mã xác thực và cảnh báo tài khoản). Có thể áp dụng cước tin nhắn và dữ liệu. Tần suất thay đổi. Trả lời STOP để hủy đăng ký, HELP để được trợ giúp. Đồng ý nhận SMS không phải là điều kiện sử dụng Zcardon.",
    "register.consent_required": "Vui lòng xác nhận bạn đã đọc Điều khoản, Chính sách bảo mật và Chính sách SMS.",
    "register.submit": "Tạo tài khoản",
    "register.submitting": "Đang tạo…",
    "register.gate_title": "Đăng ký tạm dừng",
    "register.gate_default_message": "Chúng tôi đang nâng cấp nền tảng. Vui lòng quay lại sau.",
    "register.gate_already_member": "Đã có tài khoản?",
    "register.toast_created": "Đã tạo tài khoản",
    "register.toast_failed": "Đăng ký thất bại",
    "register.already_member": "Đã là thành viên?",
    "register.signin_link": "Đăng nhập",

    // ─── Customer Dashboard
    "dash.eyebrow": "Bảng điều khiển",
    "dash.greeting": "Chào mừng trở lại, {name}",
    "dash.subtitle": "Phát hành thẻ ảo hoặc gửi chuyển khoản — trung tâm điều khiển của bạn ở bên dưới.",
    "dash.action.issue_card": "Phát hành thẻ",
    "dash.action.direct_deposit": "Chuyển khoản (wire)",
    "dash.action.support": "Hỗ trợ",
    "dash.section.cards": "Thẻ của bạn",
    "dash.section.activity": "Hoạt động gần đây",
    "dash.section.deposits": "Chuyển khoản trực tiếp",
    "dash.empty.cards": "Chưa có thẻ — phát hành thẻ đầu tiên để bắt đầu.",
    "dash.empty.activity": "Khi bạn bắt đầu phát hành thẻ hoặc gửi chuyển khoản, hoạt động sẽ hiện ở đây.",

    // ─── Referral Landing
    "ref.eyebrow": "Chào mừng đến Zcardon",
    "ref.greeting": "{name} đã mời bạn.",
    "ref.loading": "Đang tải lời mời…",
    "ref.resolved_redirect": "Vui lòng chờ — đang đưa bạn vào.",
    "ref.resolving": "Đang xử lý liên kết mời…",

    // ─── Support page
    "support.eyebrow": "Sẵn sàng phục vụ bạn",
    "support.title": "Hỗ trợ, không phải vé yêu cầu.",
    "support.subtitle": "Trò chuyện với người thật. Email được phản hồi trong vòng một giờ làm việc các ngày trong tuần.",
    "support.card.email": "Email",
    "support.card.phone": "Điện thoại",
    "support.card.live_chat": "Chat trực tiếp",
    "support.card.live_chat_value": "Trong bảng điều khiển của bạn",
    "support.card.hours": "Giờ làm việc",
    "support.faq.title": "Câu hỏi thường gặp",
    "support.faq.q1": "Bao lâu thì người nhận sẽ có thẻ?",
    "support.faq.a1": "Thường trong vòng 2 phút sau khi nạp tiền thành công. Việc gửi qua email (Resend) và MMS (Twilio) diễn ra tự động.",
    "support.faq.q2": "Tôi có thể huỷ hoặc khoá thẻ sau khi gửi không?",
    "support.faq.a2": "Có — mở trang chi tiết thẻ và chạm Khoá. Bạn cũng có thể khoá trực tiếp từ email cảnh báo người nhận.",
    "support.faq.q3": "Thẻ có thể chi tiêu bằng những loại tiền nào?",
    "support.faq.a3": "Tất cả thẻ đều quy đổi sang USD. Người nhận có thể chi tiêu ở bất kỳ nơi nào chấp nhận Visa/Mastercard — quy đổi ngoại tệ do mạng thẻ xử lý.",
    "support.faq.q4": "Có những loại phí nào?",
    "support.faq.a4": "Phí dịch vụ minh bạch cộng với phí cổng Stripe. Xem báo giá trực tiếp trên trang Biểu phí trước khi thanh toán.",

    // ─── Rates page
    "rates.eyebrow": "Minh bạch",
    "rates.title_a": "Không có chênh lệch",
    "rates.title_b": "ẩn",
    "rates.subtitle": "Bạn xem phí dịch vụ và phí cổng trước khi thanh toán. Tiền gốc luôn được ghi vào số dư thẻ ảo bằng USD.",
    "rates.row.service_fee": "Phí dịch vụ",
    "rates.row.service_fee_value": "{pct} của số tiền gửi",
    "rates.row.gateway": "Phí cổng (Stripe)",
    "rates.row.settlement": "Quy đổi",
    "rates.row.settlement_value": "Số dư USD trên thẻ ảo",
    "rates.row.topup": "Phương thức nạp",
    "rates.row.topup_value": "Thẻ · Ghi nợ · Ngân hàng (ACH)",
    "rates.sim.eyebrow": "Báo giá trực tiếp",
    "rates.sim.title_a": "Gửi bất kỳ số tiền nào,",
    "rates.sim.title_b": "xem chi tiết ngay lập tức.",
    "rates.sim.amount_label": "Số tiền cần gửi",
    "rates.sim.line.principal": "Trên thẻ (gốc)",
    "rates.sim.line.service": "Phí dịch vụ · {pct}",
    "rates.sim.line.gateway": "Phí cổng · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Bạn thanh toán",
    "rates.sim.cta": "Bắt đầu với ${amount} →",
    "rates.sim.note": "Mức phí áp dụng phía trên do quản trị viên cấu hình. Người nhận luôn nhận đủ tiền gốc.",

    // ─── Direct Deposits
    "dd.title": "Chuyển khoản trực tiếp",
    "dd.intro": "Gửi tiền đến tài khoản ngân hàng của người nhận, ở bất cứ đâu trên thế giới. Thanh toán bằng thẻ (Stripe Checkout) hoặc số dư Zcardon của bạn — chúng tôi xử lý chuyển khoản đến ngân hàng của người nhận tự động ở nơi hỗ trợ, hoặc thủ công cho các quốc gia chưa được tự động định tuyến. Mọi giao dịch đều được ghi lại với biên nhận có thể in.",
    "dd.new_btn": "Ghi nhận chuyển khoản mới",
    "dd.close": "Đóng",
    "dd.history_eyebrow": "Chuyển khoản của bạn",
    "dd.loading": "Đang tải…",
    "dd.empty": "Chưa có chuyển khoản nào.",
    "dd.status.approved": "đã duyệt",
    "dd.status.pending": "chờ duyệt",
    "dd.status.rejected": "bị từ chối",
    "dd.row.confirmed": "✓ Người nhận đã xác nhận đã nhận tiền",
    "dd.row.disputed": "⚠ Người nhận đã báo cáo sai lệch",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Không thể tải danh sách chuyển khoản",
    "dd.error.pdf": "Không thể tải biên nhận",

    // ─── Pay (public charge / receiver tap-to-pay) page
    "pay.mode.view": "Thẻ Zcardon của bạn",
    "pay.mode.charge": "Thanh toán bằng QR",
    "pay.heading.view": "Xin chào, {name}.",
    "pay.heading.charge": "Trừ tiền thẻ này.",
    "pay.cardholder": "Chủ thẻ",
    "pay.paused.label": "Thẻ đã tạm khoá",
    "pay.paused.body": "Thẻ này đã bị khoá và không thể nhận giao dịch mới cho đến khi chủ thẻ mở lại.",
    "pay.balance.label": "Số dư khả dụng",
    "pay.charges_count": "{count} giao dịch",
    "pay.charges_count_plural": "{count} giao dịch",
    "pay.topups_count": "{count} lần nạp",
    "pay.topups_count_plural": "{count} lần nạp",
    "pay.activity.label": "Hoạt động gần đây",
    "pay.activity.empty": "Chưa có hoạt động nào",
    "pay.cta.charge": "Trừ tiền thẻ này (cửa hàng)",
    "pay.cta.charge_paused": "Thẻ đã tạm khoá",
    "pay.cta.note_active": "Chỉ chạm nút trên nếu bạn là người bán đang nhận thanh toán từ thẻ này.",
    "pay.cta.note_paused": "Thẻ này đã bị tạm khoá bởi người gửi. Vui lòng yêu cầu họ mở lại.",
    "pay.back_to_balance": "Quay lại số dư",
    "pay.form.amount_label": "Số tiền (USD)",
    "pay.form.amount_max": "Tối đa {amount}",
    "pay.form.merchant_label": "Cửa hàng / lý do",
    "pay.form.merchant_placeholder": "Starbucks Hà Nội",
    "pay.form.memo_label": "Ghi chú (tuỳ chọn)",
    "pay.form.memo_placeholder": "Hai ly latte",
    "pay.form.submit_processing": "Đang xử lý…",
    "pay.form.submit_paused": "Thẻ đã tạm khoá",
    "pay.form.submit_charge": "Trừ {amount}",
    "pay.form.submit_charge_card": "Trừ tiền thẻ",
    "pay.form.disclaimer": "Bằng việc trừ tiền thẻ này, bạn xác nhận đã được chủ thẻ uỷ quyền chi tiêu số tiền trên.",
    "pay.receipt.title": "Thanh toán hoàn tất",
    "pay.receipt.to": "đến",
    "pay.receipt.id": "biên nhận",
    "pay.receipt.time": "thời gian",
    "pay.receipt.new_balance": "số dư mới",
    "pay.receipt.done": "Xong · quay lại số dư",
    "pay.error.cant_load": "Không thể tải thẻ này",
    "pay.error.back_home": "Về trang chủ",
    "pay.error.loading": "Đang tải thẻ…",
    "pay.error.charge_failed": "Trừ tiền thất bại",
    "pay.tap.eyebrow": "Chạm để thanh toán",
    "pay.tap.title": "Hiển thị mã này",
    "pay.tap.subtitle": "cho máy quét của cửa hàng",
    "pay.tap.balance": "Số dư",
    "pay.tap.paused": "Đã khoá",
    "pay.tap.qr_unavailable": "QR không khả dụng cho thẻ này",
    "pay.tap.refresh_in": "Làm mới sau {secs}s",
    "pay.tap.show_digits": "Hiện số",
    "pay.tap.hide_digits": "Ẩn số",
    "pay.tap.card_label": "Thẻ · ••••",
    "pay.tap.auto_hides": "Tự ẩn sau 60s",
    "pay.tap.full_only_image": "Số đầy đủ chỉ hiển thị trên ảnh thẻ gốc",
    "pay.tap.disclaimer": "Thẻ vòng kín · chi tiêu tại các điểm chấp nhận Zcardon. QR luân phiên mỗi 60s để chống phát lại.",
    "pay.subscribe.eyebrow": "Cập nhật liên tục",
    "pay.subscribe.title": "Nhận email cho mỗi giao dịch",
    "pay.subscribe.desc": "Miễn phí. Mỗi lần nạp/trừ là một email. Có thể huỷ bất cứ lúc nào. Chúng tôi không chia sẻ địa chỉ của bạn.",
    "pay.subscribe.placeholder": "ban@example.com",
    "pay.subscribe.cta": "Thông báo cho tôi",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Bạn đã đăng ký",
    "pay.subscribe.done.body": "Chúng tôi sẽ gửi email đến {email} ngay khi có giao dịch trên thẻ •••• {last4}.",
    "pay.subscribe.toast.already": "Bạn đã đăng ký từ trước — không gửi trùng lặp.",
    "pay.subscribe.toast.success": "Bạn sẽ nhận email cho mỗi giao dịch. Chào mừng!",
    "pay.subscribe.toast.failed": "Đăng ký thất bại",
    "pay.save.eyebrow": "Mang thẻ theo bên mình",
    "pay.save.title": "Lưu thẻ này vào điện thoại",
    "pay.save.desc": "Lưu ảnh thẻ vào thư viện ảnh để truy cập ngoại tuyến — hiển thị QR cho cửa hàng bất kỳ lúc nào, không cần cài app.",
    "pay.save.cta": "Lưu ảnh thẻ",
    "pay.save.preparing": "Đang chuẩn bị…",
    "pay.save.add_home": "Thêm vào màn hình chính",
    "pay.save.toast.opened": "Ảnh thẻ đã mở — nhấn giữ để lưu vào Ảnh",
    "pay.save.toast.failed": "Không thể lưu thẻ",
    "pay.save.ios.title": "Để thêm Zcardon vào màn hình chính iPhone:",
    "pay.save.ios.step1": "Nhấn nút Chia sẻ ở phía dưới Safari",
    "pay.save.ios.step2": "Cuộn xuống và chọn Thêm vào màn hình chính",
    "pay.save.ios.step3": "Nhấn Thêm để xác nhận",
    "pay.save.ios.note": "Zcardon sẽ xuất hiện như một ứng dụng — chạm vào biểu tượng để mở thẻ này tức thì.",
    "pay.thanks.eyebrow": "Gửi lời cảm ơn",
    "pay.thanks.body": "${amount} vừa về thẻ — hãy cho người đã nạp tiền biết điều đó có ý nghĩa.",
    "pay.thanks.placeholder": "Tuỳ chọn — viết vài lời…",
    "pay.thanks.cta": "Gửi {emoji} cảm ơn",
    "pay.thanks.sending": "Đang gửi…",
    "pay.thanks.done": "Đã gửi cảm ơn — họ sẽ thấy trong vài giây.",
    "pay.thanks.toast.sent": "Đã gửi lời cảm ơn",
    "pay.thanks.toast.failed": "Không thể gửi lời cảm ơn",
    "pay.activity.topup_bank": "Nạp qua chuyển khoản ngân hàng",
    "pay.activity.topup_debit": "Nạp qua thẻ ghi nợ",
    "pay.activity.topup_credit": "Nạp qua thẻ tín dụng",
    "pay.activity.topup_crypto": "Nạp bằng tiền mã hoá",
    "pay.activity.topup_received": "Đã nhận nạp tiền",
    "pay.activity.merchant_charge": "Giao dịch tại cửa hàng",
    "pay.toast.payment_received": "Đã nhận thanh toán · −{amount}",

    // ─── Cash request status
    "cash.confirming": "Đang xác nhận thanh toán…",
    "cash.confirming.sub": "Stripe đang hoàn tất giao dịch.",
    "cash.notfound.title": "Liên kết đã hết hạn",
    "cash.notfound.body": "Chúng tôi không tìm thấy yêu cầu thẻ tiền mặt này. Liên kết có thể từ phiên cũ hơn, hoặc thẻ chưa được hoàn tất. Hãy bắt đầu một yêu cầu mới — chỉ mất một phút, và mọi khoản đã thanh toán sẽ được tự động ghi có khi hệ thống đối soát kế tiếp.",
    "cash.notfound.ref": "tham chiếu:",
    "cash.notfound.start": "Bắt đầu yêu cầu mới",
    "cash.notfound.contact": "Liên hệ hỗ trợ",
    "cash.error.title": "Không thể kiểm tra trạng thái",
    "cash.error.return": "Về trang chủ",
    "cash.autopay.opening": "Đang mở Chạm-để-Thanh-toán…",
    "cash.autopay.redirect": "Chuyển hướng sau {secs}s.",
    "cash.autopay.stay": "Ở lại trang này",
    "cash.ready.title": "Thẻ của bạn đã sẵn sàng",
    "cash.ready.subtitle": "Đã nạp ${amount} USD · cũng đã gửi tin nhắn tới {phone}",
    "cash.card.label": "Thẻ",
    "cash.card.exp": "HSD",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Số dư",
    "cash.cta.open_pay": "Mở màn Chạm-để-Thanh-toán",
    "cash.toast.funded": "Thẻ của bạn đã được nạp — kiểm tra điện thoại để xem tin nhắn!",
    "cash.pending.title": "Vẫn đang chờ Stripe",
    "cash.pending.body": "Chúng tôi đang truy vấn xác nhận. Việc này thường mất 5-15 giây. Nếu bạn đóng trang thanh toán mà chưa trả, có thể bắt đầu lại từ trang chủ.",
    "cash.pending.back": "Về trang chủ",
    "cash.timeout.title": "Không thể xác nhận thanh toán",
    "cash.timeout.body": "Chúng tôi đã chờ Stripe xác nhận trong 3 phút. Thường thì điều này có nghĩa là bạn đóng trang thanh toán trước khi trả — nhưng nếu bạn ĐÃ trả, thẻ vẫn sẽ được kích hoạt tự động trong vài phút tới (hệ thống đối soát chạy mỗi 5 phút) và chúng tôi sẽ gửi tin nhắn tới {phone} ngay khi xong.",
    "cash.timeout.start_over": "Bắt đầu lại",
    "cash.timeout.try_again": "Thử lại",
    "cash.resend.title": "Chưa nhận được? Gửi lại",
    "cash.resend.degraded.lead": "Việc gửi đến số US đang gặp trục trặc.",
    "cash.resend.degraded.sms": "Tin nhắn đến số US có thể bị nhà mạng âm thầm chặn (vẫn đang chờ xác minh số toll-free).",
    "cash.resend.degraded.email": "Email đang ở chế độ thử nghiệm và chỉ gửi tới địa chỉ chủ tài khoản trong khi DNS được xác minh.",
    "cash.resend.degraded.tail": "Nếu một kênh không hoạt động, hãy thử kênh khác — hoặc mở trang này trên thiết bị của người nhận để lấy thẻ trực tiếp.",
    "cash.resend.text": "Tin nhắn",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "Đang gửi…",
    "cash.resend.email_sandbox": "Email đang ở chế độ thử nghiệm. Trong khi miền zcardon.com đang được xác minh tại Resend, chúng tôi chỉ có thể gửi tới địa chỉ chủ tài khoản. Nếu email không được chấp nhận, hãy dùng Tin nhắn hoặc WhatsApp.",
    "cash.resend.email_label": "Địa chỉ email",
    "cash.resend.email_send": "Gửi tới email",
    "cash.resend.cancel": "Huỷ",
    "cash.resend.limit": "Giới hạn 5 lần gửi lại mỗi thẻ. Số điện thoại lưu trữ: {phone}",
    "cash.resend.no_phone": "(không có)",
    "cash.wallet.title": "Thêm vào Ví",
    "cash.wallet.apple": "Thêm vào Apple Wallet",
    "cash.wallet.google": "Thêm vào Google Wallet",
    "cash.wallet.generating": "Đang tạo thẻ…",
    "cash.wallet.note": "Hiển thị trên màn hình khoá · Tự cập nhật khi số dư thay đổi",
    "cash.wallet.toast.apple": "Đã tải xuống — mở trên iPhone để thêm vào Apple Wallet",
    "cash.wallet.toast.apple_failed": "Không thể tạo thẻ Apple Wallet",
    "cash.wallet.toast.google_failed": "Không thể tạo thẻ Google Wallet",
    "cash.delivery.fallback_title": "Đã gửi qua {fallback}",
    "cash.delivery.fallback_body": "Nhà mạng đã chặn SMS gốc, nên chúng tôi đã gửi lại thẻ qua {fallback} đến {phone}. Vui lòng kiểm tra kênh đó.",
    "cash.delivery.toll_blocked": "Nhà mạng đã chặn SMS",
    "cash.delivery.failed_title": "Không thể gửi tin nhắn thẻ của bạn",
    "cash.delivery.toll_body": "Nhà mạng đã từ chối tin nhắn (xảy ra với người gửi mới đang chờ xác minh). Hãy lưu thẻ ngay bây giờ: chạm",
    "cash.delivery.failed_body": "Thẻ của bạn đã được nạp — vui lòng lưu lại ngay vì SMS không gửi được. Chạm",
    "cash.delivery.cta_label": "Mở màn Chạm-để-Thanh-toán",
    "cash.delivery.body_tail": "ở trên, chụp màn hình, hoặc dùng Email bên dưới để gửi bản sao cho chính bạn.",
    "cash.handoff.eyebrow": "Trao thẻ trực tiếp",
    "cash.handoff.body": "Yêu cầu người nhận quét mã này bằng camera — thẻ sẽ mở trực tiếp trên điện thoại của họ, không cần SMS hay email.",
    "cash.handoff.share_label": "Hoặc chia sẻ liên kết này",
    "cash.handoff.copy": "Sao chép liên kết",
    "cash.handoff.copied": "✓ Đã sao chép",
    "cash.handoff.share": "Chia sẻ",
    "cash.handoff.toast.copied": "Đã sao chép liên kết — dán ở bất cứ đâu",
    "cash.handoff.toast.copy_failed": "Không thể sao chép. Nhấn giữ liên kết để sao chép thủ công.",

    // ─── DepositPortal
    "portal.loading": "Đang tải biên nhận…",
    "portal.unavailable.title": "Liên kết không khả dụng",
    "portal.unavailable.body": "Liên kết này không hợp lệ hoặc đã hết hạn.",
    "portal.unavailable.hint": "Nếu người gửi đã chia sẻ liên kết này với bạn, hãy yêu cầu họ gửi lại email biên nhận.",
    "portal.brand": "Cổng biên nhận",
    "portal.eyebrow": "{sender} đã gửi cho bạn",
    "portal.reference": "Tham chiếu",
    "portal.pending.title": "Đang chờ duyệt",
    "portal.pending.body": "Người gửi đã ghi nhận giao dịch nhưng Zcardon vẫn đang xét duyệt. Vui lòng quay lại sau — biên nhận sẽ khả dụng sau khi được duyệt.",
    "portal.row.bank": "Ngân hàng",
    "portal.row.account": "Tài khoản",
    "portal.row.routing": "Mã định tuyến",
    "portal.row.memo": "Ghi chú",
    "portal.confirmed.title": "Bạn đã xác nhận đã nhận tiền",
    "portal.confirmed.body": "Đã xác nhận vào {when}. Người gửi có thể thấy điều này.",
    "portal.disputed.title": "Bạn đã báo cáo sai lệch",
    "portal.disputed.body": "Đã báo cáo vào {when}. Người gửi đã nhận email.",
    "portal.actions.body": "Số tiền này đã về tài khoản của bạn chưa? Việc xác nhận sẽ khép vòng giao dịch với người gửi — không có tiền nào chuyển qua Zcardon, đây chỉ là bản ghi.",
    "portal.actions.confirm": "Vâng, tôi đã nhận",
    "portal.actions.flag": "Báo cáo vấn đề",
    "portal.dispute.placeholder": "Sai số tiền, chưa nhận được, sai số tài khoản…",
    "portal.dispute.submit": "Gửi báo cáo",
    "portal.dispute.too_short": "Hãy ghi lý do ngắn (ít nhất 4 ký tự)",
    "portal.toast.confirmed": "Cảm ơn — đã xác nhận biên nhận.",
    "portal.toast.confirm_failed": "Không thể xác nhận biên nhận",
    "portal.toast.reported": "Đã báo cáo. Người gửi đã được thông báo.",
    "portal.toast.report_failed": "Không thể gửi báo cáo",
    "portal.toast.pdf_failed": "Không thể tải biên nhận",
    "portal.download_pdf": "Tải biên nhận PDF",
    "portal.privacy": "Trang này riêng tư cho bạn. Bất kỳ ai có liên kết đều có thể xem biên nhận này — đừng chia sẻ công khai. Câu hỏi:",

    // ─── Card detail
    "detail.back": "Quay lại bảng điều khiển",
    "detail.eyebrow": "Thẻ của bạn",
    "detail.confirming_payment": "Đang xác nhận thanh toán…",
    "detail.active_ready": "Thẻ đang hoạt động & sẵn sàng chia sẻ.",
    "detail.balance.label": "Số dư hiện tại",
    "detail.toast.notfound": "Không tìm thấy thẻ",
    "detail.toast.loaded": "Đã nạp tiền vào thẻ.",
    "detail.toast.confirmed": "Đã xác nhận thanh toán.",
    "detail.toast.expired": "Phiên thanh toán đã hết hạn",
    // ─── Delivery confirmation (sender-facing)
    "detail.delivery.receiver_fallback": "người nhận của bạn",
    "detail.delivery.received_title": "{name} đã mở thẻ",
    "detail.delivery.received_when": "Đã xác nhận giao · {when}",
    "detail.delivery.waiting_title_on": "Bạn sẽ nhận email ngay khi thẻ được mở",
    "detail.delivery.waiting_title_off": "Thông báo giao hàng đang tắt",
    "detail.delivery.waiting_body_on": "Ngay khi người nhận quét mã QR trên thẻ này, chúng tôi sẽ gửi email cho bạn — chỉ một lần.",
    "detail.delivery.waiting_body_off": "Bật để nhận email một lần duy nhất vào thời điểm người nhận mở thẻ lần đầu.",
    "detail.delivery.toggle_on": "Đang bật",
    "detail.delivery.toggle_off": "Bật",
    "detail.delivery.toast_enabled": "Bạn sẽ được thông báo khi thẻ được mở.",
    "detail.delivery.toast_disabled": "Đã tắt thông báo giao hàng.",
    "detail.delivery.toast_failed": "Không cập nhật được thông báo giao hàng.",
    // ─── Tell-your-recipient nudge panel
    "nudge.eyebrow": "Nhắn cho người nhận",
    "nudge.title": "Gửi {receiver} một lời báo",
    "nudge.subtitle": "Chúng tôi đã soạn sẵn tin nhắn ngắn để {receiver} biết thẻ đã kích hoạt và cách chạm để thanh toán.",
    "nudge.receiver_fallback": "người nhận của bạn",
    "nudge.sender_fallback": "một người bạn",
    "nudge.template": "Chào {receiver} — {sender} vừa gửi cho bạn thẻ ảo Zcardon đã nạp {amount}. Chạm để thanh toán tại đây: {url}\n\nKhông cần cài ứng dụng. Liên kết hoạt động trên mọi điện thoại.",
    "nudge.email_subject": "{sender} đã gửi bạn thẻ Zcardon {amount}",
    "nudge.action_copy": "Sao chép",
    "nudge.action_sms": "Mở tin nhắn",
    "nudge.action_email": "Mở email",
    "nudge.toast_copied": "Đã sao chép tin nhắn.",
    "nudge.toast_copy_failed": "Không sao chép được. Vui lòng chọn và sao chép thủ công.",
    // ─── iOS install prompt
    "ios_install.title": "Cài Zcardon vào iPhone của bạn",
    "ios_install.step_1": "Chạm vào",
    "ios_install.share_label": "Chia sẻ",
    "ios_install.step_2": " rồi chọn \"Thêm vào Màn hình chính\" — Zcardon sẽ mở toàn màn hình như ứng dụng gốc.",
    // ─── Smart app banner (open in installed app)
    "smart_banner.title": "Mở trong ứng dụng Zcardon",
    "smart_banner.subtitle": "Thanh toán nhanh hơn. Chạm để trả tiền ngay trong ứng dụng.",
    "smart_banner.cta": "Mở",
    "smart_banner.dismiss": "Bỏ qua",
    // ─── Live social-proof carousel
    "social_proof.eyebrow": "Trực tiếp · 48 giờ qua",
    "social_proof.empty": "Đang chờ thẻ tiếp theo đến…",
    "social_proof.line_opened": "{name} vừa mở thẻ {amount}.",
    "social_proof.line_issued": "{name} nhận được thẻ {amount} từ bạn bè.",
    "social_proof.line_deposit": "{name} nhận được khoản chuyển khoản trực tiếp {amount}.",
    "social_proof.stat_funded": "Thẻ được nạp hôm nay",
    "social_proof.stat_delivered": "Thẻ đã giao hôm nay",
    "social_proof.stat_recent": "Hoạt động gần đây",
    "social_proof.ago_seconds": "{n} giây trước",
    "social_proof.ago_minutes": "{n} phút trước",
    "social_proof.ago_hours": "{n} giờ trước",
    // ─── Cards-in-flight widget
    "flight.eyebrow": "Thẻ đang lưu hành · 48 giờ qua",
    "flight.title": "Zcardon đến những đâu",
    "flight.subtitle": "Vị trí người nhận thật, ẩn danh đến cấp khu vực. Chỉ hiển thị quốc gia và bang Hoa Kỳ — không có tên, địa chỉ hay số tiền.",
    "flight.row_template": "Thẻ được mở tại {label}",
    "flight.more_count": "+ {n} khu vực khác",
    "flight.ago_seconds": "{n} giây trước",
    "flight.ago_minutes": "{n} phút trước",
    "flight.ago_hours": "{n} giờ trước",
    // ─── Trust strip (unified social-proof section)
    "trust_strip.eyebrow": "Tín hiệu thật · trực tiếp từ cơ sở dữ liệu",
    "trust_strip.title_a": "Không phải lời chứng thực.",
    "trust_strip.title_b": "Dữ liệu thật, đọc trực tiếp.",
    "trust_strip.how_link": "Chúng tôi thu thập thế nào",
    "trust_strip.modal_eyebrow": "Nguồn dữ liệu",
    "trust_strip.modal_title": "Tất cả là thật, tất cả ẩn danh",
    "trust_strip.modal_close": "Đóng",
    "trust_strip.modal_intro": "Mỗi con số trên dải này đến từ cơ sở dữ liệu của chính chúng tôi — không phải từ nguồn thứ ba, không phải bịa ra. Đây là nguồn của từng phần và cách ẩn danh trước khi hiển thị.",
    "trust_strip.src_carousel_title": "Hoạt động trực tiếp",
    "trust_strip.src_carousel_body": "Lấy từ collection cards và direct_deposits, 48 giờ qua. Tên hiển thị dưới dạng tên + chữ cái đầu họ; số tiền làm tròn đến 5 đô la gần nhất để chống truy ngược. Làm mới mỗi 60 giây.",
    "trust_strip.src_flight_title": "Nơi Zcardon đến",
    "trust_strip.src_flight_body": "Đọc các lượt quét QR từ card_scans, đã được giải mã địa lý tại thời điểm quét. Gom về cấp quốc gia (hoặc bang Hoa Kỳ). Không có độ chính xác đến thành phố dưới cấp bang, không hiển thị IP, không có mã thẻ. Làm mới mỗi 90 giây.",
    "trust_strip.src_badges_title": "Số liệu 30 ngày",
    "trust_strip.src_badges_body": "Hoàn toàn là số tổng — không bao giờ là dữ liệu từng dòng. Mỗi chỉ số chỉ xuất hiện sau khi đạt cỡ mẫu tối thiểu, vì vậy tỷ lệ 100% từ 2 xác nhận sẽ bị ẩn. Làm mới mỗi phút.",
    "trust_strip.modal_promise": "Chúng tôi sẽ không bao giờ công bố tên, địa chỉ, số tiền chính xác, IP hay số thẻ trong các widget công khai. Hứa.",
    // ─── TrustBadge (extracted)
    "trust_badge.eyebrow": "Số liệu thật · 30 ngày qua",
    "trust_badge.title": "Mọi người thực sự đang dùng.",
    "trust_badge.subtitle": "Không phải lời chứng thực — số đếm trực tiếp, làm mới từ cơ sở dữ liệu mỗi phút.",
    "trust_badge.confirmed_label": "người nhận xác nhận đã nhận trong 24h",
    "trust_badge.confirmed_sub": "từ {n} phản hồi trong 30 ngày qua",
    "trust_badge.thanks_label": "lời cảm ơn đã gửi",
    "trust_badge.thanks_sub": "từ người nhận trong 30 ngày qua",
    "trust_badge.cards_label": "thẻ đã được nạp",
    "trust_badge.cards_sub": "30 ngày qua · đếm trực tiếp",
    // ─── Live pulse rail (vertical timeline)
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} mở thẻ {amount}",
    "pulse.tooltip_issued": "{name} nhận thẻ {amount}",
    "pulse.tooltip_deposit": "{name} nhận chuyển khoản {amount}",
    "pulse.ago_seconds": "{n}s trước",
    "pulse.ago_minutes": "{n}p trước",
    "pulse.ago_hours": "{n}h trước",
    // ─── Note word cloud ("what people use it for")
    "note_cloud.eyebrow": "Mọi người dùng cho việc gì",
    "note_cloud.title": "Lý do thật. Người thật.",
    "note_cloud.subtitle": "Tổng hợp từ {n} ghi chú thẻ trong 90 ngày qua. Chúng tôi chỉ hiển thị những từ xuất hiện trong ít nhất {min} ghi chú khác nhau — tin nhắn của bạn vẫn là riêng tư.",
    "note_cloud.footnote": "Tên, số tiền và thông tin liên hệ được lọc bỏ trước khi hiển thị.",
    "note_cloud.lang_eyebrow": "Các ghi chú này dùng ngôn ngữ nào?",
    "note_cloud.lang_other": "Khác",
    // ─── Download app button + modal
    "download.button": "Tải ứng dụng",
    "download.close": "Đóng",
    "download.modal_eyebrow": "Tải Zcardon",
    "download.modal_title": "Cài Zcardon trên điện thoại của bạn",
    "download.footnote": "Chưa có App Store — bạn đang cài ứng dụng web bảo mật trực tiếp. Cập nhật tức thì.",
    "download.installed_title": "Đã cài Zcardon",
    "download.installed_body": "Tìm biểu tượng trên màn hình chính của bạn.",
    "download.webview_warning": "Mở trang này trong trình duyệt của điện thoại (Safari trên iPhone, Chrome trên Android) để cài đặt — trình duyệt trong ứng dụng không thể thêm vào màn hình chính.",
    "download.ios_step1_title": "Trên Safari",
    "download.ios_step1_body": "Nếu bạn chưa ở Safari, hãy sao chép liên kết này và mở ở đó. (Chrome và các trình duyệt iOS khác không cài được ứng dụng.)",
    "download.ios_step2_title": "Chạm Chia sẻ",
    "download.ios_step2_body": "Biểu tượng Chia sẻ ở giữa-dưới trên iPhone, góc trên-phải trên iPad.",
    "download.ios_step3_title": "Thêm vào Màn hình chính",
    "download.ios_step3_body": "Cuộn xuống trong menu chia sẻ và chọn \"Thêm vào Màn hình chính\". Zcardon sẽ cài đặt như ứng dụng toàn màn hình.",
    "download.android_ready_body": "Chrome đã phát hiện Zcardon và sẵn sàng cài đặt. Chạm bên dưới.",
    "download.android_install": "Cài Zcardon",
    "download.android_installing": "Đang cài…",
    "download.android_step1_title": "Mở menu Chrome",
    "download.android_step1_body": "Chạm vào ba chấm ở góc trên-phải của Chrome.",
    "download.android_step2_title": "Thêm vào Màn hình chính",
    "download.android_step2_body": "Chọn \"Cài đặt ứng dụng\" hoặc \"Thêm vào Màn hình chính\". Zcardon sẽ cài đặt như ứng dụng toàn màn hình.",
    "download.desktop_body": "Quét mã QR này bằng camera điện thoại để mở Zcardon ở đó, rồi cài đặt từ trình duyệt điện thoại.",
    "download.steps_hint": "Các bước này diễn ra trong trình duyệt của bạn, không phải trên trang này. Các dòng bên dưới không bấm được — chúng chỉ mô tả những gì cần tìm trong menu trình duyệt.",
    "download.ios_arrow_label": "Bấm vào nút này trong Safari",
    "download.android_arrow_label": "Nhìn lên góc này trong Chrome",
    // ─── /install page
    "install.eyebrow": "Cài đặt Zcardon",
    "install.title": "Thêm Zcardon vào màn hình chính",
    "install.subtitle": "Hai chạm và bạn sẽ có biểu tượng Zcardon thật sự — không cần App Store.",
    "install.back_home": "Mở Zcardon trên trình duyệt",
    "install.continue": "Tiếp tục đến Zcardon →",
    "install.copy": "Sao chép",
    "install.copied": "Đã sao chép",
    "install.ios_step1_title": "Mở trang này trong Safari",
    "install.ios_step1_body": "Nếu bạn quét từ trình duyệt khác, hãy sao chép URL và mở trong Safari — Chrome trên iOS không thể cài đặt ứng dụng.",
    "install.ios_step2_title": "Chạm nút Chia sẻ",
    "install.ios_step2_body": "Đó là hình vuông với mũi tên hướng lên — ở phía dưới Safari (hoặc trên cùng bên phải trên iPad).",
    "install.ios_step3_title": "Chọn \"Thêm vào Màn hình chính\"",
    "install.ios_step3_body": "Cuộn xuống trong bảng chia sẻ và chọn \"Thêm vào Màn hình chính\", sau đó xác nhận. Zcardon xuất hiện như một biểu tượng ứng dụng.",
    "install.ios_arrow_label": "Tìm biểu tượng này",
    "install.android_ready_title": "Sẵn sàng cài đặt",
    "install.android_ready_body": "Chrome đã phát hiện Zcardon có thể cài đặt. Chạm bên dưới và chúng tôi sẽ xuất hiện như biểu tượng ứng dụng trên màn hình chính.",
    "install.android_install": "Cài đặt Zcardon",
    "install.android_installing": "Đang cài đặt…",
    "install.android_step1_title": "Chạm menu Chrome",
    "install.android_step1_body": "Đó là ba chấm ở góc trên bên phải của Chrome.",
    "install.android_step2_title": "Chạm \"Cài đặt ứng dụng\" hoặc \"Thêm vào Màn hình chính\"",
    "install.android_step2_body": "Xác nhận để thêm biểu tượng Zcardon vào điện thoại. Chạm vào bất cứ lúc nào để khởi chạy.",
    "install.android_hint": "Nếu bạn chưa thấy \"Cài đặt ứng dụng\", hãy duyệt trang chủ một lần — Chrome sẽ mở khóa sau lần truy cập ngắn.",
    "install.desktop_title": "Quét bằng điện thoại của bạn",
    "install.desktop_body": "Zcardon là ứng dụng dành cho điện thoại. Mở URL này trên điện thoại của bạn để cài đặt:",
    "install.webview_title": "Mở trong trình duyệt của bạn",
    "install.webview_body": "Trình duyệt nội bộ (Instagram, Facebook, TikTok) không thể cài đặt ứng dụng. Chạm \"…\" hoặc menu chia sẻ, sau đó chọn \"Mở trong Safari\" / \"Mở trong Chrome\". Hoặc sao chép liên kết này:",
    "install.already_title": "Bạn đã ở trong ứng dụng",
    "install.already_body": "Zcardon đã được cài đặt trên thiết bị này. Chạm vào biểu tượng trên màn hình chính để mở bất cứ lúc nào.",
    "install.installed_title": "Đã cài đặt",
    "install.installed_body": "Tìm biểu tượng Zcardon trên màn hình chính — đó là ứng dụng của bạn.",
    "install.steps_hint": "Các bước này diễn ra trong trình duyệt của bạn, không phải trên trang này. Các dòng bên dưới không bấm được — chúng chỉ mô tả những gì cần tìm trong menu trình duyệt.",
    "install.desktop_body_qr": "Zcardon cài đặt thành biểu tượng ứng dụng trên iPhone hoặc Android — không phải trên máy tính. Hướng camera điện thoại của bạn vào mã QR này:",
    "install.desktop_or": "Hoặc sao chép liên kết",
    "install.android_arrow_label": "Nhìn lên góc này trong Chrome",
    "install.ios_arrow_caption": "Nó nằm ở dưới cùng của Safari",

    // ─── VietQR (Accept VND via VietQR card)
    "vietqr.disabled.title": "Nhận VND qua VietQR",
    "vietqr.disabled.badge": "CHỈ VIỆT NAM",
    "vietqr.disabled.desc": "Liên kết tài khoản ngân hàng Việt Nam và mã QR trên thẻ này sẽ trở thành VietQR thật — mọi ứng dụng ngân hàng Việt Nam (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) đều có thể quét và chuyển VND trực tiếp cho bạn qua mạng NAPAS. Không cần phê duyệt Stripe Issuing.",
    "vietqr.disabled.btn_enter": "Nhập thông tin ngân hàng",
    "vietqr.disabled.or": "hoặc",
    "vietqr.disabled.btn_send_link": "Gửi liên kết thiết lập cho người nhận",
    "vietqr.disabled.btn_send_link_tip": "Tạo liên kết công khai 7 ngày để người nhận tự điền thông tin",
    "vietqr.form.warn_title": "Đọc kỹ trước khi kích hoạt",
    "vietqr.form.warn_body_a": "Lần quét sẽ không còn dẫn về trang theo dõi Zcardon nữa. Tiền sẽ chuyển",
    "vietqr.form.warn_body_b": "trực tiếp vào tài khoản ngân hàng Việt Nam của bạn",
    "vietqr.form.warn_body_c": "qua liên ngân hàng NAPAS — Zcardon hoàn toàn không nhìn thấy dòng tiền. VietQR chỉ hoạt động trong lãnh thổ Việt Nam cho giao dịch VND.",
    "vietqr.form.bank": "Ngân hàng",
    "vietqr.form.account_number": "Số tài khoản",
    "vietqr.form.account_name": "Tên chủ tài khoản (tối đa 25 ký tự)",
    "vietqr.form.city": "Thành phố (tối đa 15 ký tự)",
    "vietqr.form.advanced": "Nâng cao — số tiền cố định / nội dung (không bắt buộc)",
    "vietqr.form.amount_vnd": "Số tiền (VND)",
    "vietqr.form.amount_ph": "để trống nếu không cố định",
    "vietqr.form.reference": "Nội dung (tối đa 25 ký tự)",
    "vietqr.form.confirm": "Tôi hiểu rằng việc quét mã trên thẻ này sẽ chuyển VND trực tiếp vào tài khoản ngân hàng trên. Zcardon không xử lý hoặc giữ khoản tiền này.",
    "vietqr.form.btn_attach": "Kích hoạt VietQR",
    "vietqr.form.btn_attaching": "Đang kích hoạt…",
    "vietqr.form.btn_cancel": "Hủy",
    "vietqr.enabled.title": "VietQR đang hoạt động",
    "vietqr.enabled.desc": "Khi một ứng dụng ngân hàng Việt Nam quét thẻ này, tiền VND sẽ được chuyển trực tiếp vào tài khoản bên dưới — hoàn toàn bỏ qua Zcardon.",
    "vietqr.enabled.bank_label": "Ngân hàng",
    "vietqr.enabled.account_label": "Tài khoản",
    "vietqr.enabled.fixed_amount": "Số tiền cố định",
    "vietqr.enabled.reference": "Nội dung",
    "vietqr.enabled.btn_detach": "Ngắt VietQR",
    "vietqr.enabled.btn_detaching": "Đang ngắt…",
    "vietqr.enabled.attached_at": "Đã kích hoạt",
    "vietqr.analytics.live_activity": "Hoạt động trực tiếp",
    "vietqr.analytics.today": "hôm nay",
    "vietqr.analytics.days_live": "{n} ngày hoạt động",
    "vietqr.analytics.phantom_views": "Lượt xem ước tính",
    "vietqr.analytics.confirmed_pays": "Thanh toán đã xác nhận",
    "vietqr.analytics.total_vnd": "Tổng VND",
    "vietqr.analytics.recent_confirmed": "Xác nhận gần đây",
    "vietqr.analytics.phantom_explainer": "Lượt xem ước tính ≈ số lần ảnh thẻ được tải kể từ khi kích hoạt. Các lần quét VietQR thật không đi qua máy chủ của chúng tôi, nên đây chỉ là ước lượng dựa trên hoạt động \"thẻ đã được hiển thị\". Hãy nhờ người nhận xác nhận thanh toán qua liên kết thiết lập để có số liệu chính xác.",
    "vietqr.toast.required": "Cần nhập số tài khoản và tên chủ tài khoản.",
    "vietqr.toast.confirm_first": "Vui lòng xác nhận bạn hiểu VietQR sẽ chuyển VND vào ngân hàng của bạn, bỏ qua Zcardon.",
    "vietqr.toast.attached_default": "Đã kích hoạt VietQR.",
    "vietqr.toast.attach_failed": "Không kích hoạt được VietQR.",
    "vietqr.toast.detach_confirm": "Ngắt VietQR? Mã QR trên thẻ sẽ trở lại đường dẫn theo dõi Zcardon.",
    "vietqr.toast.detached": "Đã ngắt VietQR — mã QR đã trở lại đường dẫn theo dõi Zcardon.",
    "vietqr.toast.detach_failed": "Không ngắt được.",
    "vietqr.toast.link_copied": "Đã sao chép liên kết · hết hạn sau {h} giờ",
    "vietqr.toast.link_generated": "Đã tạo liên kết · sao chép từ bên dưới",
    "vietqr.toast.link_failed": "Không tạo được liên kết thiết lập.",

    // ─── Set Password (Agent Welcome)
    "setpw.toast.too_short": "Mật khẩu phải có ít nhất 8 ký tự",
    "setpw.toast.no_match": "Mật khẩu không khớp",
    "setpw.toast.success_signin_again": "Đã thiết lập mật khẩu — vui lòng đăng nhập",
    "setpw.toast.welcome": "Chào mừng đến với chương trình đại lý Zcardon",
    "setpw.toast.expired": "Liên kết hết hạn hoặc không hợp lệ",
    "setpw.verifying": "Đang xác thực lời mời…",
    "setpw.invalid.title": "Liên kết đã hết hạn",
    "setpw.invalid.desc": "Liên kết chào mừng này đã hết hạn hoặc đã được sử dụng. Vui lòng liên hệ quản trị viên Zcardon để nhận lời mời mới.",
    "setpw.invalid.home_btn": "Trang chủ",
    "setpw.invalid.support_btn": "Liên hệ hỗ trợ",
    "setpw.fallback_name": "đại lý",
    "setpw.eyebrow": "Chào mừng đại lý",
    "setpw.heading": "Xin chào, {name}",
    "setpw.subheading": "Thiết lập mật khẩu để kích hoạt tài khoản đại lý. Bạn sẽ được đăng nhập ngay.",
    "setpw.code_label": "Mã đại lý của bạn",
    "setpw.code_tag": "Chia sẻ mã này khi đăng ký khách hàng",
    "setpw.form.title": "Chọn mật khẩu",
    "setpw.form.new_label": "Mật khẩu mới",
    "setpw.form.placeholder": "Tối thiểu 8 ký tự",
    "setpw.form.confirm_label": "Xác nhận mật khẩu",
    "setpw.form.btn_busy": "Đang kích hoạt…",
    "setpw.form.btn_submit": "Kích hoạt tài khoản đại lý",

    // ─── Transactions
    "tx.heading": "Giao dịch",
    "tx.kind.direct_deposit": "Gửi tiền trực tiếp",
    "tx.kind.card_topup": "Nạp thẻ",
    "tx.kind.outbound_payout": "Chi tiền đi",
    "tx.toast.load_failed": "Không tải được giao dịch",
    "tx.summary.deposits": "Tiền gửi",
    "tx.summary.topups": "Nạp",
    "tx.summary.topups_sub": "thẻ",
    "tx.summary.payouts": "Chi",
    "tx.summary.payouts_sub": "ra ngoài",
    "tx.filter.all": "Tất cả",
    "tx.filter.deposits": "Tiền gửi",
    "tx.filter.topups": "Nạp",
    "tx.filter.payouts": "Chi",
    "tx.loading": "Đang tải giao dịch…",
    "tx.empty.filtered": "Không có giao dịch nào khớp bộ lọc",
    "tx.empty.none": "Chưa có giao dịch nào",
    "tx.empty.cta": "Bắt đầu bằng một lần gửi tiền →",
    "tx.open_title": "Mở",

    // ─── Issuing Terms Accept
    "terms.load_error_default": "Không tải được điều khoản",
    "terms.toast.sign_and_check": "Vui lòng ký và xác nhận để chấp thuận thỏa thuận",
    "terms.toast.recorded": "Đã ghi nhận thỏa thuận",
    "terms.toast.failed": "Gửi không thành công",
    "terms.error.title": "Liên kết không hợp lệ",
    "terms.back_home": "Về trang chủ",
    "terms.loading": "Đang tải thỏa thuận…",
    "terms.done.title": "Đã chấp thuận thỏa thuận",
    "terms.done.meta": "Phiên bản {v} · {ts}",
    "terms.done.next": "Bạn có thể đóng trang này. Thẻ của bạn sẽ sẵn sàng sử dụng ngay khi được phát hành.",
    "terms.eyebrow": "Thỏa thuận chủ thẻ · v{v}",
    "terms.title_reaccept": "Chấp thuận lại thỏa thuận chủ thẻ",
    "terms.title_accept": "Chấp thuận thỏa thuận chủ thẻ",
    "terms.desc.sender_sent": "đã gửi cho bạn một thẻ Zcardon",
    "terms.desc.you_were_sent": "Bạn đã được gửi một thẻ Zcardon",
    "terms.desc.ending": "kết thúc bằng",
    "terms.desc.read_before": "Đọc thỏa thuận bên dưới, sau đó ký và xác nhận để kích hoạt thẻ.",
    "terms.already_accepted_note": "Bạn đã chấp thuận phiên bản trước. Chấp thuận lại để cập nhật phiên bản mới nhất.",
    "terms.scroll_hint": "Cuộn trong khung để đọc toàn bộ thỏa thuận.",
    "terms.view_json": "Mở dưới dạng JSON",
    "terms.sign_label": "Ký tên đầy đủ theo giấy tờ tùy thân",
    "terms.sign_placeholder": "Như trên giấy tờ chính phủ",
    "terms.consent": "Tôi đã đọc và đồng ý với Thỏa thuận chủ thẻ Zcardon v{v}.",
    "terms.btn_recording": "Đang ghi nhận…",
    "terms.btn_accept": "Chấp thuận thỏa thuận",

    // ─── Receive Hub
    "hub.eyebrow": "Trung tâm nhận của bạn",
    "hub.heading.all_done": "Bạn đã sẵn sàng 🎉",
    "hub.heading.greet": "Xin chào {name} 👋",
    "hub.fallback_name": "bạn",
    "hub.desc.sender_sent": "đã gửi cho bạn một thẻ Zcardon",
    "hub.desc.you_were_sent": "Bạn đã được gửi một thẻ Zcardon",
    "hub.desc.ending": "kết thúc bằng",
    "hub.desc.complete_steps": "Hoàn tất các bước bên dưới để kích hoạt mọi thứ.",
    "hub.progress.label": "Hoàn thành {done}/{total} bước",
    "hub.blocker.title_one": "Còn 1 bước bắt buộc",
    "hub.blocker.title_many": "Còn {n} bước bắt buộc",
    "hub.blocker.subtext": "Thẻ sẽ chưa được kích hoạt đầy đủ cho đến khi các bước này hoàn tất.",
    "hub.required_pill": "Bắt buộc",
    "hub.done_pill": "Xong",
    "hub.setup_btn": "Thiết lập",
    "hub.refresh": "Tải lại",
    "hub.loading": "Đang tải…",
    "hub.error.title": "Liên kết có vấn đề",
    "hub.error.default": "Không tải được trung tâm nhận của bạn.",
    "hub.back_home": "Về trang chủ",
    "hub.footer": "Đánh dấu trang này · còn hiệu lực 90 ngày · chỉ ai có liên kết mới xem được.",

    // ─── Payout Onboarding
    "onboarding.eyebrow": "Nhận khoản chuyển của bạn",
    "onboarding.title": "Xin chào {name}",
    "onboarding.fallback_name": "bạn",
    "onboarding.intro_via": "qua Zcardon",
    "onboarding.intro_ref_open": "(mã tham chiếu",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Thêm tài khoản ngân hàng {country} của bạn bên dưới để nhận tiền. Thông tin được gửi trực tiếp đến Stripe (đối tác thanh toán) — người gửi sẽ không nhìn thấy.",
    "onboarding.invalid.title": "Liên kết không hợp lệ",
    "onboarding.invalid.hint": "Vui lòng yêu cầu người gửi gửi một liên kết mới.",
    "onboarding.loading": "Đang tải…",
    "onboarding.complete.title": "Thông tin ngân hàng đã được lưu",
    "onboarding.complete.desc": "Bạn đã gửi thông tin tài khoản ngân hàng. Người gửi sẽ giải ngân ngay.",
    "onboarding.success.title": "Đã nhận thông tin ngân hàng ✓",
    "onboarding.success.auto_paid": "Cảm ơn — tài khoản ngân hàng của bạn đã được xác minh và tiền đã được chuyển đến ngân hàng. Sẽ về tài khoản trong 1-2 ngày làm việc.",
    "onboarding.success.pending": "Cảm ơn — tài khoản ngân hàng của bạn đã được xác minh. Người gửi sẽ được thông báo và tiền sẽ vào tài khoản của bạn trong 1-2 ngày làm việc.",
    "onboarding.success.ref": "Mã tham chiếu: {id}",
    "onboarding.beta.title": "Bản beta.",
    "onboarding.beta.body": "Trang này đã có nhưng pipeline chi trả đang chờ Stripe duyệt. Việc gửi sẽ trả về lỗi cho đến khi nền tảng được kích hoạt. Vui lòng quay lại sau hoặc liên hệ hỗ trợ nếu khẩn cấp.",
    "onboarding.select_placeholder": "Chọn…",
    "onboarding.submit_error_default": "Gửi không thành công. Vui lòng thử lại.",
    "onboarding.submit_busy": "Đang gửi…",
    "onboarding.submit": "Gửi thông tin ngân hàng →",
    "onboarding.secure_note": "Bảo mật bởi Stripe. Liên kết này hết hạn sau 7 ngày.",
    "onboarding.validate.required": "{field} là bắt buộc",
    "onboarding.validate.format": "Định dạng {field} không đúng (cần {pattern})",
    "onboarding.validate.no_schema": "Không có schema biểu mẫu",

    // ─── Agent Apply
    "apply.toast.invalid_image": "Vui lòng chọn ảnh JPEG, PNG hoặc WEBP",
    "apply.toast.too_large": "Ảnh phải nhỏ hơn hoặc bằng 6 MB",
    "apply.toast.upload_both": "Vui lòng tải lên cả hai mặt giấy phép lái xe",
    "apply.toast.invalid_ssn": "Vui lòng nhập Mã An sinh Xã hội hợp lệ gồm 9 chữ số",
    "apply.toast.password_too_short": "Mật khẩu phải có ít nhất 8 ký tự",
    "apply.toast.password_mismatch": "Hai mật khẩu không khớp",
    "apply.toast.failed": "Gửi không thành công",
    "apply.toast.submitted": "Đã gửi đơn đăng ký",
    "apply.success.title": "Đã nhận đơn đăng ký",
    "apply.success.desc": "Cảm ơn, {name}. Chúng tôi sẽ xem xét đơn đăng ký trong 2–3 ngày làm việc và gửi email cho bạn tại {email} khi được duyệt. Sau khi được duyệt, bạn có thể đăng nhập ngay bằng mật khẩu vừa chọn.",
    "apply.success.back_home": "Về trang chủ",
    "apply.success.contact_support": "Liên hệ hỗ trợ",
    "apply.eyebrow": "Chương trình đại lý",
    "apply.heading": "Đăng ký làm đại lý Zcardon",
    "apply.intro": "Kiếm hoa hồng 0,5% cho mỗi thẻ bạn giúp phát hành, mỗi lần nạp bạn thu, và mỗi khoản tiền mặt bạn nhận thay khách hàng. Chúng tôi tuyển đại lý được cộng đồng tin tưởng và có thể xác minh danh tính.",
    "apply.bullet1": "Mã đại lý riêng · theo dõi trên mọi giao dịch",
    "apply.bullet2": "Bảng điều khiển có tổng thu nhập tuần/tháng/năm",
    "apply.bullet3": "Trả thẳng ngân hàng — chi mỗi tuần",
    "apply.section.your_info": "Thông tin của bạn",
    "apply.section.choose_password": "Chọn mật khẩu đăng nhập",
    "apply.section.choose_password_helper": "Bạn sẽ dùng email + mật khẩu này để đăng nhập sau khi quản trị viên duyệt đơn (thường trong 2–3 ngày làm việc). Tối thiểu 8 ký tự · lưu dạng băm (bcrypt) — quản trị viên không nhìn thấy.",
    "apply.field.full_name": "Họ và tên đầy đủ",
    "apply.field.full_name_ph": "Đúng như trên giấy phép lái xe",
    "apply.field.email": "Email",
    "apply.field.phone": "Điện thoại",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Số giấy phép lái xe",
    "apply.field.ssn": "Mã An sinh Xã hội (SSN)",
    "apply.field.ssn_ph": "XXX-XX-XXXX",
    "apply.field.ssn_helper": "Bắt buộc theo quy định của Kho bạc Hoa Kỳ với đại lý thao tác tiền mặt · lưu trữ mã hóa · chỉ 4 chữ số cuối hiển thị trong giao diện quản trị.",
    "apply.field.address": "Địa chỉ",
    "apply.field.address_ph": "Đường, Thành phố, Bang, Mã ZIP",
    "apply.field.why_join_label": "Tại sao bạn muốn trở thành đại lý? (không bắt buộc)",
    "apply.field.why_join_ph": "Hãy chia sẻ về mối quan hệ cộng đồng hoặc kinh nghiệm chuyển tiền của bạn",
    "apply.field.password": "Mật khẩu",
    "apply.field.password_ph": "Chọn 8 ký tự trở lên",
    "apply.field.password_confirm": "Xác nhận mật khẩu",
    "apply.field.password_confirm_ph": "Nhập lại mật khẩu",
    "apply.dl.section": "Xác minh giấy phép lái xe",
    "apply.dl.helper": "Tải lên ảnh rõ ràng, đủ sáng của cả hai mặt. JPEG, PNG hoặc WEBP · tối đa 6 MB mỗi mặt. Ảnh được lưu riêng tư và chỉ quản trị viên Zcardon xem để xác minh.",
    "apply.dl.front": "Mặt trước GPLX",
    "apply.dl.back": "Mặt sau GPLX",
    "apply.dl.tap_upload": "Chạm để tải lên",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.ssn_card.section": "Thẻ An sinh xã hội",
    "apply.ssn_card.helper": "Tùy chọn — tải lên ảnh rõ nét của thẻ An sinh xã hội nếu bạn có. Giúp đẩy nhanh việc xác minh danh tính. JPEG, PNG, hoặc WEBP · tối đa 6 MB. Lưu trữ mã hóa và chỉ quản trị viên Zcardon xem được.",
    "apply.ssn_card.label": "Thẻ An sinh xã hội",
    "apply.sms_consent.label": "Tôi đồng ý nhận SMS giao dịch từ Zcardon (sản phẩm của Zeborg Water Solution Corp.) tại số điện thoại nêu trên — bao gồm mã xác thực một lần, cập nhật trạng thái đơn đăng ký và cảnh báo tài khoản. Có thể áp dụng cước tin nhắn và dữ liệu. Tần suất thay đổi. Trả lời STOP để hủy đăng ký, HELP để được trợ giúp. Bằng việc đánh dấu vào ô này, tôi cũng đồng ý với",
    "apply.sms_consent.policy_link": "Chính sách SMS",
    "apply.sms_consent.privacy_link": "Chính sách bảo mật",
    "apply.sms_consent.terms_link": "Điều khoản dịch vụ",
    "apply.sms_consent.and": "và",
    "apply.sms_consent.fineprint": "Đồng ý nhận SMS không phải là điều kiện để đăng ký trở thành đại lý Zcardon.",
    "apply.toast.sms_consent_required": "Vui lòng xác nhận ô đồng ý nhận SMS để tiếp tục.",
    "apply.cancel": "Hủy",
    "apply.submit_busy": "Đang gửi…",
    "apply.submit": "Gửi đơn đăng ký",

    // ─── Agent Portal
    "agent.toast.load_failed": "Không tải được dữ liệu đại lý",
    "agent.toast.cust_created": "Đã tạo khách hàng {name}",
    "agent.toast.cust_failed": "Không tạo được khách hàng",
    "agent.toast.card_issued": "Đã phát hành thẻ · bạn nhận {amount}",
    "agent.toast.card_issue_failed": "Không phát hành được thẻ",
    "agent.toast.cash_accepted": "Đã nhận tiền mặt · bạn nhận {amount}",
    "agent.toast.cash_failed": "Không ghi nhận được khoản tiền",
    "agent.loading": "Đang tải…",
    "agent.eyebrow": "Cổng đại lý",
    "agent.fallback_name": "Đại lý",
    "agent.code_line.code_label": "Mã đại lý",
    "agent.code_line.commission_label": "hoa hồng",
    "agent.earnings.label": "Số dư chưa thanh toán",
    "agent.earnings.sub": "{n} giao dịch chưa thanh toán",
    "agent.stat.week": "Tuần này",
    "agent.stat.month": "Tháng này",
    "agent.stat.year": "Năm nay",
    "agent.stat.txns": "{n} giao dịch",
    "agent.stat.customers": "Khách hàng của bạn",
    "agent.stat.active": "đang hoạt động",
    "agent.customers.title": "Khách hàng của bạn",
    "agent.customers.empty": "Chưa có khách hàng. Nhấn \"Đăng ký khách hàng\" để thêm khách đầu tiên.",
    "agent.customers.col_customer": "Khách hàng",
    "agent.customers.col_phone": "Điện thoại",
    "agent.customers.col_cards": "Thẻ",
    "agent.customers.col_commissions": "Hoa hồng đã nhận",
    "agent.customers.col_joined": "Tham gia",
    "agent.customers.col_actions": "Hành động",
    "agent.customers.txns": "{n} giao dịch",
    "agent.customers.no_cards": "Chưa có thẻ",
    "agent.customers.cards_more": "+{n} nữa",
    "agent.customers.card_open": "Nhấn để xem ảnh đầy đủ",
    "agent.customers.card_status.pending_payment": "Đang chờ thanh toán Stripe",
    "agent.customers.card_status.suspended": "Đã đình chỉ",
    "agent.customers.card_status.expired": "Đã hết hạn",
    "agent.customers.card_status.cancelled": "Đã hủy",
    "agent.customers.card_status_short.pending": "Chờ",
    "agent.customers.card_status_short.other": "—",
    "agent.toast.stripe_cancelled": "Thanh toán Stripe đã hủy — thẻ chưa được phát hành.",
    "agent.toast.stripe_card_active": "Thẻ đã kích hoạt! Người nhận đã được thông báo.",
    "agent.toast.stripe_card_pending": "Đã nhận thanh toán — Stripe đang hoàn tất. Thẻ sẽ kích hoạt ngay.",
    "agent.commissions.title": "Hoa hồng gần đây",
    "agent.commissions.empty": "Chưa có hoa hồng. Phát hành thẻ đầu tiên để nhận thưởng.",
    "agent.commissions.col_when": "Khi nào",
    "agent.commissions.col_receiver": "Người nhận",
    "agent.commissions.search_placeholder": "Tìm theo tên, email hoặc số điện thoại người nhận…",
    "agent.commissions.search_clear": "Xóa tìm kiếm",
    "agent.commissions.empty_search": "Không có dòng hoa hồng nào khớp với \u201c{q}\u201d.",
    "agent.commissions.col_customer": "Khách hàng",
    "agent.commissions.col_type": "Loại",
    "agent.commissions.col_txn": "Giao dịch",
    "agent.commissions.col_commission": "Hoa hồng",
    "agent.commissions.col_status": "Trạng thái",
    "agent.commissions.col_actions": "Hành động",
    "agent.commissions.paid": "đã thanh toán",
    "agent.commissions.unpaid": "chưa thanh toán",
    "agent.commissions.total_label": "Tổng · {n} dòng",
    "agent.commissions.delete_title": "Xóa dòng hoa hồng này (ẩn khỏi báo cáo, vẫn lưu để đối soát)",
    "agent.commissions.delete_confirm": "Xóa hoa hồng {amount}? Dòng này sẽ bị ẩn khỏi báo cáo của bạn.",
    "agent.commissions.delete_success": "Đã xóa dòng hoa hồng.",
    "agent.commissions.delete_failed": "Không thể xóa hoa hồng.",
    "agent.commissions.cannot_delete_paid": "Chỉ quản trị viên mới có thể xóa hoa hồng đã thanh toán.",
    "agent.commissions.cannot_delete_paid_short": "Chỉ quản trị viên mới xóa được dòng đã thanh toán",
    "agent.type.issuance": "Phát hành",
    "agent.type.topup": "Nạp",
    "agent.type.cash_deposit": "Nộp tiền mặt",
    "agent.register_btn": "Đăng ký khách hàng",
    "agent.dialog.new_cust.title": "Khách hàng mới",
    "agent.dialog.new_cust.desc": "Sẽ được liên kết với mã đại lý của bạn. Mỗi thẻ bạn phát hành thay họ kiếm cho bạn 0,5% hoa hồng.",
    "agent.dialog.field.full_name": "Họ và tên",
    "agent.dialog.field.email": "Email",
    "agent.dialog.field.phone": "Điện thoại",
    "agent.dialog.field.password": "Mật khẩu ban đầu (chia sẻ với khách hàng)",
    "agent.dialog.cancel": "Hủy",
    "agent.dialog.creating": "Đang tạo…",
    "agent.dialog.create_customer": "Tạo khách hàng",
    "agent.dialog.issue_card.title": "Phát hành thẻ cho {name}",
    "agent.dialog.issue_card.title_no_customer": "Phát hành thẻ ảo",
    "agent.issue.title": "Phát hành thẻ",
    "agent.issue.subtitle": "Nhập tên, email, số điện thoại của người nhận và số tiền. Thanh toán bằng tiền mặt thu từ người nhận hoặc bằng thẻ tín dụng/ghi nợ qua Stripe.",
    "agent.issue.cta": "Phát hành thẻ",
    "agent.dialog.issue_card.desc": "Vốn từ tiền mặt thu tận tay. Khách trả phí 3,75% — bạn giữ 0,5%.",
    "agent.dialog.field.receiver_name": "Tên người nhận",
    "agent.dialog.field.receiver_email": "Email người nhận",
    "agent.dialog.field.receiver_phone": "Điện thoại người nhận",
    "agent.dialog.field.amount_usd": "Số tiền (USD)",
    "agent.dialog.field.note": "Ghi chú (tùy chọn)",
    "agent.dialog.issuing": "Đang phát hành…",
    "agent.dialog.issue_card_btn": "Phát hành thẻ",
    "agent.dialog.redirecting": "Đang chuyển sang Stripe…",
    "agent.dialog.continue_to_stripe": "Tiếp tục đến Stripe Checkout",
    "agent.dialog.field.payment_mode": "Khách hàng thanh toán bằng cách nào?",
    "agent.dialog.mode.cash": "Đã thu tiền mặt",
    "agent.dialog.mode.cash_help": "Tôi đã nhận tiền mặt từ khách hàng. Kích hoạt thẻ ngay lập tức.",
    "agent.dialog.mode.card": "Thanh toán bằng thẻ",
    "agent.dialog.mode.card_help": "Chuyển sang Stripe Checkout để khách hàng nhập thẻ tín dụng / ghi nợ. Thẻ sẽ kích hoạt sau khi thanh toán thành công.",
    "agent.toast.redirecting_to_stripe": "Đang chuyển sang Stripe Checkout…",
    "agent.commissions.print_btn": "In",
    "agent.commissions.print_title": "In các dòng hoa hồng đang hiển thị (theo bộ lọc tìm kiếm)",
    "agent.commissions.open_receiver_title": "Nhấn để xem hồ sơ người nhận đầy đủ (thẻ, hoa hồng, liên hệ)",
    "agent.receiver.loading": "Đang tải người nhận…",
    "agent.receiver.desc": "Hồ sơ người nhận đầy đủ",
    "agent.receiver.load_failed": "Không thể tải hồ sơ người nhận.",
    "agent.receiver.field.email": "Email",
    "agent.receiver.field.phone": "Điện thoại",
    "agent.receiver.field.first_seen": "Lần đầu thấy",
    "agent.receiver.kpi.cards": "Thẻ",
    "agent.receiver.kpi.active": "đang hoạt động",
    "agent.receiver.kpi.balance": "Tổng số dư",
    "agent.receiver.kpi.funded": "Tổng nạp",
    "agent.receiver.kpi.commission": "Hoa hồng đã kiếm",
    "agent.receiver.cards_h": "Thẻ ({n})",
    "agent.receiver.commissions_h": "Hoa hồng ({n})",
    "agent.receiver.no_cards": "Người nhận này chưa có thẻ nào.",
    "agent.receiver.no_commissions": "Người nhận này chưa có hoa hồng nào.",
    "agent.receiver.open_full": "Mở ảnh thẻ đầy đủ",
    "agent.receiver.close": "Đóng",
    "agent.receiver.print": "In báo cáo người nhận",
    "agent.receiver.col_when": "Khi nào",
    "agent.receiver.col_type": "Loại",
    "agent.receiver.col_amount": "Giao dịch $",
    "agent.receiver.col_commission": "Hoa hồng",
    "agent.receiver.col_status": "Trạng thái",
    "agent.commissions.print_popup_blocked": "Cửa sổ bật lên bị chặn — trang in đã mở trong tab mới qua liên kết dự phòng.",
    "agent.actions.issue_card": "Phát hành thẻ",
    "agent.dialog.cash_deposit.title": "Nộp tiền mặt cho {name}",
    "agent.dialog.cash_deposit.desc": "Tiền mặt thu từ khách · tạo thẻ giao cho người nhận.",
    "agent.dialog.field.receiver_name_opt": "Tên người nhận (tùy chọn)",
    "agent.dialog.recording": "Đang ghi nhận…",
    "agent.dialog.record_deposit": "Ghi nhận khoản tiền",
    "agent.actions.cash_deposit": "Nộp tiền mặt",

  },
  // ─── AUTO-GENERATED TRANSLATIONS (do not edit; regenerate via backend/scripts/translate_dict.py) ───
  ar: {
    "header.nav.home": "الرئيسية",
    "header.nav.rates": "الأسعار",
    "header.nav.support": "الدعم",
    "header.nav.admin": "الإدارة",
    "header.nav.signin": "تسجيل الدخول",
    "header.nav.signout": "تسجيل الخروج",
    "header.nav.get_started": "ابدأ الآن",
    "header.nav.account": "الحساب",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "تغيير اللغة",
    "landing.hero.eyebrow": "مسار جديد للإنفاق الرقمي",
    "landing.hero.title_a": "أضف رصيدًا لبطاقة افتراضية.",
    "landing.hero.title_b_ship": "أرسلها",
    "landing.hero.title_b_rest": "إلى أي شخص،",
    "landing.hero.title_c": "جاهزة للإنفاق بـ",
    "landing.hero.subtitle": "أصدر بطاقة خصم افتراضية بتقنية اللمس للدفع مع رمز QR قابل للمسح. مدعومة بـ Stripe، رصيد بالدولار الأمريكي، رسوم خدمة وعبور شفافة — تُسلَّم لأي مستلم عبر البريد الإلكتروني أو التنزيل.",
    "landing.hero.cta_start": "ابدأ إصدار البطاقات",
    "landing.hero.cta_request_cash": "اطلب بطاقة نقدية",
    "landing.hero.cta_rates": "عرض الأسعار",
    "landing.hero.cta_find_card": "البحث عن بطاقتي",
    "landing.hero.stat_currency": "العملة",
    "landing.hero.stat_settle": "وقت التسوية",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "رسوم الخدمة",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "مبني لاقتصاد البطاقات الحديث",
    "landing.hero.live_pill": "مباشر",
    "landing.hero.live_pill_text": "التسوية بالدولار الأمريكي",
    "landing.hero.marquee": "أصدر · أضف رصيدًا · أنفق بالدولار",
    "landing.demo.eyebrow": "شاهده في 20 ثانية",
    "landing.demo.title_a": "من الإصدار إلى",
    "landing.demo.title_b_spend": "الإنفاق",
    "landing.demo.title_c": ". في أربع نقرات.",
    "landing.demo.step_issue.label": "إصدار بطاقة",
    "landing.demo.step_issue.detail": "اختر المبلغ. البطاقة جاهزة خلال ~دقيقتين.",
    "landing.demo.step_share.label": "شاركها",
    "landing.demo.step_share.detail": "بريد إلكتروني، رسالة، WhatsApp، أو تنزيل بصيغة PNG.",
    "landing.demo.step_scan.label": "المستلم يمسح الرمز",
    "landing.demo.step_scan.detail": "كاميرا الهاتف تفتح صفحة التتبع فورًا.",
    "landing.demo.step_spend.label": "الإنفاق بالدولار",
    "landing.demo.step_spend.detail": "ادفع بلمسة، أو اسحب نقدًا، أو أنفق لدى التجار.",
    "landing.demo.cta_try": "جرّبه الآن",
    "landing.demo.live_pill": "عرض حي",
    "landing.demo.scene1_caption": "جارٍ إنشاء البطاقة · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "إرسال إلى المستلم عبر WhatsApp",
    "landing.demo.scene3_caption": "مسح رمز QR · تم العثور على تطابق",
    "landing.demo.scene4_eyebrow": "تم إضافة الرصيد",
    "landing.demo.scene4_status": "✓ مقبولة · جاهزة للدفع بلمسة",
    "landing.footer.rates": "الأسعار",
    "landing.footer.support": "الدعم",
    "landing.footer.admin_portal": "بوابة الإدارة",
    "landing.footer.copy": "© 2026 Zcardon · منتج فينتك تجريبي",
    "landing.cash.dialog_title": "طلب بطاقة نقدية",
    "landing.cash.dialog_desc": "سنرسل لك رابط دفع Stripe عبر الرسائل. ادفع بأي بطاقة؛ وستُموَّل البطاقة النقدية فور تأكيد Stripe.",
    "landing.cash.phone_label": "رقم الهاتف",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "المبلغ (USD)",
    "landing.cash.name_label": "اسمك (اختياري)",
    "landing.cash.submit": "إرسال رابط الدفع",
    "landing.cash.submitting": "جارٍ الإرسال…",
    "landing.cash.success_title": "رابط الدفع في الطريق",
    "landing.cash.success_body": "أرسلنا رابط دفع Stripe إلى {phone}. اضغط عليه للدفع وستُفعَّل بطاقتك الافتراضية فورًا.",
    "landing.cash.success_open_checkout": "فتح صفحة الدفع الآن",
    "landing.find.cta": "دفعت بالفعل؟ ابحث عن بطاقتي",
    "landing.find.dialog_title": "البحث عن بطاقتي",
    "landing.find.dialog_desc": "أدخل رقم الهاتف الذي استخدمته. سنرسل لك رمزًا مكوّنًا من 6 أرقام لاسترداد أي بطاقة نقدية نشطة مسجّلة.",
    "landing.find.phone_label": "رقم الهاتف",
    "landing.find.code_label": "رمز مكوّن من 6 أرقام",
    "landing.find.send_code": "إرسال الرمز",
    "landing.find.verify": "التحقق والبحث",
    "landing.find.sending": "جارٍ الإرسال…",
    "landing.find.verifying": "جارٍ التحقق…",
    "landing.find.no_cards": "لا توجد بطاقات نشطة لهذا الرقم.",
    "login.eyebrow.welcome": "مرحبًا",
    "login.eyebrow.admin": "الإدارة",
    "login.title": "تسجيل الدخول",
    "login.title_admin": "دخول المسؤول",
    "login.admin_badge": "بوابة الإدارة",
    "login.email_label": "البريد الإلكتروني",
    "login.password_label": "كلمة المرور",
    "login.submit": "تسجيل الدخول",
    "login.submitting": "جارٍ تسجيل الدخول…",
    "login.toast_welcome": "أهلاً بعودتك، {name}",
    "login.toast_failed": "فشل تسجيل الدخول",
    "login.gate_title": "تسجيل دخول العملاء متوقف مؤقتاً",
    "login.gate_default_message": "نحن نُجري تحسينات على المنصة. يرجى العودة قريباً.",
    "login.gate_admin_note": "يمكن للمسؤولين والمديرين تسجيل الدخول أدناه.",
    "login.new_here": "جديد هنا؟",
    "login.create_account": "إنشاء حساب",
    "login.customer_q": "عميل؟",
    "login.regular_signin": "تسجيل الدخول العادي",
    "login.demo_title": "حسابات تجريبية",
    "login.demo_customers_note": "العملاء: سجّل عبر /register أو أنشئ من لوحة المسؤول",
    "login.forgot_password": "نسيت كلمة المرور؟",
    "forgot.eyebrow": "استرداد الحساب",
    "forgot.title": "إعادة تعيين كلمة المرور",
    "forgot.subtitle": "أدخل البريد الإلكتروني المرتبط بحساب Zcardon الخاص بك وسنرسل لك رابطاً آمناً لاختيار كلمة مرور جديدة.",
    "forgot.email_label": "البريد الإلكتروني",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "إرسال رابط الإعادة",
    "forgot.submitting": "جارٍ الإرسال…",
    "forgot.toast_failed": "تعذّر إرسال رابط الإعادة. يرجى المحاولة مجدداً.",
    "forgot.sent_title": "تحقق من بريدك الوارد",
    "forgot.sent_message": "إذا كان ثمة حساب مرتبط بـ {email}، فقد أرسلنا رابط إعادة تعيين كلمة المرور.",
    "forgot.sent_expiry_note": "الرابط صالح لمدة 15 دقيقة. لم تستلمه؟ تحقق من البريد المزعج أو أرسل رابطاً آخر.",
    "forgot.back_to_login": "العودة إلى تسجيل الدخول",
    "forgot.resend_link": "إرسال رابط آخر",
    "forgot.remember_q": "تتذكر كلمة مرورك؟",
    "forgot.signin_link": "تسجيل الدخول",
    "reset.eyebrow": "استرداد الحساب",
    "reset.title": "اختر كلمة مرور جديدة",
    "reset.subtitle": "اختر كلمة مرور قوية لم تستخدمها من قبل. سيتم تسجيل دخولك تلقائياً بعد تعيينها.",
    "reset.verifying": "جارٍ التحقق من رابط الإعادة…",
    "reset.invalid_title": "رابط الإعادة غير صالح أو منتهي الصلاحية",
    "reset.invalid_message": "روابط الإعادة صالحة لمدة 15 دقيقة وتُستخدم مرة واحدة فقط. اطلب رابطاً جديداً للمتابعة.",
    "reset.request_new_link": "طلب رابط إعادة جديد",
    "reset.new_password_label": "كلمة المرور الجديدة",
    "reset.confirm_password_label": "تأكيد كلمة المرور",
    "reset.password_hint": "8 أحرف على الأقل. تجنب كلمات المرور المستخدمة في مواضع أخرى.",
    "reset.submit": "تحديث كلمة المرور",
    "reset.submitting": "جارٍ التحديث…",
    "reset.toast_success": "تم تحديث كلمة المرور. أنت مسجّل الدخول.",
    "reset.toast_failed": "تعذّر إعادة تعيين كلمة المرور. يرجى المحاولة مجدداً.",
    "reset.error_min_length": "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.",
    "reset.error_mismatch": "كلمتا المرور غير متطابقتين.",
    "register.eyebrow": "انضم",
    "register.title": "إنشاء حساب",
    "register.full_name_label": "الاسم الكامل",
    "register.email_label": "البريد الإلكتروني",
    "register.password_label": "كلمة المرور",
    "register.submit": "إنشاء حساب",
    "register.submitting": "جارٍ الإنشاء…",
    "register.gate_title": "التسجيل متوقف مؤقتاً",
    "register.gate_default_message": "نحن نُجري تحسينات على المنصة. يرجى العودة قريباً.",
    "register.gate_already_member": "هل لديك حساب بالفعل؟",
    "register.toast_created": "تم إنشاء الحساب",
    "register.toast_failed": "فشل التسجيل",
    "register.already_member": "عضو بالفعل؟",
    "register.signin_link": "تسجيل الدخول",
    "dash.eyebrow": "لوحة التحكم",
    "dash.greeting": "أهلاً بعودتك، {name}",
    "dash.subtitle": "أصدر بطاقة افتراضية أو أرسل إيداعاً بنكياً — مركز التحكم الخاص بك أدناه.",
    "dash.action.issue_card": "إصدار بطاقة",
    "dash.action.direct_deposit": "الإيداع المباشر (تحويل بنكي)",
    "dash.action.support": "الدعم",
    "dash.section.cards": "بطاقاتك",
    "dash.section.activity": "النشاط الأخير",
    "dash.section.deposits": "الإيداعات المباشرة",
    "dash.empty.cards": "لا توجد بطاقات بعد — أصدر أولى بطاقاتك للبدء.",
    "dash.empty.activity": "بمجرد البدء في إصدار البطاقات أو إرسال التحويلات، سيظهر النشاط الأخير هنا.",
    "ref.eyebrow": "مرحباً بك في Zcardon",
    "ref.greeting": "أحالك {name}.",
    "ref.loading": "جارٍ تحميل دعوتك…",
    "ref.resolved_redirect": "لحظة واحدة — جارٍ توجيهك.",
    "ref.resolving": "جارٍ معالجة رابط دعوتك…",
    "support.eyebrow": "هنا من أجلك",
    "support.title": "دعم حقيقي، لا تذاكر.",
    "support.subtitle": "تحدث إلى إنسان حقيقي. ردود بالبريد الإلكتروني خلال ساعة عمل واحدة أيام الأسبوع.",
    "support.card.email": "البريد الإلكتروني",
    "support.card.phone": "الهاتف",
    "support.card.live_chat": "الدردشة المباشرة",
    "support.card.live_chat_value": "في لوحة التحكم",
    "support.card.hours": "ساعات العمل",
    "support.faq.title": "الأسئلة الشائعة",
    "support.faq.q1": "كم يستغرق وصول البطاقة إلى المستلم؟",
    "support.faq.a1": "عادةً في غضون دقيقتين من نجاح التمويل. يتم التسليم تلقائياً عبر البريد الإلكتروني (Resend) والرسائل المصورة MMS (Twilio).",
    "support.faq.q2": "هل يمكنني إلغاء البطاقة أو تجميدها بعد الإرسال؟",
    "support.faq.a2": "نعم — افتح صفحة تفاصيل البطاقة واضغط على تجميد. يمكنك أيضاً التجميد مباشرةً من رسالة التنبيه الواردة إلى المستلم.",
    "support.faq.q3": "بأي عملات يمكن استخدام البطاقة؟",
    "support.faq.a3": "تتم تسوية جميع البطاقات بالدولار USD. يمكن للمستلم الإنفاق في أي مكان يُقبل فيه Visa/Mastercard — يتولى شبكة البطاقة معالجة تحويل العملات.",
    "support.faq.q4": "ما هي الرسوم المطبّقة؟",
    "support.faq.a4": "رسوم خدمة شفافة بالإضافة إلى تكلفة بوابة Stripe. اطّلع على السعر الفوري في صفحة الأسعار قبل الدفع.",
    "rates.eyebrow": "شفافية تامة",
    "rates.title_a": "لا رسوم",
    "rates.title_b": "مخفية",
    "rates.subtitle": "تطّلع على رسوم الخدمة وتكلفة البوابة قبل الدفع. يُضاف المبلغ الأصلي دائماً إلى رصيد البطاقة الافتراضية بالدولار USD.",
    "rates.row.service_fee": "رسوم الخدمة",
    "rates.row.service_fee_value": "{pct} من المبلغ المرسل",
    "rates.row.gateway": "البوابة (Stripe)",
    "rates.row.settlement": "التسوية",
    "rates.row.settlement_value": "رصيد USD على البطاقة الافتراضية",
    "rates.row.topup": "طرق الشحن",
    "rates.row.topup_value": "بطاقة · بطاقة خصم · بنك (ACH)",
    "rates.sim.eyebrow": "سعر فوري",
    "rates.sim.title_a": "أرسل أي مبلغ،",
    "rates.sim.title_b": "واطّلع على التفاصيل فوراً.",
    "rates.sim.amount_label": "المبلغ المراد إرساله",
    "rates.sim.line.principal": "على البطاقة (المبلغ الأصلي)",
    "rates.sim.line.service": "رسوم الخدمة · {pct}",
    "rates.sim.line.gateway": "البوابة · {pct} + {fixed}",
    "rates.sim.line.you_pay": "إجمالي ما تدفعه",
    "rates.sim.cta": "ابدأ بـ ${amount} →",
    "rates.sim.note": "الأسعار المطبّقة أعلاه يضبطها المسؤول. يحصل المستلم دائماً على المبلغ الأصلي كاملاً.",
    "dd.title": "الإيداع المباشر",
    "dd.intro": "أرسل أموالاً إلى حساب بنكي للمستلم في أي مكان بالعالم. ادفع بالبطاقة (Stripe Checkout) أو من رصيد Zcardon — نتولى الحوالة إلى بنك المستلم تلقائياً حيثما كان مدعوماً، أو يدوياً للدول غير المدعومة بعد. كل معاملة مسجّلة مع إيصال قابل للطباعة.",
    "dd.new_btn": "تسجيل إيداع مباشر جديد",
    "dd.close": "إغلاق",
    "dd.history_eyebrow": "إيداعاتك",
    "dd.loading": "جارٍ التحميل…",
    "dd.empty": "لا توجد إيداعات مباشرة بعد.",
    "dd.status.approved": "مقبول",
    "dd.status.pending": "في انتظار الموافقة",
    "dd.status.rejected": "مرفوض",
    "dd.row.confirmed": "✓ أكّد المستلم الاستلام",
    "dd.row.disputed": "⚠ أبلغ المستلم عن تناقض",
    "dd.row.pdf": "PDF",
    "dd.error.load": "تعذّر تحميل الإيداعات",
    "dd.error.pdf": "تعذّر تنزيل الإيصال",
    "pay.mode.view": "بطاقة Zcardon الخاصة بك",
    "pay.mode.charge": "الدفع بالرمز QR",
    "pay.heading.view": "مرحباً، {name}.",
    "pay.heading.charge": "احتسب هذه البطاقة.",
    "pay.cardholder": "حامل البطاقة",
    "pay.paused.label": "البطاقة موقوفة",
    "pay.paused.body": "تم تجميد هذه البطاقة ولا يمكنها قبول معاملات جديدة حتى يستأنفها المالك.",
    "pay.balance.label": "الرصيد المتاح",
    "pay.charges_count": "{count} معاملة",
    "pay.charges_count_plural": "{count} معاملات",
    "pay.topups_count": "{count} شحن",
    "pay.topups_count_plural": "{count} عمليات شحن",
    "pay.activity.label": "النشاط الأخير",
    "pay.activity.empty": "لا يوجد نشاط بعد",
    "pay.cta.charge": "احتسب هذه البطاقة (التاجر)",
    "pay.cta.charge_paused": "البطاقة موقوفة",
    "pay.cta.note_active": "اضغط أعلاه فقط إذا كنت التاجر المستلم للدفع من هذه البطاقة.",
    "pay.cta.note_paused": "تم إيقاف هذه البطاقة من قبل المرسل. اطلب منه استئنافها.",
    "pay.back_to_balance": "العودة إلى الرصيد",
    "pay.form.amount_label": "المبلغ (USD)",
    "pay.form.amount_max": "الحد الأقصى {amount}",
    "pay.form.merchant_label": "التاجر / السبب",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "ملاحظة (اختياري)",
    "pay.form.memo_placeholder": "لاتيه مزدوج",
    "pay.form.submit_processing": "جارٍ المعالجة…",
    "pay.form.submit_paused": "البطاقة موقوفة",
    "pay.form.submit_charge": "احتساب {amount}",
    "pay.form.submit_charge_card": "احتساب البطاقة",
    "pay.form.disclaimer": "باحتساب هذه البطاقة، تؤكد أنك مفوّض من حامل البطاقة بإنفاق المبلغ المذكور أعلاه.",
    "pay.receipt.title": "اكتملت عملية الدفع",
    "pay.receipt.to": "إلى",
    "pay.receipt.id": "الإيصال",
    "pay.receipt.time": "الوقت",
    "pay.receipt.new_balance": "الرصيد الجديد",
    "pay.receipt.done": "تم · العودة إلى الرصيد",
    "pay.error.cant_load": "تعذّر تحميل هذه البطاقة",
    "pay.error.back_home": "العودة للرئيسية",
    "pay.error.loading": "جارٍ تحميل البطاقة…",
    "pay.error.charge_failed": "فشلت عملية الخصم",
    "pay.tap.eyebrow": "انقر للدفع",
    "pay.tap.title": "اعرض هذا الرمز",
    "pay.tap.subtitle": "على ماسح التاجر",
    "pay.tap.balance": "الرصيد",
    "pay.tap.paused": "موقوف",
    "pay.tap.qr_unavailable": "رمز QR غير متاح لهذه البطاقة",
    "pay.tap.refresh_in": "يتجدد خلال {secs}ث",
    "pay.tap.show_digits": "إظهار الأرقام",
    "pay.tap.hide_digits": "إخفاء الأرقام",
    "pay.tap.card_label": "البطاقة · ••••",
    "pay.tap.auto_hides": "يُخفى تلقائيًا خلال 60 ثانية",
    "pay.tap.full_only_image": "الرقم الكامل يظهر فقط على صورة البطاقة الأصلية",
    "pay.tap.disclaimer": "بطاقة ذات حلقة مغلقة · قابلة للإنفاق لدى أي تاجر يدعم Zcardon. يتجدد رمز QR كل 60 ثانية للحماية من إعادة التشغيل.",
    "pay.subscribe.eyebrow": "ابقَ على اطلاع",
    "pay.subscribe.title": "احصل على بريد إلكتروني مع كل معاملة",
    "pay.subscribe.desc": "مجاني. رسالة واحدة لكل خصم أو شحن. إلغاء الاشتراك في أي وقت. لن نشارك عنوانك أبدًا.",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "أخبرني",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "أنت مشترك الآن",
    "pay.subscribe.done.body": "سنرسل بريدًا إلكترونيًا إلى {email} فورًا مع كل خصم أو شحن للبطاقة •••• {last4}.",
    "pay.subscribe.toast.already": "كنت مشتركًا بالفعل — لم يُرسَل أي تكرار.",
    "pay.subscribe.toast.success": "ستتلقى بريدًا إلكترونيًا مع كل معاملة. أهلًا بك!",
    "pay.subscribe.toast.failed": "فشل الاشتراك",
    "pay.save.eyebrow": "احمل بطاقتك",
    "pay.save.title": "احفظ هذه البطاقة على هاتفك",
    "pay.save.desc": "احفظ صورة البطاقة في معرض صورك للوصول دون إنترنت — اعرض رمز QR للتجار في أي وقت، دون الحاجة لتطبيق.",
    "pay.save.cta": "حفظ صورة البطاقة",
    "pay.save.preparing": "جارٍ التحضير…",
    "pay.save.add_home": "إضافة إلى الشاشة الرئيسية",
    "pay.save.toast.opened": "تم فتح صورة البطاقة — اضغط مطولًا لحفظها في الصور",
    "pay.save.toast.failed": "تعذّر حفظ البطاقة",
    "pay.save.ios.title": "لإضافة Zcardon إلى شاشة iPhone الرئيسية:",
    "pay.save.ios.step1": "انقر على زر المشاركة في أسفل Safari",
    "pay.save.ios.step2": "مرر للأسفل وانقر على إضافة إلى الشاشة الرئيسية",
    "pay.save.ios.step3": "انقر على إضافة للتأكيد",
    "pay.save.ios.note": "سيظهر Zcardon كأي تطبيق آخر — انقر على الأيقونة لفتح هذه البطاقة فورًا.",
    "pay.thanks.eyebrow": "أرسل شكرًا",
    "pay.thanks.body": "وصل ${amount} للتو — أخبر من شحن هذه البطاقة بأنها أفادت.",
    "pay.thanks.placeholder": "اختياري — قل شيئًا…",
    "pay.thanks.cta": "إرسال شكر {emoji}",
    "pay.thanks.sending": "جارٍ الإرسال…",
    "pay.thanks.done": "تم إرسال الشكر — سيراه خلال ثوانٍ.",
    "pay.thanks.toast.sent": "تم إرسال الشكر",
    "pay.thanks.toast.failed": "تعذّر إرسال الشكر",
    "pay.activity.topup_bank": "شحن بتحويل بنكي",
    "pay.activity.topup_debit": "شحن ببطاقة مدين",
    "pay.activity.topup_credit": "شحن ببطاقة ائتمان",
    "pay.activity.topup_crypto": "شحن بالعملات المشفرة",
    "pay.activity.topup_received": "تم استلام الشحن",
    "pay.activity.merchant_charge": "خصم من التاجر",
    "pay.toast.payment_received": "تم استلام الدفعة · −{amount}",
    "cash.confirming": "جارٍ تأكيد الدفع…",
    "cash.confirming.sub": "Stripe يُنهي عملية الخصم.",
    "cash.notfound.title": "انتهت صلاحية الرابط",
    "cash.notfound.body": "تعذّر العثور على طلب الكاش هذا. قد يكون الرابط من جلسة قديمة، أو لم تُكتمل البطاقة. ابدأ طلبًا جديدًا — لن يستغرق سوى دقيقة، وأي دفعة أجريتها بالفعل ستُضاف تلقائيًا حين يعالجها النظام.",
    "cash.notfound.ref": "المرجع:",
    "cash.notfound.start": "بدء طلب جديد",
    "cash.notfound.contact": "التواصل مع الدعم",
    "cash.error.title": "تعذّر التحقق من الحالة",
    "cash.error.return": "العودة للرئيسية",
    "cash.autopay.opening": "جارٍ فتح شاشة الدفع بالنقر…",
    "cash.autopay.redirect": "إعادة التوجيه خلال {secs}ث.",
    "cash.autopay.stay": "ابقَ هنا بدلًا من ذلك",
    "cash.ready.title": "بطاقتك جاهزة",
    "cash.ready.subtitle": "تم شحن ${amount} USD · أُرسلت أيضًا إلى {phone}",
    "cash.card.label": "البطاقة",
    "cash.card.exp": "انتهاء الصلاحية",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "الرصيد",
    "cash.cta.open_pay": "فتح شاشة الدفع بالنقر",
    "cash.toast.funded": "تم تمويل بطاقتك — تحقق من هاتفك للرسالة النصية!",
    "cash.pending.title": "في انتظار Stripe",
    "cash.pending.body": "نقوم بالاستعلام عن التأكيد. يستغرق هذا عادةً 5-15 ثانية. إذا أغلقت صفحة الدفع دون سداد، يمكنك البدء من جديد من الصفحة الرئيسية.",
    "cash.pending.back": "العودة إلى الرئيسية",
    "cash.timeout.title": "تعذّر تأكيد الدفع",
    "cash.timeout.body": "انتظرنا 3 دقائق حتى يؤكد Stripe. في الغالب يعني ذلك أن صفحة الدفع أُغلقت قبل السداد — لكن إذا كنت قد دفعت فعلاً، ستُفعَّل بطاقتك تلقائياً خلال الدقائق القليلة القادمة (يعمل المعالج الخلفي كل 5 دقائق) وسنرسله إلى {phone} فور حدوث ذلك.",
    "cash.timeout.start_over": "ابدأ من جديد",
    "cash.timeout.try_again": "حاول مجدداً",
    "cash.resend.title": "لم تصلك؟ أرسل مجدداً",
    "cash.resend.degraded.lead": "التسليم إلى أرقام الولايات المتحدة متعطّل حالياً.",
    "cash.resend.degraded.sms": "قد تُحجب الرسائل النصية إلى هواتف الولايات المتحدة بصمت من قِبل مزوّد الخدمة (التحقق من الرقم المجاني لا يزال معلقاً).",
    "cash.resend.degraded.email": "البريد الإلكتروني في وضع الاختبار ولا يُرسَل إلا إلى عنوان صاحب الحساب ريثما يُتحقق من DNS.",
    "cash.resend.degraded.tail": "إذا لم تنجح إحدى القنوات، جرّب الأخرى — أو افتح هذه الصفحة على جهاز المستلم لأخذ البطاقة مباشرةً.",
    "cash.resend.text": "رسالة نصية",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "بريد إلكتروني",
    "cash.resend.sending": "جارٍ الإرسال…",
    "cash.resend.email_sandbox": "البريد الإلكتروني في وضع الاختبار. ريثما يُتحقق من نطاق zcardon.com في Resend، لا يمكننا الإرسال إلا إلى عنوان صاحب الحساب. إذا لم يُقبَل بريدك، استخدم الرسالة النصية أو WhatsApp.",
    "cash.resend.email_label": "عنوان البريد الإلكتروني",
    "cash.resend.email_send": "إرسال إلى البريد الإلكتروني",
    "cash.resend.cancel": "إلغاء",
    "cash.resend.limit": "الحد الأقصى 5 إعادة إرسال لكل بطاقة. الهاتف المسجّل: {phone}",
    "cash.resend.no_phone": "(لا يوجد)",
    "cash.wallet.title": "إضافة إلى المحفظة",
    "cash.wallet.apple": "إضافة إلى Apple Wallet",
    "cash.wallet.google": "إضافة إلى Google Wallet",
    "cash.wallet.generating": "جارٍ إنشاء البطاقة…",
    "cash.wallet.note": "تبقى على شاشة القفل · تتحدث تلقائياً عند تغيّر الرصيد",
    "cash.wallet.toast.apple": "تم تنزيل البطاقة — افتحها على iPhone لإضافتها إلى Apple Wallet",
    "cash.wallet.toast.apple_failed": "تعذّر إنشاء بطاقة Apple Wallet",
    "cash.wallet.toast.google_failed": "تعذّر إنشاء بطاقة Google Wallet",
    "cash.delivery.fallback_title": "أُرسلت عبر {fallback}",
    "cash.delivery.fallback_body": "حجب مزوّد خدمتك الرسالة النصية الأصلية، لذا أعدنا إرسال بطاقتك عبر {fallback} إلى {phone}. تحقق من تلك القناة.",
    "cash.delivery.toll_blocked": "حجب مزوّد الخدمة الرسالة النصية",
    "cash.delivery.failed_title": "تعذّر إرسال بطاقتك بالرسالة النصية",
    "cash.delivery.toll_body": "رفض مزوّد خدمتك الرسالة (يحدث هذا مع المُرسِلين الجدد في انتظار التحقق). احفظ بطاقتك الآن: اضغط",
    "cash.delivery.failed_body": "تم تمويل بطاقتك — احفظها الآن إذ لم تصل الرسالة النصية. اضغط",
    "cash.delivery.cta_label": "فتح شاشة الدفع باللمس",
    "cash.delivery.body_tail": "أعلاه، أو التقط لقطة شاشة، أو استخدم البريد الإلكتروني أدناه لإرسال نسخة لنفسك.",
    "cash.handoff.eyebrow": "تسليم شخصياً",
    "cash.handoff.body": "اطلب من المستلم مسح هذا بالكاميرا — ستُفتح البطاقة مباشرةً على هاتفه دون الحاجة إلى رسالة نصية أو بريد إلكتروني.",
    "cash.handoff.share_label": "أو شارك هذا الرابط",
    "cash.handoff.copy": "نسخ الرابط",
    "cash.handoff.copied": "✓ تم النسخ",
    "cash.handoff.share": "مشاركة",
    "cash.handoff.toast.copied": "تم نسخ الرابط — الصقه في أي مكان",
    "cash.handoff.toast.copy_failed": "تعذّر النسخ. اضغط مطوّلاً على الرابط للنسخ يدوياً.",
    "portal.loading": "جارٍ تحميل إيصالك…",
    "portal.unavailable.title": "الرابط غير متاح",
    "portal.unavailable.body": "هذا الرابط غير صالح أو انتهت صلاحيته.",
    "portal.unavailable.hint": "إذا شاركك المُرسِل هذا الرابط، اطلب منه إعادة إرسال بريد الإيصال.",
    "portal.brand": "بوابة الإيصالات",
    "portal.eyebrow": "أرسل إليك {sender}",
    "portal.reference": "المرجع",
    "portal.pending.title": "في انتظار الموافقة",
    "portal.pending.body": "سجّل المُرسِل هذه التحويلة لكنها لا تزال قيد المراجعة من قِبل Zcardon. يرجى التحقق لاحقاً — سيصبح الإيصال متاحاً بعد الموافقة.",
    "portal.row.bank": "البنك",
    "portal.row.account": "الحساب",
    "portal.row.routing": "رقم التوجيه",
    "portal.row.memo": "الملاحظة",
    "portal.confirmed.title": "أكّدت الاستلام",
    "portal.confirmed.body": "تم التأكيد في {when}. يمكن للمُرسِل رؤية ذلك.",
    "portal.disputed.title": "أبلغت عن تعارض",
    "portal.disputed.body": "تم الإبلاغ في {when}. تم إخطار المُرسِل بالبريد الإلكتروني.",
    "portal.actions.body": "هل وصل هذا المبلغ إلى حسابك؟ التأكيد يُغلق الحلقة مع المُرسِل — لا تتم أي حركة أموال عبر Zcardon، هذا مجرد سجل.",
    "portal.actions.confirm": "نعم، استلمته",
    "portal.actions.flag": "الإبلاغ عن مشكلة",
    "portal.dispute.placeholder": "مبلغ خاطئ، لم يصل، رقم حساب خاطئ…",
    "portal.dispute.submit": "إرسال البلاغ",
    "portal.dispute.too_short": "أضف سبباً مختصراً (4 أحرف على الأقل)",
    "portal.toast.confirmed": "شكراً — تم تأكيد الاستلام.",
    "portal.toast.confirm_failed": "تعذّر تأكيد الاستلام",
    "portal.toast.reported": "تم الإبلاغ. تم إخطار المُرسِل.",
    "portal.toast.report_failed": "تعذّر إرسال البلاغ",
    "portal.toast.pdf_failed": "تعذّر تنزيل الإيصال",
    "portal.download_pdf": "تنزيل الإيصال PDF",
    "portal.privacy": "هذه الصفحة خاصة بك. يمكن لأي شخص لديه الرابط عرض هذا الإيصال — لا تشاركه علناً. للاستفسار:",
    "detail.back": "العودة إلى لوحة التحكم",
    "detail.eyebrow": "بطاقتك",
    "detail.confirming_payment": "جارٍ تأكيد دفعتك…",
    "detail.active_ready": "البطاقة نشطة وجاهزة للمشاركة.",
    "detail.balance.label": "الرصيد الحالي",
    "detail.toast.notfound": "البطاقة غير موجودة",
    "detail.toast.loaded": "تم تحميل الأموال على البطاقة.",
    "detail.toast.confirmed": "تم تأكيد الدفع.",
    "detail.toast.expired": "انتهت صلاحية جلسة الدفع",
    "detail.delivery.receiver_fallback": "المستلم",
    "detail.delivery.received_title": "فتح {name} البطاقة",
    "detail.delivery.received_when": "تأكيد الاستلام · {when}",
    "detail.delivery.waiting_title_on": "ستصلك رسالة بريد إلكتروني فور استلامها",
    "detail.delivery.waiting_title_off": "إشعارات التسليم معطّلة",
    "detail.delivery.waiting_body_on": "بمجرد أن يمسح المستلم رمز QR على هذه البطاقة، سنرسل لك إشعاراً بالبريد الإلكتروني — مرة واحدة فقط.",
    "detail.delivery.waiting_body_off": "فعّل هذا الخيار لتلقّي رسالة بريد إلكتروني لمرة واحدة فور فتح المستلم البطاقة للمرة الأولى.",
    "detail.delivery.toggle_on": "مفعّل",
    "detail.delivery.toggle_off": "تفعيل",
    "detail.delivery.toast_enabled": "ستتلقى إشعاراً عند فتح البطاقة.",
    "detail.delivery.toast_disabled": "تم إيقاف إشعارات التسليم.",
    "detail.delivery.toast_failed": "تعذّر تحديث إشعارات التسليم.",
    "nudge.eyebrow": "أخبر المستلم",
    "nudge.title": "أرسل لـ {receiver} تنبيهاً مسبقاً",
    "nudge.subtitle": "صغنا رسالة قصيرة لإعلام {receiver} بأن البطاقة جاهزة وكيفية استخدامها.",
    "nudge.receiver_fallback": "المستلم",
    "nudge.sender_fallback": "صديق",
    "nudge.template": "مرحباً {receiver} — أرسل لك {sender} بطاقة Zcardon الافتراضية بقيمة {amount}. اضغط للدفع هنا: {url}\\n\\nلا حاجة لتطبيق. الرابط يعمل على أي هاتف.",
    "nudge.email_subject": "أرسل لك {sender} بطاقة Zcardon بقيمة {amount}",
    "nudge.action_copy": "نسخ الرسالة",
    "nudge.action_sms": "فتح الرسائل",
    "nudge.action_email": "فتح البريد الإلكتروني",
    "nudge.toast_copied": "تم نسخ الرسالة إلى الحافظة.",
    "nudge.toast_copy_failed": "تعذّر النسخ. حدّد الرسالة وانسخها يدوياً.",
    "ios_install.title": "ثبّت Zcardon على iPhone",
    "ios_install.step_1": "اضغط",
    "ios_install.share_label": "مشاركة",
    "ios_install.step_2": " ثم اختر \\\"إضافة إلى الشاشة الرئيسية\\\" — سيفتح Zcardon كتطبيق بملء الشاشة.",
    "smart_banner.title": "افتح في تطبيق Zcardon",
    "smart_banner.subtitle": "دفع أسرع. ادفع بنقرة دون مغادرة التطبيق.",
    "smart_banner.cta": "فتح",
    "smart_banner.dismiss": "تجاهل",
    "social_proof.eyebrow": "مباشر · آخر 48 ساعة",
    "social_proof.empty": "في انتظار وصول البطاقة التالية…",
    "social_proof.line_opened": "فتح {name} للتو بطاقة بقيمة {amount}.",
    "social_proof.line_issued": "حصل {name} على بطاقة بقيمة {amount} من صديق.",
    "social_proof.line_deposit": "استلم {name} إيداعاً مباشراً بقيمة {amount}.",
    "social_proof.stat_funded": "بطاقات ممولة اليوم",
    "social_proof.stat_delivered": "بطاقات مُسلَّمة اليوم",
    "social_proof.stat_recent": "النشاط الأخير",
    "social_proof.ago_seconds": "منذ {n}ث",
    "social_proof.ago_minutes": "منذ {n}د",
    "social_proof.ago_hours": "منذ {n}س",
    "flight.eyebrow": "بطاقات في الطريق · آخر 48 ساعة",
    "flight.title": "أين تصل Zcardon",
    "flight.subtitle": "مواقع المستلمين الفعلية، مجهّلة على مستوى المنطقة. نعرض الدول والولايات الأمريكية فقط — لا أسماء ولا عناوين ولا مبالغ.",
    "flight.row_template": "تم فتح البطاقة في {label}",
    "flight.more_count": "+ {n} منطقة أخرى",
    "flight.ago_seconds": "منذ {n}ث",
    "flight.ago_minutes": "منذ {n}د",
    "flight.ago_hours": "منذ {n}س",
    "trust_strip.eyebrow": "إشارات حقيقية · مباشرة من قاعدة البيانات",
    "trust_strip.title_a": "ليست شهادات.",
    "trust_strip.title_b": "قراءات مباشرة من قاعدة البيانات.",
    "trust_strip.how_link": "كيف نجمع هذه البيانات",
    "trust_strip.modal_eyebrow": "مصادر البيانات",
    "trust_strip.modal_title": "كلها حقيقية، كلها مجهّلة",
    "trust_strip.modal_close": "إغلاق",
    "trust_strip.modal_intro": "كل رقم في هذا الشريط مصدره قاعدة بياناتنا — لا مصادر خارجية، لا بيانات مخترعة. إليك من أين يسحب كل قسم بياناته وكيف نجهّلها قبل عرضها.",
    "trust_strip.src_carousel_title": "عرض النشاط المباشر",
    "trust_strip.src_carousel_body": "يسحب من مجموعتَي cards وdirect_deposits، آخر 48 ساعة. تظهر الأسماء باسم أول + حرف أول من اسم العائلة؛ تُقرَّب المبالغ لأقرب $5 لتفادي التتبع. يُحدَّث كل 60 ثانية.",
    "trust_strip.src_flight_title": "أين تصل Zcardon",
    "trust_strip.src_flight_body": "يقرأ بيانات مسح QR من card_scans، مع تحديد الموقع الجغرافي عند المسح. مجمّعة على مستوى الدولة أو الولاية الأمريكية. لا دقة على مستوى المدينة، لا عناوين IP، لا معرّفات بطاقات. يُحدَّث كل 90 ثانية.",
    "trust_strip.src_badges_title": "أرقام الثقة لآخر 30 يوماً",
    "trust_strip.src_badges_body": "إجماليات بحتة — لا بيانات على مستوى الصف. تظهر كل مقياس فقط بعد تجاوز حد أدنى من العينات، لذا ستُخفى نسبة 100% المستندة إلى تأكيدَين فقط. يُحدَّث كل دقيقة.",
    "trust_strip.modal_promise": "لن ننشر أبداً أسماء أو عناوين أو مبالغ دقيقة أو عناوين IP أو أرقام بطاقات في الواجهات العامة. وعد.",
    "trust_badge.eyebrow": "أرقام حقيقية · آخر 30 يوماً",
    "trust_badge.title": "الناس يستخدمون هذا فعلاً.",
    "trust_badge.subtitle": "ليست شهادة — عدد مباشر يُحدَّث من قاعدة البيانات كل دقيقة.",
    "trust_badge.confirmed_label": "من المستلمين يؤكدون الاستلام خلال 24 ساعة",
    "trust_badge.confirmed_sub": "من {n} استجابة خلال آخر 30 يوماً",
    "trust_badge.thanks_label": "رسائل شكر مُرسَلة",
    "trust_badge.thanks_sub": "من المستلمين خلال آخر 30 يوماً",
    "trust_badge.cards_label": "بطاقات ممولة",
    "trust_badge.cards_sub": "آخر 30 يومًا · عدد مباشر",
    "pulse.label": "النبض المباشر",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} فتح بطاقة بقيمة {amount}",
    "pulse.tooltip_issued": "{name} حصل على بطاقة بقيمة {amount}",
    "pulse.tooltip_deposit": "{name} استلم إيداعًا بقيمة {amount}",
    "pulse.ago_seconds": "منذ {n}ث",
    "pulse.ago_minutes": "منذ {n}د",
    "pulse.ago_hours": "منذ {n}س",
    "note_cloud.eyebrow": "ما الذي يستخدمه الناس من أجله",
    "note_cloud.title": "أسباب حقيقية. أشخاص حقيقيون.",
    "note_cloud.subtitle": "مجمَّع من {n} ملاحظة بطاقة خلال آخر 90 يومًا. نعرض فقط الكلمات التي تظهر في {min} ملاحظات مختلفة على الأقل — رسالتك تبقى لك.",
    "note_cloud.footnote": "تُحذف الأسماء والمبالغ ومعلومات الاتصال قبل العرض.",
    "note_cloud.lang_eyebrow": "بأي لغة كُتبت هذه الملاحظات؟",
    "note_cloud.lang_other": "أخرى",
    "download.button": "تنزيل التطبيق",
    "download.close": "إغلاق",
    "download.modal_eyebrow": "احصل على Zcardon",
    "download.modal_title": "ثبّت Zcardon على هاتفك",
    "download.footnote": "لا يوجد متجر تطبيقات بعد — تثبّت تطبيق الويب الآمن مباشرةً. يتحدّث فورًا.",
    "download.installed_title": "تم تثبيت Zcardon",
    "download.installed_body": "ابحث عن الأيقونة على شاشتك الرئيسية.",
    "download.webview_warning": "افتح هذه الصفحة في متصفح هاتفك (Safari على iPhone، Chrome على Android) للتثبيت — المتصفحات داخل التطبيقات لا تستطيع الإضافة إلى الشاشة الرئيسية.",
    "download.ios_step1_title": "في Safari",
    "download.ios_step1_body": "إن لم تكن في Safari بالفعل، انسخ هذا الرابط وافتحه هناك. (Chrome وغيره من متصفحات iOS لا تستطيع تثبيت التطبيقات.)",
    "download.ios_step2_title": "اضغط مشاركة",
    "download.ios_step2_body": "أيقونة المشاركة في أسفل المنتصف على iPhone، وأعلى اليمين على iPad.",
    "download.ios_step3_title": "أضف إلى الشاشة الرئيسية",
    "download.ios_step3_body": "مرّر لأسفل في قائمة المشاركة واختر \\\"إضافة إلى الشاشة الرئيسية\\\". يُثبَّت Zcardon كتطبيق ملء الشاشة.",
    "download.android_ready_body": "اكتشف Chrome أن Zcardon جاهز للتثبيت. اضغط أدناه.",
    "download.android_install": "تثبيت Zcardon",
    "download.android_installing": "جارٍ التثبيت…",
    "download.android_step1_title": "افتح قائمة Chrome",
    "download.android_step1_body": "اضغط النقاط الثلاث في أعلى يمين Chrome.",
    "download.android_step2_title": "أضف إلى الشاشة الرئيسية",
    "download.android_step2_body": "اختر \\\"تثبيت التطبيق\\\" أو \\\"إضافة إلى الشاشة الرئيسية\\\". يُثبَّت Zcardon كتطبيق ملء الشاشة.",
    "download.desktop_body": "امسح رمز QR هذا بكاميرا هاتفك لفتح Zcardon، ثم ثبّته من متصفح هاتفك.",
    "download.steps_hint": "تجري هذه الخطوات في متصفحك، لا على هذه الصفحة. لا يمكن النقر على أيٍّ من الصفوف أدناه — إنها تصف ما تبحث عنه فقط.",
    "download.ios_arrow_label": "اضغط هذا في Safari",
    "download.android_arrow_label": "ابحث هنا في Chrome",
    "install.eyebrow": "تثبيت Zcardon",
    "install.title": "أضف Zcardon إلى شاشتك الرئيسية",
    "install.subtitle": "نقرتان وسيصبح Zcardon أيقونة تطبيق حقيقية — دون الحاجة إلى متجر التطبيقات.",
    "install.back_home": "افتح Zcardon في المتصفح",
    "install.continue": "المتابعة إلى Zcardon →",
    "install.copy": "نسخ",
    "install.copied": "تم النسخ",
    "install.ios_step1_title": "افتح هذه الصفحة في Safari",
    "install.ios_step1_body": "إن فتحت الرابط من متصفح آخر، انسخ عنوان URL وافتحه في Safari أولًا — Chrome على iOS لا يستطيع تثبيت التطبيقات.",
    "install.ios_step2_title": "اضغط زر المشاركة",
    "install.ios_step2_body": "إنه المربع ذو السهم المتجه لأعلى — في أسفل Safari (أو أعلى اليمين على iPad).",
    "install.ios_step3_title": "اضغط \\\"إضافة إلى الشاشة الرئيسية\\\"",
    "install.ios_step3_body": "مرّر لأسفل في قائمة المشاركة واختر \\\"إضافة إلى الشاشة الرئيسية\\\"، ثم أكّد. يظهر Zcardon كأيقونة تطبيق.",
    "install.ios_arrow_label": "ابحث عن هذه الأيقونة",
    "install.android_ready_title": "جاهز للتثبيت",
    "install.android_ready_body": "اكتشف Chrome أن Zcardon قابل للتثبيت. اضغط أدناه وسيظهر كأيقونة تطبيق على شاشتك الرئيسية.",
    "install.android_install": "تثبيت Zcardon",
    "install.android_installing": "جارٍ التثبيت…",
    "install.android_step1_title": "اضغط قائمة Chrome",
    "install.android_step1_body": "إنها النقاط الثلاث في أعلى يمين Chrome.",
    "install.android_step2_title": "اضغط \\\"تثبيت التطبيق\\\" أو \\\"إضافة إلى الشاشة الرئيسية\\\"",
    "install.android_step2_body": "أكّد لإضافة أيقونة Zcardon إلى هاتفك. اضغطها في أي وقت للتشغيل.",
    "install.android_hint": "إن لم تر \\\"تثبيت التطبيق\\\" بعد، تصفّح الصفحة الرئيسية مرة واحدة — يفتحها Chrome بعد زيارة قصيرة.",
    "install.desktop_title": "امسح بهاتفك",
    "install.desktop_body": "Zcardon تطبيق مُحسَّن للجوال. افتح هذا الرابط على هاتفك للتثبيت:",
    "install.webview_title": "افتح في متصفحك",
    "install.webview_body": "متصفحات التطبيقات الداخلية (Instagram، Facebook، TikTok) لا تستطيع تثبيت التطبيقات. اضغط \\\"…\\\" أو قائمة المشاركة، ثم اختر \\\"فتح في Safari\\\" / \\\"فتح في Chrome\\\". أو انسخ هذا الرابط:",
    "install.already_title": "أنت بالفعل داخل التطبيق",
    "install.already_body": "Zcardon مثبَّت على هذا الجهاز. اضغط الأيقونة على شاشتك الرئيسية لفتحه في أي وقت.",
    "install.installed_title": "تم التثبيت",
    "install.installed_body": "ابحث عن أيقونة Zcardon على شاشتك الرئيسية — هذا هو تطبيقك.",
    "install.steps_hint": "تجري هذه الخطوات في متصفحك، لا على هذه الصفحة. لا يمكن النقر على أيٍّ من الصفوف أدناه — إنها تصف ما تبحث عنه في قائمة المتصفح.",
    "install.desktop_body_qr": "يُثبَّت Zcardon كأيقونة تطبيق على iPhone أو Android — ليس على سطح المكتب. وجّه كاميرا هاتفك نحو رمز QR هذا:",
    "install.desktop_or": "أو انسخ الرابط",
    "install.android_arrow_label": "ابحث هنا في Chrome",
    "install.ios_arrow_caption": "إنه في أسفل Safari تمامًا",
    "vietqr.disabled.title": "استقبل VND عبر VietQR",
    "vietqr.disabled.badge": "فيتنام فقط",
    "vietqr.disabled.desc": "اربط حساباً مصرفياً فيتنامياً وسيتحول رمز QR على هذه البطاقة إلى رمز VietQR حقيقي — يمكن لأي تطبيق مصرفي فيتنامي (Vietcombank، BIDV، MB، Techcombank، ACB، VietinBank…) مسحه ودفع VND لك مباشرة عبر شبكة NAPAS. لا حاجة لموافقة Stripe Issuing.",
    "vietqr.disabled.btn_enter": "أدخل تفاصيل البنك",
    "vietqr.disabled.or": "أو",
    "vietqr.disabled.btn_send_link": "إرسال رابط الإعداد للمستلم",
    "vietqr.disabled.btn_send_link_tip": "أنشئ رابطاً عاماً صالحاً لمدة 7 أيام ليملأه المستلم بنفسه",
    "vietqr.form.warn_title": "اقرأ هذا قبل التفعيل",
    "vietqr.form.warn_body_a": "لن توجَّه عمليات المسح إلى صفحة التتبع في Zcardon بعد الآن. تتدفق الأموال",
    "vietqr.form.warn_body_b": "مباشرة إلى حسابك المصرفي الفيتنامي",
    "vietqr.form.warn_body_c": "عبر شبكة NAPAS بين البنوك — Zcardon لا يرى المال أبداً. يعمل VietQR فقط داخل فيتنام لتحويلات VND.",
    "vietqr.form.bank": "البنك",
    "vietqr.form.account_number": "رقم الحساب",
    "vietqr.form.account_name": "اسم صاحب الحساب (25 حرفاً)",
    "vietqr.form.city": "المدينة (15 حرفاً)",
    "vietqr.form.advanced": "متقدم — مبلغ ثابت / مرجع (اختياري)",
    "vietqr.form.amount_vnd": "المبلغ (VND)",
    "vietqr.form.amount_ph": "اتركه فارغاً للمبلغ المتغير",
    "vietqr.form.reference": "المرجع (25 حرفاً)",
    "vietqr.form.confirm": "أفهم أن مسح هذه البطاقة سينقل VND مباشرة إلى الحساب المصرفي أعلاه. لا يعالج Zcardon هذه الأموال أو يحتفظ بها.",
    "vietqr.form.btn_attach": "تفعيل VietQR",
    "vietqr.form.btn_attaching": "جارٍ التفعيل…",
    "vietqr.form.btn_cancel": "إلغاء",
    "vietqr.enabled.title": "VietQR نشط",
    "vietqr.enabled.desc": "عندما يمسح تطبيق مصرفي فيتنامي هذه البطاقة، سيتم تحويل VND مباشرة إلى الحساب أدناه — متجاوزاً Zcardon تماماً.",
    "vietqr.enabled.bank_label": "البنك",
    "vietqr.enabled.account_label": "الحساب",
    "vietqr.enabled.fixed_amount": "مبلغ ثابت",
    "vietqr.enabled.reference": "المرجع",
    "vietqr.enabled.btn_detach": "إلغاء ربط VietQR",
    "vietqr.enabled.btn_detaching": "جارٍ الإلغاء…",
    "vietqr.enabled.attached_at": "تم التفعيل",
    "vietqr.analytics.live_activity": "النشاط المباشر",
    "vietqr.analytics.today": "اليوم",
    "vietqr.analytics.days_live": "{n} يوم نشط",
    "vietqr.analytics.phantom_views": "مشاهدات تقديرية",
    "vietqr.analytics.confirmed_pays": "مدفوعات مؤكدة",
    "vietqr.analytics.total_vnd": "إجمالي VND",
    "vietqr.analytics.recent_confirmed": "تأكيدات حديثة",
    "vietqr.analytics.phantom_explainer": "المشاهدات التقديرية ≈ عمليات تحميل صورة البطاقة منذ التفعيل. لا تمر عمليات مسح VietQR الحقيقية عبر خوادمنا، لذا فنحن نقدّر من نشاط \"عرض البطاقة\". اطلب من المستلم تأكيد المدفوعات عبر رابط الإعداد للحصول على بيانات دقيقة.",
    "vietqr.toast.required": "رقم الحساب والاسم مطلوبان.",
    "vietqr.toast.confirm_first": "يرجى تأكيد فهمك بأن VietQR يوجّه VND إلى بنكك، متجاوزاً Zcardon.",
    "vietqr.toast.attached_default": "تم تفعيل VietQR.",
    "vietqr.toast.attach_failed": "فشل تفعيل VietQR.",
    "vietqr.toast.detach_confirm": "إلغاء ربط VietQR؟ سيعود رمز QR على البطاقة إلى رابط تتبع Zcardon.",
    "vietqr.toast.detached": "تم إلغاء ربط VietQR — عاد رمز QR إلى رابط تتبع Zcardon.",
    "vietqr.toast.detach_failed": "فشل إلغاء الربط.",
    "vietqr.toast.link_copied": "تم نسخ رابط الإعداد · ينتهي خلال {h} ساعة",
    "vietqr.toast.link_generated": "تم إنشاء رابط الإعداد · انسخه من الأسفل",
    "vietqr.toast.link_failed": "فشل إنشاء رابط الإعداد.",

    "setpw.toast.too_short": "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل",
    "setpw.toast.no_match": "كلمتا المرور غير متطابقتين",
    "setpw.toast.success_signin_again": "تم تعيين كلمة المرور — يرجى تسجيل الدخول",
    "setpw.toast.welcome": "مرحبًا بك في برنامج وكلاء Zcardon",
    "setpw.toast.expired": "الرابط منتهي الصلاحية أو غير صالح",
    "setpw.verifying": "جارٍ التحقق من دعوتك…",
    "setpw.invalid.title": "الرابط منتهي الصلاحية",
    "setpw.invalid.desc": "انتهت صلاحية رابط الترحيب هذا أو تم استخدامه مسبقًا. يرجى التواصل مع مشرف Zcardon للحصول على دعوة جديدة.",
    "setpw.invalid.home_btn": "الرئيسية",
    "setpw.invalid.support_btn": "تواصل مع الدعم",
    "setpw.fallback_name": "وكيل",
    "setpw.eyebrow": "ترحيب بالوكيل",
    "setpw.heading": "مرحبًا، {name}",
    "setpw.subheading": "عيّن كلمة مرورك لتفعيل حساب الوكيل. سيتم تسجيل دخولك فورًا.",
    "setpw.code_label": "رمز الوكيل الخاص بك",
    "setpw.code_tag": "شاركه عند تأهيل العملاء",
    "setpw.form.title": "اختر كلمة مرور",
    "setpw.form.new_label": "كلمة المرور الجديدة",
    "setpw.form.placeholder": "8 أحرف كحد أدنى",
    "setpw.form.confirm_label": "تأكيد كلمة المرور",
    "setpw.form.btn_busy": "جارٍ التفعيل…",
    "setpw.form.btn_submit": "تفعيل حساب الوكيل",
    "tx.heading": "المعاملات",
    "tx.kind.direct_deposit": "إيداع مباشر",
    "tx.kind.card_topup": "شحن البطاقة",
    "tx.kind.outbound_payout": "دفع صادر",
    "tx.toast.load_failed": "فشل تحميل المعاملات",
    "tx.summary.deposits": "الإيداعات",
    "tx.summary.topups": "عمليات الشحن",
    "tx.summary.topups_sub": "بطاقة",
    "tx.summary.payouts": "المدفوعات",
    "tx.summary.payouts_sub": "صادر",
    "tx.filter.all": "الكل",
    "tx.filter.deposits": "الإيداعات",
    "tx.filter.topups": "عمليات الشحن",
    "tx.filter.payouts": "المدفوعات",
    "tx.loading": "جارٍ تحميل المعاملات…",
    "tx.empty.filtered": "لا توجد معاملات تطابق هذا التصفية",
    "tx.empty.none": "لا توجد معاملات بعد",
    "tx.empty.cta": "ابدأ بإيداع مباشر →",
    "tx.open_title": "فتح",
    "terms.load_error_default": "فشل تحميل الشروط",
    "terms.toast.sign_and_check": "يرجى التوقيع والتأكيد لقبول الاتفاقية",
    "terms.toast.recorded": "تم تسجيل الاتفاقية",
    "terms.toast.failed": "فشل الإرسال",
    "terms.error.title": "الرابط غير صالح",
    "terms.back_home": "العودة إلى الرئيسية",
    "terms.loading": "جارٍ تحميل الاتفاقية…",
    "terms.done.title": "تم قبول الاتفاقية",
    "terms.done.meta": "الإصدار {v} · {ts}",
    "terms.done.next": "يمكنك إغلاق هذه الصفحة. ستكون بطاقتك جاهزة للاستخدام فور إصدارها.",
    "terms.eyebrow": "اتفاقية حامل البطاقة · v{v}",
    "terms.title_reaccept": "أعد قبول اتفاقية حامل البطاقة",
    "terms.title_accept": "قبول اتفاقية حامل البطاقة",
    "terms.desc.sender_sent": "أرسل إليك بطاقة Zcardon",
    "terms.desc.you_were_sent": "تم إرسال بطاقة Zcardon إليك",
    "terms.desc.ending": "تنتهي بـ",
    "terms.desc.read_before": "اقرأ الاتفاقية أدناه، ثم وقّع وأكد لتفعيل البطاقة.",
    "terms.already_accepted_note": "لقد قبلت إصدارًا سابقًا. أعد القبول للتحديث إلى الأحدث.",
    "terms.scroll_hint": "مرر داخل المربع لقراءة الاتفاقية كاملة.",
    "terms.view_json": "فتح كـ JSON",
    "terms.sign_label": "الإمضاء باسمك القانوني الكامل",
    "terms.sign_placeholder": "كما يظهر في وثيقة هويتك الرسمية",
    "terms.consent": "لقد قرأت اتفاقية حامل بطاقة Zcardon الإصدار {v} وأوافق عليها.",
    "terms.btn_recording": "جارٍ التسجيل…",
    "terms.btn_accept": "قبول الاتفاقية",
    "hub.eyebrow": "مركز الاستلام الخاص بك",
    "hub.heading.all_done": "أنت جاهز تماماً 🎉",
    "hub.heading.greet": "مرحباً {name} 👋",
    "hub.fallback_name": "بك",
    "hub.desc.sender_sent": "أرسل إليك بطاقة Zcardon",
    "hub.desc.you_were_sent": "تم إرسال بطاقة Zcardon إليك",
    "hub.desc.ending": "تنتهي بـ",
    "hub.desc.complete_steps": "أكمل الخطوات أدناه لتفعيل كل شيء.",
    "hub.progress.label": "{done} من {total} خطوات مكتملة",
    "hub.blocker.title_one": "خطوة مطلوبة واحدة متبقية",
    "hub.blocker.title_many": "{n} خطوات مطلوبة متبقية",
    "hub.blocker.subtext": "لن تُفعَّل بطاقتك بالكامل حتى تكتمل هذه الخطوات.",
    "hub.required_pill": "مطلوب",
    "hub.done_pill": "تم",
    "hub.setup_btn": "إعداد",
    "hub.refresh": "تحديث",
    "hub.loading": "جارٍ التحميل…",
    "hub.error.title": "مشكلة في الرابط",
    "hub.error.default": "تعذّر تحميل مركز الاستلام الخاص بك.",
    "hub.back_home": "العودة إلى الرئيسية",
    "hub.footer": "احفظ هذه الصفحة · تبقى نشطة لمدة 90 يوماً · فقط من لديه هذا الرابط يمكنه رؤيتها.",
    "onboarding.eyebrow": "استلم تحويلك",
    "onboarding.title": "مرحباً {name}",
    "onboarding.fallback_name": "بك",
    "onboarding.intro_via": "عبر Zcardon",
    "onboarding.intro_ref_open": "(المرجع",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "أضف حسابك المصرفي في {country} أدناه لاستلام الأموال. تذهب بياناتك مباشرةً إلى Stripe (شريكنا في المدفوعات) — ولن يراها المُرسِل أبداً.",
    "onboarding.invalid.title": "الرابط غير صالح",
    "onboarding.invalid.hint": "يرجى مطالبة المُرسِل بإرسال رابط جديد.",
    "onboarding.loading": "جارٍ التحميل…",
    "onboarding.complete.title": "تفاصيل البنك موجودة مسبقاً",
    "onboarding.complete.desc": "لقد أرسلت بيانات حسابك المصرفي من قبل. سيُحرر المُرسِل الأموال قريباً.",
    "onboarding.success.title": "تم استلام بيانات البنك ✓",
    "onboarding.success.auto_paid": "شكراً — تم التحقق من حسابك المصرفي وتم إطلاق الأموال إلى بنكك الآن. من المتوقع وصولها خلال 1-2 أيام عمل.",
    "onboarding.success.pending": "شكراً — تم التحقق من حسابك المصرفي. سيُبلَّغ المُرسِل، وستصل الأموال إلى حسابك خلال 1-2 أيام عمل.",
    "onboarding.success.ref": "المرجع: {id}",
    "onboarding.beta.title": "معاينة تجريبية.",
    "onboarding.beta.body": "هذه الصفحة منشورة غير أن مسار الدفعات الفعلي في انتظار موافقة Stripe. سيُعيد الإرسال خطأً حتى يُفعَّل النظام. يرجى المراجعة لاحقاً أو التواصل مع الدعم في حالات الطوارئ.",
    "onboarding.select_placeholder": "اختر…",
    "onboarding.submit_error_default": "فشل الإرسال. يرجى المحاولة مجدداً.",
    "onboarding.submit_busy": "جارٍ الإرسال…",
    "onboarding.submit": "إرسال بيانات البنك →",
    "onboarding.secure_note": "مؤمَّن عبر Stripe. ينتهي هذا الرابط خلال 7 أيام.",
    "onboarding.validate.required": "{field} مطلوب",
    "onboarding.validate.format": "تنسيق {field} يبدو خاطئاً (المتوقع {pattern})",
    "onboarding.validate.no_schema": "لا يوجد مخطط نموذج متاح",
    "apply.toast.invalid_image": "يرجى اختيار صورة بصيغة JPEG أو PNG أو WEBP",
    "apply.toast.too_large": "يجب أن لا يتجاوز حجم الصورة 6 ميغابايت",
    "apply.toast.upload_both": "يرجى رفع وجهَي رخصة القيادة",
    "apply.toast.failed": "فشل الإرسال",
    "apply.toast.submitted": "تم إرسال الطلب",
    "apply.success.title": "تم استلام الطلب",
    "apply.success.desc": "شكراً لك، {name}. سنراجع طلبك خلال 2-3 أيام عمل وسنراسلك على {email} بالخطوات التالية.",
    "apply.success.back_home": "العودة إلى الرئيسية",
    "apply.success.contact_support": "التواصل مع الدعم",
    "apply.eyebrow": "برنامج الوكلاء",
    "apply.heading": "قدّم طلبًا لتصبح وكيل Zcardon",
    "apply.intro": "اكسب عمولة 0.5% على كل بطاقة تُصدرها، وكل عملية شحن تجمعها، وكل إيداع نقدي تقبله نيابةً عن العملاء. نُوظّف وكلاء موثوقين في مجتمعاتهم وقادرين على التحقق من هويتهم.",
    "apply.bullet1": "احصل على رمز وكيل فريد · قابل للتتبع عبر جميع المعاملات",
    "apply.bullet2": "لوحة تحكم بملخصات الأرباح أسبوعيًا / شهريًا / سنويًا",
    "apply.bullet3": "إيداع مباشر في البنك — يُصرف أسبوعيًا",
    "apply.section.your_info": "معلوماتك",
    "apply.field.full_name": "الاسم القانوني الكامل",
    "apply.field.full_name_ph": "كما يظهر على رخصة القيادة",
    "apply.field.email": "البريد الإلكتروني",
    "apply.field.phone": "الهاتف",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "رقم رخصة القيادة",
    "apply.field.address": "العنوان",
    "apply.field.address_ph": "الشارع، المدينة، الولاية، الرمز البريدي",
    "apply.field.why_join_label": "لماذا تريد أن تصبح وكيلًا؟ (اختياري)",
    "apply.field.why_join_ph": "أخبرنا قليلًا عن صلاتك بمجتمعك أو خبرتك في تحويل الأموال",
    "apply.dl.section": "التحقق من رخصة القيادة",
    "apply.dl.helper": "ارفع صورًا واضحة وجيدة الإضاءة لكلا الوجهين. JPEG أو PNG أو WEBP · 6 ميغابايت كحد أقصى لكل صورة. تُخزَّن الصور بشكل خاص ولا يطّلع عليها سوى مشرفي Zcardon للتحقق.",
    "apply.dl.front": "الوجه الأمامي لرخصة القيادة",
    "apply.dl.back": "الوجه الخلفي لرخصة القيادة",
    "apply.dl.tap_upload": "اضغط للرفع",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 ميغابايت",
    "apply.cancel": "إلغاء",
    "apply.submit_busy": "جارٍ الإرسال…",
    "apply.submit": "إرسال الطلب",
    "agent.toast.load_failed": "فشل تحميل بيانات الوكيل",
    "agent.toast.cust_created": "تم إنشاء العميل {name}",
    "agent.toast.cust_failed": "فشل إنشاء العميل",
    "agent.toast.card_issued": "تم إصدار البطاقة · ربحت {amount}",
    "agent.toast.card_issue_failed": "فشل إصدار البطاقة",
    "agent.toast.cash_accepted": "تم قبول الإيداع النقدي · ربحت {amount}",
    "agent.toast.cash_failed": "فشل تسجيل الإيداع",
    "agent.loading": "جارٍ التحميل…",
    "agent.eyebrow": "بوابة الوكيل",
    "agent.fallback_name": "وكيل",
    "agent.code_line.code_label": "رمز الوكيل",
    "agent.code_line.commission_label": "عمولة",
    "agent.earnings.label": "الرصيد غير المدفوع",
    "agent.earnings.sub": "{n} معاملة غير مدفوعة",
    "agent.stat.week": "هذا الأسبوع",
    "agent.stat.month": "هذا الشهر",
    "agent.stat.year": "هذا العام",
    "agent.stat.txns": "{n} معاملة",
    "agent.stat.customers": "عملاؤك",
    "agent.stat.active": "نشط",
    "agent.customers.title": "عملاؤك",
    "agent.customers.empty": "لا يوجد عملاء بعد. اضغط \\\"تسجيل عميل\\\" لإضافة أول مستخدم.",
    "agent.customers.col_customer": "العميل",
    "agent.customers.col_phone": "الهاتف",
    "agent.customers.col_cards": "البطاقات",
    "agent.customers.col_commissions": "العمولات المكتسبة",
    "agent.customers.col_joined": "تاريخ الانضمام",
    "agent.customers.col_actions": "إجراءات",
    "agent.customers.txns": "{n} معاملة",
    "agent.commissions.title": "العمولات الأخيرة",
    "agent.commissions.empty": "لا توجد عمولات بعد. أصدر أول بطاقة لتبدأ الكسب.",
    "agent.commissions.col_when": "التاريخ",
    "agent.commissions.col_customer": "العميل",
    "agent.commissions.col_type": "النوع",
    "agent.commissions.col_txn": "المعاملة",
    "agent.commissions.col_commission": "العمولة",
    "agent.commissions.col_status": "الحالة",
    "agent.commissions.paid": "مدفوع",
    "agent.commissions.unpaid": "غير مدفوع",
    "agent.type.issuance": "إصدار",
    "agent.type.topup": "شحن رصيد",
    "agent.type.cash_deposit": "إيداع نقدي",
    "agent.register_btn": "تسجيل عميل",
    "agent.dialog.new_cust.title": "عميل جديد",
    "agent.dialog.new_cust.desc": "سيتم ربطه برمز وكيلك. كل بطاقة تُصدرها نيابةً عنه تمنحك عمولة 0.5%.",
    "agent.dialog.field.full_name": "الاسم الكامل",
    "agent.dialog.field.email": "البريد الإلكتروني",
    "agent.dialog.field.phone": "الهاتف",
    "agent.dialog.field.password": "كلمة المرور الأولية (شاركها مع العميل)",
    "agent.dialog.cancel": "إلغاء",
    "agent.dialog.creating": "جارٍ الإنشاء…",
    "agent.dialog.create_customer": "إنشاء عميل",
    "agent.dialog.issue_card.title": "إصدار بطاقة لـ {name}",
    "agent.dialog.issue_card.desc": "يُموَّل من النقد المحصّل. يدفع العميل رسوم 3.75% — تحتفظ بـ 0.5%.",
    "agent.dialog.field.receiver_name": "اسم المستلم",
    "agent.dialog.field.receiver_email": "بريد المستلم الإلكتروني",
    "agent.dialog.field.receiver_phone": "هاتف المستلم",
    "agent.dialog.field.amount_usd": "المبلغ (USD)",
    "agent.dialog.field.note": "ملاحظة (اختياري)",
    "agent.dialog.issuing": "جارٍ الإصدار…",
    "agent.dialog.issue_card_btn": "إصدار بطاقة",
    "agent.actions.issue_card": "إصدار بطاقة",
    "agent.dialog.cash_deposit.title": "إيداع نقدي لـ {name}",
    "agent.dialog.cash_deposit.desc": "نقد محصّل من العميل · ينشئ بطاقة تُسلَّم إلى المستلم.",
    "agent.dialog.field.receiver_name_opt": "اسم المستلم (اختياري)",
    "agent.dialog.recording": "جارٍ التسجيل…",
    "agent.dialog.record_deposit": "تسجيل الإيداع",
    "agent.actions.cash_deposit": "إيداع نقدي",

  },
  es: {
    "header.nav.home": "Inicio",
    "header.nav.rates": "Tarifas",
    "header.nav.support": "Soporte",
    "header.nav.admin": "Admin",
    "header.nav.signin": "Iniciar sesión",
    "header.nav.signout": "Cerrar sesión",
    "header.nav.get_started": "Comenzar",
    "header.nav.account": "Cuenta",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Cambiar idioma",
    "landing.hero.eyebrow": "Un nuevo canal para el gasto digital",
    "landing.hero.title_a": "Fondea una tarjeta virtual.",
    "landing.hero.title_b_ship": "Envíala",
    "landing.hero.title_b_rest": "a cualquiera,",
    "landing.hero.title_c": "lista para gastar en",
    "landing.hero.subtitle": "Emite una tarjeta de débito virtual con pago por aproximación y QR escaneable. Checkout con Stripe, saldo en USD, tarifas de servicio y pasarela transparentes — entregada a cualquier receptor por correo o descarga.",
    "landing.hero.cta_start": "Empezar a emitir tarjetas",
    "landing.hero.cta_request_cash": "Solicitar una tarjeta de efectivo",
    "landing.hero.cta_rates": "Ver tarifas",
    "landing.hero.cta_find_card": "Encontrar mi tarjeta existente",
    "landing.hero.stat_currency": "Moneda",
    "landing.hero.stat_settle": "Tiempo de liquidación",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "Tarifa de servicio",
    "landing.hero.image_eyebrow": "SKYLINE · ALCANCE GLOBAL",
    "landing.hero.image_title": "Construido para la economía de tarjetas moderna",
    "landing.hero.live_pill": "EN VIVO",
    "landing.hero.live_pill_text": "Liquida en USD",
    "landing.hero.marquee": "Emite · Fondea · Gasta en USD",
    "landing.demo.eyebrow": "Véalo en 20 segundos",
    "landing.demo.title_a": "De la emisión al",
    "landing.demo.title_b_spend": "gasto",
    "landing.demo.title_c": ". En cuatro toques.",
    "landing.demo.step_issue.label": "Emitir una tarjeta",
    "landing.demo.step_issue.detail": "Elige un monto. Tarjeta lista en ~2 minutos.",
    "landing.demo.step_share.label": "Compártela",
    "landing.demo.step_share.detail": "Por correo, SMS, WhatsApp o descarga como PNG.",
    "landing.demo.step_scan.label": "El receptor escanea",
    "landing.demo.step_scan.detail": "La cámara del teléfono abre la página de seguimiento al instante.",
    "landing.demo.step_spend.label": "Gasta en USD",
    "landing.demo.step_spend.detail": "Pago por aproximación, retiro de efectivo, compras en comercios.",
    "landing.demo.cta_try": "Pruébalo ahora",
    "landing.demo.live_pill": "Demo en vivo",
    "landing.demo.scene1_caption": "Generando tarjeta · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Enviando al receptor por WhatsApp",
    "landing.demo.scene3_caption": "Escaneando QR · coincidencia encontrada",
    "landing.demo.scene4_eyebrow": "Saldo acreditado",
    "landing.demo.scene4_status": "✓ aprobado · pago por aproximación listo",
    "landing.footer.rates": "Tarifas",
    "landing.footer.support": "Soporte",
    "landing.footer.admin_portal": "Portal de administración",
    "landing.footer.copy": "© 2026 Zcardon · Un producto fintech de demostración",
    "landing.cash.dialog_title": "Solicitar una tarjeta de efectivo",
    "landing.cash.dialog_desc": "Te enviaremos un enlace de pago de Stripe por SMS. Paga con cualquier tarjeta; la tarjeta de efectivo se fondea en el instante en que Stripe confirma.",
    "landing.cash.phone_label": "Número de móvil",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Monto (USD)",
    "landing.cash.name_label": "Tu nombre (opcional)",
    "landing.cash.submit": "Enviar enlace de pago",
    "landing.cash.submitting": "Enviando…",
    "landing.cash.success_title": "Enlace de pago en camino",
    "landing.cash.success_body": "Enviamos un enlace de pago de Stripe a {phone}. Tócalo para pagar y tu tarjeta virtual se activará de inmediato.",
    "landing.cash.success_open_checkout": "Abrir pago ahora",
    "landing.find.cta": "¿Ya pagaste? Encontrar mi tarjeta",
    "landing.find.dialog_title": "Encontrar mi tarjeta existente",
    "landing.find.dialog_desc": "Ingresa el número de teléfono que usaste. Te enviaremos un código de 6 dígitos para recuperar cualquier tarjeta de efectivo activa registrada.",
    "landing.find.phone_label": "Número de móvil",
    "landing.find.code_label": "Código de 6 dígitos",
    "landing.find.send_code": "Enviar código",
    "landing.find.verify": "Verificar y encontrar",
    "landing.find.sending": "Enviando…",
    "landing.find.verifying": "Verificando…",
    "landing.find.no_cards": "No se encontraron tarjetas activas para ese número.",
    "login.eyebrow.welcome": "Bienvenido",
    "login.eyebrow.admin": "Administración",
    "login.title": "Iniciar sesión",
    "login.title_admin": "Inicio de sesión de administrador",
    "login.admin_badge": "Portal de administración",
    "login.email_label": "Correo electrónico",
    "login.password_label": "Contraseña",
    "login.submit": "Iniciar sesión",
    "login.submitting": "Iniciando sesión…",
    "login.toast_welcome": "Bienvenido de nuevo, {name}",
    "login.toast_failed": "Error al iniciar sesión",
    "login.gate_title": "El acceso de clientes está temporalmente suspendido",
    "login.gate_default_message": "Estamos mejorando la plataforma. Vuelve pronto.",
    "login.gate_admin_note": "Los administradores y gerentes pueden seguir iniciando sesión abajo.",
    "login.new_here": "¿Eres nuevo?",
    "login.create_account": "Crear una cuenta",
    "login.customer_q": "¿Cliente?",
    "login.regular_signin": "Inicio de sesión habitual",
    "login.demo_title": "Cuentas de demostración",
    "login.demo_customers_note": "Clientes: regístrate en /register o crea desde el panel de administración",
    "login.forgot_password": "¿Olvidaste tu contraseña?",
    "forgot.eyebrow": "Recuperación de cuenta",
    "forgot.title": "Restablece tu contraseña",
    "forgot.subtitle": "Ingresa el correo vinculado a tu cuenta de Zcardon y te enviaremos un enlace seguro para elegir una nueva contraseña.",
    "forgot.email_label": "Correo electrónico",
    "forgot.email_placeholder": "tu@ejemplo.com",
    "forgot.submit": "Enviar enlace de restablecimiento",
    "forgot.submitting": "Enviando…",
    "forgot.toast_failed": "No se pudo enviar el enlace. Inténtalo de nuevo.",
    "forgot.sent_title": "Revisa tu bandeja de entrada",
    "forgot.sent_message": "Si existe una cuenta para {email}, hemos enviado un enlace para restablecer tu contraseña.",
    "forgot.sent_expiry_note": "El enlace es válido por 15 minutos. ¿No lo recibiste? Revisa el spam o envía otro.",
    "forgot.back_to_login": "Volver a iniciar sesión",
    "forgot.resend_link": "Enviar otro enlace",
    "forgot.remember_q": "¿Recuerdas tu contraseña?",
    "forgot.signin_link": "Iniciar sesión",
    "reset.eyebrow": "Recuperación de cuenta",
    "reset.title": "Elige una nueva contraseña",
    "reset.subtitle": "Elige una contraseña segura que no hayas usado antes. Iniciarás sesión automáticamente una vez establecida.",
    "reset.verifying": "Verificando tu enlace de restablecimiento…",
    "reset.invalid_title": "Este enlace de restablecimiento no es válido o ha expirado",
    "reset.invalid_message": "Los enlaces de restablecimiento son válidos por 15 minutos y solo pueden usarse una vez. Solicita uno nuevo para continuar.",
    "reset.request_new_link": "Solicitar un nuevo enlace de restablecimiento",
    "reset.new_password_label": "Nueva contraseña",
    "reset.confirm_password_label": "Confirmar contraseña",
    "reset.password_hint": "Al menos 8 caracteres. Evita contraseñas usadas en otros sitios.",
    "reset.submit": "Actualizar contraseña",
    "reset.submitting": "Actualizando…",
    "reset.toast_success": "Contraseña actualizada. Has iniciado sesión.",
    "reset.toast_failed": "No se pudo restablecer la contraseña. Inténtalo de nuevo.",
    "reset.error_min_length": "La contraseña debe tener al menos 8 caracteres.",
    "reset.error_mismatch": "Las contraseñas no coinciden.",
    "register.eyebrow": "Únete",
    "register.title": "Crear cuenta",
    "register.full_name_label": "Nombre completo",
    "register.email_label": "Correo electrónico",
    "register.password_label": "Contraseña",
    "register.submit": "Crear cuenta",
    "register.submitting": "Creando…",
    "register.gate_title": "Los registros están temporalmente suspendidos",
    "register.gate_default_message": "Estamos mejorando la plataforma. Vuelve pronto.",
    "register.gate_already_member": "¿Ya tienes una cuenta?",
    "register.toast_created": "Cuenta creada",
    "register.toast_failed": "Error en el registro",
    "register.already_member": "¿Ya eres miembro?",
    "register.signin_link": "Iniciar sesión",
    "dash.eyebrow": "Panel",
    "dash.greeting": "Bienvenido de nuevo, {name}",
    "dash.subtitle": "Emite una tarjeta virtual o envía un depósito por transferencia — tu centro de control está abajo.",
    "dash.action.issue_card": "Emitir una tarjeta",
    "dash.action.direct_deposit": "Depósito directo (transferencia)",
    "dash.action.support": "Soporte",
    "dash.section.cards": "Tus tarjetas",
    "dash.section.activity": "Actividad reciente",
    "dash.section.deposits": "Depósitos directos",
    "dash.empty.cards": "Aún no tienes tarjetas — emite tu primera para comenzar.",
    "dash.empty.activity": "Una vez que emitas tarjetas o envíes transferencias, la actividad reciente aparecerá aquí.",
    "ref.eyebrow": "Bienvenido a Zcardon",
    "ref.greeting": "{name} te invitó.",
    "ref.loading": "Cargando tu invitación…",
    "ref.resolved_redirect": "Un momento — te estamos redirigiendo.",
    "ref.resolving": "Procesando tu enlace de invitación…",
    "support.eyebrow": "Aquí para ti",
    "support.title": "Soporte, no tickets.",
    "support.subtitle": "Habla con una persona. Respuestas por correo en menos de una hora hábil en días de semana.",
    "support.card.email": "Correo electrónico",
    "support.card.phone": "Teléfono",
    "support.card.live_chat": "Chat en vivo",
    "support.card.live_chat_value": "En tu panel",
    "support.card.hours": "Horario",
    "support.faq.title": "Preguntas frecuentes",
    "support.faq.q1": "¿Cuánto tarda el receptor en recibir la tarjeta?",
    "support.faq.a1": "Normalmente en 2 minutos tras la financiación exitosa. La entrega por correo electrónico (Resend) y MMS (Twilio) es automática.",
    "support.faq.q2": "¿Puedo cancelar o congelar una tarjeta después de enviarla?",
    "support.faq.a2": "Sí — abre la página de detalle de la tarjeta y pulsa Congelar. También puedes congelarla directamente desde el correo de alerta al receptor.",
    "support.faq.q3": "¿En qué monedas puede usarse la tarjeta?",
    "support.faq.a3": "Todas las tarjetas se liquidan en USD. El receptor puede gastar donde se acepte Visa/Mastercard — el cambio de divisas lo gestiona la red de tarjetas.",
    "support.faq.q4": "¿Qué comisiones aplican?",
    "support.faq.a4": "Una comisión de servicio transparente más el costo del gateway Stripe. Consulta la cotización en vivo en la página de Tarifas antes de pagar.",
    "rates.eyebrow": "Transparente",
    "rates.title_a": "Sin costos",
    "rates.title_b": "ocultos",
    "rates.subtitle": "Ves la comisión de servicio y el costo del gateway antes de pagar. El principal siempre se acredita al saldo de la tarjeta virtual en USD.",
    "rates.row.service_fee": "Comisión de servicio",
    "rates.row.service_fee_value": "{pct} del monto enviado",
    "rates.row.gateway": "Gateway (Stripe)",
    "rates.row.settlement": "Liquidación",
    "rates.row.settlement_value": "Saldo en USD en tarjeta virtual",
    "rates.row.topup": "Métodos de recarga",
    "rates.row.topup_value": "Tarjeta · Débito · Banco (ACH)",
    "rates.sim.eyebrow": "Cotización en vivo",
    "rates.sim.title_a": "Envía cualquier monto,",
    "rates.sim.title_b": "ve el desglose al instante.",
    "rates.sim.amount_label": "Monto a enviar",
    "rates.sim.line.principal": "En tarjeta (principal)",
    "rates.sim.line.service": "Comisión de servicio · {pct}",
    "rates.sim.line.gateway": "Gateway · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Tú pagas",
    "rates.sim.cta": "Comenzar con ${amount} →",
    "rates.sim.note": "Las tarifas aplicadas arriba son configuradas por el administrador. El receptor siempre recibe el principal completo.",
    "dd.title": "Depósito Directo",
    "dd.intro": "Envía dinero a la cuenta bancaria de un receptor en cualquier parte del mundo. Paga con tarjeta (Stripe Checkout) o con tu saldo de Zcardon — nosotros gestionamos la transferencia al banco del receptor automáticamente donde se admite, o manualmente para países que aún no enrutamos de forma automática. Cada transacción queda registrada con un recibo imprimible.",
    "dd.new_btn": "Registrar un nuevo depósito directo",
    "dd.close": "Cerrar",
    "dd.history_eyebrow": "Tus depósitos",
    "dd.loading": "Cargando…",
    "dd.empty": "Aún no hay depósitos directos.",
    "dd.status.approved": "aprobado",
    "dd.status.pending": "pendiente de aprobación",
    "dd.status.rejected": "rechazado",
    "dd.row.confirmed": "✓ Receptor confirmó la recepción",
    "dd.row.disputed": "⚠ Receptor reportó una discrepancia",
    "dd.row.pdf": "PDF",
    "dd.error.load": "No se pudieron cargar los depósitos",
    "dd.error.pdf": "No se pudo descargar el recibo",
    "pay.mode.view": "Tu tarjeta Zcardon",
    "pay.mode.charge": "Cobrar con QR",
    "pay.heading.view": "Hola, {name}.",
    "pay.heading.charge": "Cobrar esta tarjeta.",
    "pay.cardholder": "Titular",
    "pay.paused.label": "Tarjeta pausada",
    "pay.paused.body": "Esta tarjeta ha sido congelada y no puede recibir nuevos cargos hasta que el titular la reactive.",
    "pay.balance.label": "Saldo disponible",
    "pay.charges_count": "{count} cargo",
    "pay.charges_count_plural": "{count} cargos",
    "pay.topups_count": "{count} recarga",
    "pay.topups_count_plural": "{count} recargas",
    "pay.activity.label": "Actividad reciente",
    "pay.activity.empty": "Sin actividad aún",
    "pay.cta.charge": "Cobrar esta tarjeta (comercio)",
    "pay.cta.charge_paused": "Tarjeta pausada",
    "pay.cta.note_active": "Pulsa arriba solo si eres el comercio que acepta el pago de esta tarjeta.",
    "pay.cta.note_paused": "Esta tarjeta ha sido pausada por el remitente. Pídele que la reactive.",
    "pay.back_to_balance": "Volver al saldo",
    "pay.form.amount_label": "Monto (USD)",
    "pay.form.amount_max": "Máx {amount}",
    "pay.form.merchant_label": "Comercio / motivo",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Nota (opcional)",
    "pay.form.memo_placeholder": "Dos lattes",
    "pay.form.submit_processing": "Procesando…",
    "pay.form.submit_paused": "Tarjeta pausada",
    "pay.form.submit_charge": "Cobrar {amount}",
    "pay.form.submit_charge_card": "Cobrar tarjeta",
    "pay.form.disclaimer": "Al cobrar esta tarjeta confirmas que el titular te ha autorizado a gastar el monto indicado.",
    "pay.receipt.title": "Pago completado",
    "pay.receipt.to": "a",
    "pay.receipt.id": "recibo",
    "pay.receipt.time": "hora",
    "pay.receipt.new_balance": "nuevo saldo",
    "pay.receipt.done": "Listo · volver al saldo",
    "pay.error.cant_load": "No se puede cargar esta tarjeta",
    "pay.error.back_home": "Volver al inicio",
    "pay.error.loading": "Cargando tarjeta…",
    "pay.error.charge_failed": "Cargo fallido",
    "pay.tap.eyebrow": "Toca para pagar",
    "pay.tap.title": "Muestra este código",
    "pay.tap.subtitle": "al lector del comercio",
    "pay.tap.balance": "Saldo",
    "pay.tap.paused": "Pausado",
    "pay.tap.qr_unavailable": "QR no disponible para esta tarjeta",
    "pay.tap.refresh_in": "Se actualiza en {secs}s",
    "pay.tap.show_digits": "Mostrar dígitos",
    "pay.tap.hide_digits": "Ocultar dígitos",
    "pay.tap.card_label": "Tarjeta · ••••",
    "pay.tap.auto_hides": "Se oculta automáticamente en 60s",
    "pay.tap.full_only_image": "El número completo solo se muestra en la imagen original de la tarjeta",
    "pay.tap.disclaimer": "Tarjeta de circuito cerrado · utilizable en cualquier comercio habilitado con Zcardon. El QR rota cada 60s como protección antirrepetición.",
    "pay.subscribe.eyebrow": "Mantente informado",
    "pay.subscribe.title": "Recibe un correo por cada transacción",
    "pay.subscribe.desc": "Gratis. Un correo por cargo o recarga. Cancela cuando quieras. Nunca compartimos tu dirección.",
    "pay.subscribe.placeholder": "tu@ejemplo.com",
    "pay.subscribe.cta": "Notificarme",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Estás suscrito",
    "pay.subscribe.done.body": "Enviaremos un correo a {email} al instante por cada cargo o recarga a la tarjeta •••• {last4}.",
    "pay.subscribe.toast.already": "Ya estabas suscrito — no se envió un duplicado.",
    "pay.subscribe.toast.success": "Recibirás un correo por cada transacción. ¡Bienvenido!",
    "pay.subscribe.toast.failed": "Error al suscribirse",
    "pay.save.eyebrow": "Lleva tu tarjeta contigo",
    "pay.save.title": "Guarda esta tarjeta en tu teléfono",
    "pay.save.desc": "Guarda la imagen de la tarjeta en tus fotos para acceso sin conexión — muestra el QR a los comercios en cualquier momento, sin necesidad de app.",
    "pay.save.cta": "Guardar imagen de tarjeta",
    "pay.save.preparing": "Preparando…",
    "pay.save.add_home": "Añadir a la pantalla de inicio",
    "pay.save.toast.opened": "Imagen de tarjeta abierta — mantén presionado para guardar en Fotos",
    "pay.save.toast.failed": "No se pudo guardar la tarjeta",
    "pay.save.ios.title": "Para añadir Zcardon a la pantalla de inicio de tu iPhone:",
    "pay.save.ios.step1": "Toca el botón Compartir en la parte inferior de Safari",
    "pay.save.ios.step2": "Desplázate hacia abajo y toca Añadir a la pantalla de inicio",
    "pay.save.ios.step3": "Toca Añadir para confirmar",
    "pay.save.ios.note": "Zcardon aparecerá como cualquier otra app — toca el ícono para abrir esta tarjeta al instante.",
    "pay.thanks.eyebrow": "Envía un agradecimiento",
    "pay.thanks.body": "${amount} acaba de llegar — hazle saber a quien recargó esta tarjeta que fue de ayuda.",
    "pay.thanks.placeholder": "Opcional — escribe algo…",
    "pay.thanks.cta": "Enviar {emoji} gracias",
    "pay.thanks.sending": "Enviando…",
    "pay.thanks.done": "Agradecimiento enviado — lo verán en segundos.",
    "pay.thanks.toast.sent": "Agradecimiento enviado",
    "pay.thanks.toast.failed": "No se pudo enviar el agradecimiento",
    "pay.activity.topup_bank": "Recarga por transferencia bancaria",
    "pay.activity.topup_debit": "Recarga con tarjeta de débito",
    "pay.activity.topup_credit": "Recarga con tarjeta de crédito",
    "pay.activity.topup_crypto": "Recarga con criptomoneda",
    "pay.activity.topup_received": "Recarga recibida",
    "pay.activity.merchant_charge": "Cargo de comercio",
    "pay.toast.payment_received": "Pago recibido · −{amount}",
    "cash.confirming": "Confirmando pago…",
    "cash.confirming.sub": "Stripe está finalizando el cargo.",
    "cash.notfound.title": "Enlace expirado",
    "cash.notfound.body": "No encontramos esta solicitud de efectivo. El enlace puede ser de una sesión anterior o la tarjeta nunca se finalizó. Inicia una nueva solicitud — solo toma un minuto, y cualquier pago que hayas realizado se acreditará automáticamente en cuanto el conciliador en segundo plano lo detecte.",
    "cash.notfound.ref": "ref:",
    "cash.notfound.start": "Iniciar una nueva solicitud",
    "cash.notfound.contact": "Contactar soporte",
    "cash.error.title": "No se pudo verificar el estado",
    "cash.error.return": "Volver al inicio",
    "cash.autopay.opening": "Abriendo Tap-to-Pay…",
    "cash.autopay.redirect": "Redirigiendo en {secs}s.",
    "cash.autopay.stay": "Quedarse aquí",
    "cash.ready.title": "Tu tarjeta está lista",
    "cash.ready.subtitle": "Recargada con ${amount} USD · también enviado por mensaje a {phone}",
    "cash.card.label": "Tarjeta",
    "cash.card.exp": "Vence",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Saldo",
    "cash.cta.open_pay": "Abrir pantalla Tap-to-Pay",
    "cash.toast.funded": "Tu tarjeta está cargada — ¡revisa tu teléfono para el SMS!",
    "cash.pending.title": "Esperando a Stripe",
    "cash.pending.body": "Estamos consultando por confirmación. Esto suele tardar 5-15 segundos. Si cerraste el pago sin pagar, puedes empezar de nuevo desde la página de inicio.",
    "cash.pending.back": "Volver al inicio",
    "cash.timeout.title": "No se pudo confirmar el pago",
    "cash.timeout.body": "Esperamos 3 minutos a que Stripe confirmara. Generalmente esto significa que el pago se cerró antes de pagar — pero si SÍ pagaste, tu tarjeta se activará automáticamente en los próximos minutos (nuestro reconciliador en segundo plano se ejecuta cada 5 min) y te lo enviaremos a {phone} en cuanto suceda.",
    "cash.timeout.start_over": "Empezar de nuevo",
    "cash.timeout.try_again": "Intentar de nuevo",
    "cash.resend.title": "¿No lo recibiste? Enviar de nuevo",
    "cash.resend.degraded.lead": "La entrega a números de EE. UU. está degradada ahora mismo.",
    "cash.resend.degraded.sms": "Los mensajes a teléfonos de EE. UU. pueden ser bloqueados silenciosamente por el operador (verificación de número gratuito pendiente).",
    "cash.resend.degraded.email": "El correo está en modo de prueba y solo se entrega a la dirección del titular de la cuenta mientras se valida el DNS.",
    "cash.resend.degraded.tail": "Si un canal no funciona, prueba el otro — o abre esta página en el dispositivo del receptor para obtener la tarjeta directamente.",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Correo",
    "cash.resend.sending": "Enviando…",
    "cash.resend.email_sandbox": "El correo está en modo de prueba. Mientras el dominio zcardon.com se verifica en Resend, solo podemos enviar a la dirección del titular de la cuenta. Si tu correo no es aceptado, usa SMS o WhatsApp.",
    "cash.resend.email_label": "Dirección de correo",
    "cash.resend.email_send": "Enviar por correo",
    "cash.resend.cancel": "Cancelar",
    "cash.resend.limit": "Límite de 5 reenvíos por tarjeta. Teléfono registrado: {phone}",
    "cash.resend.no_phone": "(ninguno)",
    "cash.wallet.title": "Agregar a Wallet",
    "cash.wallet.apple": "Agregar a Apple Wallet",
    "cash.wallet.google": "Agregar a Google Wallet",
    "cash.wallet.generating": "Generando pase…",
    "cash.wallet.note": "Permanece en tu pantalla de bloqueo · Se actualiza automáticamente al cambiar el saldo",
    "cash.wallet.toast.apple": "Pase descargado — ábrelo en tu iPhone para agregarlo a Apple Wallet",
    "cash.wallet.toast.apple_failed": "No se pudo generar el pase de Apple Wallet",
    "cash.wallet.toast.google_failed": "No se pudo generar el pase de Google Wallet",
    "cash.delivery.fallback_title": "Enviado vía {fallback}",
    "cash.delivery.fallback_body": "Tu operador bloqueó el SMS original, así que reenviamos tu tarjeta vía {fallback} a {phone}. Revisa ese canal.",
    "cash.delivery.toll_blocked": "El operador bloqueó el SMS",
    "cash.delivery.failed_title": "No pudimos enviarte tu tarjeta por SMS",
    "cash.delivery.toll_body": "Tu operador rechazó el mensaje (esto ocurre con nuevos remitentes pendientes de verificación). Guarda tu tarjeta ahora: toca",
    "cash.delivery.failed_body": "Tu tarjeta está cargada — guárdala ahora ya que el SMS no llegó. Toca",
    "cash.delivery.cta_label": "Abrir pantalla Tap-to-Pay",
    "cash.delivery.body_tail": "arriba, toma una captura de pantalla o usa Correo abajo para enviarte una copia.",
    "cash.handoff.eyebrow": "Entrega en persona",
    "cash.handoff.body": "Pide al receptor que escanee esto con su cámara — la tarjeta se abre directamente en su teléfono, sin SMS ni correo.",
    "cash.handoff.share_label": "O comparte este enlace",
    "cash.handoff.copy": "Copiar enlace",
    "cash.handoff.copied": "✓ Copiado",
    "cash.handoff.share": "Compartir",
    "cash.handoff.toast.copied": "Enlace copiado — pégalo donde quieras",
    "cash.handoff.toast.copy_failed": "No se pudo copiar. Mantén presionado el enlace para copiarlo manualmente.",
    "portal.loading": "Cargando tu recibo…",
    "portal.unavailable.title": "Enlace no disponible",
    "portal.unavailable.body": "Este enlace no es válido o ha expirado.",
    "portal.unavailable.hint": "Si el remitente te compartió este enlace, pídele que reenvíe el correo del recibo.",
    "portal.brand": "Portal de recibos",
    "portal.eyebrow": "{sender} te envió",
    "portal.reference": "Referencia",
    "portal.pending.title": "Aprobación pendiente",
    "portal.pending.body": "El remitente registró esta transferencia pero Zcardon aún la está revisando. Vuelve pronto — el recibo estará disponible una vez aprobado.",
    "portal.row.bank": "Banco",
    "portal.row.account": "Cuenta",
    "portal.row.routing": "Número de ruta",
    "portal.row.memo": "Nota",
    "portal.confirmed.title": "Confirmaste la recepción",
    "portal.confirmed.body": "Confirmado el {when}. El remitente puede ver esto.",
    "portal.disputed.title": "Reportaste una discrepancia",
    "portal.disputed.body": "Reportado el {when}. Se ha notificado al remitente por correo.",
    "portal.actions.body": "¿Ha llegado este monto a tu cuenta? Confirmar cierra el ciclo con el remitente — Zcardon no mueve dinero, esto es solo un registro.",
    "portal.actions.confirm": "Sí, lo recibí",
    "portal.actions.flag": "Reportar un problema",
    "portal.dispute.placeholder": "Monto incorrecto, no llegó, número de cuenta equivocado…",
    "portal.dispute.submit": "Enviar reporte",
    "portal.dispute.too_short": "Agrega una razón breve (al menos 4 caracteres)",
    "portal.toast.confirmed": "Gracias — recibo confirmado.",
    "portal.toast.confirm_failed": "No se pudo confirmar el recibo",
    "portal.toast.reported": "Reportado. Se ha notificado al remitente.",
    "portal.toast.report_failed": "No se pudo enviar el reporte",
    "portal.toast.pdf_failed": "No se pudo descargar el recibo",
    "portal.download_pdf": "Descargar recibo en PDF",
    "portal.privacy": "Esta página es privada para ti. Cualquier persona con el enlace puede ver este recibo — no lo compartas públicamente. Preguntas:",
    "detail.back": "Volver al panel",
    "detail.eyebrow": "Tu tarjeta",
    "detail.confirming_payment": "Confirmando tu pago…",
    "detail.active_ready": "La tarjeta está activa y lista para compartir.",
    "detail.balance.label": "Saldo actual",
    "detail.toast.notfound": "Tarjeta no encontrada",
    "detail.toast.loaded": "Fondos cargados a la tarjeta.",
    "detail.toast.confirmed": "Pago confirmado.",
    "detail.toast.expired": "Sesión de pago expirada",
    "detail.delivery.receiver_fallback": "tu destinatario",
    "detail.delivery.received_title": "{name} abrió la tarjeta",
    "detail.delivery.received_when": "Entrega confirmada · {when}",
    "detail.delivery.waiting_title_on": "Recibirás un correo en cuanto sea recibida",
    "detail.delivery.waiting_title_off": "Las notificaciones de entrega están desactivadas",
    "detail.delivery.waiting_body_on": "En cuanto tu destinatario escanee el QR de esta tarjeta, te avisaremos por correo — solo una vez.",
    "detail.delivery.waiting_body_off": "Activa esto para recibir un correo único en el momento en que tu destinatario abra la tarjeta por primera vez.",
    "detail.delivery.toggle_on": "Activado",
    "detail.delivery.toggle_off": "Activar",
    "detail.delivery.toast_enabled": "Se te notificará cuando se abra la tarjeta.",
    "detail.delivery.toast_disabled": "Notificaciones de entrega desactivadas.",
    "detail.delivery.toast_failed": "No se pudieron actualizar las notificaciones de entrega.",
    "nudge.eyebrow": "Avisa a tu destinatario",
    "nudge.title": "Envía un aviso a {receiver}",
    "nudge.subtitle": "Redactamos un mensaje breve para que {receiver} sepa que la tarjeta está activa y cómo usarla.",
    "nudge.receiver_fallback": "tu destinatario",
    "nudge.sender_fallback": "un amigo",
    "nudge.template": "Hola {receiver} — {sender} te acaba de enviar una tarjeta virtual Zcardon cargada con {amount}. Toca para pagar aquí: {url}\\n\\nNo se requiere app. El enlace funciona en cualquier teléfono.",
    "nudge.email_subject": "{sender} te envió una tarjeta Zcardon de {amount}",
    "nudge.action_copy": "Copiar mensaje",
    "nudge.action_sms": "Abrir SMS",
    "nudge.action_email": "Abrir correo",
    "nudge.toast_copied": "Mensaje copiado al portapapeles.",
    "nudge.toast_copy_failed": "No se pudo copiar. Selecciona y copia manualmente.",
    "ios_install.title": "Instala Zcardon en tu iPhone",
    "ios_install.step_1": "Toca",
    "ios_install.share_label": "Compartir",
    "ios_install.step_2": " y elige \\\"Agregar a pantalla de inicio\\\" — Zcardon se abrirá como app en pantalla completa.",
    "smart_banner.title": "Abrir en la app de Zcardon",
    "smart_banner.subtitle": "Pago más rápido. Toca para pagar sin salir de la app.",
    "smart_banner.cta": "Abrir",
    "smart_banner.dismiss": "Descartar",
    "social_proof.eyebrow": "En vivo · últimas 48 horas",
    "social_proof.empty": "Esperando la próxima tarjeta…",
    "social_proof.line_opened": "{name} acaba de abrir una tarjeta de {amount}.",
    "social_proof.line_issued": "{name} recibió una tarjeta de {amount} de un amigo.",
    "social_proof.line_deposit": "{name} recibió un depósito directo de {amount}.",
    "social_proof.stat_funded": "Tarjetas financiadas hoy",
    "social_proof.stat_delivered": "Tarjetas entregadas hoy",
    "social_proof.stat_recent": "Actividad reciente",
    "social_proof.ago_seconds": "hace {n}s",
    "social_proof.ago_minutes": "hace {n}m",
    "social_proof.ago_hours": "hace {n}h",
    "flight.eyebrow": "Tarjetas en camino · últimas 48h",
    "flight.title": "Dónde llega Zcardon",
    "flight.subtitle": "Ubicaciones reales de destinatarios, anonimizadas por región. Solo mostramos países y estados de EE. UU. — nunca nombres, direcciones ni montos.",
    "flight.row_template": "Tarjeta abierta en {label}",
    "flight.more_count": "+ {n} regiones más",
    "flight.ago_seconds": "hace {n}s",
    "flight.ago_minutes": "hace {n}m",
    "flight.ago_hours": "hace {n}h",
    "trust_strip.eyebrow": "Señal real · en vivo desde la base de datos",
    "trust_strip.title_a": "No son testimonios.",
    "trust_strip.title_b": "Lecturas en vivo de la base de datos.",
    "trust_strip.how_link": "Cómo recopilamos esto",
    "trust_strip.modal_eyebrow": "Fuentes de datos",
    "trust_strip.modal_title": "Todo real, todo anonimizado",
    "trust_strip.modal_close": "Cerrar",
    "trust_strip.modal_intro": "Cada número en esta franja proviene de nuestra propia base de datos — nunca de un feed de terceros, nunca inventado. Aquí se explica de dónde proviene cada sección y cómo lo anonimizamos antes de mostrártelo.",
    "trust_strip.src_carousel_title": "Carrusel de actividad en vivo",
    "trust_strip.src_carousel_body": "Extrae datos de las colecciones cards y direct_deposits, últimas 48h. Los nombres se muestran como nombre + inicial del apellido; los montos se redondean al $5 más cercano para evitar triangulación. Se actualiza cada 60 segundos.",
    "trust_strip.src_flight_title": "Dónde llega Zcardon",
    "trust_strip.src_flight_body": "Lee los escaneos QR de card_scans, geolocalizados en el momento del escaneo. Agrupados por país (o estado de EE. UU.). Sin precisión de ciudad por debajo del nivel estatal, sin IPs visibles, sin identificadores de tarjeta. Se actualiza cada 90 segundos.",
    "trust_strip.src_badges_title": "Cifras de confianza de los últimos 30 días",
    "trust_strip.src_badges_body": "Solo conteos agregados — nunca datos por fila. Cada métrica solo aparece una vez que supera un tamaño mínimo de muestra, por lo que una tasa del 100% con 2 confirmaciones quedaría oculta. Se actualiza cada minuto.",
    "trust_strip.modal_promise": "Nunca publicaremos nombres, direcciones, montos exactos, IPs ni números de tarjeta en widgets públicos. Lo prometemos.",
    "trust_badge.eyebrow": "Cifras reales · últimos 30 días",
    "trust_badge.title": "La gente realmente usa esto.",
    "trust_badge.subtitle": "No es un testimonio — es un conteo en vivo, actualizado desde la base de datos cada minuto.",
    "trust_badge.confirmed_label": "destinatarios confirman la recepción en menos de 24h",
    "trust_badge.confirmed_sub": "de {n} respuestas en los últimos 30 días",
    "trust_badge.thanks_label": "notas de agradecimiento enviadas",
    "trust_badge.thanks_sub": "de destinatarios en los últimos 30 días",
    "trust_badge.cards_label": "tarjetas financiadas",
    "trust_badge.cards_sub": "últimos 30 días · conteo en vivo",
    "pulse.label": "PULSO EN VIVO",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} abrió una tarjeta de {amount}",
    "pulse.tooltip_issued": "{name} obtuvo una tarjeta de {amount}",
    "pulse.tooltip_deposit": "{name} recibió un depósito de {amount}",
    "pulse.ago_seconds": "hace {n}s",
    "pulse.ago_minutes": "hace {n}m",
    "pulse.ago_hours": "hace {n}h",
    "note_cloud.eyebrow": "Para qué lo usan",
    "note_cloud.title": "Razones reales. Personas reales.",
    "note_cloud.subtitle": "Datos agregados de {n} notas de tarjetas en los últimos 90 días. Solo mostramos palabras que aparecen en al menos {min} notas distintas — tu mensaje es tuyo.",
    "note_cloud.footnote": "Nombres, montos e información de contacto se filtran antes de mostrarse.",
    "note_cloud.lang_eyebrow": "¿En qué idioma están estas notas?",
    "note_cloud.lang_other": "Otro",
    "download.button": "Descargar app",
    "download.close": "Cerrar",
    "download.modal_eyebrow": "Obtén Zcardon",
    "download.modal_title": "Instala Zcardon en tu teléfono",
    "download.footnote": "Aún no hay App Store — estás instalando la app web segura directamente. Se actualiza al instante.",
    "download.installed_title": "Zcardon instalado",
    "download.installed_body": "Busca el ícono en tu pantalla de inicio.",
    "download.webview_warning": "Abre esta página en el navegador de tu teléfono (Safari en iPhone, Chrome en Android) para instalar — los navegadores integrados no pueden agregar a la pantalla de inicio.",
    "download.ios_step1_title": "En Safari",
    "download.ios_step1_body": "Si aún no estás en Safari, copia este enlace y ábrelo allí. (Chrome y otros navegadores de iOS no pueden instalar apps.)",
    "download.ios_step2_title": "Toca Compartir",
    "download.ios_step2_body": "El ícono de Compartir está en la parte inferior central del iPhone, arriba a la derecha en iPad.",
    "download.ios_step3_title": "Agregar a pantalla de inicio",
    "download.ios_step3_body": "Desplázate hacia abajo en el menú de compartir y elige \\\"Agregar a pantalla de inicio\\\". Zcardon se instala como app de pantalla completa.",
    "download.android_ready_body": "Chrome detectó Zcardon y está listo para instalar. Toca abajo.",
    "download.android_install": "Instalar Zcardon",
    "download.android_installing": "Instalando…",
    "download.android_step1_title": "Abre el menú de Chrome",
    "download.android_step1_body": "Toca los tres puntos en la parte superior derecha de Chrome.",
    "download.android_step2_title": "Agregar a pantalla de inicio",
    "download.android_step2_body": "Elige \\\"Instalar app\\\" o \\\"Agregar a pantalla de inicio\\\". Zcardon se instala como app de pantalla completa.",
    "download.desktop_body": "Escanea este código QR con la cámara de tu teléfono para abrir Zcardon allí, luego instálalo desde el navegador de tu teléfono.",
    "download.steps_hint": "Estos pasos ocurren en tu navegador, no en esta página. Ninguna de las filas de abajo es interactiva — describen qué buscar.",
    "download.ios_arrow_label": "Toca esto en Safari",
    "download.android_arrow_label": "Mira aquí arriba en Chrome",
    "install.eyebrow": "Instalar Zcardon",
    "install.title": "Agrega Zcardon a tu pantalla de inicio",
    "install.subtitle": "Dos toques y tendrás Zcardon como ícono de app real — sin necesidad de App Store.",
    "install.back_home": "Abrir Zcardon en el navegador",
    "install.continue": "Continuar a Zcardon →",
    "install.copy": "Copiar",
    "install.copied": "Copiado",
    "install.ios_step1_title": "Abre esta página en Safari",
    "install.ios_step1_body": "Si escaneaste desde otro navegador, copia la URL y ábrela en Safari primero — Chrome en iOS no puede instalar apps.",
    "install.ios_step2_title": "Toca el botón Compartir",
    "install.ios_step2_body": "Es el cuadrado con una flecha apuntando hacia arriba — en la parte inferior de Safari (o arriba a la derecha en iPad).",
    "install.ios_step3_title": "Toca \\\"Agregar a pantalla de inicio\\\"",
    "install.ios_step3_body": "Desplázate hacia abajo en el menú de compartir y elige \\\"Agregar a pantalla de inicio\\\", luego confirma. Zcardon aparece como ícono de app.",
    "install.ios_arrow_label": "Busca este ícono",
    "install.android_ready_title": "Listo para instalar",
    "install.android_ready_body": "Chrome detectó Zcardon como instalable. Toca abajo y apareceremos como ícono de app en tu pantalla de inicio.",
    "install.android_install": "Instalar Zcardon",
    "install.android_installing": "Instalando…",
    "install.android_step1_title": "Toca el menú de Chrome",
    "install.android_step1_body": "Son los tres puntos en la esquina superior derecha de Chrome.",
    "install.android_step2_title": "Toca \\\"Instalar app\\\" o \\\"Agregar a pantalla de inicio\\\"",
    "install.android_step2_body": "Confirma para agregar el ícono de Zcardon a tu teléfono. Tócalo cuando quieras para abrirlo.",
    "install.android_hint": "Si aún no ves \\\"Instalar app\\\", navega por la página de inicio una vez — Chrome lo habilita tras una breve visita.",
    "install.desktop_title": "Escanea con tu teléfono",
    "install.desktop_body": "Zcardon es una app pensada para móvil. Abre esta URL en tu teléfono para instalarla:",
    "install.webview_title": "Abre en tu navegador",
    "install.webview_body": "Los navegadores integrados (Instagram, Facebook, TikTok) no pueden instalar apps. Toca \\\"…\\\" o el menú de compartir, luego elige \\\"Abrir en Safari\\\" / \\\"Abrir en Chrome\\\". O copia este enlace:",
    "install.already_title": "Ya estás en la app",
    "install.already_body": "Zcardon está instalado en este dispositivo. Toca el ícono en tu pantalla de inicio para abrirlo cuando quieras.",
    "install.installed_title": "Instalado",
    "install.installed_body": "Busca el ícono de Zcardon en tu pantalla de inicio — esa es tu app.",
    "install.steps_hint": "Estos pasos ocurren en tu navegador, no en esta página. Ninguna de las filas de abajo es interactiva — describen qué buscar en el menú del navegador.",
    "install.desktop_body_qr": "Zcardon se instala como ícono de app en iPhone o Android — no en escritorio. Apunta la cámara de tu teléfono a este código QR:",
    "install.desktop_or": "O copia el enlace",
    "install.android_arrow_label": "Mira aquí arriba en Chrome",
    "install.ios_arrow_caption": "Está en la parte inferior de Safari",
    "vietqr.disabled.title": "Recibe VND vía VietQR",
    "vietqr.disabled.badge": "SOLO VIETNAM",
    "vietqr.disabled.desc": "Vincula una cuenta bancaria vietnamita y el QR de esta tarjeta se convertirá en un VietQR real — cualquier app bancaria vietnamita (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) podrá escanearlo y pagarte VND directamente vía la red NAPAS. No requiere aprobación de Stripe Issuing.",
    "vietqr.disabled.btn_enter": "Introducir datos bancarios",
    "vietqr.disabled.or": "o",
    "vietqr.disabled.btn_send_link": "Enviar enlace de configuración al destinatario",
    "vietqr.disabled.btn_send_link_tip": "Genera un enlace público válido 7 días para que el destinatario lo complete",
    "vietqr.form.warn_title": "Léelo antes de activar",
    "vietqr.form.warn_body_a": "Los escaneos ya no se dirigirán a tu página de seguimiento de Zcardon. Los fondos van",
    "vietqr.form.warn_body_b": "directamente a tu cuenta bancaria vietnamita",
    "vietqr.form.warn_body_c": "vía interbancario NAPAS — Zcardon nunca ve el dinero. VietQR solo funciona dentro de Vietnam para transferencias en VND.",
    "vietqr.form.bank": "Banco",
    "vietqr.form.account_number": "Número de cuenta",
    "vietqr.form.account_name": "Titular de la cuenta (25 caracteres)",
    "vietqr.form.city": "Ciudad (15 caracteres)",
    "vietqr.form.advanced": "Avanzado — importe fijo / referencia (opcional)",
    "vietqr.form.amount_vnd": "Importe (VND)",
    "vietqr.form.amount_ph": "déjalo vacío para importe variable",
    "vietqr.form.reference": "Referencia (25 caracteres)",
    "vietqr.form.confirm": "Entiendo que al escanear esta tarjeta los VND se transferirán directamente a la cuenta bancaria de arriba. Zcardon no procesa ni retiene estos fondos.",
    "vietqr.form.btn_attach": "Activar VietQR",
    "vietqr.form.btn_attaching": "Activando…",
    "vietqr.form.btn_cancel": "Cancelar",
    "vietqr.enabled.title": "VietQR activo",
    "vietqr.enabled.desc": "Cuando una app bancaria vietnamita escanee esta tarjeta, transferirá VND directamente a la cuenta de abajo — sin pasar por Zcardon.",
    "vietqr.enabled.bank_label": "Banco",
    "vietqr.enabled.account_label": "Cuenta",
    "vietqr.enabled.fixed_amount": "Importe fijo",
    "vietqr.enabled.reference": "Referencia",
    "vietqr.enabled.btn_detach": "Desvincular VietQR",
    "vietqr.enabled.btn_detaching": "Desvinculando…",
    "vietqr.enabled.attached_at": "Activado el",
    "vietqr.analytics.live_activity": "Actividad en vivo",
    "vietqr.analytics.today": "hoy",
    "vietqr.analytics.days_live": "{n}d activo",
    "vietqr.analytics.phantom_views": "Vistas estimadas",
    "vietqr.analytics.confirmed_pays": "Pagos confirmados",
    "vietqr.analytics.total_vnd": "Total VND",
    "vietqr.analytics.recent_confirmed": "Confirmados recientes",
    "vietqr.analytics.phantom_explainer": "Vistas estimadas ≈ descargas de la imagen de la tarjeta desde la activación. Los escaneos reales de VietQR no pasan por nuestros servidores, así que estimamos a partir de la actividad de \"tarjeta mostrada\". Pide al destinatario que confirme los pagos vía su enlace de configuración para datos precisos.",
    "vietqr.toast.required": "El número de cuenta y el nombre son obligatorios.",
    "vietqr.toast.confirm_first": "Confirma que entiendes que VietQR envía VND a tu banco, sin pasar por Zcardon.",
    "vietqr.toast.attached_default": "VietQR activado.",
    "vietqr.toast.attach_failed": "No se pudo activar VietQR.",
    "vietqr.toast.detach_confirm": "¿Desvincular VietQR? El QR volverá al enlace de seguimiento de Zcardon.",
    "vietqr.toast.detached": "VietQR desvinculado — el QR vuelve al enlace de seguimiento de Zcardon.",
    "vietqr.toast.detach_failed": "No se pudo desvincular.",
    "vietqr.toast.link_copied": "Enlace de configuración copiado · expira en {h}h",
    "vietqr.toast.link_generated": "Enlace generado · copia abajo",
    "vietqr.toast.link_failed": "No se pudo generar el enlace.",

    "setpw.toast.too_short": "La contraseña debe tener al menos 8 caracteres",
    "setpw.toast.no_match": "Las contraseñas no coinciden",
    "setpw.toast.success_signin_again": "Contraseña establecida — por favor inicia sesión",
    "setpw.toast.welcome": "Bienvenido al programa de agentes Zcardon",
    "setpw.toast.expired": "Enlace expirado o inválido",
    "setpw.verifying": "Verificando tu invitación…",
    "setpw.invalid.title": "Enlace expirado",
    "setpw.invalid.desc": "Este enlace de bienvenida ha expirado o ya fue usado. Contacta a tu administrador de Zcardon para recibir una nueva invitación.",
    "setpw.invalid.home_btn": "Inicio",
    "setpw.invalid.support_btn": "Contactar soporte",
    "setpw.fallback_name": "agente",
    "setpw.eyebrow": "Bienvenida al agente",
    "setpw.heading": "Bienvenido, {name}",
    "setpw.subheading": "Establece tu contraseña para activar tu cuenta de agente. Iniciarás sesión de inmediato.",
    "setpw.code_label": "Tu código de agente",
    "setpw.code_tag": "Compártelo al incorporar clientes",
    "setpw.form.title": "Elige una contraseña",
    "setpw.form.new_label": "Nueva contraseña",
    "setpw.form.placeholder": "Mínimo 8 caracteres",
    "setpw.form.confirm_label": "Confirmar contraseña",
    "setpw.form.btn_busy": "Activando…",
    "setpw.form.btn_submit": "Activar mi cuenta de agente",
    "tx.heading": "Transacciones",
    "tx.kind.direct_deposit": "Depósito directo",
    "tx.kind.card_topup": "Recarga de tarjeta",
    "tx.kind.outbound_payout": "Pago saliente",
    "tx.toast.load_failed": "Error al cargar las transacciones",
    "tx.summary.deposits": "Depósitos",
    "tx.summary.topups": "Recargas",
    "tx.summary.topups_sub": "tarjeta",
    "tx.summary.payouts": "Pagos",
    "tx.summary.payouts_sub": "saliente",
    "tx.filter.all": "Todos",
    "tx.filter.deposits": "Depósitos",
    "tx.filter.topups": "Recargas",
    "tx.filter.payouts": "Pagos",
    "tx.loading": "Cargando transacciones…",
    "tx.empty.filtered": "Ninguna transacción coincide con este filtro",
    "tx.empty.none": "Aún no hay transacciones",
    "tx.empty.cta": "Empieza con un depósito directo →",
    "tx.open_title": "Abrir",
    "terms.load_error_default": "Error al cargar los términos",
    "terms.toast.sign_and_check": "Por favor firma y confirma para aceptar el acuerdo",
    "terms.toast.recorded": "Acuerdo registrado",
    "terms.toast.failed": "Error al enviar",
    "terms.error.title": "Enlace no válido",
    "terms.back_home": "Volver al inicio",
    "terms.loading": "Cargando acuerdo…",
    "terms.done.title": "Acuerdo aceptado",
    "terms.done.meta": "Versión {v} · {ts}",
    "terms.done.next": "Puedes cerrar esta página. Tu tarjeta estará lista para usar en cuanto sea emitida.",
    "terms.eyebrow": "Acuerdo del titular · v{v}",
    "terms.title_reaccept": "Vuelve a aceptar tu acuerdo de titular",
    "terms.title_accept": "Acepta tu acuerdo de titular",
    "terms.desc.sender_sent": "te envió una tarjeta Zcardon",
    "terms.desc.you_were_sent": "Se te ha enviado una tarjeta Zcardon",
    "terms.desc.ending": "terminada en",
    "terms.desc.read_before": "Lee el acuerdo a continuación, luego firma y confirma para activar la tarjeta.",
    "terms.already_accepted_note": "Ya aceptaste una versión anterior. Vuelve a aceptar para actualizar a la más reciente.",
    "terms.scroll_hint": "Desplázate dentro del recuadro para leer el acuerdo completo.",
    "terms.view_json": "Abrir como JSON",
    "terms.sign_label": "Firma con tu nombre legal completo",
    "terms.sign_placeholder": "Como aparece en tu documento de identidad",
    "terms.consent": "He leído y acepto el Acuerdo de Titular de Tarjeta Zcardon v{v}.",
    "terms.btn_recording": "Grabando…",
    "terms.btn_accept": "Aceptar acuerdo",
    "hub.eyebrow": "Tu hub de recepción",
    "hub.heading.all_done": "Todo listo 🎉",
    "hub.heading.greet": "Hola {name} 👋",
    "hub.fallback_name": "",
    "hub.desc.sender_sent": "te envió una tarjeta Zcardon",
    "hub.desc.you_were_sent": "Te han enviado una tarjeta Zcardon",
    "hub.desc.ending": "terminada en",
    "hub.desc.complete_steps": "Completa los pasos a continuación para activar todo.",
    "hub.progress.label": "{done} de {total} pasos completados",
    "hub.blocker.title_one": "1 paso obligatorio pendiente",
    "hub.blocker.title_many": "{n} pasos obligatorios pendientes",
    "hub.blocker.subtext": "Tu tarjeta no se activará completamente hasta que estén listos.",
    "hub.required_pill": "Obligatorio",
    "hub.done_pill": "Hecho",
    "hub.setup_btn": "Configurar",
    "hub.refresh": "Actualizar",
    "hub.loading": "Cargando…",
    "hub.error.title": "Error de enlace",
    "hub.error.default": "No se pudo cargar tu hub de recepción.",
    "hub.back_home": "Volver al inicio",
    "hub.footer": "Guarda esta página como favorito · permanece activa 90 días · solo quienes tengan este enlace pueden verla.",
    "onboarding.eyebrow": "Recibe tu transferencia",
    "onboarding.title": "Hola {name}",
    "onboarding.fallback_name": "",
    "onboarding.intro_via": "vía Zcardon",
    "onboarding.intro_ref_open": "(referencia",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Añade tu cuenta bancaria de {country} a continuación para recibir los fondos. Tus datos van directamente a Stripe (nuestro socio de pagos) — el remitente nunca los ve.",
    "onboarding.invalid.title": "Enlace no válido",
    "onboarding.invalid.hint": "Por favor, pide al remitente que envíe un enlace nuevo.",
    "onboarding.loading": "Cargando…",
    "onboarding.complete.title": "Datos bancarios ya registrados",
    "onboarding.complete.desc": "Ya enviaste tu cuenta bancaria. El remitente liberará los fondos en breve.",
    "onboarding.success.title": "Datos bancarios recibidos ✓",
    "onboarding.success.auto_paid": "Gracias — tu cuenta bancaria está verificada y los fondos han sido enviados ahora. Deberían llegar en 1-2 días hábiles.",
    "onboarding.success.pending": "Gracias — tu cuenta bancaria está verificada. Se notificará al remitente y los fondos llegarán a tu cuenta en 1-2 días hábiles.",
    "onboarding.success.ref": "Referencia: {id}",
    "onboarding.beta.title": "Vista previa beta.",
    "onboarding.beta.body": "Esta página está publicada, pero el proceso de pagos en vivo está pendiente de aprobación de Stripe. El envío devolverá un error hasta que la plataforma esté habilitada. Vuelve más tarde o contacta con soporte si es urgente.",
    "onboarding.select_placeholder": "Selecciona…",
    "onboarding.submit_error_default": "Error al enviar. Por favor, inténtalo de nuevo.",
    "onboarding.submit_busy": "Enviando…",
    "onboarding.submit": "Enviar datos bancarios →",
    "onboarding.secure_note": "Protegido por Stripe. Este enlace caduca en 7 días.",
    "onboarding.validate.required": "{field} es obligatorio",
    "onboarding.validate.format": "El formato de {field} parece incorrecto (se esperaba {pattern})",
    "onboarding.validate.no_schema": "No hay esquema de formulario disponible",
    "apply.toast.invalid_image": "Por favor, elige una imagen JPEG, PNG o WEBP",
    "apply.toast.too_large": "La imagen debe pesar 6 MB o menos",
    "apply.toast.upload_both": "Por favor, sube ambas caras de tu licencia de conducir",
    "apply.toast.failed": "Error al enviar",
    "apply.toast.submitted": "Solicitud enviada",
    "apply.success.title": "Solicitud recibida",
    "apply.success.desc": "Gracias, {name}. Revisaremos tu solicitud en 2–3 días hábiles y te escribiremos a {email} con los próximos pasos.",
    "apply.success.back_home": "Volver al inicio",
    "apply.success.contact_support": "Contactar soporte",
    "apply.eyebrow": "Programa de agentes",
    "apply.heading": "Solicita convertirte en agente de Zcardon",
    "apply.intro": "Gana 0.5% de comisión por cada tarjeta que ayudes a emitir, cada recarga que recojas y cada depósito en efectivo que aceptes en nombre de los clientes. Incorporamos agentes de confianza en su comunidad que puedan verificar su identidad.",
    "apply.bullet1": "Obtén tu código de agente único · rastreable en todas las transacciones",
    "apply.bullet2": "Panel con resúmenes de ganancias semanal/mensual/anual",
    "apply.bullet3": "Depósitos bancarios directos — pagados semanalmente",
    "apply.section.your_info": "Tu información",
    "apply.field.full_name": "Nombre legal completo",
    "apply.field.full_name_ph": "Tal como aparece en tu licencia de conducir",
    "apply.field.email": "Correo electrónico",
    "apply.field.phone": "Teléfono",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Número de licencia de conducir",
    "apply.field.address": "Dirección",
    "apply.field.address_ph": "Calle, ciudad, estado, código postal",
    "apply.field.why_join_label": "¿Por qué quieres ser agente? (opcional)",
    "apply.field.why_join_ph": "Cuéntanos sobre tus vínculos comunitarios o experiencia en remesas",
    "apply.dl.section": "Verificación de licencia de conducir",
    "apply.dl.helper": "Sube fotos claras y bien iluminadas de ambos lados. JPEG, PNG o WEBP · máx. 6 MB cada una. Las imágenes se almacenan de forma privada y solo las ven los administradores de Zcardon para verificación.",
    "apply.dl.front": "Frente de la licencia de conducir",
    "apply.dl.back": "Reverso de la licencia de conducir",
    "apply.dl.tap_upload": "Toca para subir",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "Cancelar",
    "apply.submit_busy": "Enviando…",
    "apply.submit": "Enviar solicitud",
    "agent.toast.load_failed": "Error al cargar datos del agente",
    "agent.toast.cust_created": "Cliente {name} creado",
    "agent.toast.cust_failed": "Error al crear cliente",
    "agent.toast.card_issued": "Tarjeta emitida · ganaste {amount}",
    "agent.toast.card_issue_failed": "Error al emitir tarjeta",
    "agent.toast.cash_accepted": "Depósito en efectivo aceptado · ganaste {amount}",
    "agent.toast.cash_failed": "Error al registrar depósito",
    "agent.loading": "Cargando…",
    "agent.eyebrow": "Portal de agentes",
    "agent.fallback_name": "Agente",
    "agent.code_line.code_label": "Código de agente",
    "agent.code_line.commission_label": "comisión",
    "agent.earnings.label": "Saldo pendiente",
    "agent.earnings.sub": "{n} transacciones sin pagar",
    "agent.stat.week": "Esta semana",
    "agent.stat.month": "Este mes",
    "agent.stat.year": "Este año",
    "agent.stat.txns": "{n} txns",
    "agent.stat.customers": "Tus clientes",
    "agent.stat.active": "activos",
    "agent.customers.title": "Tus clientes",
    "agent.customers.empty": "Aún no hay clientes. Toca \\\"Registrar cliente\\\" para incorporar tu primer usuario.",
    "agent.customers.col_customer": "Cliente",
    "agent.customers.col_phone": "Teléfono",
    "agent.customers.col_cards": "Tarjetas",
    "agent.customers.col_commissions": "Comisiones ganadas",
    "agent.customers.col_joined": "Registro",
    "agent.customers.col_actions": "Acciones",
    "agent.customers.txns": "{n} txns",
    "agent.commissions.title": "Comisiones recientes",
    "agent.commissions.empty": "Aún no hay comisiones. Emite tu primera tarjeta para ganar.",
    "agent.commissions.col_when": "Cuándo",
    "agent.commissions.col_customer": "Cliente",
    "agent.commissions.col_type": "Tipo",
    "agent.commissions.col_txn": "Transacción",
    "agent.commissions.col_commission": "Comisión",
    "agent.commissions.col_status": "Estado",
    "agent.commissions.paid": "pagado",
    "agent.commissions.unpaid": "pendiente",
    "agent.type.issuance": "Emisión",
    "agent.type.topup": "Recarga",
    "agent.type.cash_deposit": "Depósito en efectivo",
    "agent.register_btn": "Registrar cliente",
    "agent.dialog.new_cust.title": "Nuevo cliente",
    "agent.dialog.new_cust.desc": "Quedará vinculado a tu código de agente. Cada tarjeta que emitas en su nombre te genera un 0,5% de comisión.",
    "agent.dialog.field.full_name": "Nombre completo",
    "agent.dialog.field.email": "Correo electrónico",
    "agent.dialog.field.phone": "Teléfono",
    "agent.dialog.field.password": "Contraseña inicial (compártela con el cliente)",
    "agent.dialog.cancel": "Cancelar",
    "agent.dialog.creating": "Creando…",
    "agent.dialog.create_customer": "Crear cliente",
    "agent.dialog.issue_card.title": "Emitir tarjeta para {name}",
    "agent.dialog.issue_card.desc": "Financiado con efectivo recibido. El cliente paga una comisión del 3,75% — tú conservas el 0,5%.",
    "agent.dialog.field.receiver_name": "Nombre del receptor",
    "agent.dialog.field.receiver_email": "Correo del receptor",
    "agent.dialog.field.receiver_phone": "Teléfono del receptor",
    "agent.dialog.field.amount_usd": "Monto (USD)",
    "agent.dialog.field.note": "Nota (opcional)",
    "agent.dialog.issuing": "Emitiendo…",
    "agent.dialog.issue_card_btn": "Emitir tarjeta",
    "agent.actions.issue_card": "Emitir tarjeta",
    "agent.dialog.cash_deposit.title": "Depósito en efectivo para {name}",
    "agent.dialog.cash_deposit.desc": "Efectivo recibido del cliente · genera una tarjeta entregada al receptor.",
    "agent.dialog.field.receiver_name_opt": "Nombre del receptor (opcional)",
    "agent.dialog.recording": "Registrando…",
    "agent.dialog.record_deposit": "Registrar depósito",
    "agent.actions.cash_deposit": "Depósito en efectivo",

  },
  fr: {
    "header.nav.home": "Accueil",
    "header.nav.rates": "Tarifs",
    "header.nav.support": "Assistance",
    "header.nav.admin": "Admin",
    "header.nav.signin": "Se connecter",
    "header.nav.signout": "Se déconnecter",
    "header.nav.get_started": "Commencer",
    "header.nav.account": "Compte",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Changer de langue",
    "landing.hero.eyebrow": "Un nouveau rail pour les dépenses numériques",
    "landing.hero.title_a": "Alimentez une carte virtuelle.",
    "landing.hero.title_b_ship": "Envoyez",
    "landing.hero.title_b_rest": "-la à n'importe qui,",
    "landing.hero.title_c": "utilisable en",
    "landing.hero.subtitle": "Émettez une carte de débit virtuelle sans contact avec un QR scannable. Paiement via Stripe, solde en USD, frais de service et de passerelle transparents — transmis à tout destinataire par e-mail ou téléchargement.",
    "landing.hero.cta_start": "Émettre des cartes",
    "landing.hero.cta_request_cash": "Demander une carte cash",
    "landing.hero.cta_rates": "Voir les tarifs",
    "landing.hero.cta_find_card": "Retrouver ma carte",
    "landing.hero.stat_currency": "Devise",
    "landing.hero.stat_settle": "Délai de règlement",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "Frais de service",
    "landing.hero.image_eyebrow": "SKYLINE · PORTÉE MONDIALE",
    "landing.hero.image_title": "Conçu pour l'économie de carte moderne",
    "landing.hero.live_pill": "EN DIRECT",
    "landing.hero.live_pill_text": "Règlement en USD",
    "landing.hero.marquee": "Émettre · Alimenter · Dépenser en USD",
    "landing.demo.eyebrow": "Voyez-le en 20 secondes",
    "landing.demo.title_a": "De l'émission à la",
    "landing.demo.title_b_spend": "dépense",
    "landing.demo.title_c": ". En quatre étapes.",
    "landing.demo.step_issue.label": "Émettre une carte",
    "landing.demo.step_issue.detail": "Choisissez un montant. Carte prête en ~2 minutes.",
    "landing.demo.step_share.label": "Partagez-la",
    "landing.demo.step_share.detail": "Par e-mail, SMS, WhatsApp ou téléchargement en PNG.",
    "landing.demo.step_scan.label": "Le destinataire scanne",
    "landing.demo.step_scan.detail": "La caméra du téléphone ouvre la page de suivi instantanément.",
    "landing.demo.step_spend.label": "Dépensez en USD",
    "landing.demo.step_spend.detail": "Paiement sans contact, retrait d'espèces, achats chez les commerçants.",
    "landing.demo.cta_try": "Essayer maintenant",
    "landing.demo.live_pill": "Démo en direct",
    "landing.demo.scene1_caption": "Génération de la carte · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Envoi au destinataire via WhatsApp",
    "landing.demo.scene3_caption": "Scan du QR · correspondance trouvée",
    "landing.demo.scene4_eyebrow": "Solde crédité",
    "landing.demo.scene4_status": "✓ approuvé · paiement sans contact prêt",
    "landing.footer.rates": "Tarifs",
    "landing.footer.support": "Assistance",
    "landing.footer.admin_portal": "Portail admin",
    "landing.footer.copy": "© 2026 Zcardon · Un produit fintech de démonstration",
    "landing.cash.dialog_title": "Demander une carte cash",
    "landing.cash.dialog_desc": "Nous vous enverrons un lien de paiement Stripe par SMS. Payez avec n'importe quelle carte ; la carte cash est alimentée dès la confirmation par Stripe.",
    "landing.cash.phone_label": "Numéro de mobile",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Montant (USD)",
    "landing.cash.name_label": "Votre nom (facultatif)",
    "landing.cash.submit": "Envoyer le lien de paiement",
    "landing.cash.submitting": "Envoi en cours…",
    "landing.cash.success_title": "Lien de paiement en route",
    "landing.cash.success_body": "Nous avons envoyé un lien de paiement Stripe au {phone}. Appuyez dessus pour payer et votre carte virtuelle sera activée immédiatement.",
    "landing.cash.success_open_checkout": "Ouvrir le paiement maintenant",
    "landing.find.cta": "Déjà payé ? Retrouver ma carte",
    "landing.find.dialog_title": "Retrouver ma carte existante",
    "landing.find.dialog_desc": "Saisissez le numéro de téléphone utilisé. Nous vous enverrons un code à 6 chiffres pour récupérer toute carte cash active enregistrée.",
    "landing.find.phone_label": "Numéro de mobile",
    "landing.find.code_label": "Code à 6 chiffres",
    "landing.find.send_code": "Envoyer le code",
    "landing.find.verify": "Vérifier et trouver",
    "landing.find.sending": "Envoi en cours…",
    "landing.find.verifying": "Vérification en cours…",
    "landing.find.no_cards": "Aucune carte active trouvée pour ce numéro.",
    "login.eyebrow.welcome": "Bienvenue",
    "login.eyebrow.admin": "Administration",
    "login.title": "Se connecter",
    "login.title_admin": "Connexion admin",
    "login.admin_badge": "Portail admin",
    "login.email_label": "E-mail",
    "login.password_label": "Mot de passe",
    "login.submit": "Se connecter",
    "login.submitting": "Connexion…",
    "login.toast_welcome": "Bon retour, {name}",
    "login.toast_failed": "Échec de la connexion",
    "login.gate_title": "Les connexions clients sont temporairement suspendues",
    "login.gate_default_message": "Nous améliorons la plateforme. Revenez bientôt.",
    "login.gate_admin_note": "Les administrateurs et gestionnaires peuvent toujours se connecter ci-dessous.",
    "login.new_here": "Nouveau ici ?",
    "login.create_account": "Créer un compte",
    "login.customer_q": "Client ?",
    "login.regular_signin": "Connexion standard",
    "login.demo_title": "Comptes de démonstration",
    "login.demo_customers_note": "Clients : inscrivez-vous via /register ou créez depuis le panneau Admin",
    "login.forgot_password": "Mot de passe oublié ?",
    "forgot.eyebrow": "Récupération de compte",
    "forgot.title": "Réinitialisez votre mot de passe",
    "forgot.subtitle": "Entrez l'adresse e-mail associée à votre compte Zcardon et nous vous enverrons un lien sécurisé pour choisir un nouveau mot de passe.",
    "forgot.email_label": "E-mail",
    "forgot.email_placeholder": "vous@exemple.com",
    "forgot.submit": "Envoyer le lien de réinitialisation",
    "forgot.submitting": "Envoi…",
    "forgot.toast_failed": "Impossible d'envoyer le lien de réinitialisation. Veuillez réessayer.",
    "forgot.sent_title": "Consultez votre boîte de réception",
    "forgot.sent_message": "Si un compte existe pour {email}, nous avons envoyé un lien de réinitialisation.",
    "forgot.sent_expiry_note": "Le lien est valable 15 minutes. Vous ne l'avez pas reçu ? Vérifiez les spams ou envoyez-en un autre.",
    "forgot.back_to_login": "Retour à la connexion",
    "forgot.resend_link": "Envoyer un autre lien",
    "forgot.remember_q": "Vous vous souvenez de votre mot de passe ?",
    "forgot.signin_link": "Se connecter",
    "reset.eyebrow": "Récupération de compte",
    "reset.title": "Choisissez un nouveau mot de passe",
    "reset.subtitle": "Choisissez un mot de passe fort que vous n'avez jamais utilisé. Vous serez connecté automatiquement une fois défini.",
    "reset.verifying": "Vérification de votre lien de réinitialisation…",
    "reset.invalid_title": "Ce lien de réinitialisation est invalide ou expiré",
    "reset.invalid_message": "Les liens de réinitialisation sont valables 15 minutes et ne peuvent être utilisés qu'une fois. Demandez-en un nouveau pour continuer.",
    "reset.request_new_link": "Demander un nouveau lien",
    "reset.new_password_label": "Nouveau mot de passe",
    "reset.confirm_password_label": "Confirmer le mot de passe",
    "reset.password_hint": "Au moins 8 caractères. Évitez les mots de passe utilisés ailleurs.",
    "reset.submit": "Mettre à jour le mot de passe",
    "reset.submitting": "Mise à jour…",
    "reset.toast_success": "Mot de passe mis à jour. Vous êtes connecté.",
    "reset.toast_failed": "Impossible de réinitialiser le mot de passe. Veuillez réessayer.",
    "reset.error_min_length": "Le mot de passe doit contenir au moins 8 caractères.",
    "reset.error_mismatch": "Les mots de passe ne correspondent pas.",
    "register.eyebrow": "Rejoindre",
    "register.title": "Créer un compte",
    "register.full_name_label": "Nom complet",
    "register.email_label": "E-mail",
    "register.password_label": "Mot de passe",
    "register.submit": "Créer un compte",
    "register.submitting": "Création…",
    "register.gate_title": "Les inscriptions sont temporairement suspendues",
    "register.gate_default_message": "Nous améliorons la plateforme. Revenez bientôt.",
    "register.gate_already_member": "Vous avez déjà un compte ?",
    "register.toast_created": "Compte créé",
    "register.toast_failed": "Échec de l'inscription",
    "register.already_member": "Déjà membre ?",
    "register.signin_link": "Se connecter",
    "dash.eyebrow": "Tableau de bord",
    "dash.greeting": "Bon retour, {name}",
    "dash.subtitle": "Émettez une carte virtuelle ou effectuez un virement — votre centre de contrôle est ci-dessous.",
    "dash.action.issue_card": "Émettre une carte",
    "dash.action.direct_deposit": "Dépôt direct (virement)",
    "dash.action.support": "Support",
    "dash.section.cards": "Vos cartes",
    "dash.section.activity": "Activité récente",
    "dash.section.deposits": "Dépôts directs",
    "dash.empty.cards": "Aucune carte pour l'instant — émettez la première pour commencer.",
    "dash.empty.activity": "Dès que vous émettrez des cartes ou enverrez des virements, l'activité récente apparaîtra ici.",
    "ref.eyebrow": "Bienvenue sur Zcardon",
    "ref.greeting": "{name} vous a invité.",
    "ref.loading": "Chargement de votre invitation…",
    "ref.resolved_redirect": "Un instant — nous vous redirigeons.",
    "ref.resolving": "Résolution de votre lien d'invitation…",
    "support.eyebrow": "À votre service",
    "support.title": "Du soutien, pas des tickets.",
    "support.subtitle": "Parlez à un humain. Réponses par e-mail en moins d'une heure ouvrable en semaine.",
    "support.card.email": "E-mail",
    "support.card.phone": "Téléphone",
    "support.card.live_chat": "Chat en direct",
    "support.card.live_chat_value": "Dans votre tableau de bord",
    "support.card.hours": "Horaires",
    "support.faq.title": "Questions fréquentes",
    "support.faq.q1": "Combien de temps avant que le destinataire reçoive la carte ?",
    "support.faq.a1": "Généralement dans les 2 minutes suivant un financement réussi. La livraison par e-mail (Resend) et MMS (Twilio) est automatique.",
    "support.faq.q2": "Puis-je annuler ou bloquer une carte après l'envoi ?",
    "support.faq.a2": "Oui — ouvrez la page de détail de la carte et appuyez sur Bloquer. Vous pouvez aussi la bloquer directement depuis l'e-mail d'alerte destinataire.",
    "support.faq.q3": "Dans quelles devises la carte peut-elle être utilisée ?",
    "support.faq.a3": "Toutes les cartes sont réglées en USD. Le destinataire peut dépenser partout où Visa/Mastercard est accepté — les changes sont gérés par le réseau de cartes.",
    "support.faq.q4": "Quels frais s'appliquent ?",
    "support.faq.a4": "Des frais de service transparents plus les frais de passerelle Stripe. Consultez le devis en direct sur la page Tarifs avant de payer.",
    "rates.eyebrow": "Transparent",
    "rates.title_a": "Aucun frais",
    "rates.title_b": "cachés",
    "rates.subtitle": "Vous voyez les frais de service et les coûts de passerelle avant de payer. Le principal est toujours crédité sur le solde de la carte virtuelle en USD.",
    "rates.row.service_fee": "Frais de service",
    "rates.row.service_fee_value": "{pct} du montant envoyé",
    "rates.row.gateway": "Passerelle (Stripe)",
    "rates.row.settlement": "Règlement",
    "rates.row.settlement_value": "Solde USD sur carte virtuelle",
    "rates.row.topup": "Méthodes de recharge",
    "rates.row.topup_value": "Carte · Débit · Banque (ACH)",
    "rates.sim.eyebrow": "Devis en direct",
    "rates.sim.title_a": "Envoyez n'importe quel montant,",
    "rates.sim.title_b": "voyez le détail instantanément.",
    "rates.sim.amount_label": "Montant à envoyer",
    "rates.sim.line.principal": "Sur la carte (principal)",
    "rates.sim.line.service": "Frais de service · {pct}",
    "rates.sim.line.gateway": "Passerelle · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Vous payez",
    "rates.sim.cta": "Commencer avec ${amount} →",
    "rates.sim.note": "Les tarifs appliqués ci-dessus sont configurés par l'administrateur. Le destinataire reçoit toujours le principal intégral.",
    "dd.title": "Virement direct",
    "dd.intro": "Envoyez de l'argent sur le compte bancaire d'un destinataire, partout dans le monde. Payez par carte (Stripe Checkout) ou avec votre solde Zcardon — nous gérons le virement vers la banque du destinataire automatiquement lorsque c'est possible, ou manuellement pour les pays non encore pris en charge. Chaque transaction est enregistrée avec un reçu imprimable.",
    "dd.new_btn": "Enregistrer un nouveau virement direct",
    "dd.close": "Fermer",
    "dd.history_eyebrow": "Vos virements",
    "dd.loading": "Chargement…",
    "dd.empty": "Aucun virement direct pour l'instant.",
    "dd.status.approved": "approuvé",
    "dd.status.pending": "en attente d'approbation",
    "dd.status.rejected": "rejeté",
    "dd.row.confirmed": "✓ Destinataire a confirmé la réception",
    "dd.row.disputed": "⚠ Destinataire a signalé un écart",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Impossible de charger les virements",
    "dd.error.pdf": "Impossible de télécharger le reçu",
    "pay.mode.view": "Votre carte Zcardon",
    "pay.mode.charge": "Payer par QR",
    "pay.heading.view": "Bonjour, {name}.",
    "pay.heading.charge": "Débiter cette carte.",
    "pay.cardholder": "Titulaire de la carte",
    "pay.paused.label": "Carte suspendue",
    "pay.paused.body": "Cette carte a été bloquée et ne peut pas accepter de nouveaux débits tant que son propriétaire ne la réactive pas.",
    "pay.balance.label": "Solde disponible",
    "pay.charges_count": "{count} débit",
    "pay.charges_count_plural": "{count} débits",
    "pay.topups_count": "{count} recharge",
    "pay.topups_count_plural": "{count} recharges",
    "pay.activity.label": "Activité récente",
    "pay.activity.empty": "Aucune activité pour l'instant",
    "pay.cta.charge": "Débiter cette carte (commerçant)",
    "pay.cta.charge_paused": "Carte suspendue",
    "pay.cta.note_active": "Appuyez ci-dessus uniquement si vous êtes le commerçant acceptant le paiement de cette carte.",
    "pay.cta.note_paused": "Cette carte a été suspendue par l'expéditeur. Demandez-lui de la réactiver.",
    "pay.back_to_balance": "Retour au solde",
    "pay.form.amount_label": "Montant (USD)",
    "pay.form.amount_max": "Max {amount}",
    "pay.form.merchant_label": "Commerçant / motif",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Mémo (optionnel)",
    "pay.form.memo_placeholder": "Deux lattes",
    "pay.form.submit_processing": "Traitement…",
    "pay.form.submit_paused": "Carte suspendue",
    "pay.form.submit_charge": "Débiter {amount}",
    "pay.form.submit_charge_card": "Débiter la carte",
    "pay.form.disclaimer": "En débitant cette carte, vous confirmez être autorisé par le titulaire à dépenser le montant ci-dessus.",
    "pay.receipt.title": "Paiement effectué",
    "pay.receipt.to": "à",
    "pay.receipt.id": "reçu",
    "pay.receipt.time": "heure",
    "pay.receipt.new_balance": "nouveau solde",
    "pay.receipt.done": "Terminé · retour au solde",
    "pay.error.cant_load": "Impossible de charger cette carte",
    "pay.error.back_home": "Retour à l'accueil",
    "pay.error.loading": "Chargement de la carte…",
    "pay.error.charge_failed": "Échec du débit",
    "pay.tap.eyebrow": "Appuyer pour payer",
    "pay.tap.title": "Montrez ce code",
    "pay.tap.subtitle": "au scanner du marchand",
    "pay.tap.balance": "Solde",
    "pay.tap.paused": "En pause",
    "pay.tap.qr_unavailable": "QR indisponible pour cette carte",
    "pay.tap.refresh_in": "Actualisation dans {secs}s",
    "pay.tap.show_digits": "Afficher les chiffres",
    "pay.tap.hide_digits": "Masquer les chiffres",
    "pay.tap.card_label": "Carte · ••••",
    "pay.tap.auto_hides": "Masquage auto dans 60s",
    "pay.tap.full_only_image": "Le numéro complet est affiché uniquement sur l'image de la carte originale",
    "pay.tap.disclaimer": "Carte en circuit fermé · utilisable chez tout marchand compatible Zcardon. Le QR est renouvelé toutes les 60s contre la réutilisation.",
    "pay.subscribe.eyebrow": "Restez informé",
    "pay.subscribe.title": "Recevez un e-mail à chaque transaction",
    "pay.subscribe.desc": "Gratuit. Un e-mail par débit ou rechargement. Désabonnement à tout moment. Votre adresse n'est jamais partagée.",
    "pay.subscribe.placeholder": "vous@exemple.com",
    "pay.subscribe.cta": "M'avertir",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Vous êtes abonné",
    "pay.subscribe.done.body": "Nous enverrons un e-mail à {email} instantanément à chaque débit ou rechargement de la carte •••• {last4}.",
    "pay.subscribe.toast.already": "Vous étiez déjà abonné — aucun doublon envoyé.",
    "pay.subscribe.toast.success": "Vous recevrez un e-mail à chaque transaction. Bienvenue !",
    "pay.subscribe.toast.failed": "Échec de l'abonnement",
    "pay.save.eyebrow": "Emportez votre carte",
    "pay.save.title": "Enregistrez cette carte sur votre téléphone",
    "pay.save.desc": "Enregistrez l'image de la carte dans vos photos pour un accès hors ligne — montrez le QR aux marchands à tout moment, sans application requise.",
    "pay.save.cta": "Enregistrer l'image de la carte",
    "pay.save.preparing": "Préparation…",
    "pay.save.add_home": "Ajouter à l'écran d'accueil",
    "pay.save.toast.opened": "Image de la carte ouverte — appui long pour enregistrer dans Photos",
    "pay.save.toast.failed": "Impossible d'enregistrer la carte",
    "pay.save.ios.title": "Pour ajouter Zcardon à l'écran d'accueil de votre iPhone :",
    "pay.save.ios.step1": "Appuyez sur le bouton Partager en bas de Safari",
    "pay.save.ios.step2": "Faites défiler et appuyez sur Ajouter à l'écran d'accueil",
    "pay.save.ios.step3": "Appuyez sur Ajouter pour confirmer",
    "pay.save.ios.note": "Zcardon apparaîtra comme n'importe quelle autre application — appuyez sur l'icône pour ouvrir cette carte instantanément.",
    "pay.thanks.eyebrow": "Envoyer un remerciement",
    "pay.thanks.body": "${amount} vient d'arriver — faites savoir à la personne qui a financé cette carte que ça a aidé.",
    "pay.thanks.placeholder": "Facultatif — dites quelque chose…",
    "pay.thanks.cta": "Envoyer un remerciement {emoji}",
    "pay.thanks.sending": "Envoi…",
    "pay.thanks.done": "Remerciement envoyé — ils le verront dans quelques secondes.",
    "pay.thanks.toast.sent": "Remerciement envoyé",
    "pay.thanks.toast.failed": "Impossible d'envoyer le remerciement",
    "pay.activity.topup_bank": "Rechargement par virement bancaire",
    "pay.activity.topup_debit": "Rechargement par carte de débit",
    "pay.activity.topup_credit": "Rechargement par carte de crédit",
    "pay.activity.topup_crypto": "Rechargement en crypto",
    "pay.activity.topup_received": "Rechargement reçu",
    "pay.activity.merchant_charge": "Débit marchand",
    "pay.toast.payment_received": "Paiement reçu · −{amount}",
    "cash.confirming": "Confirmation du paiement…",
    "cash.confirming.sub": "Stripe finalise le débit.",
    "cash.notfound.title": "Lien expiré",
    "cash.notfound.body": "Nous ne trouvons pas cette demande de paiement. Le lien est peut-être issu d'une session plus ancienne, ou la carte n'a jamais été finalisée. Lancez une nouvelle demande — cela ne prend qu'une minute, et tout paiement déjà effectué sera automatiquement crédité dès que le rapprochement en arrière-plan le détecte.",
    "cash.notfound.ref": "réf :",
    "cash.notfound.start": "Lancer une nouvelle demande",
    "cash.notfound.contact": "Contacter le support",
    "cash.error.title": "Impossible de vérifier le statut",
    "cash.error.return": "Retour à l'accueil",
    "cash.autopay.opening": "Ouverture du paiement sans contact…",
    "cash.autopay.redirect": "Redirection dans {secs}s.",
    "cash.autopay.stay": "Rester ici",
    "cash.ready.title": "Votre carte est prête",
    "cash.ready.subtitle": "Créditée de ${amount} USD · également envoyé par SMS au {phone}",
    "cash.card.label": "Carte",
    "cash.card.exp": "Exp",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Solde",
    "cash.cta.open_pay": "Ouvrir l'écran de paiement sans contact",
    "cash.toast.funded": "Votre carte est approvisionnée — vérifiez vos SMS !",
    "cash.pending.title": "En attente de Stripe",
    "cash.pending.body": "Nous attendons la confirmation. Cela prend généralement 5 à 15 secondes. Si vous avez fermé le paiement sans payer, vous pouvez recommencer depuis la page d'accueil.",
    "cash.pending.back": "Retour à l'accueil",
    "cash.timeout.title": "Impossible de confirmer le paiement",
    "cash.timeout.body": "Nous avons attendu 3 minutes la confirmation de Stripe. Cela signifie généralement que le paiement a été fermé avant d'être effectué — mais si vous AVEZ payé, votre carte sera tout de même activée automatiquement dans les prochaines minutes (notre réconciliateur tourne toutes les 5 min) et nous vous l'enverrons par SMS à {phone} dès que ce sera fait.",
    "cash.timeout.start_over": "Recommencer",
    "cash.timeout.try_again": "Réessayer",
    "cash.resend.title": "Pas reçu ? Renvoyer",
    "cash.resend.degraded.lead": "La livraison vers les numéros américains est dégradée en ce moment.",
    "cash.resend.degraded.sms": "Les SMS vers les téléphones américains peuvent être bloqués silencieusement par l'opérateur (vérification numéro gratuit en attente).",
    "cash.resend.degraded.email": "L'e-mail est en mode test et ne sera livré qu'à l'adresse du propriétaire du compte pendant la validation du DNS.",
    "cash.resend.degraded.tail": "Si un canal ne fonctionne pas, essayez l'autre — ou ouvrez cette page sur l'appareil du destinataire pour accéder directement à la carte.",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "E-mail",
    "cash.resend.sending": "Envoi en cours…",
    "cash.resend.email_sandbox": "L'e-mail est en mode test. Pendant la vérification du domaine zcardon.com sur Resend, nous ne pouvons envoyer qu'à l'adresse du propriétaire du compte. Si votre e-mail n'est pas accepté, utilisez SMS ou WhatsApp à la place.",
    "cash.resend.email_label": "Adresse e-mail",
    "cash.resend.email_send": "Envoyer par e-mail",
    "cash.resend.cancel": "Annuler",
    "cash.resend.limit": "Limite de 5 renvois par carte. Téléphone enregistré : {phone}",
    "cash.resend.no_phone": "(aucun)",
    "cash.wallet.title": "Ajouter au portefeuille",
    "cash.wallet.apple": "Ajouter à Apple Wallet",
    "cash.wallet.google": "Ajouter à Google Wallet",
    "cash.wallet.generating": "Génération du pass…",
    "cash.wallet.note": "Reste sur votre écran de verrouillage · Se met à jour automatiquement en cas de changement de solde",
    "cash.wallet.toast.apple": "Pass téléchargé — ouvrez-le sur votre iPhone pour l'ajouter à Apple Wallet",
    "cash.wallet.toast.apple_failed": "Impossible de générer le pass Apple Wallet",
    "cash.wallet.toast.google_failed": "Impossible de générer le pass Google Wallet",
    "cash.delivery.fallback_title": "Envoyé via {fallback}",
    "cash.delivery.fallback_body": "Votre opérateur a bloqué le SMS initial, nous avons donc renvoyé votre carte via {fallback} à {phone}. Vérifiez ce canal.",
    "cash.delivery.toll_blocked": "L'opérateur a bloqué le SMS",
    "cash.delivery.failed_title": "Impossible d'envoyer votre carte par SMS",
    "cash.delivery.toll_body": "Votre opérateur mobile a rejeté le message (cela arrive avec les nouveaux expéditeurs en attente de vérification). Enregistrez votre carte maintenant : appuyez sur",
    "cash.delivery.failed_body": "Votre carte est approvisionnée — veuillez l'enregistrer maintenant car le SMS n'a pas été envoyé. Appuyez sur",
    "cash.delivery.cta_label": "Ouvrir l'écran Tap-to-Pay",
    "cash.delivery.body_tail": "ci-dessus, faites une capture d'écran, ou utilisez E-mail ci-dessous pour vous en envoyer une copie.",
    "cash.handoff.eyebrow": "Remise en main propre",
    "cash.handoff.body": "Demandez au destinataire de scanner ceci avec son appareil photo — la carte s'ouvre directement sur son téléphone, sans SMS ni e-mail.",
    "cash.handoff.share_label": "Ou partager ce lien",
    "cash.handoff.copy": "Copier le lien",
    "cash.handoff.copied": "✓ Copié",
    "cash.handoff.share": "Partager",
    "cash.handoff.toast.copied": "Lien copié — collez-le où vous voulez",
    "cash.handoff.toast.copy_failed": "Impossible de copier. Appuyez longuement sur le lien pour le copier manuellement.",
    "portal.loading": "Chargement de votre reçu…",
    "portal.unavailable.title": "Lien indisponible",
    "portal.unavailable.body": "Ce lien est invalide ou a expiré.",
    "portal.unavailable.hint": "Si l'expéditeur vous a partagé ce lien, demandez-lui de renvoyer l'e-mail de reçu.",
    "portal.brand": "Portail de reçus",
    "portal.eyebrow": "{sender} vous a envoyé",
    "portal.reference": "Référence",
    "portal.pending.title": "En attente d'approbation",
    "portal.pending.body": "L'expéditeur a enregistré ce virement, mais il est toujours en cours d'examen par Zcardon. Revenez bientôt — le reçu sera disponible une fois approuvé.",
    "portal.row.bank": "Banque",
    "portal.row.account": "Compte",
    "portal.row.routing": "Code de routage",
    "portal.row.memo": "Mémo",
    "portal.confirmed.title": "Vous avez confirmé la réception",
    "portal.confirmed.body": "Confirmé le {when}. L'expéditeur peut le voir.",
    "portal.disputed.title": "Vous avez signalé une anomalie",
    "portal.disputed.body": "Signalé le {when}. L'expéditeur a été notifié par e-mail.",
    "portal.actions.body": "Ce montant est-il arrivé sur votre compte ? La confirmation clôture la boucle avec l'expéditeur — aucun argent ne transite par Zcardon, c'est uniquement un enregistrement.",
    "portal.actions.confirm": "Oui, je l'ai reçu",
    "portal.actions.flag": "Signaler un problème",
    "portal.dispute.placeholder": "Montant incorrect, non reçu, mauvais numéro de compte…",
    "portal.dispute.submit": "Soumettre le signalement",
    "portal.dispute.too_short": "Ajoutez une courte raison (au moins 4 caractères)",
    "portal.toast.confirmed": "Merci — réception confirmée.",
    "portal.toast.confirm_failed": "Impossible de confirmer la réception",
    "portal.toast.reported": "Signalement envoyé. L'expéditeur a été notifié.",
    "portal.toast.report_failed": "Impossible de soumettre le signalement",
    "portal.toast.pdf_failed": "Impossible de télécharger le reçu",
    "portal.download_pdf": "Télécharger le reçu PDF",
    "portal.privacy": "Cette page vous est privée. Toute personne disposant du lien peut consulter ce reçu — ne le partagez pas publiquement. Questions :",
    "detail.back": "Retour au tableau de bord",
    "detail.eyebrow": "Votre carte",
    "detail.confirming_payment": "Confirmation de votre paiement…",
    "detail.active_ready": "La carte est active et prête à partager.",
    "detail.balance.label": "Solde actuel",
    "detail.toast.notfound": "Carte introuvable",
    "detail.toast.loaded": "Fonds chargés sur la carte.",
    "detail.toast.confirmed": "Paiement confirmé.",
    "detail.toast.expired": "Session de paiement expirée",
    "detail.delivery.receiver_fallback": "votre destinataire",
    "detail.delivery.received_title": "{name} a ouvert la carte",
    "detail.delivery.received_when": "Livraison confirmée · {when}",
    "detail.delivery.waiting_title_on": "Vous recevrez un e-mail dès réception",
    "detail.delivery.waiting_title_off": "Les notifications de livraison sont désactivées",
    "detail.delivery.waiting_body_on": "Dès que votre destinataire scanne le QR de cette carte, nous vous enverrons un e-mail — une seule fois.",
    "detail.delivery.waiting_body_off": "Activez cette option pour recevoir un e-mail unique dès que votre destinataire ouvre la carte pour la première fois.",
    "detail.delivery.toggle_on": "Activé",
    "detail.delivery.toggle_off": "Activer",
    "detail.delivery.toast_enabled": "Vous serez notifié à l'ouverture de la carte.",
    "detail.delivery.toast_disabled": "Notifications de livraison désactivées.",
    "detail.delivery.toast_failed": "Impossible de mettre à jour les notifications de livraison.",
    "nudge.eyebrow": "Informez votre destinataire",
    "nudge.title": "Envoyez un message à {receiver}",
    "nudge.subtitle": "Nous avons rédigé un court message pour que {receiver} sache que la carte est active et comment l'utiliser.",
    "nudge.receiver_fallback": "votre destinataire",
    "nudge.sender_fallback": "un ami",
    "nudge.template": "Bonjour {receiver} — {sender} vient de vous envoyer une carte virtuelle Zcardon chargée de {amount}. Appuyez pour payer ici : {url}\\n\\nAucune application requise. Le lien fonctionne sur tous les téléphones.",
    "nudge.email_subject": "{sender} vous a envoyé une carte Zcardon de {amount}",
    "nudge.action_copy": "Copier le message",
    "nudge.action_sms": "Ouvrir SMS",
    "nudge.action_email": "Ouvrir l'e-mail",
    "nudge.toast_copied": "Message copié dans le presse-papiers.",
    "nudge.toast_copy_failed": "Impossible de copier. Sélectionnez et copiez manuellement.",
    "ios_install.title": "Installer Zcardon sur votre iPhone",
    "ios_install.step_1": "Appuyez sur",
    "ios_install.share_label": "Partager",
    "ios_install.step_2": " puis choisissez \\\"Ajouter à l'écran d'accueil\\\" — Zcardon s'ouvrira en plein écran.",
    "smart_banner.title": "Ouvrir dans l'application Zcardon",
    "smart_banner.subtitle": "Paiement plus rapide. Payez sans quitter l'application.",
    "smart_banner.cta": "Ouvrir",
    "smart_banner.dismiss": "Ignorer",
    "social_proof.eyebrow": "En direct · dernières 48 heures",
    "social_proof.empty": "En attente de la prochaine carte…",
    "social_proof.line_opened": "{name} vient d'ouvrir une carte de {amount}.",
    "social_proof.line_issued": "{name} a reçu une carte de {amount} d'un ami.",
    "social_proof.line_deposit": "{name} a reçu un dépôt direct de {amount}.",
    "social_proof.stat_funded": "Cartes financées aujourd'hui",
    "social_proof.stat_delivered": "Cartes livrées aujourd'hui",
    "social_proof.stat_recent": "Activité récente",
    "social_proof.ago_seconds": "il y a {n}s",
    "social_proof.ago_minutes": "il y a {n}min",
    "social_proof.ago_hours": "il y a {n}h",
    "flight.eyebrow": "Cartes en transit · dernières 48h",
    "flight.title": "Où atterrit Zcardon",
    "flight.subtitle": "Localisations réelles des destinataires, anonymisées par région. Nous affichons uniquement les pays et les États américains — jamais les noms, adresses ou montants.",
    "flight.row_template": "Carte ouverte en {label}",
    "flight.more_count": "+ {n} régions supplémentaires",
    "flight.ago_seconds": "il y a {n}s",
    "flight.ago_minutes": "il y a {n}min",
    "flight.ago_hours": "il y a {n}h",
    "trust_strip.eyebrow": "Signal réel · en direct depuis la base de données",
    "trust_strip.title_a": "Pas des témoignages.",
    "trust_strip.title_b": "Lectures en direct de la base de données.",
    "trust_strip.how_link": "Comment nous collectons cela",
    "trust_strip.modal_eyebrow": "Sources de données",
    "trust_strip.modal_title": "Tout est réel, tout est anonymisé",
    "trust_strip.modal_close": "Fermer",
    "trust_strip.modal_intro": "Chaque chiffre affiché provient de notre propre base de données — jamais d'un flux tiers, jamais inventé. Voici d'où provient chaque section et comment nous l'anonymisons avant de vous la présenter.",
    "trust_strip.src_carousel_title": "Carrousel d'activité en direct",
    "trust_strip.src_carousel_body": "Puise dans les collections cards et direct_deposits, dernières 48h. Les noms s'affichent sous la forme prénom + initiale du nom ; les montants sont arrondis au $5 le plus proche pour éviter la triangulation. Actualisé toutes les 60 secondes.",
    "trust_strip.src_flight_title": "Où atterrit Zcardon",
    "trust_strip.src_flight_body": "Lit les scans QR depuis card_scans, géolocalisés au moment du scan. Agrégés par pays (ou État américain). Aucune précision en dessous du niveau de l'État, aucune IP affichée, aucun identifiant de carte. Actualisé toutes les 90 secondes.",
    "trust_strip.src_badges_title": "Chiffres de confiance sur 30 jours",
    "trust_strip.src_badges_body": "Comptages agrégés uniquement — jamais de données par ligne. Chaque indicateur n'apparaît qu'une fois un échantillon minimum atteint ; un taux de 100 % sur 2 confirmations serait donc masqué. Actualisé chaque minute.",
    "trust_strip.modal_promise": "Nous ne publierons jamais de noms, adresses, montants exacts, IPs ou numéros de carte dans les widgets publics. Promis.",
    "trust_badge.eyebrow": "Chiffres réels · 30 derniers jours",
    "trust_badge.title": "Les gens l'utilisent vraiment.",
    "trust_badge.subtitle": "Pas un témoignage — un comptage en direct, actualisé depuis la base de données chaque minute.",
    "trust_badge.confirmed_label": "destinataires confirment la réception dans les 24h",
    "trust_badge.confirmed_sub": "sur {n} réponses les 30 derniers jours",
    "trust_badge.thanks_label": "messages de remerciement envoyés",
    "trust_badge.thanks_sub": "de la part des destinataires au cours des 30 derniers jours",
    "trust_badge.cards_label": "cartes financées",
    "trust_badge.cards_sub": "30 derniers jours · comptage en direct",
    "pulse.label": "EN DIRECT",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} a ouvert une carte de {amount}",
    "pulse.tooltip_issued": "{name} a reçu une carte de {amount}",
    "pulse.tooltip_deposit": "{name} a reçu un dépôt de {amount}",
    "pulse.ago_seconds": "il y a {n}s",
    "pulse.ago_minutes": "il y a {n}m",
    "pulse.ago_hours": "il y a {n}h",
    "note_cloud.eyebrow": "À quoi ça sert",
    "note_cloud.title": "Vraies raisons. Vraies personnes.",
    "note_cloud.subtitle": "Agrégé à partir de {n} notes de carte des 90 derniers jours. Seuls les mots apparaissant dans au moins {min} notes différentes sont affichés — votre message reste le vôtre.",
    "note_cloud.footnote": "Noms, montants et coordonnées filtrés avant affichage.",
    "note_cloud.lang_eyebrow": "Dans quelle langue sont ces notes ?",
    "note_cloud.lang_other": "Autre",
    "download.button": "Télécharger l'app",
    "download.close": "Fermer",
    "download.modal_eyebrow": "Obtenir Zcardon",
    "download.modal_title": "Installer Zcardon sur votre téléphone",
    "download.footnote": "Pas encore d'App Store — vous installez directement l'application web sécurisée. Mises à jour instantanées.",
    "download.installed_title": "Zcardon installé",
    "download.installed_body": "Cherchez l'icône sur votre écran d'accueil.",
    "download.webview_warning": "Ouvrez cette page dans le navigateur de votre téléphone (Safari sur iPhone, Chrome sur Android) pour installer — les navigateurs intégrés ne peuvent pas ajouter à l'écran d'accueil.",
    "download.ios_step1_title": "Sur Safari",
    "download.ios_step1_body": "Si vous n'êtes pas déjà dans Safari, copiez ce lien et ouvrez-le là-bas. (Chrome et les autres navigateurs iOS ne peuvent pas installer d'applications.)",
    "download.ios_step2_title": "Appuyez sur Partager",
    "download.ios_step2_body": "L'icône Partager est en bas au centre sur iPhone, en haut à droite sur iPad.",
    "download.ios_step3_title": "Ajouter à l'écran d'accueil",
    "download.ios_step3_body": "Faites défiler le menu de partage et choisissez \\\"Ajouter à l'écran d'accueil\\\". Zcardon s'installe en tant qu'application plein écran.",
    "download.android_ready_body": "Chrome a détecté Zcardon et est prêt à l'installer. Appuyez ci-dessous.",
    "download.android_install": "Installer Zcardon",
    "download.android_installing": "Installation…",
    "download.android_step1_title": "Ouvrir le menu Chrome",
    "download.android_step1_body": "Appuyez sur les trois points en haut à droite de Chrome.",
    "download.android_step2_title": "Ajouter à l'écran d'accueil",
    "download.android_step2_body": "Choisissez \\\"Installer l'application\\\" ou \\\"Ajouter à l'écran d'accueil\\\". Zcardon s'installe en tant qu'application plein écran.",
    "download.desktop_body": "Scannez ce QR code avec l'appareil photo de votre téléphone pour ouvrir Zcardon, puis installez-le depuis le navigateur de votre téléphone.",
    "download.steps_hint": "Ces étapes se déroulent dans votre navigateur, pas sur cette page. Aucune des lignes ci-dessous n'est cliquable — elles décrivent ce qu'il faut chercher.",
    "download.ios_arrow_label": "Appuyez ici dans Safari",
    "download.android_arrow_label": "Cherchez ici dans Chrome",
    "install.eyebrow": "Installer Zcardon",
    "install.title": "Ajouter Zcardon à votre écran d'accueil",
    "install.subtitle": "Deux appuis et Zcardon sera une vraie icône d'app — sans App Store.",
    "install.back_home": "Ouvrir Zcardon dans le navigateur",
    "install.continue": "Continuer vers Zcardon →",
    "install.copy": "Copier",
    "install.copied": "Copié",
    "install.ios_step1_title": "Ouvrir cette page dans Safari",
    "install.ios_step1_body": "Si vous avez scanné depuis un autre navigateur, copiez l'URL et ouvrez-la dans Safari — Chrome sur iOS ne peut pas installer d'applications.",
    "install.ios_step2_title": "Appuyez sur le bouton Partager",
    "install.ios_step2_body": "C'est le carré avec une flèche vers le haut — en bas de Safari (ou en haut à droite sur iPad).",
    "install.ios_step3_title": "Appuyez sur \\\"Ajouter à l'écran d'accueil\\\"",
    "install.ios_step3_body": "Faites défiler la feuille de partage et choisissez \\\"Ajouter à l'écran d'accueil\\\", puis confirmez. Zcardon apparaît comme icône d'application.",
    "install.ios_arrow_label": "Cherchez cette icône",
    "install.android_ready_title": "Prêt à installer",
    "install.android_ready_body": "Chrome a détecté Zcardon comme installable. Appuyez ci-dessous et nous apparaîtrons comme icône d'app sur votre écran d'accueil.",
    "install.android_install": "Installer Zcardon",
    "install.android_installing": "Installation…",
    "install.android_step1_title": "Appuyez sur le menu Chrome",
    "install.android_step1_body": "Ce sont les trois points en haut à droite de Chrome.",
    "install.android_step2_title": "Appuyez sur \\\"Installer l'application\\\" ou \\\"Ajouter à l'écran d'accueil\\\"",
    "install.android_step2_body": "Confirmez pour ajouter l'icône Zcardon sur votre téléphone. Appuyez dessus à tout moment pour lancer.",
    "install.android_hint": "Si vous ne voyez pas encore \\\"Installer l'application\\\", parcourez la page d'accueil une fois — Chrome le débloque après une courte visite.",
    "install.desktop_title": "Scannez avec votre téléphone",
    "install.desktop_body": "Zcardon est une application mobile. Ouvrez cette URL sur votre téléphone pour l'installer :",
    "install.webview_title": "Ouvrir dans votre navigateur",
    "install.webview_body": "Les navigateurs intégrés (Instagram, Facebook, TikTok) ne peuvent pas installer d'applications. Appuyez sur \\\"…\\\" ou le menu partager, puis choisissez \\\"Ouvrir dans Safari\\\" / \\\"Ouvrir dans Chrome\\\". Ou copiez ce lien :",
    "install.already_title": "Vous êtes déjà dans l'application",
    "install.already_body": "Zcardon est installé sur cet appareil. Appuyez sur l'icône de votre écran d'accueil pour l'ouvrir à tout moment.",
    "install.installed_title": "Installé",
    "install.installed_body": "Cherchez l'icône Zcardon sur votre écran d'accueil — c'est votre application.",
    "install.steps_hint": "Ces étapes se déroulent dans votre navigateur, pas sur cette page. Aucune des lignes ci-dessous n'est cliquable — elles décrivent ce qu'il faut chercher dans le menu du navigateur.",
    "install.desktop_body_qr": "Zcardon s'installe comme icône d'app sur iPhone ou Android — pas sur bureau. Pointez l'appareil photo de votre téléphone sur ce QR code :",
    "install.desktop_or": "Ou copiez le lien",
    "install.android_arrow_label": "Cherchez ici dans Chrome",
    "install.ios_arrow_caption": "C'est tout en bas de Safari",
    "vietqr.disabled.title": "Recevoir des VND via VietQR",
    "vietqr.disabled.badge": "VIETNAM UNIQUEMENT",
    "vietqr.disabled.desc": "Liez un compte bancaire vietnamien et le QR de cette carte deviendra un véritable VietQR — n'importe quelle app bancaire vietnamienne (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) pourra le scanner et vous payer en VND directement via le réseau NAPAS. Aucune approbation Stripe Issuing requise.",
    "vietqr.disabled.btn_enter": "Saisir les coordonnées bancaires",
    "vietqr.disabled.or": "ou",
    "vietqr.disabled.btn_send_link": "Envoyer le lien de configuration au destinataire",
    "vietqr.disabled.btn_send_link_tip": "Génère un lien public valable 7 jours que le destinataire remplit lui-même",
    "vietqr.form.warn_title": "À lire avant d'activer",
    "vietqr.form.warn_body_a": "Les scans ne dirigeront plus vers votre page de suivi Zcardon. Les fonds vont",
    "vietqr.form.warn_body_b": "directement vers votre compte bancaire vietnamien",
    "vietqr.form.warn_body_c": "via l'interbancaire NAPAS — Zcardon ne voit jamais l'argent. VietQR ne fonctionne qu'au Vietnam pour les transferts en VND.",
    "vietqr.form.bank": "Banque",
    "vietqr.form.account_number": "Numéro de compte",
    "vietqr.form.account_name": "Titulaire du compte (25 caractères)",
    "vietqr.form.city": "Ville (15 caractères)",
    "vietqr.form.advanced": "Avancé — montant fixe / référence (facultatif)",
    "vietqr.form.amount_vnd": "Montant (VND)",
    "vietqr.form.amount_ph": "laisser vide pour montant variable",
    "vietqr.form.reference": "Référence (25 caractères)",
    "vietqr.form.confirm": "Je comprends que scanner cette carte transférera des VND directement vers le compte bancaire ci-dessus. Zcardon ne traite ni ne détient ces fonds.",
    "vietqr.form.btn_attach": "Activer VietQR",
    "vietqr.form.btn_attaching": "Activation…",
    "vietqr.form.btn_cancel": "Annuler",
    "vietqr.enabled.title": "VietQR actif",
    "vietqr.enabled.desc": "Lorsqu'une app bancaire vietnamienne scannera cette carte, elle transférera des VND directement vers le compte ci-dessous — sans passer par Zcardon.",
    "vietqr.enabled.bank_label": "Banque",
    "vietqr.enabled.account_label": "Compte",
    "vietqr.enabled.fixed_amount": "Montant fixe",
    "vietqr.enabled.reference": "Référence",
    "vietqr.enabled.btn_detach": "Détacher VietQR",
    "vietqr.enabled.btn_detaching": "Détachement…",
    "vietqr.enabled.attached_at": "Activé le",
    "vietqr.analytics.live_activity": "Activité en direct",
    "vietqr.analytics.today": "aujourd'hui",
    "vietqr.analytics.days_live": "{n} j actif",
    "vietqr.analytics.phantom_views": "Vues estimées",
    "vietqr.analytics.confirmed_pays": "Paiements confirmés",
    "vietqr.analytics.total_vnd": "Total VND",
    "vietqr.analytics.recent_confirmed": "Confirmés récents",
    "vietqr.analytics.phantom_explainer": "Vues estimées ≈ téléchargements de l'image de la carte depuis l'activation. Les vrais scans VietQR ne passent pas par nos serveurs, nous estimons donc à partir de l'activité \"carte affichée\". Demandez au destinataire de confirmer les paiements via son lien de configuration pour des données précises.",
    "vietqr.toast.required": "Le numéro de compte et le nom du titulaire sont requis.",
    "vietqr.toast.confirm_first": "Confirmez que vous comprenez que VietQR envoie des VND à votre banque, en contournant Zcardon.",
    "vietqr.toast.attached_default": "VietQR activé.",
    "vietqr.toast.attach_failed": "Échec de l'activation de VietQR.",
    "vietqr.toast.detach_confirm": "Détacher VietQR ? Le QR de la carte reviendra au lien de suivi Zcardon.",
    "vietqr.toast.detached": "VietQR détaché — le QR revient au lien de suivi Zcardon.",
    "vietqr.toast.detach_failed": "Échec du détachement.",
    "vietqr.toast.link_copied": "Lien copié · expire dans {h}h",
    "vietqr.toast.link_generated": "Lien généré · copiez-le ci-dessous",
    "vietqr.toast.link_failed": "Échec de la génération du lien.",

    "setpw.toast.too_short": "Le mot de passe doit contenir au moins 8 caractères",
    "setpw.toast.no_match": "Les mots de passe ne correspondent pas",
    "setpw.toast.success_signin_again": "Mot de passe défini — veuillez vous connecter",
    "setpw.toast.welcome": "Bienvenue dans le programme d'agent Zcardon",
    "setpw.toast.expired": "Lien expiré ou invalide",
    "setpw.verifying": "Vérification de votre invitation…",
    "setpw.invalid.title": "Lien expiré",
    "setpw.invalid.desc": "Ce lien de bienvenue a expiré ou a déjà été utilisé. Veuillez contacter votre administrateur Zcardon pour recevoir une nouvelle invitation.",
    "setpw.invalid.home_btn": "Accueil",
    "setpw.invalid.support_btn": "Contacter le support",
    "setpw.fallback_name": "agent",
    "setpw.eyebrow": "Bienvenue agent",
    "setpw.heading": "Bienvenue, {name}",
    "setpw.subheading": "Définissez votre mot de passe pour activer votre compte agent. Vous serez connecté immédiatement.",
    "setpw.code_label": "Votre code agent",
    "setpw.code_tag": "À partager lors de l'inscription des clients",
    "setpw.form.title": "Choisissez un mot de passe",
    "setpw.form.new_label": "Nouveau mot de passe",
    "setpw.form.placeholder": "8 caractères minimum",
    "setpw.form.confirm_label": "Confirmer le mot de passe",
    "setpw.form.btn_busy": "Activation…",
    "setpw.form.btn_submit": "Activer mon compte agent",
    "tx.heading": "Transactions",
    "tx.kind.direct_deposit": "Dépôt direct",
    "tx.kind.card_topup": "Recharge carte",
    "tx.kind.outbound_payout": "Virement sortant",
    "tx.toast.load_failed": "Échec du chargement des transactions",
    "tx.summary.deposits": "Dépôts",
    "tx.summary.topups": "Recharges",
    "tx.summary.topups_sub": "carte",
    "tx.summary.payouts": "Virements",
    "tx.summary.payouts_sub": "sortant",
    "tx.filter.all": "Tous",
    "tx.filter.deposits": "Dépôts",
    "tx.filter.topups": "Recharges",
    "tx.filter.payouts": "Virements",
    "tx.loading": "Chargement des transactions…",
    "tx.empty.filtered": "Aucune transaction ne correspond à ce filtre",
    "tx.empty.none": "Aucune transaction pour l'instant",
    "tx.empty.cta": "Commencer par un dépôt direct →",
    "tx.open_title": "Ouvrir",
    "terms.load_error_default": "Échec du chargement des conditions",
    "terms.toast.sign_and_check": "Veuillez signer et confirmer pour accepter l'accord",
    "terms.toast.recorded": "Accord enregistré",
    "terms.toast.failed": "Échec de l'envoi",
    "terms.error.title": "Lien invalide",
    "terms.back_home": "Retour à l'accueil",
    "terms.loading": "Chargement de l'accord…",
    "terms.done.title": "Accord accepté",
    "terms.done.meta": "Version {v} · {ts}",
    "terms.done.next": "Vous pouvez fermer cette page. Votre carte sera prête à l'emploi dès qu'elle sera émise.",
    "terms.eyebrow": "Accord titulaire · v{v}",
    "terms.title_reaccept": "Réaccepter votre accord titulaire",
    "terms.title_accept": "Accepter votre accord titulaire",
    "terms.desc.sender_sent": "vous a envoyé une carte Zcardon",
    "terms.desc.you_were_sent": "Vous avez reçu une carte Zcardon",
    "terms.desc.ending": "se terminant par",
    "terms.desc.read_before": "Lisez l'accord ci-dessous, puis signez et confirmez pour activer la carte.",
    "terms.already_accepted_note": "Vous avez déjà accepté une version précédente. Réacceptez pour passer à la dernière version.",
    "terms.scroll_hint": "Faites défiler dans le cadre pour lire l'accord complet.",
    "terms.view_json": "Ouvrir en JSON",
    "terms.sign_label": "Signez avec votre nom légal complet",
    "terms.sign_placeholder": "Tel qu'il apparaît sur votre pièce d'identité officielle",
    "terms.consent": "J'ai lu et j'accepte le Contrat titulaire de carte Zcardon v{v}.",
    "terms.btn_recording": "Enregistrement…",
    "terms.btn_accept": "Accepter le contrat",
    "hub.eyebrow": "Votre hub de réception",
    "hub.heading.all_done": "Tout est prêt 🎉",
    "hub.heading.greet": "Bonjour {name} 👋",
    "hub.fallback_name": "vous",
    "hub.desc.sender_sent": "vous a envoyé une carte Zcardon",
    "hub.desc.you_were_sent": "Une carte Zcardon vous a été envoyée",
    "hub.desc.ending": "se terminant par",
    "hub.desc.complete_steps": "Complétez les étapes ci-dessous pour tout activer.",
    "hub.progress.label": "{done} sur {total} étapes complétées",
    "hub.blocker.title_one": "1 étape obligatoire restante",
    "hub.blocker.title_many": "{n} étapes obligatoires restantes",
    "hub.blocker.subtext": "Votre carte ne sera pas entièrement activée tant que ces étapes ne seront pas complétées.",
    "hub.required_pill": "Obligatoire",
    "hub.done_pill": "Terminé",
    "hub.setup_btn": "Configurer",
    "hub.refresh": "Actualiser",
    "hub.loading": "Chargement…",
    "hub.error.title": "Problème de lien",
    "hub.error.default": "Impossible de charger votre hub de réception.",
    "hub.back_home": "Retour à l'accueil",
    "hub.footer": "Mettez cette page en favori · reste active 90 jours · seules les personnes ayant ce lien peuvent la voir.",
    "onboarding.eyebrow": "Recevez votre virement",
    "onboarding.title": "Bonjour {name}",
    "onboarding.fallback_name": "vous",
    "onboarding.intro_via": "via Zcardon",
    "onboarding.intro_ref_open": "(référence",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Ajoutez votre compte bancaire {country} ci-dessous pour recevoir les fonds. Vos coordonnées sont transmises directement à Stripe (notre partenaire de paiement) — l'expéditeur ne les voit jamais.",
    "onboarding.invalid.title": "Lien invalide",
    "onboarding.invalid.hint": "Veuillez demander à l'expéditeur d'envoyer un nouveau lien.",
    "onboarding.loading": "Chargement…",
    "onboarding.complete.title": "Coordonnées bancaires déjà enregistrées",
    "onboarding.complete.desc": "Vous avez déjà soumis votre compte bancaire. L'expéditeur libérera les fonds prochainement.",
    "onboarding.success.title": "Coordonnées bancaires reçues ✓",
    "onboarding.success.auto_paid": "Merci — votre compte bancaire est vérifié et les fonds ont été libérés vers votre banque. Ils devraient arriver sous 1 à 2 jours ouvrés.",
    "onboarding.success.pending": "Merci — votre compte bancaire est vérifié. L'expéditeur sera notifié et les fonds arriveront sur votre compte sous 1 à 2 jours ouvrés.",
    "onboarding.success.ref": "Référence : {id}",
    "onboarding.beta.title": "Aperçu bêta.",
    "onboarding.beta.body": "Cette page est déployée, mais le pipeline de paiements en direct attend l'approbation de Stripe. La soumission retournera une erreur jusqu'à ce que la plateforme soit activée. Veuillez réessayer plus tard ou contacter le support si c'est urgent.",
    "onboarding.select_placeholder": "Sélectionner…",
    "onboarding.submit_error_default": "Échec de la soumission. Veuillez réessayer.",
    "onboarding.submit_busy": "Envoi en cours…",
    "onboarding.submit": "Soumettre les coordonnées bancaires →",
    "onboarding.secure_note": "Sécurisé via Stripe. Ce lien expire dans 7 jours.",
    "onboarding.validate.required": "{field} est obligatoire",
    "onboarding.validate.format": "Le format de {field} semble incorrect (attendu : {pattern})",
    "onboarding.validate.no_schema": "Aucun schéma de formulaire disponible",
    "apply.toast.invalid_image": "Veuillez choisir une image JPEG, PNG ou WEBP",
    "apply.toast.too_large": "L'image doit faire 6 Mo ou moins",
    "apply.toast.upload_both": "Veuillez télécharger les deux faces de votre permis de conduire",
    "apply.toast.failed": "Échec de la soumission",
    "apply.toast.submitted": "Demande soumise",
    "apply.success.title": "Demande reçue",
    "apply.success.desc": "Merci, {name}. Nous examinerons votre demande sous 2 à 3 jours ouvrés et vous enverrons un e-mail à {email} avec les prochaines étapes.",
    "apply.success.back_home": "Retour à l'accueil",
    "apply.success.contact_support": "Contacter le support",
    "apply.eyebrow": "Programme agent",
    "apply.heading": "Postuler pour devenir agent Zcardon",
    "apply.intro": "Gagnez 0,5 % de commission sur chaque carte que vous aidez à émettre, chaque recharge que vous collectez et chaque dépôt en espèces que vous acceptez pour vos clients. Nous recrutons des agents reconnus dans leur communauté et capables de vérifier leur identité.",
    "apply.bullet1": "Obtenez votre code agent unique · traçable sur toutes les transactions",
    "apply.bullet2": "Tableau de bord avec récapitulatifs des gains hebdomadaires/mensuels/annuels",
    "apply.bullet3": "Virements bancaires directs — versés chaque semaine",
    "apply.section.your_info": "Vos informations",
    "apply.field.full_name": "Nom légal complet",
    "apply.field.full_name_ph": "Tel qu'il apparaît sur votre permis de conduire",
    "apply.field.email": "E-mail",
    "apply.field.phone": "Téléphone",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Numéro de permis de conduire",
    "apply.field.address": "Adresse",
    "apply.field.address_ph": "Rue, Ville, État, Code postal",
    "apply.field.why_join_label": "Pourquoi souhaitez-vous devenir agent ? (facultatif)",
    "apply.field.why_join_ph": "Parlez-nous de vos liens communautaires ou de votre expérience en transfert d'argent",
    "apply.dl.section": "Vérification du permis de conduire",
    "apply.dl.helper": "Téléversez des photos nettes et bien éclairées des deux côtés. JPEG, PNG ou WEBP · 6 Mo max chacune. Les images sont stockées de manière privée et consultées uniquement par les administrateurs Zcardon à des fins de vérification.",
    "apply.dl.front": "Recto du permis de conduire",
    "apply.dl.back": "Verso du permis de conduire",
    "apply.dl.tap_upload": "Appuyer pour téléverser",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 Mo",
    "apply.cancel": "Annuler",
    "apply.submit_busy": "Envoi en cours…",
    "apply.submit": "Soumettre la candidature",
    "agent.toast.load_failed": "Échec du chargement des données agent",
    "agent.toast.cust_created": "Client {name} créé",
    "agent.toast.cust_failed": "Échec de la création du client",
    "agent.toast.card_issued": "Carte émise · vous avez gagné {amount}",
    "agent.toast.card_issue_failed": "Échec de l'émission de la carte",
    "agent.toast.cash_accepted": "Dépôt en espèces accepté · vous avez gagné {amount}",
    "agent.toast.cash_failed": "Échec de l'enregistrement du dépôt",
    "agent.loading": "Chargement…",
    "agent.eyebrow": "Portail agent",
    "agent.fallback_name": "Agent",
    "agent.code_line.code_label": "Code agent",
    "agent.code_line.commission_label": "commission",
    "agent.earnings.label": "Solde impayé",
    "agent.earnings.sub": "{n} transactions impayées",
    "agent.stat.week": "Cette semaine",
    "agent.stat.month": "Ce mois-ci",
    "agent.stat.year": "Cette année",
    "agent.stat.txns": "{n} txns",
    "agent.stat.customers": "Vos clients",
    "agent.stat.active": "actif",
    "agent.customers.title": "Vos clients",
    "agent.customers.empty": "Aucun client pour l'instant. Appuyez sur \\\"Inscrire un client\\\" pour enregistrer votre premier utilisateur.",
    "agent.customers.col_customer": "Client",
    "agent.customers.col_phone": "Téléphone",
    "agent.customers.col_cards": "Cartes",
    "agent.customers.col_commissions": "Commissions gagnées",
    "agent.customers.col_joined": "Inscrit le",
    "agent.customers.col_actions": "Actions",
    "agent.customers.txns": "{n} txns",
    "agent.commissions.title": "Commissions récentes",
    "agent.commissions.empty": "Aucune commission pour l'instant. Émettez votre première carte pour en gagner.",
    "agent.commissions.col_when": "Quand",
    "agent.commissions.col_customer": "Client",
    "agent.commissions.col_type": "Type",
    "agent.commissions.col_txn": "Transaction",
    "agent.commissions.col_commission": "Commission",
    "agent.commissions.col_status": "Statut",
    "agent.commissions.paid": "payé",
    "agent.commissions.unpaid": "impayé",
    "agent.type.issuance": "Émission",
    "agent.type.topup": "Recharge",
    "agent.type.cash_deposit": "Dépôt en espèces",
    "agent.register_btn": "Inscrire un client",
    "agent.dialog.new_cust.title": "Nouveau client",
    "agent.dialog.new_cust.desc": "Il sera lié à votre code agent. Chaque carte émise en son nom vous rapporte 0,5 % de commission.",
    "agent.dialog.field.full_name": "Nom complet",
    "agent.dialog.field.email": "E-mail",
    "agent.dialog.field.phone": "Téléphone",
    "agent.dialog.field.password": "Mot de passe initial (à communiquer au client)",
    "agent.dialog.cancel": "Annuler",
    "agent.dialog.creating": "Création…",
    "agent.dialog.create_customer": "Créer le client",
    "agent.dialog.issue_card.title": "Émettre une carte pour {name}",
    "agent.dialog.issue_card.desc": "Financé par les espèces collectées. Le client paie des frais de 3,75 % — vous conservez 0,5 %.",
    "agent.dialog.field.receiver_name": "Nom du bénéficiaire",
    "agent.dialog.field.receiver_email": "E-mail du bénéficiaire",
    "agent.dialog.field.receiver_phone": "Téléphone du bénéficiaire",
    "agent.dialog.field.amount_usd": "Montant (USD)",
    "agent.dialog.field.note": "Note (facultatif)",
    "agent.dialog.issuing": "Émission…",
    "agent.dialog.issue_card_btn": "Émettre la carte",
    "agent.actions.issue_card": "Émettre une carte",
    "agent.dialog.cash_deposit.title": "Dépôt en espèces pour {name}",
    "agent.dialog.cash_deposit.desc": "Espèces collectées auprès du client · crée une carte livrée au bénéficiaire.",
    "agent.dialog.field.receiver_name_opt": "Nom du bénéficiaire (facultatif)",
    "agent.dialog.recording": "Enregistrement…",
    "agent.dialog.record_deposit": "Enregistrer le dépôt",
    "agent.actions.cash_deposit": "Dépôt en espèces",

  },
  hi: {
    "header.nav.home": "होम",
    "header.nav.rates": "दरें",
    "header.nav.support": "सहायता",
    "header.nav.admin": "एडमिन",
    "header.nav.signin": "साइन इन",
    "header.nav.signout": "साइन आउट",
    "header.nav.get_started": "शुरू करें",
    "header.nav.account": "खाता",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "भाषा बदलें",
    "landing.hero.eyebrow": "डिजिटल खर्च के लिए एक नया रास्ता",
    "landing.hero.title_a": "एक वर्चुअल कार्ड फंड करें।",
    "landing.hero.title_b_ship": "भेजें",
    "landing.hero.title_b_rest": "इसे किसी को भी,",
    "landing.hero.title_c": "खर्च करें",
    "landing.hero.subtitle": "एक स्कैन करने योग्य QR के साथ टैप-एंड-पे वर्चुअल डेबिट कार्ड जारी करें। Stripe-संचालित चेकआउट, USD-आधारित बैलेंस, पारदर्शी सेवा और गेटवे शुल्क — ईमेल या डाउनलोड द्वारा किसी भी प्राप्तकर्ता को डिलीवर किया जाता है।",
    "landing.hero.cta_start": "कार्ड जारी करना शुरू करें",
    "landing.hero.cta_request_cash": "कैश कार्ड का अनुरोध करें",
    "landing.hero.cta_rates": "दरें देखें",
    "landing.hero.cta_find_card": "मेरा मौजूदा कार्ड खोजें",
    "landing.hero.stat_currency": "मुद्रा",
    "landing.hero.stat_settle": "सेटल समय",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "सेवा शुल्क",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "आधुनिक कार्ड अर्थव्यवस्था के लिए बनाया गया",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "USD में सेटल होता है",
    "landing.hero.marquee": "जारी करें · फंड करें · USD में खर्च करें",
    "landing.demo.eyebrow": "20 सेकंड में देखें",
    "landing.demo.title_a": "जारी करने से",
    "landing.demo.title_b_spend": "खर्च",
    "landing.demo.title_c": "तक। चार टैप में।",
    "landing.demo.step_issue.label": "कार्ड जारी करें",
    "landing.demo.step_issue.detail": "राशि चुनें। कार्ड ~2 मिनट में तैयार।",
    "landing.demo.step_share.label": "शेयर करें",
    "landing.demo.step_share.detail": "ईमेल, टेक्स्ट, WhatsApp, या PNG के रूप में डाउनलोड करें।",
    "landing.demo.step_scan.label": "प्राप्तकर्ता स्कैन करे",
    "landing.demo.step_scan.detail": "फोन कैमरा तुरंत ट्रैक पेज खोलता है।",
    "landing.demo.step_spend.label": "USD में खर्च करें",
    "landing.demo.step_spend.detail": "टैप-टू-पे, कैश निकालें, मर्चेंट पर खर्च करें।",
    "landing.demo.cta_try": "अभी आज़माएं",
    "landing.demo.live_pill": "लाइव डेमो",
    "landing.demo.scene1_caption": "कार्ड बन रहा है · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "WhatsApp के ज़रिए प्राप्तकर्ता को भेजा जा रहा है",
    "landing.demo.scene3_caption": "QR स्कैन हो रहा है · मिल गया",
    "landing.demo.scene4_eyebrow": "बैलेंस जमा हो गया",
    "landing.demo.scene4_status": "✓ स्वीकृत · टैप-टू-पे तैयार",
    "landing.footer.rates": "दरें",
    "landing.footer.support": "सहायता",
    "landing.footer.admin_portal": "एडमिन पोर्टल",
    "landing.footer.copy": "© 2026 Zcardon · एक डेमो फिनटेक उत्पाद",
    "landing.cash.dialog_title": "कैश कार्ड का अनुरोध करें",
    "landing.cash.dialog_desc": "हम आपको एक Stripe चेकआउट लिंक टेक्स्ट करेंगे। किसी भी कार्ड से भुगतान करें; Stripe की पुष्टि होते ही कैश कार्ड फंड हो जाएगा।",
    "landing.cash.phone_label": "मोबाइल नंबर",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "राशि (USD)",
    "landing.cash.name_label": "आपका नाम (वैकल्पिक)",
    "landing.cash.submit": "चेकआउट लिंक भेजें",
    "landing.cash.submitting": "भेजा जा रहा है…",
    "landing.cash.success_title": "चेकआउट लिंक आने वाला है",
    "landing.cash.success_body": "हमने {phone} पर एक Stripe चेकआउट लिंक भेजा है। भुगतान करने के लिए उसे टैप करें और आपका वर्चुअल कार्ड तुरंत सक्रिय हो जाएगा।",
    "landing.cash.success_open_checkout": "अभी चेकआउट खोलें",
    "landing.find.cta": "पहले से भुगतान किया? मेरा कार्ड खोजें",
    "landing.find.dialog_title": "मेरा मौजूदा कार्ड खोजें",
    "landing.find.dialog_desc": "वह फोन नंबर दर्ज करें जो आपने उपयोग किया था। हम आपको फ़ाइल पर किसी भी सक्रिय कैश कार्ड को पुनः प्राप्त करने के लिए 6-अंकीय कोड टेक्स्ट करेंगे।",
    "landing.find.phone_label": "मोबाइल नंबर",
    "landing.find.code_label": "6-अंकीय कोड",
    "landing.find.send_code": "कोड भेजें",
    "landing.find.verify": "सत्यापित करें और खोजें",
    "landing.find.sending": "भेजा जा रहा है…",
    "landing.find.verifying": "सत्यापित हो रहा है…",
    "landing.find.no_cards": "उस नंबर के लिए कोई सक्रिय कार्ड नहीं मिला।",
    "login.eyebrow.welcome": "स्वागत है",
    "login.eyebrow.admin": "प्रशासन",
    "login.title": "साइन इन",
    "login.title_admin": "एडमिन साइन इन",
    "login.admin_badge": "एडमिन पोर्टल",
    "login.email_label": "ईमेल",
    "login.password_label": "पासवर्ड",
    "login.submit": "साइन इन करें",
    "login.submitting": "साइन इन हो रहा है…",
    "login.toast_welcome": "वापस स्वागत है, {name}",
    "login.toast_failed": "लॉगिन विफल हुआ",
    "login.gate_title": "ग्राहक साइन-इन अस्थायी रूप से रोके गए हैं",
    "login.gate_default_message": "हम प्लेटफ़ॉर्म में सुधार कर रहे हैं। कृपया जल्द वापस देखें।",
    "login.gate_admin_note": "एडमिन और मैनेजर नीचे साइन इन कर सकते हैं।",
    "login.new_here": "नए हैं?",
    "login.create_account": "खाता बनाएं",
    "login.customer_q": "ग्राहक हैं?",
    "login.regular_signin": "सामान्य साइन इन",
    "login.demo_title": "डेमो खाते",
    "login.demo_customers_note": "ग्राहक: /register के ज़रिए रजिस्टर करें या Admin पैनल से बनाएं",
    "login.forgot_password": "पासवर्ड भूल गए?",
    "forgot.eyebrow": "खाता पुनर्प्राप्ति",
    "forgot.title": "अपना पासवर्ड रीसेट करें",
    "forgot.subtitle": "अपने Zcardon खाते से जुड़ा ईमेल दर्ज करें और हम आपको नया पासवर्ड चुनने के लिए एक सुरक्षित लिंक भेजेंगे।",
    "forgot.email_label": "ईमेल",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "रीसेट लिंक भेजें",
    "forgot.submitting": "भेजा जा रहा है…",
    "forgot.toast_failed": "रीसेट लिंक नहीं भेजा जा सका। कृपया पुनः प्रयास करें।",
    "forgot.sent_title": "अपना इनबॉक्स जांचें",
    "forgot.sent_message": "अगर {email} के लिए कोई खाता मौजूद है, तो हमने पासवर्ड-रीसेट लिंक भेज दिया है।",
    "forgot.sent_expiry_note": "लिंक 15 मिनट के लिए मान्य है। नहीं मिला? स्पैम जांचें, या दोबारा भेजें।",
    "forgot.back_to_login": "साइन इन पर वापस जाएं",
    "forgot.resend_link": "दूसरा लिंक भेजें",
    "forgot.remember_q": "पासवर्ड याद है?",
    "forgot.signin_link": "साइन इन करें",
    "reset.eyebrow": "खाता पुनर्प्राप्ति",
    "reset.title": "नया पासवर्ड चुनें",
    "reset.subtitle": "एक मज़बूत पासवर्ड चुनें जो पहले इस्तेमाल न किया हो। सेट होते ही आप स्वतः साइन इन हो जाएंगे।",
    "reset.verifying": "आपका रीसेट लिंक सत्यापित हो रहा है…",
    "reset.invalid_title": "यह रीसेट लिंक अमान्य या समाप्त हो गया है",
    "reset.invalid_message": "रीसेट लिंक 15 मिनट के लिए मान्य होते हैं और एक बार ही उपयोग किए जा सकते हैं। जारी रखने के लिए नया लिंक अनुरोध करें।",
    "reset.request_new_link": "नया रीसेट लिंक अनुरोध करें",
    "reset.new_password_label": "नया पासवर्ड",
    "reset.confirm_password_label": "पासवर्ड की पुष्टि करें",
    "reset.password_hint": "कम से कम 8 अक्षर। कहीं और इस्तेमाल किए गए पासवर्ड से बचें।",
    "reset.submit": "पासवर्ड अपडेट करें",
    "reset.submitting": "अपडेट हो रहा है…",
    "reset.toast_success": "पासवर्ड अपडेट हो गया। आप साइन इन हो गए हैं।",
    "reset.toast_failed": "पासवर्ड रीसेट नहीं हो सका। कृपया पुनः प्रयास करें।",
    "reset.error_min_length": "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए।",
    "reset.error_mismatch": "पासवर्ड मेल नहीं खाते।",
    "register.eyebrow": "जुड़ें",
    "register.title": "खाता बनाएं",
    "register.full_name_label": "पूरा नाम",
    "register.email_label": "ईमेल",
    "register.password_label": "पासवर्ड",
    "register.submit": "खाता बनाएं",
    "register.submitting": "बनाया जा रहा है…",
    "register.gate_title": "साइन-अप अस्थायी रूप से रोके गए हैं",
    "register.gate_default_message": "हम प्लेटफ़ॉर्म में सुधार कर रहे हैं। कृपया जल्द वापस देखें।",
    "register.gate_already_member": "पहले से खाता है?",
    "register.toast_created": "खाता बन गया",
    "register.toast_failed": "रजिस्ट्रेशन विफल हुआ",
    "register.already_member": "पहले से सदस्य हैं?",
    "register.signin_link": "साइन इन करें",
    "dash.eyebrow": "डैशबोर्ड",
    "dash.greeting": "वापस स्वागत है, {name}",
    "dash.subtitle": "वर्चुअल कार्ड जारी करें या वायर डिपॉज़िट भेजें — आपका कंट्रोल सेंटर नीचे है।",
    "dash.action.issue_card": "कार्ड जारी करें",
    "dash.action.direct_deposit": "डायरेक्ट डिपॉज़िट (वायर)",
    "dash.action.support": "सहायता",
    "dash.section.cards": "आपके कार्ड",
    "dash.section.activity": "हाल की गतिविधि",
    "dash.section.deposits": "डायरेक्ट डिपॉज़िट",
    "dash.empty.cards": "अभी कोई कार्ड नहीं — शुरू करने के लिए पहला कार्ड जारी करें।",
    "dash.empty.activity": "जैसे ही आप कार्ड जारी करेंगे या वायर भेजेंगे, हाल की गतिविधि यहाँ दिखेगी।",
    "ref.eyebrow": "Zcardon में आपका स्वागत है",
    "ref.greeting": "{name} ने आपको भेजा।",
    "ref.loading": "आपका इनवाइट लोड हो रहा है…",
    "ref.resolved_redirect": "एक पल — आपको अंदर भेजा जा रहा है।",
    "ref.resolving": "आपका इनवाइट लिंक रिज़ॉल्व हो रहा है…",
    "support.eyebrow": "आपके लिए यहाँ",
    "support.title": "सहायता, टिकट नहीं।",
    "support.subtitle": "किसी इंसान से बात करें। कार्यदिवसों पर एक कारोबारी घंटे के भीतर ईमेल का जवाब।",
    "support.card.email": "ईमेल",
    "support.card.phone": "फ़ोन",
    "support.card.live_chat": "लाइव चैट",
    "support.card.live_chat_value": "आपके डैशबोर्ड में",
    "support.card.hours": "समय",
    "support.faq.title": "सामान्य प्रश्न",
    "support.faq.q1": "रिसीवर को कार्ड कितने समय में मिलता है?",
    "support.faq.a1": "सफल फंडिंग के आमतौर पर 2 मिनट के भीतर। डिलीवरी ईमेल (Resend) और MMS (Twilio) के ज़रिए स्वचालित रूप से होती है।",
    "support.faq.q2": "भेजने के बाद क्या मैं कार्ड रद्द या फ्रीज़ कर सकता हूँ?",
    "support.faq.a2": "हाँ — कार्ड विवरण पृष्ठ खोलें और Freeze टैप करें। आप रिसीवर अलर्ट ईमेल से भी सीधे फ्रीज़ कर सकते हैं।",
    "support.faq.q3": "कार्ड किन मुद्राओं में खर्च कर सकता है?",
    "support.faq.a3": "सभी कार्ड USD में सेटल होते हैं। रिसीवर जहाँ भी Visa/Mastercard स्वीकार होता है वहाँ खर्च कर सकता है — FX कार्ड नेटवर्क द्वारा संभाली जाती है।",
    "support.faq.q4": "कौन से शुल्क लागू होते हैं?",
    "support.faq.a4": "एक पारदर्शी सेवा शुल्क के साथ Stripe गेटवे लागत। भुगतान से पहले Rates पृष्ठ पर लाइव कोट देखें।",
    "rates.eyebrow": "पारदर्शी",
    "rates.title_a": "कोई छुपा हुआ",
    "rates.title_b": "स्प्रेड नहीं",
    "rates.subtitle": "भुगतान से पहले आप सेवा शुल्क और गेटवे लागत देखते हैं। मूलधन हमेशा USD में वर्चुअल कार्ड बैलेंस में क्रेडिट होता है।",
    "rates.row.service_fee": "सेवा शुल्क",
    "rates.row.service_fee_value": "भेजी गई राशि का {pct}",
    "rates.row.gateway": "गेटवे (Stripe)",
    "rates.row.settlement": "सेटलमेंट",
    "rates.row.settlement_value": "वर्चुअल कार्ड पर USD बैलेंस",
    "rates.row.topup": "टॉप-अप तरीके",
    "rates.row.topup_value": "कार्ड · डेबिट · बैंक (ACH)",
    "rates.sim.eyebrow": "लाइव कोट",
    "rates.sim.title_a": "कोई भी राशि भेजें,",
    "rates.sim.title_b": "तुरंत विवरण देखें।",
    "rates.sim.amount_label": "भेजने की राशि",
    "rates.sim.line.principal": "कार्ड पर (मूलधन)",
    "rates.sim.line.service": "सेवा शुल्क · {pct}",
    "rates.sim.line.gateway": "गेटवे · {pct} + {fixed}",
    "rates.sim.line.you_pay": "आप भुगतान करते हैं",
    "rates.sim.cta": "${amount} से शुरू करें →",
    "rates.sim.note": "ऊपर लागू दरें एडमिन-कॉन्फ़िगर्ड हैं। प्राप्तकर्ता को हमेशा पूरा मूलधन मिलता है।",
    "dd.title": "डायरेक्ट डिपॉज़िट",
    "dd.intro": "दुनिया में कहीं भी रिसीवर के बैंक खाते में पैसे भेजें। कार्ड (Stripe Checkout) या अपने Zcardon बैलेंस से भुगतान करें — हम जहाँ समर्थित है वहाँ स्वचालित रूप से, या जिन देशों को हम अभी ऑटो-रूट नहीं करते वहाँ मैन्युअल रूप से रिसीवर के बैंक में वायर संभालते हैं। हर लेनदेन प्रिंट करने योग्य रसीद के साथ लॉग होता है।",
    "dd.new_btn": "नया डायरेक्ट डिपॉज़िट लॉग करें",
    "dd.close": "बंद करें",
    "dd.history_eyebrow": "आपके डिपॉज़िट",
    "dd.loading": "लोड हो रहा है…",
    "dd.empty": "अभी तक कोई डायरेक्ट डिपॉज़िट नहीं।",
    "dd.status.approved": "स्वीकृत",
    "dd.status.pending": "अनुमोदन लंबित",
    "dd.status.rejected": "अस्वीकृत",
    "dd.row.confirmed": "✓ रिसीवर ने प्राप्ति की पुष्टि की",
    "dd.row.disputed": "⚠ रिसीवर ने विसंगति की सूचना दी",
    "dd.row.pdf": "PDF",
    "dd.error.load": "डिपॉज़िट लोड नहीं हो सके",
    "dd.error.pdf": "रसीद डाउनलोड नहीं हो सकी",
    "pay.mode.view": "आपका Zcardon कार्ड",
    "pay.mode.charge": "QR से भुगतान करें",
    "pay.heading.view": "नमस्ते, {name}.",
    "pay.heading.charge": "इस कार्ड से चार्ज करें।",
    "pay.cardholder": "कार्डधारक",
    "pay.paused.label": "कार्ड रुका हुआ है",
    "pay.paused.body": "यह कार्ड फ्रीज़ कर दिया गया है और स्वामी द्वारा पुनः शुरू किए जाने तक नए चार्ज स्वीकार नहीं कर सकता।",
    "pay.balance.label": "उपलब्ध बैलेंस",
    "pay.charges_count": "{count} चार्ज",
    "pay.charges_count_plural": "{count} चार्ज",
    "pay.topups_count": "{count} टॉप-अप",
    "pay.topups_count_plural": "{count} टॉप-अप",
    "pay.activity.label": "हालिया गतिविधि",
    "pay.activity.empty": "अभी तक कोई गतिविधि नहीं",
    "pay.cta.charge": "इस कार्ड से चार्ज करें (मर्चेंट)",
    "pay.cta.charge_paused": "कार्ड रुका हुआ है",
    "pay.cta.note_active": "ऊपर केवल तभी टैप करें जब आप इस कार्ड से भुगतान स्वीकार करने वाले मर्चेंट हों।",
    "pay.cta.note_paused": "यह कार्ड प्रेषक द्वारा रोका गया है। उन्हें इसे पुनः शुरू करने के लिए कहें।",
    "pay.back_to_balance": "बैलेंस पर वापस जाएं",
    "pay.form.amount_label": "राशि (USD)",
    "pay.form.amount_max": "अधिकतम {amount}",
    "pay.form.merchant_label": "मर्चेंट / कारण",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "मेमो (वैकल्पिक)",
    "pay.form.memo_placeholder": "दो लैटे",
    "pay.form.submit_processing": "प्रोसेस हो रहा है…",
    "pay.form.submit_paused": "कार्ड रुका हुआ है",
    "pay.form.submit_charge": "{amount} चार्ज करें",
    "pay.form.submit_charge_card": "कार्ड चार्ज करें",
    "pay.form.disclaimer": "इस कार्ड को चार्ज करके आप पुष्टि करते हैं कि आप कार्डधारक द्वारा उपरोक्त राशि खर्च करने के लिए अधिकृत हैं।",
    "pay.receipt.title": "भुगतान पूर्ण",
    "pay.receipt.to": "को",
    "pay.receipt.id": "रसीद",
    "pay.receipt.time": "समय",
    "pay.receipt.new_balance": "नया बैलेंस",
    "pay.receipt.done": "हो गया · बैलेंस पर वापस जाएं",
    "pay.error.cant_load": "यह कार्ड लोड नहीं हो सका",
    "pay.error.back_home": "होम पर वापस जाएं",
    "pay.error.loading": "कार्ड लोड हो रहा है…",
    "pay.error.charge_failed": "चार्ज विफल हुआ",
    "pay.tap.eyebrow": "टैप करके भुगतान करें",
    "pay.tap.title": "यह कोड दिखाएं",
    "pay.tap.subtitle": "मर्चेंट के स्कैनर को",
    "pay.tap.balance": "बैलेंस",
    "pay.tap.paused": "रोका गया",
    "pay.tap.qr_unavailable": "इस कार्ड के लिए QR उपलब्ध नहीं है",
    "pay.tap.refresh_in": "{secs}s में रिफ्रेश होगा",
    "pay.tap.show_digits": "अंक दिखाएं",
    "pay.tap.hide_digits": "अंक छुपाएं",
    "pay.tap.card_label": "कार्ड · ••••",
    "pay.tap.auto_hides": "60s में स्वतः छुप जाएगा",
    "pay.tap.full_only_image": "पूरा नंबर केवल मूल कार्ड इमेज पर दिखता है",
    "pay.tap.disclaimer": "क्लोज़्ड-लूप कार्ड · किसी भी Zcardon-सक्षम मर्चेंट पर उपयोग योग्य। एंटी-रिप्ले सुरक्षा के लिए QR हर 60s में बदलता है।",
    "pay.subscribe.eyebrow": "अपडेट रहें",
    "pay.subscribe.title": "हर लेनदेन पर ईमेल पाएं",
    "pay.subscribe.desc": "निःशुल्क। हर चार्ज या टॉप-अप पर एक ईमेल। कभी भी अनसब्सक्राइब करें। हम आपका पता कभी साझा नहीं करते।",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "मुझे सूचित करें",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "आप सब्सक्राइब हो गए",
    "pay.subscribe.done.body": "कार्ड •••• {last4} पर हर चार्ज या टॉप-अप पर हम {email} को तुरंत ईमेल करेंगे।",
    "pay.subscribe.toast.already": "आप पहले से सब्सक्राइब थे — कोई डुप्लिकेट नहीं भेजा गया।",
    "pay.subscribe.toast.success": "हर लेनदेन पर आपको ईमेल मिलेगा। स्वागत है!",
    "pay.subscribe.toast.failed": "सब्सक्राइब विफल हुआ",
    "pay.save.eyebrow": "अपना कार्ड साथ रखें",
    "pay.save.title": "यह कार्ड अपने फ़ोन में सेव करें",
    "pay.save.desc": "ऑफलाइन एक्सेस के लिए कार्ड इमेज अपनी फ़ोटो में सेव करें — कभी भी मर्चेंट को QR दिखाएं, कोई ऐप ज़रूरी नहीं।",
    "pay.save.cta": "कार्ड इमेज सेव करें",
    "pay.save.preparing": "तैयार हो रहा है…",
    "pay.save.add_home": "होम स्क्रीन पर जोड़ें",
    "pay.save.toast.opened": "कार्ड इमेज खुली — फ़ोटो में सेव करने के लिए लंबे समय तक दबाएं",
    "pay.save.toast.failed": "कार्ड सेव नहीं हो सका",
    "pay.save.ios.title": "Zcardon को अपने iPhone होम स्क्रीन पर जोड़ने के लिए:",
    "pay.save.ios.step1": "Safari के नीचे Share बटन टैप करें",
    "pay.save.ios.step2": "नीचे स्क्रॉल करें और Add to Home Screen टैप करें",
    "pay.save.ios.step3": "पुष्टि करने के लिए Add टैप करें",
    "pay.save.ios.note": "Zcardon किसी भी अन्य ऐप की तरह दिखेगा — इस कार्ड को तुरंत खोलने के लिए आइकन टैप करें।",
    "pay.thanks.eyebrow": "धन्यवाद भेजें",
    "pay.thanks.body": "${amount} अभी मिला — जिसने यह कार्ड फंड किया उन्हें बताएं कि इससे मदद हुई।",
    "pay.thanks.placeholder": "वैकल्पिक — कुछ कहना चाहते हैं…",
    "pay.thanks.cta": "{emoji} धन्यवाद भेजें",
    "pay.thanks.sending": "भेजा जा रहा है…",
    "pay.thanks.done": "धन्यवाद भेज दिया — वे कुछ ही सेकंड में देखेंगे।",
    "pay.thanks.toast.sent": "धन्यवाद भेजा गया",
    "pay.thanks.toast.failed": "धन्यवाद नहीं भेजा जा सका",
    "pay.activity.topup_bank": "बैंक ट्रांसफर टॉप-अप",
    "pay.activity.topup_debit": "डेबिट कार्ड टॉप-अप",
    "pay.activity.topup_credit": "क्रेडिट कार्ड टॉप-अप",
    "pay.activity.topup_crypto": "क्रिप्टो टॉप-अप",
    "pay.activity.topup_received": "टॉप-अप प्राप्त हुआ",
    "pay.activity.merchant_charge": "मर्चेंट चार्ज",
    "pay.toast.payment_received": "भुगतान प्राप्त हुआ · −{amount}",
    "cash.confirming": "भुगतान की पुष्टि हो रही है…",
    "cash.confirming.sub": "Stripe चार्ज को अंतिम रूप दे रहा है।",
    "cash.notfound.title": "लिंक समाप्त हो गया",
    "cash.notfound.body": "हमें यह कैश अनुरोध नहीं मिला। यह लिंक किसी पुराने सेशन का हो सकता है, या कार्ड कभी फाइनल नहीं हुआ। नया अनुरोध शुरू करें — इसमें केवल एक मिनट लगता है, और आपके द्वारा पहले किया गया कोई भी भुगतान बैकग्राउंड रिकॉन्सिलर द्वारा पिक अप होते ही स्वतः क्रेडिट हो जाएगा।",
    "cash.notfound.ref": "ref:",
    "cash.notfound.start": "नया अनुरोध शुरू करें",
    "cash.notfound.contact": "सहायता से संपर्क करें",
    "cash.error.title": "स्थिति जांच नहीं हो सकी",
    "cash.error.return": "होम पर वापस जाएं",
    "cash.autopay.opening": "Tap-to-Pay खोला जा रहा है…",
    "cash.autopay.redirect": "{secs}s में रीडायरेक्ट होगा।",
    "cash.autopay.stay": "यहीं रहें",
    "cash.ready.title": "आपका कार्ड तैयार है",
    "cash.ready.subtitle": "${amount} USD फंड किया गया · {phone} पर भी SMS भेजा गया",
    "cash.card.label": "कार्ड",
    "cash.card.exp": "समाप्ति",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "बैलेंस",
    "cash.cta.open_pay": "Tap-to-Pay स्क्रीन खोलें",
    "cash.toast.funded": "आपका कार्ड फंड हो गया है — SMS के लिए अपना फोन देखें!",
    "cash.pending.title": "अभी भी Stripe का इंतजार है",
    "cash.pending.body": "हम पुष्टि के लिए जाँच कर रहे हैं। इसमें आमतौर पर 5-15 सेकंड लगते हैं। अगर आपने बिना भुगतान किए चेकआउट बंद कर दिया, तो आप होमपेज से फिर शुरू कर सकते हैं।",
    "cash.pending.back": "होम पर वापस जाएं",
    "cash.timeout.title": "भुगतान की पुष्टि नहीं हो सकी",
    "cash.timeout.body": "हमने Stripe की पुष्टि के लिए 3 मिनट इंतजार किया। अक्सर इसका मतलब है कि भुगतान से पहले चेकआउट बंद हो गया — लेकिन अगर आपने भुगतान किया था, तो आपका कार्ड अगले कुछ मिनटों में स्वतः सक्रिय हो जाएगा (हमारा बैकग्राउंड reconciler हर 5 मिनट में चलता है) और होते ही हम इसे {phone} पर टेक्स्ट करेंगे।",
    "cash.timeout.start_over": "फिर से शुरू करें",
    "cash.timeout.try_again": "पुनः प्रयास करें",
    "cash.resend.title": "नहीं मिला? फिर से भेजें",
    "cash.resend.degraded.lead": "अभी US नंबरों पर डिलीवरी प्रभावित है।",
    "cash.resend.degraded.sms": "US फोन पर SMS कैरियर द्वारा चुपचाप ब्लॉक हो सकते हैं (toll-free वेरिफिकेशन अभी pending है)।",
    "cash.resend.degraded.email": "Email टेस्ट मोड में है और DNS सत्यापन के दौरान केवल account-owner पते पर डिलीवर होती है।",
    "cash.resend.degraded.tail": "अगर कोई चैनल काम न करे, तो दूसरा आजमाएं — या कार्ड सीधे पाने के लिए प्राप्तकर्ता के डिवाइस पर यह पेज खोलें।",
    "cash.resend.text": "टेक्स्ट",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "भेज रहे हैं…",
    "cash.resend.email_sandbox": "Email टेस्ट मोड में है। जब तक zcardon.com डोमेन Resend पर सत्यापित नहीं हो जाता, हम केवल account-owner पते पर भेज सकते हैं। अगर आपका email स्वीकार न हो, तो Text या WhatsApp का उपयोग करें।",
    "cash.resend.email_label": "Email पता",
    "cash.resend.email_send": "Email पर भेजें",
    "cash.resend.cancel": "रद्द करें",
    "cash.resend.limit": "प्रति कार्ड अधिकतम 5 बार भेजा जा सकता है। फाइल पर फोन: {phone}",
    "cash.resend.no_phone": "(कोई नहीं)",
    "cash.wallet.title": "Wallet में जोड़ें",
    "cash.wallet.apple": "Apple Wallet में जोड़ें",
    "cash.wallet.google": "Google Wallet में जोड़ें",
    "cash.wallet.generating": "पास बना रहे हैं…",
    "cash.wallet.note": "आपकी लॉक स्क्रीन पर रहता है · बैलेंस बदलने पर स्वतः अपडेट होता है",
    "cash.wallet.toast.apple": "पास डाउनलोड हुआ — Apple Wallet में जोड़ने के लिए अपने iPhone पर खोलें",
    "cash.wallet.toast.apple_failed": "Apple Wallet पास नहीं बन सका",
    "cash.wallet.toast.google_failed": "Google Wallet पास नहीं बन सका",
    "cash.delivery.fallback_title": "{fallback} के ज़रिए भेजा गया",
    "cash.delivery.fallback_body": "आपके कैरियर ने मूल SMS ब्लॉक कर दिया, इसलिए हमने आपका कार्ड {fallback} के ज़रिए {phone} पर फिर से भेजा। वह चैनल जाँचें।",
    "cash.delivery.toll_blocked": "कैरियर ने SMS ब्लॉक किया",
    "cash.delivery.failed_title": "हम आपका कार्ड टेक्स्ट नहीं कर सके",
    "cash.delivery.toll_body": "आपके मोबाइल कैरियर ने संदेश अस्वीकार कर दिया (यह नए सेंडर के वेरिफिकेशन pending होने पर होता है)। अभी अपना कार्ड सेव करें: टैप करें",
    "cash.delivery.failed_body": "आपका कार्ड फंड हो गया है — कृपया इसे अभी सेव करें क्योंकि SMS नहीं पहुँचा। टैप करें",
    "cash.delivery.cta_label": "Tap-to-Pay स्क्रीन खोलें",
    "cash.delivery.body_tail": "ऊपर, स्क्रीनशॉट लें, या खुद को कॉपी भेजने के लिए नीचे Email का उपयोग करें।",
    "cash.handoff.eyebrow": "व्यक्तिगत रूप से सौंपें",
    "cash.handoff.body": "प्राप्तकर्ता से अपने कैमरे से यह स्कैन करवाएं — कार्ड सीधे उनके फोन पर खुलेगा, कोई SMS या email की ज़रूरत नहीं।",
    "cash.handoff.share_label": "या यह लिंक शेयर करें",
    "cash.handoff.copy": "लिंक कॉपी करें",
    "cash.handoff.copied": "✓ कॉपी हुआ",
    "cash.handoff.share": "शेयर करें",
    "cash.handoff.toast.copied": "लिंक कॉपी हुआ — कहीं भी पेस्ट करें",
    "cash.handoff.toast.copy_failed": "कॉपी नहीं हो सका। मैन्युअली कॉपी करने के लिए लिंक को लॉन्ग-प्रेस करें।",
    "portal.loading": "आपकी रसीद लोड हो रही है…",
    "portal.unavailable.title": "लिंक उपलब्ध नहीं",
    "portal.unavailable.body": "यह लिंक अमान्य है या समाप्त हो गया है।",
    "portal.unavailable.hint": "अगर भेजने वाले ने यह लिंक शेयर किया था, तो उनसे रसीद email फिर से भेजने को कहें।",
    "portal.brand": "रसीद पोर्टल",
    "portal.eyebrow": "{sender} ने आपको भेजा",
    "portal.reference": "संदर्भ",
    "portal.pending.title": "अनुमोदन pending",
    "portal.pending.body": "भेजने वाले ने यह ट्रांसफर दर्ज किया है लेकिन इसकी Zcardon द्वारा समीक्षा हो रही है। कृपया थोड़ी देर बाद जाँचें — अनुमोदन के बाद रसीद उपलब्ध होगी।",
    "portal.row.bank": "बैंक",
    "portal.row.account": "खाता",
    "portal.row.routing": "रूटिंग",
    "portal.row.memo": "मेमो",
    "portal.confirmed.title": "आपने रसीद की पुष्टि की",
    "portal.confirmed.body": "{when} को पुष्टि की गई। भेजने वाला यह देख सकता है।",
    "portal.disputed.title": "आपने एक विसंगति की सूचना दी",
    "portal.disputed.body": "{when} को रिपोर्ट किया गया। भेजने वाले को email कर दी गई है।",
    "portal.actions.body": "क्या यह राशि आपके खाते में आ गई है? पुष्टि करने से भेजने वाले को जानकारी मिलती है — Zcardon के ज़रिए कोई पैसा नहीं जाता, यह केवल एक रिकॉर्ड है।",
    "portal.actions.confirm": "हाँ, मुझे मिल गया",
    "portal.actions.flag": "समस्या की सूचना दें",
    "portal.dispute.placeholder": "गलत राशि, नहीं पहुँचा, गलत खाता नंबर…",
    "portal.dispute.submit": "रिपोर्ट सबमिट करें",
    "portal.dispute.too_short": "एक संक्षिप्त कारण जोड़ें (कम से कम 4 अक्षर)",
    "portal.toast.confirmed": "धन्यवाद — रसीद की पुष्टि हो गई।",
    "portal.toast.confirm_failed": "रसीद की पुष्टि नहीं हो सकी",
    "portal.toast.reported": "रिपोर्ट हो गई। भेजने वाले को सूचित कर दिया गया है।",
    "portal.toast.report_failed": "विवाद सबमिट नहीं हो सका",
    "portal.toast.pdf_failed": "रसीद डाउनलोड नहीं हो सकी",
    "portal.download_pdf": "PDF रसीद डाउनलोड करें",
    "portal.privacy": "यह पेज केवल आपके लिए निजी है। लिंक वाला कोई भी इस रसीद को देख सकता है — इसे सार्वजनिक रूप से शेयर न करें। प्रश्नों के लिए:",
    "detail.back": "डैशबोर्ड पर वापस जाएं",
    "detail.eyebrow": "आपका कार्ड",
    "detail.confirming_payment": "आपके भुगतान की पुष्टि हो रही है…",
    "detail.active_ready": "कार्ड सक्रिय है और शेयर करने के लिए तैयार है।",
    "detail.balance.label": "वर्तमान बैलेंस",
    "detail.toast.notfound": "कार्ड नहीं मिला",
    "detail.toast.loaded": "कार्ड में फंड लोड किए गए।",
    "detail.toast.confirmed": "भुगतान की पुष्टि हो गई।",
    "detail.toast.expired": "भुगतान सत्र समाप्त हो गया",
    "detail.delivery.receiver_fallback": "आपका प्राप्तकर्ता",
    "detail.delivery.received_title": "{name} ने कार्ड खोला",
    "detail.delivery.received_when": "डिलीवरी की पुष्टि · {when}",
    "detail.delivery.waiting_title_on": "कार्ड मिलते ही आपको ईमेल मिलेगा",
    "detail.delivery.waiting_title_off": "डिलीवरी सूचनाएं बंद हैं",
    "detail.delivery.waiting_body_on": "जैसे ही आपका प्राप्तकर्ता इस कार्ड का QR स्कैन करेगा, हम आपको एक बार ईमेल करेंगे।",
    "detail.delivery.waiting_body_off": "इसे चालू करें ताकि जब आपका प्राप्तकर्ता पहली बार कार्ड खोले, तो आपको एक बार ईमेल मिले।",
    "detail.delivery.toggle_on": "चालू",
    "detail.delivery.toggle_off": "चालू करें",
    "detail.delivery.toast_enabled": "कार्ड खुलने पर आपको सूचना मिलेगी।",
    "detail.delivery.toast_disabled": "डिलीवरी सूचनाएं बंद कर दी गईं।",
    "detail.delivery.toast_failed": "डिलीवरी सूचनाएं अपडेट नहीं हो सकीं।",
    "nudge.eyebrow": "अपने प्राप्तकर्ता को बताएं",
    "nudge.title": "{receiver} को पहले से सूचित करें",
    "nudge.subtitle": "हमने एक छोटा संदेश तैयार किया है ताकि {receiver} को पता चले कि कार्ड तैयार है और इसे कैसे उपयोग करें।",
    "nudge.receiver_fallback": "आपका प्राप्तकर्ता",
    "nudge.sender_fallback": "एक दोस्त",
    "nudge.template": "नमस्ते {receiver} — {sender} ने आपको {amount} वाला Zcardon वर्चुअल कार्ड भेजा है। यहाँ टैप करके भुगतान करें: {url}\\n\\nकोई ऐप ज़रूरी नहीं। लिंक किसी भी फोन पर काम करता है।",
    "nudge.email_subject": "{sender} ने आपको {amount} का Zcardon कार्ड भेजा",
    "nudge.action_copy": "संदेश कॉपी करें",
    "nudge.action_sms": "SMS खोलें",
    "nudge.action_email": "ईमेल खोलें",
    "nudge.toast_copied": "संदेश क्लिपबोर्ड पर कॉपी हो गया।",
    "nudge.toast_copy_failed": "कॉपी नहीं हो सका। मैन्युअली चुनकर कॉपी करें।",
    "ios_install.title": "अपने iPhone पर Zcardon इंस्टॉल करें",
    "ios_install.step_1": "टैप करें",
    "ios_install.share_label": "Share",
    "ios_install.step_2": " फिर \\\"Add to Home Screen\\\" चुनें — Zcardon फुल-स्क्रीन ऐप की तरह खुलेगा।",
    "smart_banner.title": "Zcardon ऐप में खोलें",
    "smart_banner.subtitle": "तेज़ चेकआउट। ऐप छोड़े बिना टैप करके भुगतान करें।",
    "smart_banner.cta": "खोलें",
    "smart_banner.dismiss": "बंद करें",
    "social_proof.eyebrow": "लाइव · पिछले 48 घंटे",
    "social_proof.empty": "अगले कार्ड का इंतज़ार है…",
    "social_proof.line_opened": "{name} ने अभी {amount} का कार्ड खोला।",
    "social_proof.line_issued": "{name} को एक दोस्त से {amount} का कार्ड मिला।",
    "social_proof.line_deposit": "{name} को {amount} का डायरेक्ट डिपॉज़िट मिला।",
    "social_proof.stat_funded": "आज फंड किए गए कार्ड",
    "social_proof.stat_delivered": "आज डिलीवर किए गए कार्ड",
    "social_proof.stat_recent": "हाल की गतिविधि",
    "social_proof.ago_seconds": "{n}सेकंड पहले",
    "social_proof.ago_minutes": "{n}मिनट पहले",
    "social_proof.ago_hours": "{n}घंटे पहले",
    "flight.eyebrow": "उड़ान में कार्ड · पिछले 48 घंटे",
    "flight.title": "Zcardon कहाँ पहुँचता है",
    "flight.subtitle": "वास्तविक प्राप्तकर्ता स्थान, क्षेत्र तक अज्ञात। हम केवल देश और US राज्य दिखाते हैं — कभी नाम, पते या राशि नहीं।",
    "flight.row_template": "{label} में कार्ड खोला गया",
    "flight.more_count": "+ {n} और क्षेत्र",
    "flight.ago_seconds": "{n}सेकंड पहले",
    "flight.ago_minutes": "{n}मिनट पहले",
    "flight.ago_hours": "{n}घंटे पहले",
    "trust_strip.eyebrow": "असली डेटा · डेटाबेस से लाइव",
    "trust_strip.title_a": "प्रशंसापत्र नहीं।",
    "trust_strip.title_b": "लाइव डेटाबेस रीड।",
    "trust_strip.how_link": "हम यह कैसे एकत्र करते हैं",
    "trust_strip.modal_eyebrow": "डेटा स्रोत",
    "trust_strip.modal_title": "सब असली, सब अज्ञात",
    "trust_strip.modal_close": "बंद करें",
    "trust_strip.modal_intro": "इस स्ट्रिप पर हर संख्या हमारे अपने डेटाबेस से आती है — कभी तृतीय-पक्ष फ़ीड से नहीं, कभी बनावटी नहीं। यहाँ देखें कि प्रत्येक अनुभाग कहाँ से डेटा लेता है और हम इसे आपको दिखाने से पहले कैसे अज्ञात करते हैं।",
    "trust_strip.src_carousel_title": "लाइव गतिविधि कैरोसेल",
    "trust_strip.src_carousel_body": "cards और direct_deposits कलेक्शन से पिछले 48 घंटे का डेटा। नाम पहले नाम + अंतिम नाम के पहले अक्षर के रूप में दिखते हैं; राशि निकटतम $5 तक गोल की जाती है। हर 60 सेकंड में रिफ्रेश होता है।",
    "trust_strip.src_flight_title": "Zcardon कहाँ पहुँचता है",
    "trust_strip.src_flight_body": "card_scans से QR-स्कैन लैंडिंग पढ़ता है, स्कैन के समय जियो-रिज़ॉल्व। देश (या US राज्य) तक संक्षिप्त। राज्य स्तर से नीचे कोई शहर सटीकता नहीं, कोई IP प्रदर्शित नहीं, कोई कार्ड पहचानकर्ता नहीं। हर 90 सेकंड में रिफ्रेश होता है।",
    "trust_strip.src_badges_title": "30-दिन के भरोसे के आँकड़े",
    "trust_strip.src_badges_body": "केवल कुल गिनती — प्रति-पंक्ति डेटा कभी नहीं। प्रत्येक मेट्रिक तभी दिखता है जब वह न्यूनतम नमूना आकार पार करे, इसलिए 2 पुष्टियों से 100% दर छिपी रहेगी। हर मिनट रिफ्रेश होता है।",
    "trust_strip.modal_promise": "हम सार्वजनिक विजेट में कभी नाम, पते, सटीक राशि, IP या कार्ड नंबर प्रकाशित नहीं करेंगे। वादा।",
    "trust_badge.eyebrow": "असली संख्याएँ · पिछले 30 दिन",
    "trust_badge.title": "लोग वास्तव में इसका उपयोग कर रहे हैं।",
    "trust_badge.subtitle": "कोई प्रशंसापत्र नहीं — हर मिनट डेटाबेस से रिफ्रेश होती लाइव गिनती।",
    "trust_badge.confirmed_label": "प्राप्तकर्ता 24 घंटे के भीतर प्राप्ति की पुष्टि करते हैं",
    "trust_badge.confirmed_sub": "पिछले 30 दिनों में {n} जवाबों से",
    "trust_badge.thanks_label": "धन्यवाद नोट भेजे गए",
    "trust_badge.thanks_sub": "पिछले 30 दिनों में प्राप्तकर्ताओं से",
    "trust_badge.cards_label": "कार्ड फंड किए गए",
    "trust_badge.cards_sub": "पिछले 30 दिन · लाइव काउंट",
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} ने {amount} का कार्ड खोला",
    "pulse.tooltip_issued": "{name} को {amount} का कार्ड मिला",
    "pulse.tooltip_deposit": "{name} को {amount} की जमा राशि मिली",
    "pulse.ago_seconds": "{n}s पहले",
    "pulse.ago_minutes": "{n}m पहले",
    "pulse.ago_hours": "{n}h पहले",
    "note_cloud.eyebrow": "लोग इसे किसके लिए उपयोग करते हैं",
    "note_cloud.title": "असली कारण। असली लोग।",
    "note_cloud.subtitle": "पिछले 90 दिनों के {n} कार्ड नोट्स से एकत्रित। हम केवल वे शब्द दिखाते हैं जो कम से कम {min} अलग-अलग नोट्स में आते हैं — आपका संदेश आपका रहता है।",
    "note_cloud.footnote": "नाम, राशि और संपर्क जानकारी प्रदर्शन से पहले हटा दी जाती है।",
    "note_cloud.lang_eyebrow": "ये नोट्स किस भाषा में हैं?",
    "note_cloud.lang_other": "अन्य",
    "download.button": "ऐप डाउनलोड करें",
    "download.close": "बंद करें",
    "download.modal_eyebrow": "Zcardon पाएं",
    "download.modal_title": "अपने फोन पर Zcardon इंस्टॉल करें",
    "download.footnote": "अभी App Store नहीं है — आप सीधे सुरक्षित वेब ऐप इंस्टॉल कर रहे हैं। तुरंत अपडेट होता है।",
    "download.installed_title": "Zcardon इंस्टॉल हो गया",
    "download.installed_body": "अपनी होम स्क्रीन पर आइकन देखें।",
    "download.webview_warning": "इंस्टॉल करने के लिए यह पेज अपने फोन के ब्राउज़र में खोलें (iPhone पर Safari, Android पर Chrome) — इन-ऐप ब्राउज़र होम स्क्रीन पर नहीं जोड़ सकते।",
    "download.ios_step1_title": "Safari पर",
    "download.ios_step1_body": "अगर आप पहले से Safari में नहीं हैं, तो यह लिंक कॉपी करें और वहां खोलें। (Chrome और अन्य iOS ब्राउज़र ऐप इंस्टॉल नहीं कर सकते।)",
    "download.ios_step2_title": "Share टैप करें",
    "download.ios_step2_body": "Share आइकन iPhone पर नीचे-मध्य में और iPad पर ऊपर-दाईं ओर है।",
    "download.ios_step3_title": "होम स्क्रीन पर जोड़ें",
    "download.ios_step3_body": "शेयर मेनू में नीचे स्क्रॉल करें और \\\"Add to Home Screen\\\" चुनें। Zcardon फुल-स्क्रीन ऐप के रूप में इंस्टॉल हो जाता है।",
    "download.android_ready_body": "Chrome ने Zcardon को पहचान लिया है और इंस्टॉल के लिए तैयार है। नीचे टैप करें।",
    "download.android_install": "Zcardon इंस्टॉल करें",
    "download.android_installing": "इंस्टॉल हो रहा है…",
    "download.android_step1_title": "Chrome मेनू खोलें",
    "download.android_step1_body": "Chrome के ऊपर-दाईं ओर तीन बिंदुओं पर टैप करें।",
    "download.android_step2_title": "होम स्क्रीन पर जोड़ें",
    "download.android_step2_body": "\\\"Install app\\\" या \\\"Add to Home screen\\\" चुनें। Zcardon फुल-स्क्रीन ऐप के रूप में इंस्टॉल हो जाता है।",
    "download.desktop_body": "Zcardon खोलने के लिए अपने फोन के कैमरे से यह QR कोड स्कैन करें, फिर अपने फोन के ब्राउज़र से इंस्टॉल करें।",
    "download.steps_hint": "ये चरण आपके ब्राउज़र में होते हैं, इस पेज पर नहीं। नीचे की कोई भी पंक्ति टैप करने योग्य नहीं है — वे बता रही हैं कि क्या देखना है।",
    "download.ios_arrow_label": "Safari में यह टैप करें",
    "download.android_arrow_label": "Chrome में यहां ऊपर देखें",
    "install.eyebrow": "Zcardon इंस्टॉल करें",
    "install.title": "Zcardon को अपनी होम स्क्रीन पर जोड़ें",
    "install.subtitle": "दो टैप में Zcardon एक असली ऐप आइकन बन जाएगा — App Store की जरूरत नहीं।",
    "install.back_home": "ब्राउज़र में Zcardon खोलें",
    "install.continue": "Zcardon पर जाएं →",
    "install.copy": "कॉपी करें",
    "install.copied": "कॉपी हो गया",
    "install.ios_step1_title": "यह पेज Safari में खोलें",
    "install.ios_step1_body": "अगर आपने किसी अन्य ब्राउज़र से स्कैन किया है, तो URL कॉपी करें और पहले Safari में खोलें — iOS पर Chrome ऐप इंस्टॉल नहीं कर सकता।",
    "install.ios_step2_title": "Share बटन टैप करें",
    "install.ios_step2_body": "यह ऊपर तीर वाला वर्गाकार आइकन है — Safari की नीचे (या iPad पर ऊपर-दाईं ओर)।",
    "install.ios_step3_title": "\\\"Add to Home Screen\\\" टैप करें",
    "install.ios_step3_body": "शेयर शीट में नीचे स्क्रॉल करें और \\\"Add to Home Screen\\\" चुनें, फिर पुष्टि करें। Zcardon एक ऐप आइकन के रूप में दिखेगा।",
    "install.ios_arrow_label": "इस आइकन को देखें",
    "install.android_ready_title": "इंस्टॉल के लिए तैयार",
    "install.android_ready_body": "Chrome ने Zcardon को इंस्टॉल करने योग्य पाया। नीचे टैप करें और हम आपकी होम स्क्रीन पर ऐप आइकन के रूप में दिखेंगे।",
    "install.android_install": "Zcardon इंस्टॉल करें",
    "install.android_installing": "इंस्टॉल हो रहा है…",
    "install.android_step1_title": "Chrome का मेनू टैप करें",
    "install.android_step1_body": "Chrome के ऊपर-दाईं कोने में तीन बिंदु हैं।",
    "install.android_step2_title": "\\\"Install app\\\" या \\\"Add to Home screen\\\" टैप करें",
    "install.android_step2_body": "अपने फोन पर Zcardon आइकन जोड़ने की पुष्टि करें। लॉन्च करने के लिए कभी भी टैप करें।",
    "install.android_hint": "अगर अभी \\\"Install app\\\" नहीं दिख रहा, तो होम पेज एक बार ब्राउज़ करें — Chrome थोड़ी देर बाद यह विकल्प खोलता है।",
    "install.desktop_title": "अपने फोन से स्कैन करें",
    "install.desktop_body": "Zcardon एक मोबाइल-फर्स्ट ऐप है। इंस्टॉल करने के लिए यह URL अपने फोन पर खोलें:",
    "install.webview_title": "अपने ब्राउज़र में खोलें",
    "install.webview_body": "इन-ऐप ब्राउज़र (Instagram, Facebook, TikTok) ऐप इंस्टॉल नहीं कर सकते। \\\"…\\\" या शेयर मेनू टैप करें, फिर \\\"Open in Safari\\\" / \\\"Open in Chrome\\\" चुनें। या यह लिंक कॉपी करें:",
    "install.already_title": "आप पहले से ऐप में हैं",
    "install.already_body": "Zcardon इस डिवाइस पर इंस्टॉल है। कभी भी खोलने के लिए होम स्क्रीन पर आइकन टैप करें।",
    "install.installed_title": "इंस्टॉल हो गया",
    "install.installed_body": "अपनी होम स्क्रीन पर Zcardon आइकन देखें — वही आपका ऐप है।",
    "install.steps_hint": "ये चरण आपके ब्राउज़र में होते हैं, इस पेज पर नहीं। नीचे की कोई भी पंक्ति टैप करने योग्य नहीं है — वे ब्राउज़र मेनू में क्या देखना है, यह बता रही हैं।",
    "install.desktop_body_qr": "Zcardon iPhone या Android पर ऐप आइकन के रूप में इंस्टॉल होता है — डेस्कटॉप पर नहीं। अपने फोन का कैमरा इस QR कोड पर लगाएं:",
    "install.desktop_or": "या लिंक कॉपी करें",
    "install.android_arrow_label": "Chrome में यहां ऊपर देखें",
    "install.ios_arrow_caption": "यह Safari की एकदम नीचे है",
    "vietqr.disabled.title": "VietQR के माध्यम से VND स्वीकार करें",
    "vietqr.disabled.badge": "केवल वियतनाम",
    "vietqr.disabled.desc": "एक वियतनामी बैंक खाता लिंक करें और इस कार्ड का QR एक वास्तविक VietQR बन जाएगा — कोई भी वियतनामी बैंकिंग ऐप (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) इसे स्कैन कर सकता है और NAPAS नेटवर्क के माध्यम से सीधे आपको VND में भुगतान कर सकता है। Stripe Issuing अनुमोदन की आवश्यकता नहीं है।",
    "vietqr.disabled.btn_enter": "बैंक विवरण दर्ज करें",
    "vietqr.disabled.or": "या",
    "vietqr.disabled.btn_send_link": "प्राप्तकर्ता को सेटअप लिंक भेजें",
    "vietqr.disabled.btn_send_link_tip": "7-दिन का सार्वजनिक लिंक बनाएँ जिसे प्राप्तकर्ता स्वयं भर सके",
    "vietqr.form.warn_title": "सक्षम करने से पहले पढ़ें",
    "vietqr.form.warn_body_a": "स्कैन अब आपके Zcardon ट्रैकिंग पेज पर नहीं जाएंगे। धन सीधे जाएगा",
    "vietqr.form.warn_body_b": "आपके वियतनामी बैंक खाते में",
    "vietqr.form.warn_body_c": "NAPAS इंटरबैंक के माध्यम से — Zcardon कभी भी पैसा नहीं देखता। VietQR केवल वियतनाम के भीतर VND ट्रांसफर के लिए काम करता है।",
    "vietqr.form.bank": "बैंक",
    "vietqr.form.account_number": "खाता संख्या",
    "vietqr.form.account_name": "खाताधारक का नाम (25 अक्षर)",
    "vietqr.form.city": "शहर (15 अक्षर)",
    "vietqr.form.advanced": "उन्नत — निश्चित राशि / संदर्भ (वैकल्पिक)",
    "vietqr.form.amount_vnd": "राशि (VND)",
    "vietqr.form.amount_ph": "परिवर्तनीय राशि के लिए खाली छोड़ें",
    "vietqr.form.reference": "संदर्भ (25 अक्षर)",
    "vietqr.form.confirm": "मैं समझता हूँ कि इस कार्ड को स्कैन करने से VND सीधे ऊपर दिए गए बैंक खाते में स्थानांतरित हो जाएगा। Zcardon इन निधियों को संसाधित या धारण नहीं करता।",
    "vietqr.form.btn_attach": "VietQR सक्षम करें",
    "vietqr.form.btn_attaching": "सक्षम कर रहे हैं…",
    "vietqr.form.btn_cancel": "रद्द करें",
    "vietqr.enabled.title": "VietQR सक्रिय",
    "vietqr.enabled.desc": "जब कोई वियतनामी बैंकिंग ऐप इस कार्ड को स्कैन करेगा, तो वह सीधे नीचे दिए गए बैंक खाते में VND स्थानांतरित करेगा — Zcardon को पूरी तरह से बायपास करते हुए।",
    "vietqr.enabled.bank_label": "बैंक",
    "vietqr.enabled.account_label": "खाता",
    "vietqr.enabled.fixed_amount": "निश्चित राशि",
    "vietqr.enabled.reference": "संदर्भ",
    "vietqr.enabled.btn_detach": "VietQR हटाएँ",
    "vietqr.enabled.btn_detaching": "हटा रहे हैं…",
    "vietqr.enabled.attached_at": "सक्षम किया गया",
    "vietqr.analytics.live_activity": "लाइव गतिविधि",
    "vietqr.analytics.today": "आज",
    "vietqr.analytics.days_live": "{n} दिन सक्रिय",
    "vietqr.analytics.phantom_views": "अनुमानित दृश्य",
    "vietqr.analytics.confirmed_pays": "पुष्ट भुगतान",
    "vietqr.analytics.total_vnd": "कुल VND",
    "vietqr.analytics.recent_confirmed": "हालिया पुष्टि",
    "vietqr.analytics.phantom_explainer": "अनुमानित दृश्य ≈ सक्षम होने के बाद कार्ड छवि के डाउनलोड। वास्तविक VietQR स्कैन हमारे सर्वर से नहीं गुज़रते, इसलिए हम \"कार्ड दिखाया गया\" गतिविधि से अनुमान लगाते हैं। सटीक डेटा के लिए प्राप्तकर्ता से उनके सेटअप लिंक के माध्यम से भुगतान की पुष्टि करने को कहें।",
    "vietqr.toast.required": "बैंक खाता संख्या और नाम आवश्यक हैं।",
    "vietqr.toast.confirm_first": "कृपया पुष्टि करें कि आप समझते हैं कि VietQR VND को Zcardon को बायपास करके आपके बैंक में भेजता है।",
    "vietqr.toast.attached_default": "VietQR सक्षम किया गया।",
    "vietqr.toast.attach_failed": "VietQR सक्षम करने में विफल।",
    "vietqr.toast.detach_confirm": "VietQR हटाएँ? कार्ड पर QR Zcardon ट्रैकिंग URL पर लौट आएगा।",
    "vietqr.toast.detached": "VietQR हटा दिया गया — QR Zcardon ट्रैकिंग URL पर लौट आया।",
    "vietqr.toast.detach_failed": "हटाने में विफल।",
    "vietqr.toast.link_copied": "सेटअप लिंक कॉपी किया गया · {h} घंटे में समाप्त",
    "vietqr.toast.link_generated": "सेटअप लिंक उत्पन्न हुआ · नीचे से कॉपी करें",
    "vietqr.toast.link_failed": "सेटअप लिंक बनाने में विफल।",

    "setpw.toast.too_short": "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए",
    "setpw.toast.no_match": "पासवर्ड मेल नहीं खाते",
    "setpw.toast.success_signin_again": "पासवर्ड सेट हो गया — कृपया साइन इन करें",
    "setpw.toast.welcome": "Zcardon एजेंट प्रोग्राम में आपका स्वागत है",
    "setpw.toast.expired": "लिंक समाप्त या अमान्य है",
    "setpw.verifying": "आपका आमंत्रण सत्यापित हो रहा है…",
    "setpw.invalid.title": "लिंक समाप्त हो गया",
    "setpw.invalid.desc": "यह वेलकम लिंक समाप्त हो चुका है या पहले ही उपयोग किया जा चुका है। नया आमंत्रण पाने के लिए अपने Zcardon एडमिन से संपर्क करें।",
    "setpw.invalid.home_btn": "होम",
    "setpw.invalid.support_btn": "सहायता से संपर्क करें",
    "setpw.fallback_name": "एजेंट",
    "setpw.eyebrow": "एजेंट स्वागत",
    "setpw.heading": "स्वागत है, {name}",
    "setpw.subheading": "अपना एजेंट अकाउंट सक्रिय करने के लिए पासवर्ड सेट करें। आप तुरंत साइन इन हो जाएंगे।",
    "setpw.code_label": "आपका एजेंट कोड",
    "setpw.code_tag": "ग्राहकों को जोड़ते समय यह शेयर करें",
    "setpw.form.title": "पासवर्ड चुनें",
    "setpw.form.new_label": "नया पासवर्ड",
    "setpw.form.placeholder": "न्यूनतम 8 अक्षर",
    "setpw.form.confirm_label": "पासवर्ड की पुष्टि करें",
    "setpw.form.btn_busy": "सक्रिय हो रहा है…",
    "setpw.form.btn_submit": "मेरा एजेंट अकाउंट सक्रिय करें",
    "tx.heading": "लेनदेन",
    "tx.kind.direct_deposit": "डायरेक्ट डिपॉज़िट",
    "tx.kind.card_topup": "कार्ड टॉप-अप",
    "tx.kind.outbound_payout": "आउटबाउंड पेआउट",
    "tx.toast.load_failed": "लेनदेन लोड करने में विफल",
    "tx.summary.deposits": "जमा",
    "tx.summary.topups": "टॉप-अप",
    "tx.summary.topups_sub": "कार्ड",
    "tx.summary.payouts": "पेआउट",
    "tx.summary.payouts_sub": "आउटबाउंड",
    "tx.filter.all": "सभी",
    "tx.filter.deposits": "जमा",
    "tx.filter.topups": "टॉप-अप",
    "tx.filter.payouts": "पेआउट",
    "tx.loading": "लेनदेन लोड हो रहे हैं…",
    "tx.empty.filtered": "इस फ़िल्टर से कोई लेनदेन नहीं मिला",
    "tx.empty.none": "अभी तक कोई लेनदेन नहीं",
    "tx.empty.cta": "डायरेक्ट डिपॉज़िट से शुरू करें →",
    "tx.open_title": "खुला",
    "terms.load_error_default": "शर्तें लोड करने में विफल",
    "terms.toast.sign_and_check": "अनुबंध स्वीकार करने के लिए हस्ताक्षर करें और पुष्टि करें",
    "terms.toast.recorded": "अनुबंध दर्ज हो गया",
    "terms.toast.failed": "सबमिशन विफल हो गया",
    "terms.error.title": "लिंक अमान्य है",
    "terms.back_home": "होम पर वापस जाएं",
    "terms.loading": "अनुबंध लोड हो रहा है…",
    "terms.done.title": "अनुबंध स्वीकार किया गया",
    "terms.done.meta": "संस्करण {v} · {ts}",
    "terms.done.next": "आप यह पेज बंद कर सकते हैं। कार्ड जारी होते ही उपयोग के लिए तैयार हो जाएगा।",
    "terms.eyebrow": "कार्डधारक अनुबंध · v{v}",
    "terms.title_reaccept": "अपना कार्डधारक अनुबंध दोबारा स्वीकार करें",
    "terms.title_accept": "अपना कार्डधारक अनुबंध स्वीकार करें",
    "terms.desc.sender_sent": "ने आपको एक Zcardon कार्ड भेजा",
    "terms.desc.you_were_sent": "आपको एक Zcardon कार्ड भेजा गया है",
    "terms.desc.ending": "अंत में",
    "terms.desc.read_before": "नीचे दिया गया अनुबंध पढ़ें, फिर कार्ड सक्रिय करने के लिए हस्ताक्षर करें और पुष्टि करें।",
    "terms.already_accepted_note": "आपने पहले का संस्करण स्वीकार किया था। नवीनतम पर अपडेट करने के लिए दोबारा स्वीकार करें।",
    "terms.scroll_hint": "पूरा अनुबंध पढ़ने के लिए बॉक्स के अंदर स्क्रॉल करें।",
    "terms.view_json": "JSON के रूप में खोलें",
    "terms.sign_label": "अपने पूरे कानूनी नाम से हस्ताक्षर करें",
    "terms.sign_placeholder": "जैसा आपके सरकारी ID पर दिखता है",
    "terms.consent": "मैंने Zcardon कार्डधारक अनुबंध v{v} पढ़ लिया है और सहमत हूँ।",
    "terms.btn_recording": "रिकॉर्ड हो रहा है…",
    "terms.btn_accept": "अनुबंध स्वीकार करें",
    "hub.eyebrow": "आपका रिसीव हब",
    "hub.heading.all_done": "सब तैयार है 🎉",
    "hub.heading.greet": "नमस्ते {name} 👋",
    "hub.fallback_name": "आप",
    "hub.desc.sender_sent": "ने आपको एक Zcardon कार्ड भेजा",
    "hub.desc.you_were_sent": "आपको एक Zcardon कार्ड भेजा गया है",
    "hub.desc.ending": "अंत में",
    "hub.desc.complete_steps": "सब कुछ सक्रिय करने के लिए नीचे दिए चरण पूरे करें।",
    "hub.progress.label": "{total} में से {done} चरण पूरे",
    "hub.blocker.title_one": "1 आवश्यक चरण शेष",
    "hub.blocker.title_many": "{n} आवश्यक चरण शेष",
    "hub.blocker.subtext": "जब तक ये पूरे नहीं होते, आपका कार्ड पूरी तरह सक्रिय नहीं होगा।",
    "hub.required_pill": "आवश्यक",
    "hub.done_pill": "हो गया",
    "hub.setup_btn": "सेट अप करें",
    "hub.refresh": "रीफ्रेश करें",
    "hub.loading": "लोड हो रहा है…",
    "hub.error.title": "लिंक समस्या",
    "hub.error.default": "आपका रिसीव हब लोड नहीं हो सका।",
    "hub.back_home": "होम पर वापस जाएं",
    "hub.footer": "इस पृष्ठ को बुकमार्क करें · 90 दिनों तक सक्रिय रहता है · केवल इस लिंक वाले लोग इसे देख सकते हैं।",
    "onboarding.eyebrow": "अपना ट्रांसफर प्राप्त करें",
    "onboarding.title": "नमस्ते {name}",
    "onboarding.fallback_name": "आप",
    "onboarding.intro_via": "Zcardon के माध्यम से",
    "onboarding.intro_ref_open": "(संदर्भ",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "धनराशि प्राप्त करने के लिए नीचे अपना {country} बैंक खाता जोड़ें। आपका विवरण सीधे Stripe (हमारे भुगतान भागीदार) को जाता है — प्रेषक इसे कभी नहीं देखता।",
    "onboarding.invalid.title": "लिंक मान्य नहीं है",
    "onboarding.invalid.hint": "कृपया प्रेषक से एक नया लिंक भेजने के लिए कहें।",
    "onboarding.loading": "लोड हो रहा है…",
    "onboarding.complete.title": "बैंक विवरण पहले से मौजूद है",
    "onboarding.complete.desc": "आपने पहले ही अपना बैंक खाता सबमिट कर दिया है। प्रेषक शीघ्र ही धनराशि जारी करेगा।",
    "onboarding.success.title": "बैंक विवरण प्राप्त हुआ ✓",
    "onboarding.success.auto_paid": "धन्यवाद — आपका बैंक खाता सत्यापित हो गया है और धनराशि अभी आपके बैंक में जारी कर दी गई है। यह 1-2 कार्यदिवसों में पहुँच जाएगी।",
    "onboarding.success.pending": "धन्यवाद — आपका बैंक खाता सत्यापित हो गया है। प्रेषक को सूचित किया जाएगा और धनराशि 1-2 कार्यदिवसों में आपके खाते में आ जाएगी।",
    "onboarding.success.ref": "संदर्भ: {id}",
    "onboarding.beta.title": "बीटा पूर्वावलोकन।",
    "onboarding.beta.body": "यह पृष्ठ तैयार है लेकिन लाइव पेआउट पाइपलाइन Stripe की मंज़ूरी का इंतज़ार कर रही है। प्लेटफ़ॉर्म सक्षम होने तक सबमिट करने पर त्रुटि आएगी। कृपया बाद में देखें, या अत्यावश्यक होने पर सहायता से संपर्क करें।",
    "onboarding.select_placeholder": "चुनें…",
    "onboarding.submit_error_default": "सबमिशन विफल रहा। कृपया पुनः प्रयास करें।",
    "onboarding.submit_busy": "सबमिट हो रहा है…",
    "onboarding.submit": "बैंक विवरण सबमिट करें →",
    "onboarding.secure_note": "Stripe द्वारा सुरक्षित। यह लिंक 7 दिनों में समाप्त हो जाएगा।",
    "onboarding.validate.required": "{field} आवश्यक है",
    "onboarding.validate.format": "{field} का प्रारूप गलत लगता है (अपेक्षित {pattern})",
    "onboarding.validate.no_schema": "कोई फ़ॉर्म स्कीमा उपलब्ध नहीं है",
    "apply.toast.invalid_image": "कृपया JPEG, PNG या WEBP इमेज चुनें",
    "apply.toast.too_large": "इमेज 6 MB या उससे छोटी होनी चाहिए",
    "apply.toast.upload_both": "कृपया अपने ड्राइवर लाइसेंस के दोनों तरफ अपलोड करें",
    "apply.toast.failed": "सबमिशन विफल रहा",
    "apply.toast.submitted": "आवेदन सबमिट किया गया",
    "apply.success.title": "आवेदन प्राप्त हुआ",
    "apply.success.desc": "धन्यवाद, {name}। हम 2–3 कार्यदिवसों के भीतर आपके आवेदन की समीक्षा करेंगे और अगले चरणों के साथ {email} पर ईमेल करेंगे।",
    "apply.success.back_home": "होम पर वापस जाएं",
    "apply.success.contact_support": "सहायता से संपर्क करें",
    "apply.eyebrow": "एजेंट प्रोग्राम",
    "apply.heading": "Zcardon एजेंट बनने के लिए आवेदन करें",
    "apply.intro": "हर कार्ड जारी करने पर, हर टॉप-अप संग्रह पर, और ग्राहकों की ओर से स्वीकार किए गए हर नकद जमा पर 0.5% कमीशन कमाएं। हम उन एजेंटों को शामिल करते हैं जो अपने समुदाय में विश्वसनीय हों और अपनी पहचान सत्यापित कर सकें।",
    "apply.bullet1": "अपना अनूठा एजेंट कोड पाएं · सभी लेनदेन में ट्रैक करने योग्य",
    "apply.bullet2": "साप्ताहिक/मासिक/वार्षिक आय सारांश सहित डैशबोर्ड",
    "apply.bullet3": "सीधे बैंक में जमा — साप्ताहिक भुगतान",
    "apply.section.your_info": "आपकी जानकारी",
    "apply.field.full_name": "पूरा कानूनी नाम",
    "apply.field.full_name_ph": "जैसा आपके ड्राइवर लाइसेंस पर दिखता है",
    "apply.field.email": "ईमेल",
    "apply.field.phone": "फ़ोन",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "ड्राइवर लाइसेंस नंबर",
    "apply.field.address": "पता",
    "apply.field.address_ph": "गली, शहर, राज्य, ZIP",
    "apply.field.why_join_label": "आप एजेंट क्यों बनना चाहते हैं? (वैकल्पिक)",
    "apply.field.why_join_ph": "अपने सामुदायिक संबंधों या रेमिटेंस अनुभव के बारे में बताएं",
    "apply.dl.section": "ड्राइवर लाइसेंस सत्यापन",
    "apply.dl.helper": "दोनों तरफ की स्पष्ट, अच्छी रोशनी वाली फ़ोटो अपलोड करें। JPEG, PNG, या WEBP · अधिकतम 6 MB प्रत्येक। छवियां निजी रूप से संग्रहीत की जाती हैं और केवल Zcardon एडमिन द्वारा सत्यापन के लिए देखी जाती हैं।",
    "apply.dl.front": "ड्राइवर लाइसेंस का अगला भाग",
    "apply.dl.back": "ड्राइवर लाइसेंस का पिछला भाग",
    "apply.dl.tap_upload": "अपलोड करने के लिए टैप करें",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "रद्द करें",
    "apply.submit_busy": "सबमिट हो रहा है…",
    "apply.submit": "आवेदन सबमिट करें",
    "agent.toast.load_failed": "एजेंट डेटा लोड करने में विफल",
    "agent.toast.cust_created": "ग्राहक {name} बनाया गया",
    "agent.toast.cust_failed": "ग्राहक बनाने में विफल",
    "agent.toast.card_issued": "कार्ड जारी हुआ · आपने {amount} कमाए",
    "agent.toast.card_issue_failed": "कार्ड जारी करने में विफल",
    "agent.toast.cash_accepted": "नकद जमा स्वीकृत · आपने {amount} कमाए",
    "agent.toast.cash_failed": "जमा दर्ज करने में विफल",
    "agent.loading": "लोड हो रहा है…",
    "agent.eyebrow": "एजेंट पोर्टल",
    "agent.fallback_name": "एजेंट",
    "agent.code_line.code_label": "एजेंट कोड",
    "agent.code_line.commission_label": "कमीशन",
    "agent.earnings.label": "अवैतनिक शेष",
    "agent.earnings.sub": "{n} अवैतनिक लेनदेन",
    "agent.stat.week": "इस सप्ताह",
    "agent.stat.month": "इस महीने",
    "agent.stat.year": "इस वर्ष",
    "agent.stat.txns": "{n} txns",
    "agent.stat.customers": "आपके ग्राहक",
    "agent.stat.active": "सक्रिय",
    "agent.customers.title": "आपके ग्राहक",
    "agent.customers.empty": "अभी कोई ग्राहक नहीं। पहले उपयोगकर्ता को जोड़ने के लिए \\\"ग्राहक पंजीकृत करें\\\" टैप करें।",
    "agent.customers.col_customer": "ग्राहक",
    "agent.customers.col_phone": "फ़ोन",
    "agent.customers.col_cards": "कार्ड",
    "agent.customers.col_commissions": "अर्जित कमीशन",
    "agent.customers.col_joined": "शामिल हुए",
    "agent.customers.col_actions": "क्रियाएं",
    "agent.customers.txns": "{n} txns",
    "agent.commissions.title": "हालिया कमीशन",
    "agent.commissions.empty": "अभी कोई कमीशन नहीं। कमाई के लिए पहला कार्ड जारी करें।",
    "agent.commissions.col_when": "कब",
    "agent.commissions.col_customer": "ग्राहक",
    "agent.commissions.col_type": "प्रकार",
    "agent.commissions.col_txn": "लेनदेन",
    "agent.commissions.col_commission": "कमीशन",
    "agent.commissions.col_status": "स्थिति",
    "agent.commissions.paid": "भुगतान किया",
    "agent.commissions.unpaid": "अभुगतान",
    "agent.type.issuance": "जारी करना",
    "agent.type.topup": "Top-up",
    "agent.type.cash_deposit": "नकद जमा",
    "agent.register_btn": "ग्राहक पंजीकृत करें",
    "agent.dialog.new_cust.title": "नया ग्राहक",
    "agent.dialog.new_cust.desc": "वे आपके एजेंट कोड से जुड़े होंगे। उनकी ओर से जारी हर कार्ड पर आपको 0.5% कमीशन मिलेगा।",
    "agent.dialog.field.full_name": "पूरा नाम",
    "agent.dialog.field.email": "ईमेल",
    "agent.dialog.field.phone": "फ़ोन",
    "agent.dialog.field.password": "प्रारंभिक पासवर्ड (ग्राहक से साझा करें)",
    "agent.dialog.cancel": "रद्द करें",
    "agent.dialog.creating": "बन रहा है…",
    "agent.dialog.create_customer": "ग्राहक बनाएं",
    "agent.dialog.issue_card.title": "{name} के लिए कार्ड जारी करें",
    "agent.dialog.issue_card.desc": "हाथ में एकत्र नकद से वित्त पोषित। ग्राहक 3.75% शुल्क देता है — आपको 0.5% मिलता है।",
    "agent.dialog.field.receiver_name": "प्राप्तकर्ता का नाम",
    "agent.dialog.field.receiver_email": "प्राप्तकर्ता का ईमेल",
    "agent.dialog.field.receiver_phone": "प्राप्तकर्ता का फ़ोन",
    "agent.dialog.field.amount_usd": "राशि (USD)",
    "agent.dialog.field.note": "नोट (वैकल्पिक)",
    "agent.dialog.issuing": "जारी हो रहा है…",
    "agent.dialog.issue_card_btn": "कार्ड जारी करें",
    "agent.actions.issue_card": "कार्ड जारी करें",
    "agent.dialog.cash_deposit.title": "{name} के लिए नकद जमा",
    "agent.dialog.cash_deposit.desc": "ग्राहक से एकत्र नकद · प्राप्तकर्ता को कार्ड दिया जाता है।",
    "agent.dialog.field.receiver_name_opt": "प्राप्तकर्ता का नाम (वैकल्पिक)",
    "agent.dialog.recording": "दर्ज हो रहा है…",
    "agent.dialog.record_deposit": "जमा दर्ज करें",
    "agent.actions.cash_deposit": "नकद जमा",

  },
  it: {
    "header.nav.home": "Home",
    "header.nav.rates": "Tariffe",
    "header.nav.support": "Supporto",
    "header.nav.admin": "Admin",
    "header.nav.signin": "Accedi",
    "header.nav.signout": "Esci",
    "header.nav.get_started": "Inizia",
    "header.nav.account": "Account",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Cambia lingua",
    "landing.hero.eyebrow": "Un nuovo canale per la spesa digitale",
    "landing.hero.title_a": "Ricarica una carta virtuale.",
    "landing.hero.title_b_ship": "Inviala",
    "landing.hero.title_b_rest": "a chiunque,",
    "landing.hero.title_c": "spendibile in",
    "landing.hero.subtitle": "Emetti una carta di debito virtuale tap-and-pay con QR scansionabile. Checkout powered by Stripe, saldo in USD, commissioni di servizio e gateway trasparenti — recapitata al destinatario via email o download.",
    "landing.hero.cta_start": "Inizia a emettere carte",
    "landing.hero.cta_request_cash": "Richiedi una carta cash",
    "landing.hero.cta_rates": "Vedi le tariffe",
    "landing.hero.cta_find_card": "Trova la mia carta",
    "landing.hero.stat_currency": "Valuta",
    "landing.hero.stat_settle": "Tempo di liquidazione",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "Commissione di servizio",
    "landing.hero.image_eyebrow": "SKYLINE · PORTATA GLOBALE",
    "landing.hero.image_title": "Costruito per l'economia delle carte moderne",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "Liquidato in USD",
    "landing.hero.marquee": "Emetti · Ricarica · Spendi in USD",
    "landing.demo.eyebrow": "Scoprilo in 20 secondi",
    "landing.demo.title_a": "Dall'emissione alla",
    "landing.demo.title_b_spend": "spesa",
    "landing.demo.title_c": ". In quattro tap.",
    "landing.demo.step_issue.label": "Emetti una carta",
    "landing.demo.step_issue.detail": "Scegli un importo. Carta pronta in ~2 minuti.",
    "landing.demo.step_share.label": "Condividila",
    "landing.demo.step_share.detail": "Email, SMS, WhatsApp o scarica come PNG.",
    "landing.demo.step_scan.label": "Il destinatario scansiona",
    "landing.demo.step_scan.detail": "La fotocamera apre istantaneamente la pagina di tracciamento.",
    "landing.demo.step_spend.label": "Spendi in USD",
    "landing.demo.step_spend.detail": "Tap-to-pay, prelievo contanti, spesa presso i commercianti.",
    "landing.demo.cta_try": "Provalo ora",
    "landing.demo.live_pill": "Demo live",
    "landing.demo.scene1_caption": "Generazione carta · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Invio al destinatario via WhatsApp",
    "landing.demo.scene3_caption": "Scansione QR · corrispondenza trovata",
    "landing.demo.scene4_eyebrow": "Saldo accreditato",
    "landing.demo.scene4_status": "✓ approvato · tap-to-pay pronto",
    "landing.footer.rates": "Tariffe",
    "landing.footer.support": "Supporto",
    "landing.footer.admin_portal": "Portale admin",
    "landing.footer.copy": "© 2026 Zcardon · Un prodotto fintech demo",
    "landing.cash.dialog_title": "Richiedi una carta cash",
    "landing.cash.dialog_desc": "Ti invieremo via SMS un link di pagamento Stripe. Paga con qualsiasi carta; la carta cash viene finanziata nel momento in cui Stripe conferma.",
    "landing.cash.phone_label": "Numero di cellulare",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Importo (USD)",
    "landing.cash.name_label": "Il tuo nome (opzionale)",
    "landing.cash.submit": "Invia link di pagamento",
    "landing.cash.submitting": "Invio in corso…",
    "landing.cash.success_title": "Link di pagamento in arrivo",
    "landing.cash.success_body": "Abbiamo inviato un link di pagamento Stripe a {phone}. Toccalo per pagare e la tua carta virtuale si attiverà immediatamente.",
    "landing.cash.success_open_checkout": "Apri il checkout ora",
    "landing.find.cta": "Hai già pagato? Trova la mia carta",
    "landing.find.dialog_title": "Trova la mia carta esistente",
    "landing.find.dialog_desc": "Inserisci il numero di telefono che hai utilizzato. Ti invieremo un codice a 6 cifre per recuperare qualsiasi carta cash attiva registrata.",
    "landing.find.phone_label": "Numero di cellulare",
    "landing.find.code_label": "Codice a 6 cifre",
    "landing.find.send_code": "Invia codice",
    "landing.find.verify": "Verifica e trova",
    "landing.find.sending": "Invio in corso…",
    "landing.find.verifying": "Verifica in corso…",
    "landing.find.no_cards": "Nessuna carta attiva trovata per quel numero.",
    "login.eyebrow.welcome": "Benvenuto",
    "login.eyebrow.admin": "Amministrazione",
    "login.title": "Accedi",
    "login.title_admin": "Accesso amministratore",
    "login.admin_badge": "Portale admin",
    "login.email_label": "Email",
    "login.password_label": "Password",
    "login.submit": "Accedi",
    "login.submitting": "Accesso in corso…",
    "login.toast_welcome": "Bentornato, {name}",
    "login.toast_failed": "Accesso non riuscito",
    "login.gate_title": "Gli accessi dei clienti sono temporaneamente sospesi",
    "login.gate_default_message": "Stiamo migliorando la piattaforma. Riprova a breve.",
    "login.gate_admin_note": "Amministratori e manager possono ancora accedere qui sotto.",
    "login.new_here": "Nuovo utente?",
    "login.create_account": "Crea un account",
    "login.customer_q": "Sei un cliente?",
    "login.regular_signin": "Accesso normale",
    "login.demo_title": "Account demo",
    "login.demo_customers_note": "Clienti: registrati tramite /register o crea dal pannello Admin",
    "login.forgot_password": "Hai dimenticato la password?",
    "forgot.eyebrow": "Recupero account",
    "forgot.title": "Reimposta la password",
    "forgot.subtitle": "Inserisci l'email associata al tuo account Zcardon e ti invieremo un link sicuro per scegliere una nuova password.",
    "forgot.email_label": "Email",
    "forgot.email_placeholder": "tu@esempio.com",
    "forgot.submit": "Invia link di reimpostazione",
    "forgot.submitting": "Invio in corso…",
    "forgot.toast_failed": "Impossibile inviare il link di reimpostazione. Riprova.",
    "forgot.sent_title": "Controlla la tua casella",
    "forgot.sent_message": "Se esiste un account per {email}, ti abbiamo inviato un link per reimpostare la password.",
    "forgot.sent_expiry_note": "Il link è valido per 15 minuti. Non lo hai ricevuto? Controlla lo spam o inviane un altro.",
    "forgot.back_to_login": "Torna all'accesso",
    "forgot.resend_link": "Invia un altro link",
    "forgot.remember_q": "Ricordi la tua password?",
    "forgot.signin_link": "Accedi",
    "reset.eyebrow": "Recupero account",
    "reset.title": "Scegli una nuova password",
    "reset.subtitle": "Scegli una password robusta che non hai mai usato prima. Verrai connesso automaticamente una volta impostata.",
    "reset.verifying": "Verifica del link di reimpostazione in corso…",
    "reset.invalid_title": "Questo link di reimpostazione non è valido o è scaduto",
    "reset.invalid_message": "I link di reimpostazione sono validi per 15 minuti e possono essere utilizzati una sola volta. Richiedine uno nuovo per continuare.",
    "reset.request_new_link": "Richiedi un nuovo link di reimpostazione",
    "reset.new_password_label": "Nuova password",
    "reset.confirm_password_label": "Conferma password",
    "reset.password_hint": "Almeno 8 caratteri. Evita password usate altrove.",
    "reset.submit": "Aggiorna password",
    "reset.submitting": "Aggiornamento in corso…",
    "reset.toast_success": "Password aggiornata. Sei connesso.",
    "reset.toast_failed": "Impossibile reimpostare la password. Riprova.",
    "reset.error_min_length": "La password deve contenere almeno 8 caratteri.",
    "reset.error_mismatch": "Le password non coincidono.",
    "register.eyebrow": "Unisciti",
    "register.title": "Crea account",
    "register.full_name_label": "Nome completo",
    "register.email_label": "Email",
    "register.password_label": "Password",
    "register.submit": "Crea account",
    "register.submitting": "Creazione in corso…",
    "register.gate_title": "Le registrazioni sono temporaneamente sospese",
    "register.gate_default_message": "Stiamo migliorando la piattaforma. Riprova a breve.",
    "register.gate_already_member": "Hai già un account?",
    "register.toast_created": "Account creato",
    "register.toast_failed": "Registrazione non riuscita",
    "register.already_member": "Sei già membro?",
    "register.signin_link": "Accedi",
    "dash.eyebrow": "Dashboard",
    "dash.greeting": "Bentornato, {name}",
    "dash.subtitle": "Emetti una carta virtuale o invia un deposito tramite bonifico — il tuo centro di controllo è qui sotto.",
    "dash.action.issue_card": "Emetti una carta",
    "dash.action.direct_deposit": "Deposito diretto (bonifico)",
    "dash.action.support": "Supporto",
    "dash.section.cards": "Le tue carte",
    "dash.section.activity": "Attività recente",
    "dash.section.deposits": "Depositi diretti",
    "dash.empty.cards": "Nessuna carta ancora — emetti la prima per iniziare.",
    "dash.empty.activity": "Non appena inizierai a emettere carte o inviare bonifici, l'attività recente apparirà qui.",
    "ref.eyebrow": "Benvenuto su Zcardon",
    "ref.greeting": "{name} ti ha invitato.",
    "ref.loading": "Caricamento del tuo invito…",
    "ref.resolved_redirect": "Un momento — ti stiamo reindirizzando.",
    "ref.resolving": "Risoluzione del tuo link di invito…",
    "support.eyebrow": "Siamo qui per te",
    "support.title": "Supporto, non ticket.",
    "support.subtitle": "Parla con una persona. Risposte via email entro un'ora lavorativa nei giorni feriali.",
    "support.card.email": "Email",
    "support.card.phone": "Telefono",
    "support.card.live_chat": "Live chat",
    "support.card.live_chat_value": "Nella tua dashboard",
    "support.card.hours": "Orari",
    "support.faq.title": "Domande frequenti",
    "support.faq.q1": "Quanto tempo ci vuole perché il destinatario riceva la carta?",
    "support.faq.a1": "In genere entro 2 minuti dal finanziamento riuscito. La consegna via email (Resend) e MMS (Twilio) avviene automaticamente.",
    "support.faq.q2": "Posso annullare o bloccare una carta dopo averla inviata?",
    "support.faq.a2": "Sì — apri la pagina di dettaglio della carta e tocca Blocca. Puoi anche bloccarla direttamente dall'email di avviso al destinatario.",
    "support.faq.q3": "In quali valute può spendere la carta?",
    "support.faq.a3": "Tutte le carte si regolano in USD. Il destinatario può spendere ovunque sia accettato Visa/Mastercard — il cambio valuta è gestito dal circuito della carta.",
    "support.faq.q4": "Quali commissioni si applicano?",
    "support.faq.a4": "Una commissione di servizio trasparente più il costo del gateway Stripe. Consulta il preventivo in tempo reale nella pagina Tariffe prima di pagare.",
    "rates.eyebrow": "Trasparente",
    "rates.title_a": "Nessun margine",
    "rates.title_b": "nascosto",
    "rates.subtitle": "Vedi la commissione di servizio e il costo del gateway prima di pagare. Il capitale accredita sempre il saldo della carta virtuale in USD.",
    "rates.row.service_fee": "Commissione di servizio",
    "rates.row.service_fee_value": "{pct} dell'importo inviato",
    "rates.row.gateway": "Gateway (Stripe)",
    "rates.row.settlement": "Regolamento",
    "rates.row.settlement_value": "Saldo USD sulla carta virtuale",
    "rates.row.topup": "Metodi di ricarica",
    "rates.row.topup_value": "Carta · Debito · Banca (ACH)",
    "rates.sim.eyebrow": "Preventivo in tempo reale",
    "rates.sim.title_a": "Invia qualsiasi importo,",
    "rates.sim.title_b": "vedi il dettaglio all'istante.",
    "rates.sim.amount_label": "Importo da inviare",
    "rates.sim.line.principal": "Sulla carta (capitale)",
    "rates.sim.line.service": "Commissione di servizio · {pct}",
    "rates.sim.line.gateway": "Gateway · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Paghi",
    "rates.sim.cta": "Inizia con ${amount} →",
    "rates.sim.note": "Le tariffe applicate sopra sono configurate dall'amministratore. Il destinatario riceve sempre il capitale intero.",
    "dd.title": "Bonifico Diretto",
    "dd.intro": "Invia denaro sul conto bancario di un destinatario, ovunque nel mondo. Paga con carta (Stripe Checkout) o il tuo saldo Zcardon — gestiamo il bonifico alla banca del destinatario automaticamente dove supportato, o manualmente per i paesi che non instradamo ancora automaticamente. Ogni transazione è registrata con una ricevuta stampabile.",
    "dd.new_btn": "Registra un nuovo bonifico diretto",
    "dd.close": "Chiudi",
    "dd.history_eyebrow": "I tuoi bonifici",
    "dd.loading": "Caricamento…",
    "dd.empty": "Nessun bonifico diretto ancora.",
    "dd.status.approved": "approvato",
    "dd.status.pending": "in attesa di approvazione",
    "dd.status.rejected": "rifiutato",
    "dd.row.confirmed": "✓ Destinatario ha confermato la ricezione",
    "dd.row.disputed": "⚠ Destinatario ha segnalato una discrepanza",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Impossibile caricare i bonifici",
    "dd.error.pdf": "Impossibile scaricare la ricevuta",
    "pay.mode.view": "La tua carta Zcardon",
    "pay.mode.charge": "Paga con QR",
    "pay.heading.view": "Ciao, {name}.",
    "pay.heading.charge": "Addebita questa carta.",
    "pay.cardholder": "Titolare della carta",
    "pay.paused.label": "Carta sospesa",
    "pay.paused.body": "Questa carta è stata bloccata e non può accettare nuovi addebiti finché il titolare non la riattiva.",
    "pay.balance.label": "Saldo disponibile",
    "pay.charges_count": "{count} addebito",
    "pay.charges_count_plural": "{count} addebiti",
    "pay.topups_count": "{count} ricarica",
    "pay.topups_count_plural": "{count} ricariche",
    "pay.activity.label": "Attività recente",
    "pay.activity.empty": "Nessuna attività ancora",
    "pay.cta.charge": "Addebita questa carta (commerciante)",
    "pay.cta.charge_paused": "Carta sospesa",
    "pay.cta.note_active": "Tocca sopra solo se sei il commerciante che accetta il pagamento da questa carta.",
    "pay.cta.note_paused": "Questa carta è stata sospesa dal mittente. Chiedigli di riattivarla.",
    "pay.back_to_balance": "Torna al saldo",
    "pay.form.amount_label": "Importo (USD)",
    "pay.form.amount_max": "Max {amount}",
    "pay.form.merchant_label": "Commerciante / causale",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Nota (opzionale)",
    "pay.form.memo_placeholder": "Due caffè latte",
    "pay.form.submit_processing": "Elaborazione…",
    "pay.form.submit_paused": "Carta sospesa",
    "pay.form.submit_charge": "Addebita {amount}",
    "pay.form.submit_charge_card": "Addebita carta",
    "pay.form.disclaimer": "Addebitando questa carta confermi di essere autorizzato dal titolare a spendere l'importo indicato sopra.",
    "pay.receipt.title": "Pagamento completato",
    "pay.receipt.to": "a",
    "pay.receipt.id": "ricevuta",
    "pay.receipt.time": "ora",
    "pay.receipt.new_balance": "nuovo saldo",
    "pay.receipt.done": "Fine · torna al saldo",
    "pay.error.cant_load": "Impossibile caricare questa carta",
    "pay.error.back_home": "Torna alla home",
    "pay.error.loading": "Caricamento carta…",
    "pay.error.charge_failed": "Addebito fallito",
    "pay.tap.eyebrow": "Tocca per pagare",
    "pay.tap.title": "Mostra questo codice",
    "pay.tap.subtitle": "allo scanner del commerciante",
    "pay.tap.balance": "Saldo",
    "pay.tap.paused": "In pausa",
    "pay.tap.qr_unavailable": "QR non disponibile per questa carta",
    "pay.tap.refresh_in": "Aggiorna tra {secs}s",
    "pay.tap.show_digits": "Mostra cifre",
    "pay.tap.hide_digits": "Nascondi cifre",
    "pay.tap.card_label": "Carta · ••••",
    "pay.tap.auto_hides": "Si nasconde automaticamente in 60s",
    "pay.tap.full_only_image": "Il numero completo è visibile solo sull'immagine originale della carta",
    "pay.tap.disclaimer": "Carta a circuito chiuso · spendibile presso qualsiasi esercente abilitato Zcardon. Il QR ruota ogni 60s per protezione anti-replay.",
    "pay.subscribe.eyebrow": "Resta aggiornato",
    "pay.subscribe.title": "Ricevi un'email per ogni transazione",
    "pay.subscribe.desc": "Gratuito. Un'email per ogni addebito o ricarica. Disdici quando vuoi. Non condividiamo mai il tuo indirizzo.",
    "pay.subscribe.placeholder": "tu@esempio.com",
    "pay.subscribe.cta": "Avvisami",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Sei iscritto",
    "pay.subscribe.done.body": "Invieremo un'email a {email} istantaneamente per ogni addebito o ricarica sulla carta •••• {last4}.",
    "pay.subscribe.toast.already": "Eri già iscritto — nessun duplicato inviato.",
    "pay.subscribe.toast.success": "Riceverai un'email per ogni transazione. Benvenuto!",
    "pay.subscribe.toast.failed": "Iscrizione fallita",
    "pay.save.eyebrow": "Porta con te la carta",
    "pay.save.title": "Salva questa carta sul tuo telefono",
    "pay.save.desc": "Salva l'immagine della carta nelle tue foto per accesso offline — mostra il QR ai commercianti in qualsiasi momento, senza app.",
    "pay.save.cta": "Salva immagine carta",
    "pay.save.preparing": "Preparazione…",
    "pay.save.add_home": "Aggiungi alla schermata Home",
    "pay.save.toast.opened": "Immagine carta aperta — tieni premuto per salvare nelle Foto",
    "pay.save.toast.failed": "Impossibile salvare la carta",
    "pay.save.ios.title": "Per aggiungere Zcardon alla schermata home del tuo iPhone:",
    "pay.save.ios.step1": "Tocca il pulsante Condividi in basso in Safari",
    "pay.save.ios.step2": "Scorri verso il basso e tocca Aggiungi alla schermata Home",
    "pay.save.ios.step3": "Tocca Aggiungi per confermare",
    "pay.save.ios.note": "Zcardon apparirà come qualsiasi altra app — tocca l'icona per aprire questa carta immediatamente.",
    "pay.thanks.eyebrow": "Invia un ringraziamento",
    "pay.thanks.body": "${amount} è appena arrivato — fai sapere a chi ha finanziato questa carta che è stato utile.",
    "pay.thanks.placeholder": "Facoltativo — scrivi qualcosa…",
    "pay.thanks.cta": "Invia {emoji} ringraziamento",
    "pay.thanks.sending": "Invio in corso…",
    "pay.thanks.done": "Ringraziamento inviato — lo vedranno in pochi secondi.",
    "pay.thanks.toast.sent": "Ringraziamento inviato",
    "pay.thanks.toast.failed": "Impossibile inviare il ringraziamento",
    "pay.activity.topup_bank": "Ricarica tramite bonifico bancario",
    "pay.activity.topup_debit": "Ricarica con carta di debito",
    "pay.activity.topup_credit": "Ricarica con carta di credito",
    "pay.activity.topup_crypto": "Ricarica in crypto",
    "pay.activity.topup_received": "Ricarica ricevuta",
    "pay.activity.merchant_charge": "Addebito commerciante",
    "pay.toast.payment_received": "Pagamento ricevuto · −{amount}",
    "cash.confirming": "Conferma del pagamento…",
    "cash.confirming.sub": "Stripe sta finalizzando l'addebito.",
    "cash.notfound.title": "Link scaduto",
    "cash.notfound.body": "Non riusciamo a trovare questa richiesta di pagamento. Il link potrebbe provenire da una sessione precedente, o la carta non è mai stata finalizzata. Avvia una nuova richiesta — richiede solo un minuto, e qualsiasi pagamento già effettuato verrà accreditato automaticamente non appena il sistema di riconciliazione lo rileverà.",
    "cash.notfound.ref": "rif.:",
    "cash.notfound.start": "Avvia una nuova richiesta",
    "cash.notfound.contact": "Contatta il supporto",
    "cash.error.title": "Impossibile verificare lo stato",
    "cash.error.return": "Torna alla home",
    "cash.autopay.opening": "Apertura Tap-to-Pay…",
    "cash.autopay.redirect": "Reindirizzamento tra {secs}s.",
    "cash.autopay.stay": "Rimani qui",
    "cash.ready.title": "La tua carta è pronta",
    "cash.ready.subtitle": "Finanziata di ${amount} USD · inviato anche via SMS a {phone}",
    "cash.card.label": "Carta",
    "cash.card.exp": "Scad.",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Saldo",
    "cash.cta.open_pay": "Apri la schermata Tap-to-Pay",
    "cash.toast.funded": "La tua carta è stata finanziata — controlla il telefono per l'SMS!",
    "cash.pending.title": "In attesa di Stripe",
    "cash.pending.body": "Stiamo verificando la conferma. Di solito richiede 5-15 secondi. Se hai chiuso il checkout senza pagare, puoi ricominciare dalla homepage.",
    "cash.pending.back": "Torna alla home",
    "cash.timeout.title": "Impossibile confermare il pagamento",
    "cash.timeout.body": "Abbiamo atteso 3 minuti la conferma di Stripe. Nella maggior parte dei casi significa che il checkout è stato chiuso prima del pagamento — ma se HAI pagato, la tua carta verrà comunque attivata automaticamente entro pochi minuti (il nostro riconciliatore in background viene eseguito ogni 5 min) e ti invieremo un messaggio a {phone} non appena avviene.",
    "cash.timeout.start_over": "Ricomincia",
    "cash.timeout.try_again": "Riprova",
    "cash.resend.title": "Non l'hai ricevuto? Invia di nuovo",
    "cash.resend.degraded.lead": "La consegna ai numeri statunitensi è attualmente degradata.",
    "cash.resend.degraded.sms": "Gli SMS verso telefoni statunitensi potrebbero essere bloccati silenziosamente dal gestore (verifica toll-free ancora in attesa).",
    "cash.resend.degraded.email": "L'email è in modalità test e viene consegnata solo all'indirizzo del titolare dell'account mentre il DNS è in fase di validazione.",
    "cash.resend.degraded.tail": "Se un canale non funziona, prova l'altro — oppure apri questa pagina sul dispositivo del destinatario per recuperare la carta direttamente.",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "Invio in corso…",
    "cash.resend.email_sandbox": "L'email è in modalità test. Mentre il dominio zcardon.com viene verificato su Resend, possiamo inviare solo all'indirizzo del titolare dell'account. Se la tua email non viene accettata, usa SMS o WhatsApp.",
    "cash.resend.email_label": "Indirizzo email",
    "cash.resend.email_send": "Invia via email",
    "cash.resend.cancel": "Annulla",
    "cash.resend.limit": "Limite di 5 reinvii per carta. Telefono registrato: {phone}",
    "cash.resend.no_phone": "(nessuno)",
    "cash.wallet.title": "Aggiungi al Wallet",
    "cash.wallet.apple": "Aggiungi ad Apple Wallet",
    "cash.wallet.google": "Aggiungi a Google Wallet",
    "cash.wallet.generating": "Generazione pass…",
    "cash.wallet.note": "Rimane sulla schermata di blocco · Si aggiorna automaticamente al cambio del saldo",
    "cash.wallet.toast.apple": "Pass scaricato — aprilo sul tuo iPhone per aggiungerlo ad Apple Wallet",
    "cash.wallet.toast.apple_failed": "Impossibile generare il pass per Apple Wallet",
    "cash.wallet.toast.google_failed": "Impossibile generare il pass per Google Wallet",
    "cash.delivery.fallback_title": "Inviato tramite {fallback}",
    "cash.delivery.fallback_body": "Il tuo gestore ha bloccato l'SMS originale, quindi abbiamo reinviato la tua carta tramite {fallback} a {phone}. Controlla quel canale.",
    "cash.delivery.toll_blocked": "Il gestore ha bloccato l'SMS",
    "cash.delivery.failed_title": "Impossibile inviarti la carta via SMS",
    "cash.delivery.toll_body": "Il tuo gestore ha rifiutato il messaggio (può succedere con nuovi mittenti in attesa di verifica). Salva subito la tua carta: tocca",
    "cash.delivery.failed_body": "La tua carta è stata finanziata — salvala ora poiché l'SMS non è stato consegnato. Tocca",
    "cash.delivery.cta_label": "Apri la schermata Tap-to-Pay",
    "cash.delivery.body_tail": "qui sopra, fai uno screenshot, oppure usa Email qui sotto per inviarne una copia a te stesso.",
    "cash.handoff.eyebrow": "Consegna di persona",
    "cash.handoff.body": "Fai scansionare questo al destinatario con la fotocamera — la carta si apre direttamente sul suo telefono, senza SMS o email.",
    "cash.handoff.share_label": "Oppure condividi questo link",
    "cash.handoff.copy": "Copia link",
    "cash.handoff.copied": "✓ Copiato",
    "cash.handoff.share": "Condividi",
    "cash.handoff.toast.copied": "Link copiato — incollalo dove vuoi",
    "cash.handoff.toast.copy_failed": "Impossibile copiare. Tieni premuto il link per copiarlo manualmente.",
    "portal.loading": "Caricamento della ricevuta…",
    "portal.unavailable.title": "Link non disponibile",
    "portal.unavailable.body": "Questo link non è valido o è scaduto.",
    "portal.unavailable.hint": "Se il mittente ti ha condiviso questo link, chiedigli di reinviare l'email con la ricevuta.",
    "portal.brand": "Portale ricevute",
    "portal.eyebrow": "{sender} ti ha inviato",
    "portal.reference": "Riferimento",
    "portal.pending.title": "Approvazione in attesa",
    "portal.pending.body": "Il mittente ha registrato questo trasferimento, ma è ancora in revisione da parte di Zcardon. Ricontrolla a breve — la ricevuta sarà disponibile una volta approvata.",
    "portal.row.bank": "Banca",
    "portal.row.account": "Conto",
    "portal.row.routing": "Codice di routing",
    "portal.row.memo": "Causale",
    "portal.confirmed.title": "Hai confermato la ricezione",
    "portal.confirmed.body": "Confermato il {when}. Il mittente può visualizzarlo.",
    "portal.disputed.title": "Hai segnalato una discrepanza",
    "portal.disputed.body": "Segnalato il {when}. Il mittente è stato avvisato via email.",
    "portal.actions.body": "Questo importo è arrivato sul tuo conto? Confermare chiude il cerchio con il mittente — nessun denaro transita tramite Zcardon, è solo una registrazione.",
    "portal.actions.confirm": "Sì, l'ho ricevuto",
    "portal.actions.flag": "Segnala un problema",
    "portal.dispute.placeholder": "Importo errato, non arrivato, numero di conto errato…",
    "portal.dispute.submit": "Invia segnalazione",
    "portal.dispute.too_short": "Aggiungi una breve motivazione (almeno 4 caratteri)",
    "portal.toast.confirmed": "Grazie — ricezione confermata.",
    "portal.toast.confirm_failed": "Impossibile confermare la ricezione",
    "portal.toast.reported": "Segnalazione inviata. Il mittente è stato notificato.",
    "portal.toast.report_failed": "Impossibile inviare la contestazione",
    "portal.toast.pdf_failed": "Impossibile scaricare la ricevuta",
    "portal.download_pdf": "Scarica ricevuta PDF",
    "portal.privacy": "Questa pagina è privata. Chiunque abbia il link può visualizzare questa ricevuta — non condividerla pubblicamente. Domande:",
    "detail.back": "Torna alla dashboard",
    "detail.eyebrow": "La tua carta",
    "detail.confirming_payment": "Conferma del pagamento in corso…",
    "detail.active_ready": "La carta è attiva e pronta da condividere.",
    "detail.balance.label": "Saldo attuale",
    "detail.toast.notfound": "Carta non trovata",
    "detail.toast.loaded": "Fondi caricati sulla carta.",
    "detail.toast.confirmed": "Pagamento confermato.",
    "detail.toast.expired": "Sessione di pagamento scaduta",
    "detail.delivery.receiver_fallback": "il tuo destinatario",
    "detail.delivery.received_title": "{name} ha aperto la carta",
    "detail.delivery.received_when": "Consegna confermata · {when}",
    "detail.delivery.waiting_title_on": "Riceverai un'email nel momento in cui verrà aperta",
    "detail.delivery.waiting_title_off": "Le notifiche di consegna sono disattivate",
    "detail.delivery.waiting_body_on": "Non appena il destinatario scansiona il QR su questa carta, ti invieremo un'email — una sola volta.",
    "detail.delivery.waiting_body_off": "Attiva questa opzione per ricevere un'email una tantum nel momento in cui il destinatario apre la carta per la prima volta.",
    "detail.delivery.toggle_on": "Attivo",
    "detail.delivery.toggle_off": "Attiva",
    "detail.delivery.toast_enabled": "Riceverai una notifica quando la carta verrà aperta.",
    "detail.delivery.toast_disabled": "Notifiche di consegna disattivate.",
    "detail.delivery.toast_failed": "Impossibile aggiornare le notifiche di consegna.",
    "nudge.eyebrow": "Avvisa il destinatario",
    "nudge.title": "Manda un avviso a {receiver}",
    "nudge.subtitle": "Abbiamo preparato un breve messaggio affinché {receiver} sappia che la carta è attiva e come usarla.",
    "nudge.receiver_fallback": "il tuo destinatario",
    "nudge.sender_fallback": "un amico",
    "nudge.template": "Ciao {receiver} — {sender} ti ha appena inviato una carta virtuale Zcardon con {amount}. Tocca per pagare qui: {url}\\n\\nNessuna app richiesta. Il link funziona su qualsiasi telefono.",
    "nudge.email_subject": "{sender} ti ha inviato una carta Zcardon da {amount}",
    "nudge.action_copy": "Copia messaggio",
    "nudge.action_sms": "Apri SMS",
    "nudge.action_email": "Apri email",
    "nudge.toast_copied": "Messaggio copiato negli appunti.",
    "nudge.toast_copy_failed": "Impossibile copiare. Seleziona e copia manualmente.",
    "ios_install.title": "Installa Zcardon sul tuo iPhone",
    "ios_install.step_1": "Tocca",
    "ios_install.share_label": "Condividi",
    "ios_install.step_2": " poi scegli \\\"Aggiungi a Home\\\" — Zcardon si aprirà come app a schermo intero.",
    "smart_banner.title": "Apri nell'app Zcardon",
    "smart_banner.subtitle": "Checkout più veloce. Paga senza uscire dall'app.",
    "smart_banner.cta": "Apri",
    "smart_banner.dismiss": "Ignora",
    "social_proof.eyebrow": "In diretta · ultime 48 ore",
    "social_proof.empty": "In attesa della prossima carta…",
    "social_proof.line_opened": "{name} ha appena aperto una carta da {amount}.",
    "social_proof.line_issued": "{name} ha ricevuto una carta da {amount} da un amico.",
    "social_proof.line_deposit": "{name} ha ricevuto un deposito diretto di {amount}.",
    "social_proof.stat_funded": "Carte finanziate oggi",
    "social_proof.stat_delivered": "Carte consegnate oggi",
    "social_proof.stat_recent": "Attività recente",
    "social_proof.ago_seconds": "{n}s fa",
    "social_proof.ago_minutes": "{n}m fa",
    "social_proof.ago_hours": "{n}h fa",
    "flight.eyebrow": "Carte in transito · ultime 48h",
    "flight.title": "Dove arriva Zcardon",
    "flight.subtitle": "Posizioni reali dei destinatari, anonimizzate per regione. Mostriamo solo paesi e stati USA — mai nomi, indirizzi o importi.",
    "flight.row_template": "Carta aperta in {label}",
    "flight.more_count": "+ {n} altre regioni",
    "flight.ago_seconds": "{n}s fa",
    "flight.ago_minutes": "{n}m fa",
    "flight.ago_hours": "{n}h fa",
    "trust_strip.eyebrow": "Dati reali · in diretta dal database",
    "trust_strip.title_a": "Non testimonianze.",
    "trust_strip.title_b": "Letture live dal database.",
    "trust_strip.how_link": "Come raccogliamo questi dati",
    "trust_strip.modal_eyebrow": "Fonti dei dati",
    "trust_strip.modal_title": "Tutto reale, tutto anonimizzato",
    "trust_strip.modal_close": "Chiudi",
    "trust_strip.modal_intro": "Ogni numero in questa sezione proviene dal nostro database — mai da feed di terze parti, mai inventato. Ecco da dove proviene ogni sezione e come anonimizziamo i dati prima di mostrarli.",
    "trust_strip.src_carousel_title": "Carosello attività in diretta",
    "trust_strip.src_carousel_body": "Estrae dalle collezioni cards e direct_deposits, ultime 48h. I nomi appaiono come nome + iniziale del cognome; gli importi sono arrotondati al $5 più vicino per evitare la triangolazione. Aggiornato ogni 60 secondi.",
    "trust_strip.src_flight_title": "Dove arriva Zcardon",
    "trust_strip.src_flight_body": "Legge gli atterraggi da scansione QR da card_scans, geolocalizzati al momento della scansione. Aggregati per paese (o stato USA). Nessuna precisione sotto il livello statale, nessun IP visualizzato, nessun identificatore di carta. Aggiornato ogni 90 secondi.",
    "trust_strip.src_badges_title": "Dati di fiducia degli ultimi 30 giorni",
    "trust_strip.src_badges_body": "Solo conteggi aggregati — mai dati per riga. Ogni metrica appare solo dopo aver superato una dimensione minima del campione, quindi un tasso del 100% da 2 conferme verrebbe nascosto. Aggiornato ogni minuto.",
    "trust_strip.modal_promise": "Non pubblicheremo mai nomi, indirizzi, importi esatti, IP o numeri di carta nei widget pubblici. Promesso.",
    "trust_badge.eyebrow": "Dati reali · ultimi 30 giorni",
    "trust_badge.title": "Le persone lo usano davvero.",
    "trust_badge.subtitle": "Non una testimonianza — un conteggio live, aggiornato dal database ogni minuto.",
    "trust_badge.confirmed_label": "i destinatari confermano la ricezione entro 24h",
    "trust_badge.confirmed_sub": "su {n} risposte negli ultimi 30 giorni",
    "trust_badge.thanks_label": "messaggi di ringraziamento inviati",
    "trust_badge.thanks_sub": "dai destinatari negli ultimi 30 giorni",
    "trust_badge.cards_label": "carte finanziate",
    "trust_badge.cards_sub": "ultimi 30 giorni · conteggio in tempo reale",
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} ha aperto una carta da {amount}",
    "pulse.tooltip_issued": "{name} ha ricevuto una carta da {amount}",
    "pulse.tooltip_deposit": "{name} ha ricevuto un deposito di {amount}",
    "pulse.ago_seconds": "{n}s fa",
    "pulse.ago_minutes": "{n}m fa",
    "pulse.ago_hours": "{n}h fa",
    "note_cloud.eyebrow": "A cosa serve",
    "note_cloud.title": "Motivi reali. Persone reali.",
    "note_cloud.subtitle": "Aggregato da {n} note carta negli ultimi 90 giorni. Mostriamo solo parole presenti in almeno {min} note diverse — il tuo messaggio resta tuo.",
    "note_cloud.footnote": "Nomi, importi e contatti vengono rimossi prima della visualizzazione.",
    "note_cloud.lang_eyebrow": "In che lingua sono queste note?",
    "note_cloud.lang_other": "Altro",
    "download.button": "Scarica l'app",
    "download.close": "Chiudi",
    "download.modal_eyebrow": "Scarica Zcardon",
    "download.modal_title": "Installa Zcardon sul tuo telefono",
    "download.footnote": "Non ancora sull'App Store — stai installando direttamente la web app sicura. Si aggiorna istantaneamente.",
    "download.installed_title": "Zcardon installato",
    "download.installed_body": "Cerca l'icona sulla tua schermata home.",
    "download.webview_warning": "Apri questa pagina nel browser del tuo telefono (Safari su iPhone, Chrome su Android) per installare — i browser in-app non possono aggiungere alla schermata home.",
    "download.ios_step1_title": "Su Safari",
    "download.ios_step1_body": "Se non sei già in Safari, copia questo link e aprilo lì. (Chrome e altri browser iOS non possono installare app.)",
    "download.ios_step2_title": "Tocca Condividi",
    "download.ios_step2_body": "L'icona Condividi si trova in basso al centro su iPhone, in alto a destra su iPad.",
    "download.ios_step3_title": "Aggiungi alla schermata Home",
    "download.ios_step3_body": "Scorri verso il basso nel menu di condivisione e scegli \\\"Aggiungi alla schermata Home\\\". Zcardon si installa come app a schermo intero.",
    "download.android_ready_body": "Chrome ha rilevato Zcardon ed è pronto per l'installazione. Tocca qui sotto.",
    "download.android_install": "Installa Zcardon",
    "download.android_installing": "Installazione in corso…",
    "download.android_step1_title": "Apri il menu di Chrome",
    "download.android_step1_body": "Tocca i tre puntini in alto a destra in Chrome.",
    "download.android_step2_title": "Aggiungi alla schermata Home",
    "download.android_step2_body": "Scegli \\\"Installa app\\\" o \\\"Aggiungi alla schermata Home\\\". Zcardon si installa come app a schermo intero.",
    "download.desktop_body": "Scansiona questo codice QR con la fotocamera del tuo telefono per aprire Zcardon, poi installalo dal browser del telefono.",
    "download.steps_hint": "Questi passaggi avvengono nel browser, non su questa pagina. Nessuna delle righe qui sotto è toccabile — descrivono cosa cercare.",
    "download.ios_arrow_label": "Tocca questo in Safari",
    "download.android_arrow_label": "Cerca qui in alto in Chrome",
    "install.eyebrow": "Installa Zcardon",
    "install.title": "Aggiungi Zcardon alla tua schermata Home",
    "install.subtitle": "Due tocchi e avrai Zcardon come vera icona app — senza App Store.",
    "install.back_home": "Apri Zcardon nel browser",
    "install.continue": "Continua su Zcardon →",
    "install.copy": "Copia",
    "install.copied": "Copiato",
    "install.ios_step1_title": "Apri questa pagina in Safari",
    "install.ios_step1_body": "Se hai scansionato da un altro browser, copia l'URL e aprilo prima in Safari — Chrome su iOS non può installare app.",
    "install.ios_step2_title": "Tocca il pulsante Condividi",
    "install.ios_step2_body": "È il quadrato con una freccia verso l'alto — in fondo a Safari (o in alto a destra su iPad).",
    "install.ios_step3_title": "Tocca \\\"Aggiungi alla schermata Home\\\"",
    "install.ios_step3_body": "Scorri verso il basso nel foglio di condivisione e scegli \\\"Aggiungi alla schermata Home\\\", poi conferma. Zcardon appare come icona app.",
    "install.ios_arrow_label": "Cerca questa icona",
    "install.android_ready_title": "Pronto per l'installazione",
    "install.android_ready_body": "Chrome ha rilevato Zcardon come installabile. Tocca qui sotto e appariremo come icona app sulla tua schermata Home.",
    "install.android_install": "Installa Zcardon",
    "install.android_installing": "Installazione in corso…",
    "install.android_step1_title": "Tocca il menu di Chrome",
    "install.android_step1_body": "Sono i tre puntini nell'angolo in alto a destra di Chrome.",
    "install.android_step2_title": "Tocca \\\"Installa app\\\" o \\\"Aggiungi alla schermata Home\\\"",
    "install.android_step2_body": "Conferma per aggiungere l'icona Zcardon al telefono. Toccala quando vuoi per avviarla.",
    "install.android_hint": "Se non vedi ancora \\\"Installa app\\\", visita la pagina iniziale una volta — Chrome la sblocca dopo una breve visita.",
    "install.desktop_title": "Scansiona con il tuo telefono",
    "install.desktop_body": "Zcardon è un'app mobile. Apri questo URL sul tuo telefono per installarla:",
    "install.webview_title": "Apri nel browser",
    "install.webview_body": "I browser in-app (Instagram, Facebook, TikTok) non possono installare app. Tocca \\\"…\\\" o il menu di condivisione, poi scegli \\\"Apri in Safari\\\" / \\\"Apri in Chrome\\\". Oppure copia questo link:",
    "install.already_title": "Sei già nell'app",
    "install.already_body": "Zcardon è installato su questo dispositivo. Tocca l'icona sulla tua schermata Home per aprirlo in qualsiasi momento.",
    "install.installed_title": "Installato",
    "install.installed_body": "Cerca l'icona Zcardon sulla tua schermata Home — quella è la tua app.",
    "install.steps_hint": "Questi passaggi avvengono nel browser, non su questa pagina. Nessuna delle righe qui sotto è toccabile — descrivono cosa cercare nel menu del browser.",
    "install.desktop_body_qr": "Zcardon si installa come icona app su iPhone o Android — non su desktop. Punta la fotocamera del telefono su questo codice QR:",
    "install.desktop_or": "Oppure copia il link",
    "install.android_arrow_label": "Cerca qui in alto in Chrome",
    "install.ios_arrow_caption": "Si trova in fondo a Safari",
    "vietqr.disabled.title": "Ricevi VND tramite VietQR",
    "vietqr.disabled.badge": "SOLO VIETNAM",
    "vietqr.disabled.desc": "Collega un conto bancario vietnamita e il QR di questa carta diventerà un vero VietQR — qualunque app bancaria vietnamita (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) potrà scansionarlo e pagarti in VND direttamente tramite la rete NAPAS. Nessuna approvazione Stripe Issuing necessaria.",
    "vietqr.disabled.btn_enter": "Inserisci dati bancari",
    "vietqr.disabled.or": "oppure",
    "vietqr.disabled.btn_send_link": "Invia il link di configurazione al destinatario",
    "vietqr.disabled.btn_send_link_tip": "Genera un link pubblico valido 7 giorni che il destinatario compila autonomamente",
    "vietqr.form.warn_title": "Leggi prima di attivare",
    "vietqr.form.warn_body_a": "Le scansioni non porteranno più alla tua pagina di tracciamento Zcardon. I fondi vanno",
    "vietqr.form.warn_body_b": "direttamente sul tuo conto bancario vietnamita",
    "vietqr.form.warn_body_c": "tramite interbancario NAPAS — Zcardon non vede mai il denaro. VietQR funziona solo all'interno del Vietnam per trasferimenti in VND.",
    "vietqr.form.bank": "Banca",
    "vietqr.form.account_number": "Numero di conto",
    "vietqr.form.account_name": "Intestatario (25 caratteri)",
    "vietqr.form.city": "Città (15 caratteri)",
    "vietqr.form.advanced": "Avanzate — importo fisso / riferimento (opzionale)",
    "vietqr.form.amount_vnd": "Importo (VND)",
    "vietqr.form.amount_ph": "lascia vuoto per importo variabile",
    "vietqr.form.reference": "Riferimento (25 caratteri)",
    "vietqr.form.confirm": "Comprendo che la scansione di questa carta trasferirà VND direttamente sul conto bancario sopra indicato. Zcardon non elabora né detiene questi fondi.",
    "vietqr.form.btn_attach": "Attiva VietQR",
    "vietqr.form.btn_attaching": "Attivazione…",
    "vietqr.form.btn_cancel": "Annulla",
    "vietqr.enabled.title": "VietQR attivo",
    "vietqr.enabled.desc": "Quando un'app bancaria vietnamita scansionerà questa carta, trasferirà VND direttamente sul conto qui sotto — bypassando completamente Zcardon.",
    "vietqr.enabled.bank_label": "Banca",
    "vietqr.enabled.account_label": "Conto",
    "vietqr.enabled.fixed_amount": "Importo fisso",
    "vietqr.enabled.reference": "Riferimento",
    "vietqr.enabled.btn_detach": "Scollega VietQR",
    "vietqr.enabled.btn_detaching": "Scollegamento…",
    "vietqr.enabled.attached_at": "Attivato il",
    "vietqr.analytics.live_activity": "Attività in tempo reale",
    "vietqr.analytics.today": "oggi",
    "vietqr.analytics.days_live": "{n}g attivo",
    "vietqr.analytics.phantom_views": "Visualizzazioni stimate",
    "vietqr.analytics.confirmed_pays": "Pagamenti confermati",
    "vietqr.analytics.total_vnd": "Totale VND",
    "vietqr.analytics.recent_confirmed": "Conferme recenti",
    "vietqr.analytics.phantom_explainer": "Visualizzazioni stimate ≈ download dell'immagine della carta dall'attivazione. Le scansioni VietQR reali non passano dai nostri server, quindi stimiamo dall'attività \"carta mostrata\". Chiedi al destinatario di confermare i pagamenti tramite il suo link di configurazione per dati precisi.",
    "vietqr.toast.required": "Numero di conto e intestatario sono obbligatori.",
    "vietqr.toast.confirm_first": "Conferma di aver capito che VietQR invia VND alla tua banca, bypassando Zcardon.",
    "vietqr.toast.attached_default": "VietQR attivato.",
    "vietqr.toast.attach_failed": "Impossibile attivare VietQR.",
    "vietqr.toast.detach_confirm": "Scollegare VietQR? Il QR tornerà al link di tracciamento Zcardon.",
    "vietqr.toast.detached": "VietQR scollegato — il QR torna al link di tracciamento Zcardon.",
    "vietqr.toast.detach_failed": "Impossibile scollegare.",
    "vietqr.toast.link_copied": "Link copiato · scade tra {h}h",
    "vietqr.toast.link_generated": "Link generato · copia qui sotto",
    "vietqr.toast.link_failed": "Impossibile generare il link.",

    "setpw.toast.too_short": "La password deve contenere almeno 8 caratteri",
    "setpw.toast.no_match": "Le password non coincidono",
    "setpw.toast.success_signin_again": "Password impostata — accedi di nuovo",
    "setpw.toast.welcome": "Benvenuto nel programma agenti Zcardon",
    "setpw.toast.expired": "Link scaduto o non valido",
    "setpw.verifying": "Verifica del tuo invito in corso…",
    "setpw.invalid.title": "Link scaduto",
    "setpw.invalid.desc": "Questo link di benvenuto è scaduto o è già stato utilizzato. Contatta il tuo amministratore Zcardon per ricevere un nuovo invito.",
    "setpw.invalid.home_btn": "Home",
    "setpw.invalid.support_btn": "Contatta il supporto",
    "setpw.fallback_name": "agente",
    "setpw.eyebrow": "Benvenuto agente",
    "setpw.heading": "Benvenuto, {name}",
    "setpw.subheading": "Imposta la tua password per attivare l'account agente. Effettuerai l'accesso immediatamente.",
    "setpw.code_label": "Il tuo codice agente",
    "setpw.code_tag": "Condividilo durante l'onboarding dei clienti",
    "setpw.form.title": "Scegli una password",
    "setpw.form.new_label": "Nuova password",
    "setpw.form.placeholder": "Minimo 8 caratteri",
    "setpw.form.confirm_label": "Conferma password",
    "setpw.form.btn_busy": "Attivazione in corso…",
    "setpw.form.btn_submit": "Attiva il mio account agente",
    "tx.heading": "Transazioni",
    "tx.kind.direct_deposit": "Deposito diretto",
    "tx.kind.card_topup": "Ricarica carta",
    "tx.kind.outbound_payout": "Pagamento in uscita",
    "tx.toast.load_failed": "Caricamento transazioni non riuscito",
    "tx.summary.deposits": "Depositi",
    "tx.summary.topups": "Ricariche",
    "tx.summary.topups_sub": "carta",
    "tx.summary.payouts": "Pagamenti",
    "tx.summary.payouts_sub": "in uscita",
    "tx.filter.all": "Tutti",
    "tx.filter.deposits": "Depositi",
    "tx.filter.topups": "Ricariche",
    "tx.filter.payouts": "Pagamenti",
    "tx.loading": "Caricamento transazioni…",
    "tx.empty.filtered": "Nessuna transazione corrisponde a questo filtro",
    "tx.empty.none": "Nessuna transazione ancora",
    "tx.empty.cta": "Inizia con un deposito diretto →",
    "tx.open_title": "Apri",
    "terms.load_error_default": "Caricamento dei termini non riuscito",
    "terms.toast.sign_and_check": "Firma e conferma per accettare il contratto",
    "terms.toast.recorded": "Accordo registrato",
    "terms.toast.failed": "Invio non riuscito",
    "terms.error.title": "Link non valido",
    "terms.back_home": "Torna alla home",
    "terms.loading": "Caricamento contratto…",
    "terms.done.title": "Accordo accettato",
    "terms.done.meta": "Versione {v} · {ts}",
    "terms.done.next": "Puoi chiudere questa pagina. La tua carta sarà pronta all'uso non appena emessa.",
    "terms.eyebrow": "Contratto titolare carta · v{v}",
    "terms.title_reaccept": "Riaccetta il contratto titolare carta",
    "terms.title_accept": "Accetta il contratto titolare carta",
    "terms.desc.sender_sent": "ti ha inviato una carta Zcardon",
    "terms.desc.you_were_sent": "Ti è stata inviata una carta Zcardon",
    "terms.desc.ending": "con finale",
    "terms.desc.read_before": "Leggi il contratto qui sotto, quindi firma e conferma per attivare la carta.",
    "terms.already_accepted_note": "Hai già accettato una versione precedente. Riaccetta per aggiornare all'ultima versione.",
    "terms.scroll_hint": "Scorri all'interno del riquadro per leggere il contratto completo.",
    "terms.view_json": "Apri come JSON",
    "terms.sign_label": "Firma con il tuo nome legale completo",
    "terms.sign_placeholder": "Come indicato sul tuo documento d'identità",
    "terms.consent": "Ho letto e accetto il Contratto per i titolari di carta Zcardon v{v}.",
    "terms.btn_recording": "Registrazione…",
    "terms.btn_accept": "Accetta il contratto",
    "hub.eyebrow": "Il tuo hub di ricezione",
    "hub.heading.all_done": "Sei pronto 🎉",
    "hub.heading.greet": "Ciao {name} 👋",
    "hub.fallback_name": "",
    "hub.desc.sender_sent": "ti ha inviato una carta Zcardon",
    "hub.desc.you_were_sent": "Ti è stata inviata una carta Zcardon",
    "hub.desc.ending": "che termina con",
    "hub.desc.complete_steps": "Completa i passaggi seguenti per attivare tutto.",
    "hub.progress.label": "{done} di {total} passaggi completati",
    "hub.blocker.title_one": "1 passaggio obbligatorio rimanente",
    "hub.blocker.title_many": "{n} passaggi obbligatori rimanenti",
    "hub.blocker.subtext": "La tua carta non sarà completamente attiva finché questi passaggi non saranno completati.",
    "hub.required_pill": "Obbligatorio",
    "hub.done_pill": "Fatto",
    "hub.setup_btn": "Configura",
    "hub.refresh": "Aggiorna",
    "hub.loading": "Caricamento…",
    "hub.error.title": "Problema con il link",
    "hub.error.default": "Impossibile caricare il tuo hub di ricezione.",
    "hub.back_home": "Torna alla home",
    "hub.footer": "Aggiungi questa pagina ai preferiti · rimane attiva per 90 giorni · solo chi ha questo link può vederla.",
    "onboarding.eyebrow": "Ricevi il tuo trasferimento",
    "onboarding.title": "Ciao {name}",
    "onboarding.fallback_name": "",
    "onboarding.intro_via": "tramite Zcardon",
    "onboarding.intro_ref_open": "(riferimento",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Aggiungi il tuo conto bancario {country} di seguito per ricevere i fondi. I tuoi dati vengono inviati direttamente a Stripe (il nostro partner per i pagamenti) — il mittente non li vedrà mai.",
    "onboarding.invalid.title": "Link non valido",
    "onboarding.invalid.hint": "Chiedi al mittente di inviare un nuovo link.",
    "onboarding.loading": "Caricamento…",
    "onboarding.complete.title": "Dati bancari già registrati",
    "onboarding.complete.desc": "Hai già inviato il tuo conto bancario. Il mittente rilascerà i fondi a breve.",
    "onboarding.success.title": "Dati bancari ricevuti ✓",
    "onboarding.success.auto_paid": "Grazie — il tuo conto bancario è verificato e i fondi sono stati trasferiti ora. Dovrebbero arrivare entro 1-2 giorni lavorativi.",
    "onboarding.success.pending": "Grazie — il tuo conto bancario è verificato. Il mittente verrà avvisato e i fondi arriveranno sul tuo conto entro 1-2 giorni lavorativi.",
    "onboarding.success.ref": "Riferimento: {id}",
    "onboarding.beta.title": "Anteprima beta.",
    "onboarding.beta.body": "Questa pagina è disponibile, ma la pipeline di pagamento live è in attesa dell'approvazione di Stripe. L'invio restituirà un errore finché la piattaforma non sarà abilitata. Riprova più tardi o contatta il supporto se urgente.",
    "onboarding.select_placeholder": "Seleziona…",
    "onboarding.submit_error_default": "Invio non riuscito. Riprova.",
    "onboarding.submit_busy": "Invio in corso…",
    "onboarding.submit": "Invia dati bancari →",
    "onboarding.secure_note": "Protetto tramite Stripe. Questo link scade tra 7 giorni.",
    "onboarding.validate.required": "{field} è obbligatorio",
    "onboarding.validate.format": "Il formato di {field} sembra errato (atteso {pattern})",
    "onboarding.validate.no_schema": "Nessuno schema modulo disponibile",
    "apply.toast.invalid_image": "Scegli un'immagine JPEG, PNG o WEBP",
    "apply.toast.too_large": "L'immagine deve essere di 6 MB o meno",
    "apply.toast.upload_both": "Carica entrambi i lati della tua patente di guida",
    "apply.toast.failed": "Invio non riuscito",
    "apply.toast.submitted": "Domanda inviata",
    "apply.success.title": "Domanda ricevuta",
    "apply.success.desc": "Grazie, {name}. Esamineremo la tua domanda entro 2–3 giorni lavorativi e ti invieremo un'email a {email} con i passaggi successivi.",
    "apply.success.back_home": "Torna alla home",
    "apply.success.contact_support": "Contatta il supporto",
    "apply.eyebrow": "Programma agenti",
    "apply.heading": "Richiedi di diventare un agente Zcardon",
    "apply.intro": "Guadagna una commissione dello 0,5% su ogni carta che aiuti a emettere, ogni ricarica che raccogli e ogni deposito in contanti che accetti per conto dei clienti. Accettiamo agenti affidabili nella loro comunità e in grado di verificare la propria identità.",
    "apply.bullet1": "Ottieni il tuo codice agente univoco · tracciabile su tutte le transazioni",
    "apply.bullet2": "Dashboard con riepiloghi guadagni settimanali/mensili/annuali",
    "apply.bullet3": "Bonifici bancari diretti — pagati settimanalmente",
    "apply.section.your_info": "Le tue informazioni",
    "apply.field.full_name": "Nome legale completo",
    "apply.field.full_name_ph": "Come indicato sulla tua patente di guida",
    "apply.field.email": "Email",
    "apply.field.phone": "Telefono",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Numero patente di guida",
    "apply.field.address": "Indirizzo",
    "apply.field.address_ph": "Via, Città, Provincia, CAP",
    "apply.field.why_join_label": "Perché vuoi diventare un agente? (opzionale)",
    "apply.field.why_join_ph": "Raccontaci del tuo legame con la comunità o della tua esperienza con le rimesse",
    "apply.dl.section": "Verifica patente di guida",
    "apply.dl.helper": "Carica foto nitide e ben illuminate di entrambi i lati. JPEG, PNG o WEBP · max 6 MB ciascuna. Le immagini sono archiviate in modo privato e visualizzate solo dagli amministratori Zcardon per la verifica.",
    "apply.dl.front": "Fronte della patente di guida",
    "apply.dl.back": "Retro della patente di guida",
    "apply.dl.tap_upload": "Tocca per caricare",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "Annulla",
    "apply.submit_busy": "Invio in corso…",
    "apply.submit": "Invia candidatura",
    "agent.toast.load_failed": "Caricamento dati agente fallito",
    "agent.toast.cust_created": "Cliente {name} creato",
    "agent.toast.cust_failed": "Creazione cliente fallita",
    "agent.toast.card_issued": "Carta emessa · hai guadagnato {amount}",
    "agent.toast.card_issue_failed": "Emissione carta fallita",
    "agent.toast.cash_accepted": "Deposito in contanti accettato · hai guadagnato {amount}",
    "agent.toast.cash_failed": "Registrazione deposito fallita",
    "agent.loading": "Caricamento…",
    "agent.eyebrow": "Portale agenti",
    "agent.fallback_name": "Agente",
    "agent.code_line.code_label": "Codice agente",
    "agent.code_line.commission_label": "commissione",
    "agent.earnings.label": "Saldo non pagato",
    "agent.earnings.sub": "{n} transazioni non pagate",
    "agent.stat.week": "Questa settimana",
    "agent.stat.month": "Questo mese",
    "agent.stat.year": "Quest'anno",
    "agent.stat.txns": "{n} txns",
    "agent.stat.customers": "I tuoi clienti",
    "agent.stat.active": "attivi",
    "agent.customers.title": "I tuoi clienti",
    "agent.customers.empty": "Nessun cliente ancora. Tocca \\\"Registra cliente\\\" per aggiungere il tuo primo utente.",
    "agent.customers.col_customer": "Cliente",
    "agent.customers.col_phone": "Telefono",
    "agent.customers.col_cards": "Carte",
    "agent.customers.col_commissions": "Commissioni guadagnate",
    "agent.customers.col_joined": "Iscritto",
    "agent.customers.col_actions": "Azioni",
    "agent.customers.txns": "{n} txns",
    "agent.commissions.title": "Commissioni recenti",
    "agent.commissions.empty": "Nessuna commissione ancora. Emetti la tua prima carta per guadagnare.",
    "agent.commissions.col_when": "Quando",
    "agent.commissions.col_customer": "Cliente",
    "agent.commissions.col_type": "Tipo",
    "agent.commissions.col_txn": "Transazione",
    "agent.commissions.col_commission": "Commissione",
    "agent.commissions.col_status": "Stato",
    "agent.commissions.paid": "pagato",
    "agent.commissions.unpaid": "non pagato",
    "agent.type.issuance": "Emissione",
    "agent.type.topup": "Ricarica",
    "agent.type.cash_deposit": "Deposito contanti",
    "agent.register_btn": "Registra cliente",
    "agent.dialog.new_cust.title": "Nuovo cliente",
    "agent.dialog.new_cust.desc": "Sarà collegato al tuo codice agente. Ogni carta emessa per suo conto ti frutta una commissione dello 0,5%.",
    "agent.dialog.field.full_name": "Nome completo",
    "agent.dialog.field.email": "Email",
    "agent.dialog.field.phone": "Telefono",
    "agent.dialog.field.password": "Password iniziale (da condividere con il cliente)",
    "agent.dialog.cancel": "Annulla",
    "agent.dialog.creating": "Creazione…",
    "agent.dialog.create_customer": "Crea cliente",
    "agent.dialog.issue_card.title": "Emetti carta per {name}",
    "agent.dialog.issue_card.desc": "Finanziata con contanti raccolti di persona. Il cliente paga una commissione del 3,75% — tu trattieni lo 0,5%.",
    "agent.dialog.field.receiver_name": "Nome destinatario",
    "agent.dialog.field.receiver_email": "Email destinatario",
    "agent.dialog.field.receiver_phone": "Telefono destinatario",
    "agent.dialog.field.amount_usd": "Importo (USD)",
    "agent.dialog.field.note": "Nota (opzionale)",
    "agent.dialog.issuing": "Emissione…",
    "agent.dialog.issue_card_btn": "Emetti carta",
    "agent.actions.issue_card": "Emetti carta",
    "agent.dialog.cash_deposit.title": "Deposito contanti per {name}",
    "agent.dialog.cash_deposit.desc": "Contanti raccolti dal cliente · crea una carta consegnata al destinatario.",
    "agent.dialog.field.receiver_name_opt": "Nome destinatario (opzionale)",
    "agent.dialog.recording": "Registrazione…",
    "agent.dialog.record_deposit": "Registra deposito",
    "agent.actions.cash_deposit": "Deposito contanti",

  },
  ja: {
    "header.nav.home": "ホーム",
    "header.nav.rates": "レート",
    "header.nav.support": "サポート",
    "header.nav.admin": "管理",
    "header.nav.signin": "ログイン",
    "header.nav.signout": "ログアウト",
    "header.nav.get_started": "始める",
    "header.nav.account": "アカウント",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "言語を切り替える",
    "landing.hero.eyebrow": "デジタル決済の新しい仕組み",
    "landing.hero.title_a": "バーチャルカードにチャージ。",
    "landing.hero.title_b_ship": "送る",
    "landing.hero.title_b_rest": "誰にでも、",
    "landing.hero.title_c": "すぐに使える",
    "landing.hero.subtitle": "スキャン可能なQRコード付きのタップ決済バーチャルデビットカードを発行。Stripe決済、USD建て残高、透明なサービス・ゲートウェイ手数料——メールまたはダウンロードで受取人に届けます。",
    "landing.hero.cta_start": "カードを発行する",
    "landing.hero.cta_request_cash": "キャッシュカードをリクエスト",
    "landing.hero.cta_rates": "レートを見る",
    "landing.hero.cta_find_card": "既存カードを探す",
    "landing.hero.stat_currency": "通貨",
    "landing.hero.stat_settle": "決済時間",
    "landing.hero.stat_settle_value": "約2分",
    "landing.hero.stat_service_fee": "サービス手数料",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "現代のカード経済のために構築",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "USDで決済",
    "landing.hero.marquee": "発行 · チャージ · USDで利用",
    "landing.demo.eyebrow": "20秒で体験",
    "landing.demo.title_a": "発行から",
    "landing.demo.title_b_spend": "利用",
    "landing.demo.title_c": "まで。4タップで。",
    "landing.demo.step_issue.label": "カードを発行",
    "landing.demo.step_issue.detail": "金額を選択。約2分でカード準備完了。",
    "landing.demo.step_share.label": "シェアする",
    "landing.demo.step_share.detail": "メール、テキスト、WhatsApp、またはPNGでダウンロード。",
    "landing.demo.step_scan.label": "受取人がスキャン",
    "landing.demo.step_scan.detail": "スマホカメラでトラックページが即座に開く。",
    "landing.demo.step_spend.label": "USDで利用",
    "landing.demo.step_spend.detail": "タップ決済、現金引き出し、店舗での利用。",
    "landing.demo.cta_try": "今すぐ試す",
    "landing.demo.live_pill": "ライブデモ",
    "landing.demo.scene1_caption": "カード生成中 · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "WhatsAppで受取人に送信中",
    "landing.demo.scene3_caption": "QRスキャン中 · 一致確認",
    "landing.demo.scene4_eyebrow": "残高クレジット済み",
    "landing.demo.scene4_status": "✓ 承認済み · タップ決済準備完了",
    "landing.footer.rates": "レート",
    "landing.footer.support": "サポート",
    "landing.footer.admin_portal": "管理ポータル",
    "landing.footer.copy": "© 2026 Zcardon · デモフィンテックプロダクト",
    "landing.cash.dialog_title": "キャッシュカードをリクエスト",
    "landing.cash.dialog_desc": "Stripeチェックアウトリンクをテキストでお送りします。任意のカードでお支払いいただくと、Stripeの確認と同時にキャッシュカードがチャージされます。",
    "landing.cash.phone_label": "携帯番号",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "金額（USD）",
    "landing.cash.name_label": "お名前（任意）",
    "landing.cash.submit": "チェックアウトリンクを送信",
    "landing.cash.submitting": "送信中…",
    "landing.cash.success_title": "チェックアウトリンクを送信しました",
    "landing.cash.success_body": "{phone}にStripeチェックアウトリンクを送信しました。タップしてお支払いいただくと、バーチャルカードがすぐに有効になります。",
    "landing.cash.success_open_checkout": "今すぐチェックアウトを開く",
    "landing.find.cta": "支払い済みの方はこちら：カードを探す",
    "landing.find.dialog_title": "既存カードを探す",
    "landing.find.dialog_desc": "ご登録の電話番号を入力してください。6桁のコードをテキストでお送りし、有効なキャッシュカードを確認します。",
    "landing.find.phone_label": "携帯番号",
    "landing.find.code_label": "6桁のコード",
    "landing.find.send_code": "コードを送信",
    "landing.find.verify": "確認して探す",
    "landing.find.sending": "送信中…",
    "landing.find.verifying": "確認中…",
    "landing.find.no_cards": "その番号に有効なカードが見つかりませんでした。",
    "login.eyebrow.welcome": "ようこそ",
    "login.eyebrow.admin": "管理",
    "login.title": "ログイン",
    "login.title_admin": "管理者ログイン",
    "login.admin_badge": "管理ポータル",
    "login.email_label": "メールアドレス",
    "login.password_label": "パスワード",
    "login.submit": "ログイン",
    "login.submitting": "ログイン中…",
    "login.toast_welcome": "おかえりなさい、{name}",
    "login.toast_failed": "ログインに失敗しました",
    "login.gate_title": "お客様のログインは一時的に停止しています",
    "login.gate_default_message": "プラットフォームの改善を行っています。しばらくしてからご確認ください。",
    "login.gate_admin_note": "管理者およびマネージャーは引き続き下記からログインできます。",
    "login.new_here": "初めての方は",
    "login.create_account": "アカウントを作成",
    "login.customer_q": "お客様の方は",
    "login.regular_signin": "通常のログイン",
    "login.demo_title": "デモアカウント",
    "login.demo_customers_note": "お客様：/register から登録するか、管理者パネルから作成してください",
    "login.forgot_password": "パスワードをお忘れですか？",
    "forgot.eyebrow": "アカウントの回復",
    "forgot.title": "パスワードをリセット",
    "forgot.subtitle": "Zcardon アカウントに登録したメールアドレスを入力してください。新しいパスワードを設定するためのリンクをお送りします。",
    "forgot.email_label": "メールアドレス",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "リセットリンクを送信",
    "forgot.submitting": "送信中…",
    "forgot.toast_failed": "リセットリンクを送信できませんでした。もう一度お試しください。",
    "forgot.sent_title": "受信トレイをご確認ください",
    "forgot.sent_message": "{email} のアカウントが存在する場合、パスワードリセットリンクをお送りしました。",
    "forgot.sent_expiry_note": "リンクの有効期限は15分です。届かない場合は、迷惑メールを確認するか、再送信してください。",
    "forgot.back_to_login": "ログインに戻る",
    "forgot.resend_link": "再送信する",
    "forgot.remember_q": "パスワードを思い出しましたか？",
    "forgot.signin_link": "ログイン",
    "reset.eyebrow": "アカウントの回復",
    "reset.title": "新しいパスワードを設定",
    "reset.subtitle": "これまでに使用したことのない強力なパスワードを設定してください。設定後、自動的にログインされます。",
    "reset.verifying": "リセットリンクを確認中…",
    "reset.invalid_title": "このリセットリンクは無効または期限切れです",
    "reset.invalid_message": "リセットリンクの有効期限は15分で、1回のみ使用できます。新しいリンクをリクエストしてください。",
    "reset.request_new_link": "新しいリセットリンクをリクエスト",
    "reset.new_password_label": "新しいパスワード",
    "reset.confirm_password_label": "パスワードの確認",
    "reset.password_hint": "8文字以上。他のサービスで使用しているパスワードは避けてください。",
    "reset.submit": "パスワードを更新",
    "reset.submitting": "更新中…",
    "reset.toast_success": "パスワードを更新しました。ログインしました。",
    "reset.toast_failed": "パスワードをリセットできませんでした。もう一度お試しください。",
    "reset.error_min_length": "パスワードは8文字以上にしてください。",
    "reset.error_mismatch": "パスワードが一致しません。",
    "register.eyebrow": "登録",
    "register.title": "アカウントを作成",
    "register.full_name_label": "氏名",
    "register.email_label": "メールアドレス",
    "register.password_label": "パスワード",
    "register.submit": "アカウントを作成",
    "register.submitting": "作成中…",
    "register.gate_title": "新規登録は一時的に停止しています",
    "register.gate_default_message": "プラットフォームの改善を行っています。しばらくしてからご確認ください。",
    "register.gate_already_member": "すでにアカウントをお持ちですか？",
    "register.toast_created": "アカウントを作成しました",
    "register.toast_failed": "登録に失敗しました",
    "register.already_member": "すでにメンバーの方は",
    "register.signin_link": "ログイン",
    "dash.eyebrow": "ダッシュボード",
    "dash.greeting": "おかえりなさい、{name}",
    "dash.subtitle": "バーチャルカードの発行や電信送金が可能です。コントロールセンターは下記にあります。",
    "dash.action.issue_card": "カードを発行",
    "dash.action.direct_deposit": "ダイレクトデポジット（電信送金）",
    "dash.action.support": "サポート",
    "dash.section.cards": "カード一覧",
    "dash.section.activity": "最近のアクティビティ",
    "dash.section.deposits": "ダイレクトデポジット",
    "dash.empty.cards": "カードはまだありません。最初のカードを発行して始めましょう。",
    "dash.empty.activity": "カードの発行や電信送金を開始すると、最近のアクティビティがここに表示されます。",
    "ref.eyebrow": "Zcardon へようこそ",
    "ref.greeting": "{name} からの招待です。",
    "ref.loading": "招待情報を読み込み中…",
    "ref.resolved_redirect": "少々お待ちください。移動します。",
    "ref.resolving": "招待リンクを確認中…",
    "support.eyebrow": "いつでもご相談ください",
    "support.title": "チケット不要のサポート。",
    "support.subtitle": "スタッフが直接対応します。平日は1営業時間以内にメールにてご返信します。",
    "support.card.email": "メール",
    "support.card.phone": "電話",
    "support.card.live_chat": "ライブチャット",
    "support.card.live_chat_value": "ダッシュボード内",
    "support.card.hours": "営業時間",
    "support.faq.title": "よくある質問",
    "support.faq.q1": "受取人にカードが届くまでどのくらいかかりますか？",
    "support.faq.a1": "通常、入金完了から2分以内に届きます。メール（Resend）およびMMS（Twilio）経由で自動配信されます。",
    "support.faq.q2": "送信後にカードをキャンセルまたは凍結できますか？",
    "support.faq.a2": "はい — カード詳細ページを開いて「凍結」をタップしてください。受取人向けのアラートメールからも直接凍結できます。",
    "support.faq.q3": "カードはどの通貨で使えますか？",
    "support.faq.a3": "すべてのカードはUSDで決済されます。受取人はVisa/Mastercardが使える場所であればどこでも利用でき、外国為替はカードネットワークが処理します。",
    "support.faq.q4": "どのような手数料がかかりますか？",
    "support.faq.a4": "透明性のあるサービス手数料とStripeのゲートウェイコストが発生します。支払い前にレートページでライブ見積もりをご確認ください。",
    "rates.eyebrow": "透明性",
    "rates.title_a": "隠れた",
    "rates.title_b": "スプレッドなし",
    "rates.subtitle": "サービス手数料とゲートウェイコストは支払い前に確認できます。元本は常にUSDで仮想カード残高に入金されます。",
    "rates.row.service_fee": "サービス手数料",
    "rates.row.service_fee_value": "送金額の{pct}",
    "rates.row.gateway": "ゲートウェイ（Stripe）",
    "rates.row.settlement": "決済",
    "rates.row.settlement_value": "仮想カードのUSD残高",
    "rates.row.topup": "チャージ方法",
    "rates.row.topup_value": "クレジットカード · デビット · 銀行口座（ACH）",
    "rates.sim.eyebrow": "ライブ見積もり",
    "rates.sim.title_a": "金額を入力して、",
    "rates.sim.title_b": "内訳をすぐに確認。",
    "rates.sim.amount_label": "送金額",
    "rates.sim.line.principal": "カード入金額（元本）",
    "rates.sim.line.service": "サービス手数料 · {pct}",
    "rates.sim.line.gateway": "ゲートウェイ · {pct} + {fixed}",
    "rates.sim.line.you_pay": "お支払い合計",
    "rates.sim.cta": "${amount}で始める →",
    "rates.sim.note": "上記のレートは管理者設定によるものです。受取人には常に元本全額が届きます。",
    "dd.title": "ダイレクトデポジット",
    "dd.intro": "世界中の受取人の銀行口座に送金できます。カード（Stripe Checkout）またはZcardon残高でお支払いいただけます。対応国では自動送金、未対応国では手動で受取人の銀行への送金を行います。すべての取引は印刷可能な領収書とともに記録されます。",
    "dd.new_btn": "新しい振込を記録する",
    "dd.close": "閉じる",
    "dd.history_eyebrow": "振込履歴",
    "dd.loading": "読み込み中…",
    "dd.empty": "ダイレクトデポジットはまだありません。",
    "dd.status.approved": "承認済み",
    "dd.status.pending": "承認待ち",
    "dd.status.rejected": "却下",
    "dd.row.confirmed": "✓ 受取人が受領を確認しました",
    "dd.row.disputed": "⚠ 受取人が差異を報告しました",
    "dd.row.pdf": "PDF",
    "dd.error.load": "振込データを読み込めませんでした",
    "dd.error.pdf": "領収書をダウンロードできませんでした",
    "pay.mode.view": "あなたのZcardonカード",
    "pay.mode.charge": "QRで支払う",
    "pay.heading.view": "{name}さん、こんにちは。",
    "pay.heading.charge": "このカードに請求する。",
    "pay.cardholder": "カード所有者",
    "pay.paused.label": "カードは一時停止中",
    "pay.paused.body": "このカードは凍結されており、所有者が再開するまで新たな請求を受け付けられません。",
    "pay.balance.label": "利用可能残高",
    "pay.charges_count": "{count}件の請求",
    "pay.charges_count_plural": "{count}件の請求",
    "pay.topups_count": "{count}回のチャージ",
    "pay.topups_count_plural": "{count}回のチャージ",
    "pay.activity.label": "最近のアクティビティ",
    "pay.activity.empty": "アクティビティはまだありません",
    "pay.cta.charge": "このカードに請求する（加盟店）",
    "pay.cta.charge_paused": "カードは一時停止中",
    "pay.cta.note_active": "このカードからの支払いを受け取る加盟店の方のみタップしてください。",
    "pay.cta.note_paused": "このカードは送付者によって一時停止されています。再開を依頼してください。",
    "pay.back_to_balance": "残高に戻る",
    "pay.form.amount_label": "金額（USD）",
    "pay.form.amount_max": "上限 {amount}",
    "pay.form.merchant_label": "加盟店 / 理由",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "メモ（任意）",
    "pay.form.memo_placeholder": "ラテ2杯",
    "pay.form.submit_processing": "処理中…",
    "pay.form.submit_paused": "カードは一時停止中",
    "pay.form.submit_charge": "{amount}を請求する",
    "pay.form.submit_charge_card": "カードに請求する",
    "pay.form.disclaimer": "このカードに請求することで、カード所有者から上記金額の支出を承認されていることを確認します。",
    "pay.receipt.title": "支払い完了",
    "pay.receipt.to": "宛先",
    "pay.receipt.id": "領収書番号",
    "pay.receipt.time": "日時",
    "pay.receipt.new_balance": "新残高",
    "pay.receipt.done": "完了・残高に戻る",
    "pay.error.cant_load": "このカードを読み込めません",
    "pay.error.back_home": "ホームに戻る",
    "pay.error.loading": "カードを読み込み中…",
    "pay.error.charge_failed": "請求に失敗しました",
    "pay.tap.eyebrow": "タップして支払う",
    "pay.tap.title": "このコードを提示",
    "pay.tap.subtitle": "加盟店のスキャナーに",
    "pay.tap.balance": "残高",
    "pay.tap.paused": "一時停止中",
    "pay.tap.qr_unavailable": "このカードではQR利用不可",
    "pay.tap.refresh_in": "{secs}秒後に更新",
    "pay.tap.show_digits": "番号を表示",
    "pay.tap.hide_digits": "番号を隠す",
    "pay.tap.card_label": "カード · ••••",
    "pay.tap.auto_hides": "60秒後に自動非表示",
    "pay.tap.full_only_image": "全番号はカード画像上にのみ表示されます",
    "pay.tap.disclaimer": "クローズドループカード · Zcardon対応加盟店で利用可能。リプレイ防止のためQRは60秒ごとに更新されます。",
    "pay.subscribe.eyebrow": "最新情報を受け取る",
    "pay.subscribe.title": "すべての取引をメールで受け取る",
    "pay.subscribe.desc": "無料。チャージまたはチャージごとに1通。いつでも配信停止可能。アドレスを第三者に共有しません。",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "通知を受け取る",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "登録完了",
    "pay.subscribe.done.body": "カード •••• {last4} へのすべてのチャージまたはチャージ時に {email} へ即時メールをお送りします。",
    "pay.subscribe.toast.already": "すでに登録済みです — 重複送信はありません。",
    "pay.subscribe.toast.success": "すべての取引でメールが届きます。ようこそ！",
    "pay.subscribe.toast.failed": "登録に失敗しました",
    "pay.save.eyebrow": "カードを持ち歩く",
    "pay.save.title": "このカードをスマートフォンに保存",
    "pay.save.desc": "カード画像を写真に保存してオフラインでも利用できます — アプリ不要でいつでもQRを提示できます。",
    "pay.save.cta": "カード画像を保存",
    "pay.save.preparing": "準備中…",
    "pay.save.add_home": "ホーム画面に追加",
    "pay.save.toast.opened": "カード画像を開きました — 長押しして写真に保存してください",
    "pay.save.toast.failed": "カードを保存できませんでした",
    "pay.save.ios.title": "ZcardonをiPhoneのホーム画面に追加するには：",
    "pay.save.ios.step1": "Safariの下部にある共有ボタンをタップ",
    "pay.save.ios.step2": "下にスクロールして「ホーム画面に追加」をタップ",
    "pay.save.ios.step3": "「追加」をタップして確認",
    "pay.save.ios.note": "Zcardonは通常のアプリのように表示されます — アイコンをタップするとこのカードがすぐに開きます。",
    "pay.thanks.eyebrow": "お礼を送る",
    "pay.thanks.body": "${amount}が届きました — このカードに入金してくれた方に感謝を伝えましょう。",
    "pay.thanks.placeholder": "任意 — 一言添えてみましょう…",
    "pay.thanks.cta": "{emoji}お礼を送る",
    "pay.thanks.sending": "送信中…",
    "pay.thanks.done": "お礼を送りました — すぐに届きます。",
    "pay.thanks.toast.sent": "お礼を送りました",
    "pay.thanks.toast.failed": "お礼を送れませんでした",
    "pay.activity.topup_bank": "銀行振込チャージ",
    "pay.activity.topup_debit": "デビットカードチャージ",
    "pay.activity.topup_credit": "クレジットカードチャージ",
    "pay.activity.topup_crypto": "暗号資産チャージ",
    "pay.activity.topup_received": "チャージ受取",
    "pay.activity.merchant_charge": "加盟店請求",
    "pay.toast.payment_received": "入金を受け取りました · −{amount}",
    "cash.confirming": "支払いを確認中…",
    "cash.confirming.sub": "Stripeが請求を処理中です。",
    "cash.notfound.title": "リンクの有効期限切れ",
    "cash.notfound.body": "この入金リクエストが見つかりません。リンクが古いセッションのものか、カードが確定されていない可能性があります。新しいリクエストを開始してください — 1分もかかりません。すでに支払い済みの場合は、バックグラウンドの照合処理が検出次第、自動的にクレジットされます。",
    "cash.notfound.ref": "参照番号：",
    "cash.notfound.start": "新しいリクエストを開始",
    "cash.notfound.contact": "サポートに連絡",
    "cash.error.title": "ステータスを確認できませんでした",
    "cash.error.return": "ホームに戻る",
    "cash.autopay.opening": "タップ払い画面を開いています…",
    "cash.autopay.redirect": "{secs}秒後にリダイレクトします。",
    "cash.autopay.stay": "このままここにいる",
    "cash.ready.title": "カードの準備ができました",
    "cash.ready.subtitle": "${amount} USD をチャージ済み · {phone} にもSMSを送信しました",
    "cash.card.label": "カード",
    "cash.card.exp": "有効期限",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "残高",
    "cash.cta.open_pay": "タップ払い画面を開く",
    "cash.toast.funded": "カードに資金が入金されました — SMSを確認してください！",
    "cash.pending.title": "Stripeの確認待ち中",
    "cash.pending.body": "確認のためポーリング中です。通常5〜15秒かかります。支払わずにチェックアウトを閉じた場合は、ホームページから最初からやり直せます。",
    "cash.pending.back": "ホームに戻る",
    "cash.timeout.title": "支払いを確認できませんでした",
    "cash.timeout.body": "Stripeの確認を3分間待ちました。多くの場合、支払い前にチェックアウトが閉じられたことを意味しますが、支払い済みの場合、カードは数分以内に自動的に有効化されます（バックグラウンド処理は5分ごとに実行）。有効化され次第、{phone}にテキストメッセージでお知らせします。",
    "cash.timeout.start_over": "最初からやり直す",
    "cash.timeout.try_again": "再試行",
    "cash.resend.title": "届きませんでしたか？再送信する",
    "cash.resend.degraded.lead": "現在、米国番号への配信が低下しています。",
    "cash.resend.degraded.sms": "米国の携帯電話へのSMSは、キャリアによって無通知でブロックされる場合があります（フリーダイヤル認証は保留中）。",
    "cash.resend.degraded.email": "メールはテストモードであり、DNSの検証中はアカウントオーナーのアドレスにのみ配信されます。",
    "cash.resend.degraded.tail": "チャンネルが機能しない場合は、別のチャンネルをお試しいただくか、受取人のデバイスでこのページを開いてカードを直接取得してください。",
    "cash.resend.text": "テキスト",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "メール",
    "cash.resend.sending": "送信中…",
    "cash.resend.email_sandbox": "メールはテストモードです。zcardon.comドメインがResendで確認される間、アカウントオーナーのアドレスにのみ送信できます。メールが受け付けられない場合は、テキストまたはWhatsAppをご利用ください。",
    "cash.resend.email_label": "メールアドレス",
    "cash.resend.email_send": "メールで送信",
    "cash.resend.cancel": "キャンセル",
    "cash.resend.limit": "1カードにつき再送信は5回まで。登録電話番号：{phone}",
    "cash.resend.no_phone": "（なし）",
    "cash.wallet.title": "ウォレットに追加",
    "cash.wallet.apple": "Apple Walletに追加",
    "cash.wallet.google": "Google Walletに追加",
    "cash.wallet.generating": "パスを生成中…",
    "cash.wallet.note": "ロック画面に表示 · 残高変更時に自動更新",
    "cash.wallet.toast.apple": "パスをダウンロードしました — iPhoneで開いてApple Walletに追加してください",
    "cash.wallet.toast.apple_failed": "Apple Walletパスを生成できませんでした",
    "cash.wallet.toast.google_failed": "Google Walletパスを生成できませんでした",
    "cash.delivery.fallback_title": "{fallback}経由で送信しました",
    "cash.delivery.fallback_body": "キャリアが元のSMSをブロックしたため、{fallback}経由で{phone}にカードを再送信しました。そちらをご確認ください。",
    "cash.delivery.toll_blocked": "キャリアがSMSをブロックしました",
    "cash.delivery.failed_title": "カードをテキスト送信できませんでした",
    "cash.delivery.toll_body": "携帯キャリアがメッセージを拒否しました（認証保留中の新規送信者で発生することがあります）。今すぐカードを保存してください：タップ",
    "cash.delivery.failed_body": "カードに資金が入金されました — SMSが届かなかったため、今すぐ保存してください。タップ",
    "cash.delivery.cta_label": "タップ支払い画面を開く",
    "cash.delivery.body_tail": "上記をタップするか、スクリーンショットを撮るか、下のメールを使ってご自身にコピーを送信してください。",
    "cash.handoff.eyebrow": "対面で手渡し",
    "cash.handoff.body": "受取人にカメラでこれをスキャンしてもらってください — SMSやメール不要で、カードが直接相手のスマホで開きます。",
    "cash.handoff.share_label": "またはこのリンクを共有",
    "cash.handoff.copy": "リンクをコピー",
    "cash.handoff.copied": "✓ コピーしました",
    "cash.handoff.share": "共有",
    "cash.handoff.toast.copied": "リンクをコピーしました — どこにでも貼り付けてください",
    "cash.handoff.toast.copy_failed": "コピーできませんでした。リンクを長押しして手動でコピーしてください。",
    "portal.loading": "領収書を読み込み中…",
    "portal.unavailable.title": "リンクは無効です",
    "portal.unavailable.body": "このリンクは無効または期限切れです。",
    "portal.unavailable.hint": "送信者がこのリンクを共有した場合は、領収書メールの再送を依頼してください。",
    "portal.brand": "領収書ポータル",
    "portal.eyebrow": "{sender}から送金されました",
    "portal.reference": "参照番号",
    "portal.pending.title": "承認待ち",
    "portal.pending.body": "送信者がこの送金を記録しましたが、Zcardonで審査中です。しばらくしてから再度ご確認ください — 承認後に領収書が表示されます。",
    "portal.row.bank": "銀行",
    "portal.row.account": "口座番号",
    "portal.row.routing": "ルーティング番号",
    "portal.row.memo": "メモ",
    "portal.confirmed.title": "受取を確認済み",
    "portal.confirmed.body": "{when}に確認されました。送信者に通知されています。",
    "portal.disputed.title": "不一致を報告済み",
    "portal.disputed.body": "{when}に報告されました。送信者にメールで通知されました。",
    "portal.actions.body": "この金額はお客様の口座に入金されましたか？確認することで送信者との確認が完了します — Zcardonを通じてお金は動きません。これは記録のみです。",
    "portal.actions.confirm": "はい、受け取りました",
    "portal.actions.flag": "問題を報告する",
    "portal.dispute.placeholder": "金額が違う、届いていない、口座番号が違う…",
    "portal.dispute.submit": "報告を送信",
    "portal.dispute.too_short": "短い理由を入力してください（4文字以上）",
    "portal.toast.confirmed": "ありがとうございます — 領収書を確認しました。",
    "portal.toast.confirm_failed": "領収書を確認できませんでした",
    "portal.toast.reported": "報告しました。送信者に通知されました。",
    "portal.toast.report_failed": "異議申し立てを送信できませんでした",
    "portal.toast.pdf_failed": "領収書をダウンロードできませんでした",
    "portal.download_pdf": "PDF領収書をダウンロード",
    "portal.privacy": "このページはお客様専用です。リンクを持つ人は誰でもこの領収書を閲覧できます — 公開しないでください。お問い合わせ：",
    "detail.back": "ダッシュボードに戻る",
    "detail.eyebrow": "あなたのカード",
    "detail.confirming_payment": "支払いを確認中…",
    "detail.active_ready": "カードは有効で、共有できます。",
    "detail.balance.label": "現在の残高",
    "detail.toast.notfound": "カードが見つかりません",
    "detail.toast.loaded": "カードに資金が入金されました。",
    "detail.toast.confirmed": "支払いが確認されました。",
    "detail.toast.expired": "支払いセッションが期限切れです",
    "detail.delivery.receiver_fallback": "受取人",
    "detail.delivery.received_title": "{name}がカードを開封しました",
    "detail.delivery.received_when": "配送確認済み · {when}",
    "detail.delivery.waiting_title_on": "受け取られた瞬間にメールでお知らせします",
    "detail.delivery.waiting_title_off": "配送通知はオフです",
    "detail.delivery.waiting_body_on": "受取人がこのカードのQRをスキャンした瞬間に、メールで一度だけお知らせします。",
    "detail.delivery.waiting_body_off": "受取人が初めてカードを開封した瞬間に、メールで一度だけ通知を受け取るにはオンにしてください。",
    "detail.delivery.toggle_on": "オン",
    "detail.delivery.toggle_off": "オンにする",
    "detail.delivery.toast_enabled": "カードが開封されたときに通知されます。",
    "detail.delivery.toast_disabled": "配送通知をオフにしました。",
    "detail.delivery.toast_failed": "配送通知を更新できませんでした。",
    "nudge.eyebrow": "受取人に伝える",
    "nudge.title": "{receiver}に事前連絡を送る",
    "nudge.subtitle": "{receiver}がカードの有効化と使い方を把握できるよう、短いメッセージを下書きしました。",
    "nudge.receiver_fallback": "受取人",
    "nudge.sender_fallback": "友人",
    "nudge.template": "こんにちは {receiver} — {sender}があなたに{amount}がチャージされたZcardonバーチャルカードを送りました。こちらからタップして支払い: {url}\\n\\nアプリ不要。リンクはどのスマートフォンでも使えます。",
    "nudge.email_subject": "{sender}から{amount}のZcardonカードが届きました",
    "nudge.action_copy": "メッセージをコピー",
    "nudge.action_sms": "SMSを開く",
    "nudge.action_email": "メールを開く",
    "nudge.toast_copied": "メッセージをクリップボードにコピーしました。",
    "nudge.toast_copy_failed": "コピーできませんでした。手動で選択してコピーしてください。",
    "ios_install.title": "iPhoneにZcardonをインストール",
    "ios_install.step_1": "タップして",
    "ios_install.share_label": "共有",
    "ios_install.step_2": " 「ホーム画面に追加」を選択 — Zcardonがフルスクリーンアプリとして開きます。",
    "smart_banner.title": "Zcardonアプリで開く",
    "smart_banner.subtitle": "より速いチェックアウト。アプリを離れずにタップして支払い。",
    "smart_banner.cta": "開く",
    "smart_banner.dismiss": "閉じる",
    "social_proof.eyebrow": "ライブ · 過去48時間",
    "social_proof.empty": "次のカードの到着を待っています…",
    "social_proof.line_opened": "{name}が{amount}のカードを開封しました。",
    "social_proof.line_issued": "{name}が友人から{amount}のカードを受け取りました。",
    "social_proof.line_deposit": "{name}が{amount}の直接入金を受け取りました。",
    "social_proof.stat_funded": "本日入金されたカード",
    "social_proof.stat_delivered": "本日配送されたカード",
    "social_proof.stat_recent": "最近のアクティビティ",
    "social_proof.ago_seconds": "{n}秒前",
    "social_proof.ago_minutes": "{n}分前",
    "social_proof.ago_hours": "{n}時間前",
    "flight.eyebrow": "配送中のカード · 過去48時間",
    "flight.title": "Zcardonの届く場所",
    "flight.subtitle": "実際の受取人の位置情報を地域単位で匿名化して表示しています。国とアメリカの州のみ表示 — 氏名・住所・金額は表示しません。",
    "flight.row_template": "{label}でカードが開封されました",
    "flight.more_count": "+ {n}件の地域",
    "flight.ago_seconds": "{n}秒前",
    "flight.ago_minutes": "{n}分前",
    "flight.ago_hours": "{n}時間前",
    "trust_strip.eyebrow": "実データ · データベースからライブ取得",
    "trust_strip.title_a": "口コミではありません。",
    "trust_strip.title_b": "ライブのデータベース読み取りです。",
    "trust_strip.how_link": "収集方法について",
    "trust_strip.modal_eyebrow": "データソース",
    "trust_strip.modal_title": "すべて実データ、すべて匿名化済み",
    "trust_strip.modal_close": "閉じる",
    "trust_strip.modal_intro": "このストリップに表示されるすべての数値は自社のデータベースから取得したものです — サードパーティのフィードや作り話は一切含まれません。各セクションのデータ元と匿名化の方法をご説明します。",
    "trust_strip.src_carousel_title": "ライブアクティビティカルーセル",
    "trust_strip.src_carousel_body": "cardsおよびdirect_depositsコレクションから過去48時間分を取得。名前は名＋姓の頭文字で表示し、金額は三角測量対策のため最も近い$5単位に丸めています。60秒ごとに更新。",
    "trust_strip.src_flight_title": "Zcardonの届く場所",
    "trust_strip.src_flight_body": "card_scansからQRスキャンの着地情報をスキャン時にジオ解決して読み取り。国（または米国の州）単位に集約。州レベル以下の都市精度なし、IP表示なし、カード識別子なし。90秒ごとに更新。",
    "trust_strip.src_badges_title": "30日間の信頼指標",
    "trust_strip.src_badges_body": "純粋な集計カウントのみ — 行単位のデータは含みません。各指標は最小サンプルサイズを超えた場合にのみ表示されるため、2件の確認から算出された100%の達成率は非表示となります。毎分更新。",
    "trust_strip.modal_promise": "氏名・住所・正確な金額・IPアドレス・カード番号は公開ウィジェットに掲載しません。約束します。",
    "trust_badge.eyebrow": "実際の数値 · 過去30日間",
    "trust_badge.title": "実際に利用されています。",
    "trust_badge.subtitle": "口コミではなく、データベースから毎分更新されるライブカウントです。",
    "trust_badge.confirmed_label": "受取人が24時間以内に受取確認",
    "trust_badge.confirmed_sub": "過去30日間の{n}件の回答より",
    "trust_badge.thanks_label": "送られたお礼メッセージ",
    "trust_badge.thanks_sub": "過去30日間の受取人より",
    "trust_badge.cards_label": "入金済みカード",
    "trust_badge.cards_sub": "過去30日間・リアルタイム集計",
    "pulse.label": "ライブ状況",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name}が{amount}のカードを開きました",
    "pulse.tooltip_issued": "{name}が{amount}のカードを取得しました",
    "pulse.tooltip_deposit": "{name}が{amount}の入金を受け取りました",
    "pulse.ago_seconds": "{n}秒前",
    "pulse.ago_minutes": "{n}分前",
    "pulse.ago_hours": "{n}時間前",
    "note_cloud.eyebrow": "みんなの使い道",
    "note_cloud.title": "リアルな理由。リアルな人々。",
    "note_cloud.subtitle": "過去90日間の{n}件のカードメモから集計。{min}件以上のメモに登場した言葉のみ表示 — あなたのメッセージは守られます。",
    "note_cloud.footnote": "表示前に名前・金額・連絡先を除外しています。",
    "note_cloud.lang_eyebrow": "これらのメモの言語は？",
    "note_cloud.lang_other": "その他",
    "download.button": "アプリをダウンロード",
    "download.close": "閉じる",
    "download.modal_eyebrow": "Zcardonを入手",
    "download.modal_title": "Zcardonをスマホにインストール",
    "download.footnote": "App Storeには未対応 — セキュアなウェブアプリを直接インストールします。常に最新版。",
    "download.installed_title": "Zcardonをインストール済み",
    "download.installed_body": "ホーム画面のアイコンをご確認ください。",
    "download.webview_warning": "インストールするには、スマホのブラウザ（iPhoneはSafari、AndroidはChrome）でこのページを開いてください — アプリ内ブラウザではホーム画面に追加できません。",
    "download.ios_step1_title": "Safariで開く",
    "download.ios_step1_body": "Safariでない場合は、このリンクをコピーしてSafariで開いてください。（ChromeなどiOSの他のブラウザではインストールできません。）",
    "download.ios_step2_title": "共有をタップ",
    "download.ios_step2_body": "共有アイコンはiPhoneでは下部中央、iPadでは右上にあります。",
    "download.ios_step3_title": "ホーム画面に追加",
    "download.ios_step3_body": "共有メニューをスクロールして\\\"ホーム画面に追加\\\"を選択。Zcardonがフルスクリーンアプリとしてインストールされます。",
    "download.android_ready_body": "ChromeがZcardonを検出し、インストール準備完了です。以下をタップしてください。",
    "download.android_install": "Zcardonをインストール",
    "download.android_installing": "インストール中…",
    "download.android_step1_title": "Chromeメニューを開く",
    "download.android_step1_body": "Chrome右上の三点メニューをタップ。",
    "download.android_step2_title": "ホーム画面に追加",
    "download.android_step2_body": "\\\"アプリをインストール\\\"または\\\"ホーム画面に追加\\\"を選択。Zcardonがフルスクリーンアプリとしてインストールされます。",
    "download.desktop_body": "スマホのカメラでこのQRコードを読み取ってZcardonを開き、スマホのブラウザからインストールしてください。",
    "download.steps_hint": "以下の手順はブラウザ上で行います（このページではありません）。各項目はタップできません — ブラウザで何を探すかを説明しています。",
    "download.ios_arrow_label": "Safariでここをタップ",
    "download.android_arrow_label": "Chromeの上部を確認",
    "install.eyebrow": "Zcardonをインストール",
    "install.title": "Zcardonをホーム画面に追加",
    "install.subtitle": "2回タップするだけで、Zcardonが本物のアプリアイコンに — App Store不要。",
    "install.back_home": "ブラウザでZcardonを開く",
    "install.continue": "Zcardonへ進む →",
    "install.copy": "コピー",
    "install.copied": "コピー済み",
    "install.ios_step1_title": "Safariでこのページを開く",
    "install.ios_step1_body": "別のブラウザからスキャンした場合は、URLをコピーしてSafariで開いてください — iOS版ChromeではアプリをインストールできNません。",
    "install.ios_step2_title": "共有ボタンをタップ",
    "install.ios_step2_body": "上向き矢印の付いた四角アイコンです — Safariの下部（iPadでは右上）にあります。",
    "install.ios_step3_title": "\\\"ホーム画面に追加\\\"をタップ",
    "install.ios_step3_body": "共有シートをスクロールして\\\"ホーム画面に追加\\\"を選び、確認してください。Zcardonがアプリアイコンとして表示されます。",
    "install.ios_arrow_label": "このアイコンを探してください",
    "install.android_ready_title": "インストール準備完了",
    "install.android_ready_body": "ChromeがZcardonをインストール可能として検出しました。以下をタップするとホーム画面にアプリアイコンが追加されます。",
    "install.android_install": "Zcardonをインストール",
    "install.android_installing": "インストール中…",
    "install.android_step1_title": "Chromeのメニューをタップ",
    "install.android_step1_body": "Chrome右上の三点メニューです。",
    "install.android_step2_title": "\\\"アプリをインストール\\\"または\\\"ホーム画面に追加\\\"をタップ",
    "install.android_step2_body": "確認してZcardonアイコンをスマホに追加。いつでもタップして起動できます。",
    "install.android_hint": "\\\"アプリをインストール\\\"が表示されない場合は、ホームページを一度閲覧してください — Chromeが短い訪問後に解除します。",
    "install.desktop_title": "スマホでスキャン",
    "install.desktop_body": "Zcardonはモバイル向けアプリです。スマホでこのURLを開いてインストールしてください：",
    "install.webview_title": "ブラウザで開く",
    "install.webview_body": "アプリ内ブラウザ（Instagram、Facebook、TikTok）ではインストールできません。\\\"…\\\"または共有メニューをタップし、\\\"Safariで開く\\\" / \\\"Chromeで開く\\\"を選択してください。またはこのリンクをコピー：",
    "install.already_title": "すでにアプリが入っています",
    "install.already_body": "このデバイスにZcardonはインストール済みです。ホーム画面のアイコンをタップすればいつでも開けます。",
    "install.installed_title": "インストール完了",
    "install.installed_body": "ホーム画面のZcardonアイコンを探してください — それがあなたのアプリです。",
    "install.steps_hint": "以下の手順はブラウザ上で行います（このページではありません）。各項目はタップできません — ブラウザのメニューで何を探すかを説明しています。",
    "install.desktop_body_qr": "ZcardonはiPhoneまたはAndroidにアプリアイコンとしてインストールされます — デスクトップには対応していません。スマホのカメラをこのQRコードに向けてください：",
    "install.desktop_or": "またはリンクをコピー",
    "install.android_arrow_label": "Chromeの上部を確認",
    "install.ios_arrow_caption": "Safariの最下部にあります",
    "vietqr.disabled.title": "VietQRでVNDを受け取る",
    "vietqr.disabled.badge": "ベトナム限定",
    "vietqr.disabled.desc": "ベトナムの銀行口座を紐付けると、このカードのQRが本物のVietQRになります — あらゆるベトナムの銀行アプリ（Vietcombank、BIDV、MB、Techcombank、ACB、VietinBankなど）でスキャンでき、NAPASネットワーク経由で直接VNDを受け取れます。Stripe Issuingの承認は不要です。",
    "vietqr.disabled.btn_enter": "銀行情報を入力",
    "vietqr.disabled.or": "または",
    "vietqr.disabled.btn_send_link": "受取人にセットアップリンクを送る",
    "vietqr.disabled.btn_send_link_tip": "受取人自身が記入できる7日間有効な公開リンクを発行",
    "vietqr.form.warn_title": "有効化前にお読みください",
    "vietqr.form.warn_body_a": "スキャンはZcardon追跡ページに転送されなくなります。資金は",
    "vietqr.form.warn_body_b": "あなたのベトナム銀行口座に直接",
    "vietqr.form.warn_body_c": "NAPAS銀行間ネットワーク経由で送られます — Zcardonは資金を一切扱いません。VietQRはベトナム国内でのVND送金のみ動作します。",
    "vietqr.form.bank": "銀行",
    "vietqr.form.account_number": "口座番号",
    "vietqr.form.account_name": "口座名義（25文字まで）",
    "vietqr.form.city": "都市（15文字まで）",
    "vietqr.form.advanced": "詳細 — 固定金額 / 摘要（任意）",
    "vietqr.form.amount_vnd": "金額（VND）",
    "vietqr.form.amount_ph": "可変金額の場合は空欄",
    "vietqr.form.reference": "摘要（25文字まで）",
    "vietqr.form.confirm": "このカードのスキャンによりVNDが上記の銀行口座に直接送金されることを理解しています。Zcardonはこれらの資金を処理または保持しません。",
    "vietqr.form.btn_attach": "VietQRを有効化",
    "vietqr.form.btn_attaching": "有効化中…",
    "vietqr.form.btn_cancel": "キャンセル",
    "vietqr.enabled.title": "VietQR有効",
    "vietqr.enabled.desc": "ベトナムの銀行アプリがこのカードをスキャンすると、VNDが下記の口座に直接送金されます — Zcardonを完全にバイパスします。",
    "vietqr.enabled.bank_label": "銀行",
    "vietqr.enabled.account_label": "口座",
    "vietqr.enabled.fixed_amount": "固定金額",
    "vietqr.enabled.reference": "摘要",
    "vietqr.enabled.btn_detach": "VietQRを解除",
    "vietqr.enabled.btn_detaching": "解除中…",
    "vietqr.enabled.attached_at": "有効化日時",
    "vietqr.analytics.live_activity": "リアルタイム活動",
    "vietqr.analytics.today": "今日",
    "vietqr.analytics.days_live": "{n}日稼働",
    "vietqr.analytics.phantom_views": "推定表示数",
    "vietqr.analytics.confirmed_pays": "確認済み支払い",
    "vietqr.analytics.total_vnd": "VND合計",
    "vietqr.analytics.recent_confirmed": "最近の確認",
    "vietqr.analytics.phantom_explainer": "推定表示数 ≈ 有効化以降のカード画像ダウンロード数。実際のVietQRスキャンは当社サーバを経由しないため、「カードが表示された」アクティビティから推定しています。正確なデータについては、受取人にセットアップリンク経由で支払いを確認してもらってください。",
    "vietqr.toast.required": "口座番号と名義は必須です。",
    "vietqr.toast.confirm_first": "VietQRはZcardonをバイパスしてあなたの銀行にVNDを送ることを確認してください。",
    "vietqr.toast.attached_default": "VietQRを有効化しました。",
    "vietqr.toast.attach_failed": "VietQRの有効化に失敗しました。",
    "vietqr.toast.detach_confirm": "VietQRを解除しますか？カードのQRはZcardon追跡URLに戻ります。",
    "vietqr.toast.detached": "VietQRを解除しました — QRはZcardon追跡URLに戻りました。",
    "vietqr.toast.detach_failed": "解除に失敗しました。",
    "vietqr.toast.link_copied": "セットアップリンクをコピーしました · {h}時間で期限切れ",
    "vietqr.toast.link_generated": "セットアップリンクを生成しました · 下記からコピー",
    "vietqr.toast.link_failed": "セットアップリンクの生成に失敗しました。",

    "setpw.toast.too_short": "パスワードは8文字以上必要です",
    "setpw.toast.no_match": "パスワードが一致しません",
    "setpw.toast.success_signin_again": "パスワードを設定しました — 再度サインインしてください",
    "setpw.toast.welcome": "Zcardonエージェントプログラムへようこそ",
    "setpw.toast.expired": "リンクが期限切れまたは無効です",
    "setpw.verifying": "招待を確認中…",
    "setpw.invalid.title": "リンクの有効期限切れ",
    "setpw.invalid.desc": "このウェルカムリンクは期限切れか、すでに使用済みです。新しい招待を受け取るには、Zcardon管理者にお問い合わせください。",
    "setpw.invalid.home_btn": "ホーム",
    "setpw.invalid.support_btn": "サポートに連絡",
    "setpw.fallback_name": "エージェント",
    "setpw.eyebrow": "エージェント歓迎",
    "setpw.heading": "{name}さん、ようこそ",
    "setpw.subheading": "パスワードを設定してエージェントアカウントを有効化してください。すぐにサインインされます。",
    "setpw.code_label": "エージェントコード",
    "setpw.code_tag": "顧客オンボーディング時に共有してください",
    "setpw.form.title": "パスワードを設定",
    "setpw.form.new_label": "新しいパスワード",
    "setpw.form.placeholder": "最低8文字",
    "setpw.form.confirm_label": "パスワードの確認",
    "setpw.form.btn_busy": "有効化中…",
    "setpw.form.btn_submit": "エージェントアカウントを有効化",
    "tx.heading": "取引",
    "tx.kind.direct_deposit": "ダイレクトデポジット",
    "tx.kind.card_topup": "カードチャージ",
    "tx.kind.outbound_payout": "送金",
    "tx.toast.load_failed": "取引の読み込みに失敗しました",
    "tx.summary.deposits": "入金",
    "tx.summary.topups": "チャージ",
    "tx.summary.topups_sub": "カード",
    "tx.summary.payouts": "支払い",
    "tx.summary.payouts_sub": "送金",
    "tx.filter.all": "すべて",
    "tx.filter.deposits": "入金",
    "tx.filter.topups": "チャージ",
    "tx.filter.payouts": "支払い",
    "tx.loading": "取引を読み込み中…",
    "tx.empty.filtered": "該当する取引はありません",
    "tx.empty.none": "取引はまだありません",
    "tx.empty.cta": "ダイレクトデポジットで始める →",
    "tx.open_title": "開く",
    "terms.load_error_default": "規約の読み込みに失敗しました",
    "terms.toast.sign_and_check": "契約に同意するには署名と確認が必要です",
    "terms.toast.recorded": "契約が記録されました",
    "terms.toast.failed": "送信に失敗しました",
    "terms.error.title": "リンクが無効です",
    "terms.back_home": "ホームに戻る",
    "terms.loading": "契約書を読み込み中…",
    "terms.done.title": "契約に同意しました",
    "terms.done.meta": "バージョン {v} · {ts}",
    "terms.done.next": "このページを閉じても構いません。カードは発行次第ご利用いただけます。",
    "terms.eyebrow": "カードホルダー契約 · v{v}",
    "terms.title_reaccept": "カードホルダー契約を再承認する",
    "terms.title_accept": "カードホルダー契約に同意する",
    "terms.desc.sender_sent": "Zcardonカードを送りました",
    "terms.desc.you_were_sent": "Zcardonカードが送られました",
    "terms.desc.ending": "末尾",
    "terms.desc.read_before": "以下の契約書をお読みのうえ、署名・確認してカードを有効化してください。",
    "terms.already_accepted_note": "以前のバージョンにすでに同意済みです。最新版に更新するには再承認してください。",
    "terms.scroll_hint": "ボックス内をスクロールして契約書の全文をお読みください。",
    "terms.view_json": "JSONで開く",
    "terms.sign_label": "法的な氏名でサイン",
    "terms.sign_placeholder": "公的身分証明書に記載の通り",
    "terms.consent": "Zcardon カードホルダー規約 v{v} を読み、同意します。",
    "terms.btn_recording": "録音中…",
    "terms.btn_accept": "規約に同意する",
    "hub.eyebrow": "受け取りハブ",
    "hub.heading.all_done": "準備完了 🎉",
    "hub.heading.greet": "こんにちは、{name} 👋",
    "hub.fallback_name": "こんにちは",
    "hub.desc.sender_sent": "Zcardon カードを送信しました",
    "hub.desc.you_were_sent": "Zcardon カードが送られました",
    "hub.desc.ending": "末尾",
    "hub.desc.complete_steps": "以下の手順を完了してすべてを有効化してください。",
    "hub.progress.label": "{total} 件中 {done} 件完了",
    "hub.blocker.title_one": "必須ステップが残り 1 件あります",
    "hub.blocker.title_many": "必須ステップが残り {n} 件あります",
    "hub.blocker.subtext": "完了するまでカードは完全に有効化されません。",
    "hub.required_pill": "必須",
    "hub.done_pill": "完了",
    "hub.setup_btn": "設定する",
    "hub.refresh": "更新",
    "hub.loading": "読み込み中…",
    "hub.error.title": "リンクの問題",
    "hub.error.default": "受け取りハブを読み込めませんでした。",
    "hub.back_home": "ホームに戻る",
    "hub.footer": "このページをブックマーク · 90 日間有効 · このリンクを持つ人だけが閲覧できます。",
    "onboarding.eyebrow": "振込を受け取る",
    "onboarding.title": "こんにちは、{name}",
    "onboarding.fallback_name": "こんにちは",
    "onboarding.intro_via": "Zcardon 経由",
    "onboarding.intro_ref_open": "（参照番号",
    "onboarding.intro_ref_close": "）",
    "onboarding.country_intro": "下記に {country} の銀行口座を追加して資金を受け取ってください。入力内容は決済パートナーの Stripe に直接送信され、送金者には表示されません。",
    "onboarding.invalid.title": "無効なリンク",
    "onboarding.invalid.hint": "送金者に新しいリンクを送り直してもらってください。",
    "onboarding.loading": "読み込み中…",
    "onboarding.complete.title": "銀行口座情報登録済み",
    "onboarding.complete.desc": "銀行口座はすでに送信済みです。送金者がまもなく資金を送金します。",
    "onboarding.success.title": "銀行口座情報を受信しました ✓",
    "onboarding.success.auto_paid": "ありがとうございます — 銀行口座が確認され、資金はすでに送金されました。1〜2 営業日以内に着金予定です。",
    "onboarding.success.pending": "ありがとうございます — 銀行口座が確認されました。送金者に通知され、1〜2 営業日以内に口座へ着金します。",
    "onboarding.success.ref": "参照番号: {id}",
    "onboarding.beta.title": "ベータプレビュー。",
    "onboarding.beta.body": "このページは公開済みですが、ライブ支払いパイプラインは Stripe の承認待ちです。プラットフォームが有効化されるまで送信するとエラーが返されます。後ほど再確認するか、急ぎの場合はサポートにお問い合わせください。",
    "onboarding.select_placeholder": "選択…",
    "onboarding.submit_error_default": "送信に失敗しました。もう一度お試しください。",
    "onboarding.submit_busy": "送信中…",
    "onboarding.submit": "銀行口座情報を送信 →",
    "onboarding.secure_note": "Stripe により保護されています。このリンクは 7 日で失効します。",
    "onboarding.validate.required": "{field} は必須です",
    "onboarding.validate.format": "{field} の形式が正しくありません（期待値: {pattern}）",
    "onboarding.validate.no_schema": "利用可能なフォームスキーマがありません",
    "apply.toast.invalid_image": "JPEG、PNG、または WEBP 画像を選択してください",
    "apply.toast.too_large": "画像は 6 MB 以下にしてください",
    "apply.toast.upload_both": "運転免許証の表裏両面をアップロードしてください",
    "apply.toast.failed": "送信に失敗しました",
    "apply.toast.submitted": "申請を送信しました",
    "apply.success.title": "申請を受け付けました",
    "apply.success.desc": "ありがとうございます、{name}。2〜3 営業日以内に申請を審査し、次のステップを {email} にメールでお知らせします。",
    "apply.success.back_home": "ホームに戻る",
    "apply.success.contact_support": "サポートに連絡",
    "apply.eyebrow": "エージェントプログラム",
    "apply.heading": "Zcardonエージェントに申し込む",
    "apply.intro": "発行したカード、受け取ったチャージ、顧客から預かった現金入金ごとに0.5%の手数料を獲得できます。地域で信頼されており、本人確認ができる方をエージェントとして登録します。",
    "apply.bullet1": "すべての取引で追跡可能な固有のエージェントコードを取得 · 全取引対応",
    "apply.bullet2": "週次・月次・年次の収益サマリーを表示するダッシュボード",
    "apply.bullet3": "銀行への直接振込 — 毎週支払い",
    "apply.section.your_info": "お客様情報",
    "apply.field.full_name": "氏名（正式名称）",
    "apply.field.full_name_ph": "運転免許証に記載の通り",
    "apply.field.email": "メールアドレス",
    "apply.field.phone": "電話番号",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "運転免許証番号",
    "apply.field.address": "住所",
    "apply.field.address_ph": "番地、市区町村、都道府県、郵便番号",
    "apply.field.why_join_label": "エージェントになりたい理由（任意）",
    "apply.field.why_join_ph": "地域とのつながりや送金業務の経験をお聞かせください",
    "apply.dl.section": "運転免許証の確認",
    "apply.dl.helper": "両面を明るく鮮明に撮影した写真をアップロードしてください。JPEG、PNG、またはWEBP · 各6 MB以内。画像は非公開で保管され、Zcardon管理者のみが本人確認のために閲覧します。",
    "apply.dl.front": "運転免許証（表面）",
    "apply.dl.back": "運転免許証（裏面）",
    "apply.dl.tap_upload": "タップしてアップロード",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "キャンセル",
    "apply.submit_busy": "送信中…",
    "apply.submit": "申し込みを送信",
    "agent.toast.load_failed": "エージェントデータの読み込みに失敗しました",
    "agent.toast.cust_created": "顧客 {name} を作成しました",
    "agent.toast.cust_failed": "顧客の作成に失敗しました",
    "agent.toast.card_issued": "カードを発行しました · {amount} 獲得",
    "agent.toast.card_issue_failed": "カードの発行に失敗しました",
    "agent.toast.cash_accepted": "現金入金を受け付けました · {amount} 獲得",
    "agent.toast.cash_failed": "入金の記録に失敗しました",
    "agent.loading": "読み込み中…",
    "agent.eyebrow": "エージェントポータル",
    "agent.fallback_name": "エージェント",
    "agent.code_line.code_label": "エージェントコード",
    "agent.code_line.commission_label": "手数料率",
    "agent.earnings.label": "未払い残高",
    "agent.earnings.sub": "{n} 件の未払い取引",
    "agent.stat.week": "今週",
    "agent.stat.month": "今月",
    "agent.stat.year": "今年",
    "agent.stat.txns": "{n} 件",
    "agent.stat.customers": "あなたの顧客",
    "agent.stat.active": "アクティブ",
    "agent.customers.title": "あなたの顧客",
    "agent.customers.empty": "顧客がいません。\"顧客を登録\" をタップして最初のユーザーを登録してください。",
    "agent.customers.col_customer": "顧客",
    "agent.customers.col_phone": "電話番号",
    "agent.customers.col_cards": "カード",
    "agent.customers.col_commissions": "獲得手数料",
    "agent.customers.col_joined": "登録日",
    "agent.customers.col_actions": "操作",
    "agent.customers.txns": "{n} 件",
    "agent.commissions.title": "最近の手数料",
    "agent.commissions.empty": "手数料はまだありません。最初のカードを発行して収益を得ましょう。",
    "agent.commissions.col_when": "日時",
    "agent.commissions.col_customer": "顧客",
    "agent.commissions.col_type": "種別",
    "agent.commissions.col_txn": "取引",
    "agent.commissions.col_commission": "手数料",
    "agent.commissions.col_status": "ステータス",
    "agent.commissions.paid": "支払済",
    "agent.commissions.unpaid": "未払い",
    "agent.type.issuance": "発行",
    "agent.type.topup": "チャージ",
    "agent.type.cash_deposit": "現金入金",
    "agent.register_btn": "顧客を登録",
    "agent.dialog.new_cust.title": "新規顧客",
    "agent.dialog.new_cust.desc": "お客様はあなたのエージェントコードに紐付けられます。代理で発行したカード1枚ごとに0.5%の手数料が得られます。",
    "agent.dialog.field.full_name": "氏名",
    "agent.dialog.field.email": "メールアドレス",
    "agent.dialog.field.phone": "電話番号",
    "agent.dialog.field.password": "初期パスワード（顧客に共有）",
    "agent.dialog.cancel": "キャンセル",
    "agent.dialog.creating": "作成中…",
    "agent.dialog.create_customer": "顧客を作成",
    "agent.dialog.issue_card.title": "{name} にカードを発行",
    "agent.dialog.issue_card.desc": "手元で回収した現金から資金調達。顧客は3.75%の手数料を支払い、あなたは0.5%を受け取ります。",
    "agent.dialog.field.receiver_name": "受取人名",
    "agent.dialog.field.receiver_email": "受取人メールアドレス",
    "agent.dialog.field.receiver_phone": "受取人電話番号",
    "agent.dialog.field.amount_usd": "金額（USD）",
    "agent.dialog.field.note": "メモ（任意）",
    "agent.dialog.issuing": "発行中…",
    "agent.dialog.issue_card_btn": "カードを発行",
    "agent.actions.issue_card": "カードを発行",
    "agent.dialog.cash_deposit.title": "{name} への現金入金",
    "agent.dialog.cash_deposit.desc": "顧客から回収した現金 · 受取人に届けられるカードを作成します。",
    "agent.dialog.field.receiver_name_opt": "受取人名（任意）",
    "agent.dialog.recording": "記録中…",
    "agent.dialog.record_deposit": "入金を記録",
    "agent.actions.cash_deposit": "現金入金",

  },
  ko: {
    "header.nav.home": "홈",
    "header.nav.rates": "요금",
    "header.nav.support": "지원",
    "header.nav.admin": "관리자",
    "header.nav.signin": "로그인",
    "header.nav.signout": "로그아웃",
    "header.nav.get_started": "시작하기",
    "header.nav.account": "계정",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "언어 변경",
    "landing.hero.eyebrow": "디지털 지출을 위한 새로운 방식",
    "landing.hero.title_a": "가상 카드를 충전하세요.",
    "landing.hero.title_b_ship": "전송",
    "landing.hero.title_b_rest": "하세요, 누구에게나,",
    "landing.hero.title_c": "바로 사용 가능,",
    "landing.hero.subtitle": "스캔 가능한 QR이 포함된 탭 결제 가상 직불카드를 발급하세요. Stripe 기반 결제, USD 잔액, 투명한 서비스 및 게이트웨이 수수료 — 이메일 또는 다운로드로 수신자에게 전달됩니다.",
    "landing.hero.cta_start": "카드 발급 시작",
    "landing.hero.cta_request_cash": "현금 카드 요청",
    "landing.hero.cta_rates": "요금 보기",
    "landing.hero.cta_find_card": "기존 카드 찾기",
    "landing.hero.stat_currency": "통화",
    "landing.hero.stat_settle": "정산 시간",
    "landing.hero.stat_settle_value": "~2분",
    "landing.hero.stat_service_fee": "서비스 수수료",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "현대 카드 경제를 위해 구축됨",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "USD로 정산",
    "landing.hero.marquee": "발급 · 충전 · USD로 결제",
    "landing.demo.eyebrow": "20초 만에 확인하세요",
    "landing.demo.title_a": "발급부터",
    "landing.demo.title_b_spend": "결제",
    "landing.demo.title_c": "까지. 네 번의 탭으로.",
    "landing.demo.step_issue.label": "카드 발급",
    "landing.demo.step_issue.detail": "금액을 선택하세요. 약 2분 내에 카드가 준비됩니다.",
    "landing.demo.step_share.label": "공유하기",
    "landing.demo.step_share.detail": "이메일, 문자, WhatsApp 또는 PNG로 다운로드.",
    "landing.demo.step_scan.label": "수신자 스캔",
    "landing.demo.step_scan.detail": "카메라로 스캔하면 즉시 트랙 페이지가 열립니다.",
    "landing.demo.step_spend.label": "USD로 결제",
    "landing.demo.step_spend.detail": "탭 결제, 현금 인출, 가맹점 결제.",
    "landing.demo.cta_try": "지금 시도하기",
    "landing.demo.live_pill": "라이브 데모",
    "landing.demo.scene1_caption": "카드 생성 중 · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "WhatsApp으로 수신자에게 전송 중",
    "landing.demo.scene3_caption": "QR 스캔 중 · 일치 항목 발견",
    "landing.demo.scene4_eyebrow": "잔액 입금 완료",
    "landing.demo.scene4_status": "✓ 승인됨 · 탭 결제 준비 완료",
    "landing.footer.rates": "요금",
    "landing.footer.support": "지원",
    "landing.footer.admin_portal": "관리자 포털",
    "landing.footer.copy": "© 2026 Zcardon · 데모 핀테크 제품",
    "landing.cash.dialog_title": "현금 카드 요청",
    "landing.cash.dialog_desc": "Stripe 결제 링크를 문자로 보내드립니다. 원하는 카드로 결제하시면 Stripe 확인 즉시 현금 카드가 충전됩니다.",
    "landing.cash.phone_label": "휴대폰 번호",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "금액 (USD)",
    "landing.cash.name_label": "이름 (선택 사항)",
    "landing.cash.submit": "결제 링크 전송",
    "landing.cash.submitting": "전송 중…",
    "landing.cash.success_title": "결제 링크가 전송되었습니다",
    "landing.cash.success_body": "{phone}으로 Stripe 결제 링크를 보냈습니다. 링크를 탭하여 결제하시면 가상 카드가 즉시 활성화됩니다.",
    "landing.cash.success_open_checkout": "지금 결제하기",
    "landing.find.cta": "이미 결제하셨나요? 카드 찾기",
    "landing.find.dialog_title": "기존 카드 찾기",
    "landing.find.dialog_desc": "사용하신 휴대폰 번호를 입력하세요. 등록된 활성 현금 카드를 복구할 수 있도록 6자리 코드를 문자로 보내드립니다.",
    "landing.find.phone_label": "휴대폰 번호",
    "landing.find.code_label": "6자리 코드",
    "landing.find.send_code": "코드 전송",
    "landing.find.verify": "확인 후 찾기",
    "landing.find.sending": "전송 중…",
    "landing.find.verifying": "확인 중…",
    "landing.find.no_cards": "해당 번호로 등록된 활성 카드가 없습니다.",
    "login.eyebrow.welcome": "환영합니다",
    "login.eyebrow.admin": "관리자",
    "login.title": "로그인",
    "login.title_admin": "관리자 로그인",
    "login.admin_badge": "관리자 포털",
    "login.email_label": "이메일",
    "login.password_label": "비밀번호",
    "login.submit": "로그인",
    "login.submitting": "로그인 중…",
    "login.toast_welcome": "{name}님, 환영합니다",
    "login.toast_failed": "로그인 실패",
    "login.gate_title": "고객 로그인이 일시적으로 중단되었습니다",
    "login.gate_default_message": "플랫폼을 개선하고 있습니다. 잠시 후 다시 확인해 주세요.",
    "login.gate_admin_note": "관리자와 매니저는 아래에서 로그인할 수 있습니다.",
    "login.new_here": "처음이신가요?",
    "login.create_account": "계정 만들기",
    "login.customer_q": "고객이신가요?",
    "login.regular_signin": "일반 로그인",
    "login.demo_title": "데모 계정",
    "login.demo_customers_note": "고객: /register를 통해 등록하거나 관리자 패널에서 생성하세요",
    "login.forgot_password": "비밀번호를 잊으셨나요?",
    "forgot.eyebrow": "계정 복구",
    "forgot.title": "비밀번호 재설정",
    "forgot.subtitle": "Zcardon 계정에 등록된 이메일을 입력하시면 새 비밀번호 설정을 위한 보안 링크를 보내드립니다.",
    "forgot.email_label": "이메일",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "재설정 링크 보내기",
    "forgot.submitting": "전송 중…",
    "forgot.toast_failed": "재설정 링크를 보낼 수 없습니다. 다시 시도해 주세요.",
    "forgot.sent_title": "받은 편지함을 확인하세요",
    "forgot.sent_message": "{email}로 등록된 계정이 있으면 비밀번호 재설정 링크를 보내드렸습니다.",
    "forgot.sent_expiry_note": "링크는 15분간 유효합니다. 받지 못하셨나요? 스팸함을 확인하거나 다시 전송하세요.",
    "forgot.back_to_login": "로그인으로 돌아가기",
    "forgot.resend_link": "링크 다시 보내기",
    "forgot.remember_q": "비밀번호가 기억나시나요?",
    "forgot.signin_link": "로그인",
    "reset.eyebrow": "계정 복구",
    "reset.title": "새 비밀번호 설정",
    "reset.subtitle": "이전에 사용하지 않은 강력한 비밀번호를 선택하세요. 설정 후 자동으로 로그인됩니다.",
    "reset.verifying": "재설정 링크를 확인하는 중…",
    "reset.invalid_title": "이 재설정 링크는 유효하지 않거나 만료되었습니다",
    "reset.invalid_message": "재설정 링크는 15분간 유효하며 한 번만 사용할 수 있습니다. 계속하려면 새 링크를 요청하세요.",
    "reset.request_new_link": "새 재설정 링크 요청",
    "reset.new_password_label": "새 비밀번호",
    "reset.confirm_password_label": "비밀번호 확인",
    "reset.password_hint": "최소 8자 이상. 다른 곳에서 사용한 비밀번호는 피하세요.",
    "reset.submit": "비밀번호 변경",
    "reset.submitting": "변경 중…",
    "reset.toast_success": "비밀번호가 변경되었습니다. 로그인되었습니다.",
    "reset.toast_failed": "비밀번호를 재설정할 수 없습니다. 다시 시도해 주세요.",
    "reset.error_min_length": "비밀번호는 최소 8자 이상이어야 합니다.",
    "reset.error_mismatch": "비밀번호가 일치하지 않습니다.",
    "register.eyebrow": "가입",
    "register.title": "계정 만들기",
    "register.full_name_label": "이름",
    "register.email_label": "이메일",
    "register.password_label": "비밀번호",
    "register.submit": "계정 만들기",
    "register.submitting": "생성 중…",
    "register.gate_title": "신규 가입이 일시적으로 중단되었습니다",
    "register.gate_default_message": "플랫폼을 개선하고 있습니다. 잠시 후 다시 확인해 주세요.",
    "register.gate_already_member": "이미 계정이 있으신가요?",
    "register.toast_created": "계정이 생성되었습니다",
    "register.toast_failed": "가입 실패",
    "register.already_member": "이미 회원이신가요?",
    "register.signin_link": "로그인",
    "dash.eyebrow": "대시보드",
    "dash.greeting": "{name}님, 환영합니다",
    "dash.subtitle": "가상 카드를 발급하거나 전신 송금을 보내세요 — 아래에서 모든 것을 관리할 수 있습니다.",
    "dash.action.issue_card": "카드 발급",
    "dash.action.direct_deposit": "직접 입금 (전신 송금)",
    "dash.action.support": "고객 지원",
    "dash.section.cards": "내 카드",
    "dash.section.activity": "최근 활동",
    "dash.section.deposits": "직접 입금",
    "dash.empty.cards": "카드가 없습니다 — 첫 번째 카드를 발급하여 시작하세요.",
    "dash.empty.activity": "카드를 발급하거나 전신 송금을 보내면 최근 활동이 여기에 표시됩니다.",
    "ref.eyebrow": "Zcardon에 오신 것을 환영합니다",
    "ref.greeting": "{name}님이 초대했습니다.",
    "ref.loading": "초대 정보를 불러오는 중…",
    "ref.resolved_redirect": "잠시만요 — 이동 중입니다.",
    "ref.resolving": "초대 링크를 확인하는 중…",
    "support.eyebrow": "도움이 필요하신가요",
    "support.title": "티켓 없는 고객 지원.",
    "support.subtitle": "실제 사람과 대화하세요. 평일 한 시간 이내에 이메일로 답변드립니다.",
    "support.card.email": "이메일",
    "support.card.phone": "전화",
    "support.card.live_chat": "실시간 채팅",
    "support.card.live_chat_value": "대시보드에서",
    "support.card.hours": "운영 시간",
    "support.faq.title": "자주 묻는 질문",
    "support.faq.q1": "수신자가 카드를 받기까지 얼마나 걸리나요?",
    "support.faq.a1": "충전 성공 후 보통 2분 이내입니다. 이메일(Resend)과 MMS(Twilio)를 통해 자동으로 전달됩니다.",
    "support.faq.q2": "카드를 전송한 후 취소하거나 동결할 수 있나요?",
    "support.faq.a2": "네 — 카드 상세 페이지를 열고 동결을 탭하세요. 수신자 알림 이메일에서 바로 동결할 수도 있습니다.",
    "support.faq.q3": "카드로 어떤 통화를 사용할 수 있나요?",
    "support.faq.a3": "모든 카드는 USD로 정산됩니다. 수신자는 Visa/Mastercard가 허용되는 곳이라면 어디서든 사용할 수 있으며, 환전은 카드 네트워크에서 처리됩니다.",
    "support.faq.q4": "수수료는 어떻게 되나요?",
    "support.faq.a4": "투명한 서비스 수수료와 Stripe 게이트웨이 비용이 적용됩니다. 결제 전 요금 페이지에서 실시간 견적을 확인하세요.",
    "rates.eyebrow": "투명한",
    "rates.title_a": "숨겨진",
    "rates.title_b": "스프레드 없음",
    "rates.subtitle": "결제 전에 서비스 수수료와 게이트웨이 비용을 확인할 수 있습니다. 원금은 항상 USD로 가상 카드 잔액에 충전됩니다.",
    "rates.row.service_fee": "서비스 수수료",
    "rates.row.service_fee_value": "송금액의 {pct}",
    "rates.row.gateway": "게이트웨이 (Stripe)",
    "rates.row.settlement": "정산",
    "rates.row.settlement_value": "가상 카드의 USD 잔액",
    "rates.row.topup": "충전 방법",
    "rates.row.topup_value": "카드 · 체크카드 · 은행 (ACH)",
    "rates.sim.eyebrow": "실시간 견적",
    "rates.sim.title_a": "얼마든지 보내고,",
    "rates.sim.title_b": "즉시 내역을 확인하세요.",
    "rates.sim.amount_label": "송금액",
    "rates.sim.line.principal": "카드 수령액 (원금)",
    "rates.sim.line.service": "서비스 수수료 · {pct}",
    "rates.sim.line.gateway": "게이트웨이 · {pct} + {fixed}",
    "rates.sim.line.you_pay": "총 결제액",
    "rates.sim.cta": "${amount}로 시작하기 →",
    "rates.sim.note": "위 요율은 관리자가 설정합니다. 수신자는 항상 전액 원금을 받습니다.",
    "dd.title": "직접 송금",
    "dd.intro": "전 세계 수신자의 은행 계좌로 송금하세요. 카드(Stripe Checkout) 또는 Zcardon 잔액으로 결제하면, 지원되는 국가는 자동으로, 아직 자동 라우팅이 안 되는 국가는 수동으로 수신자 계좌에 이체해 드립니다. 모든 거래는 출력 가능한 영수증과 함께 기록됩니다.",
    "dd.new_btn": "새 직접 송금 등록",
    "dd.close": "닫기",
    "dd.history_eyebrow": "송금 내역",
    "dd.loading": "불러오는 중…",
    "dd.empty": "아직 직접 송금 내역이 없습니다.",
    "dd.status.approved": "승인됨",
    "dd.status.pending": "승인 대기 중",
    "dd.status.rejected": "거절됨",
    "dd.row.confirmed": "✓ 수신자가 수령을 확인했습니다",
    "dd.row.disputed": "⚠ 수신자가 불일치를 신고했습니다",
    "dd.row.pdf": "PDF",
    "dd.error.load": "송금 내역을 불러올 수 없습니다",
    "dd.error.pdf": "영수증을 다운로드할 수 없습니다",
    "pay.mode.view": "내 Zcardon 카드",
    "pay.mode.charge": "QR로 결제",
    "pay.heading.view": "안녕하세요, {name}.",
    "pay.heading.charge": "이 카드로 결제합니다.",
    "pay.cardholder": "카드 소유자",
    "pay.paused.label": "카드가 일시 정지됨",
    "pay.paused.body": "이 카드는 동결되어 소유자가 재개할 때까지 새로운 결제를 받을 수 없습니다.",
    "pay.balance.label": "사용 가능 잔액",
    "pay.charges_count": "{count}건 결제",
    "pay.charges_count_plural": "{count}건 결제",
    "pay.topups_count": "{count}회 충전",
    "pay.topups_count_plural": "{count}회 충전",
    "pay.activity.label": "최근 활동",
    "pay.activity.empty": "아직 활동 내역이 없습니다",
    "pay.cta.charge": "이 카드로 결제 (가맹점)",
    "pay.cta.charge_paused": "카드가 일시 정지됨",
    "pay.cta.note_active": "이 카드로 결제를 받는 가맹점인 경우에만 위 버튼을 탭하세요.",
    "pay.cta.note_paused": "이 카드는 송신자가 일시 정지했습니다. 송신자에게 재개를 요청하세요.",
    "pay.back_to_balance": "잔액으로 돌아가기",
    "pay.form.amount_label": "금액 (USD)",
    "pay.form.amount_max": "최대 {amount}",
    "pay.form.merchant_label": "가맹점 / 사유",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "메모 (선택)",
    "pay.form.memo_placeholder": "라떼 두 잔",
    "pay.form.submit_processing": "처리 중…",
    "pay.form.submit_paused": "카드가 일시 정지됨",
    "pay.form.submit_charge": "{amount} 결제",
    "pay.form.submit_charge_card": "카드 결제",
    "pay.form.disclaimer": "이 카드를 결제에 사용함으로써 카드 소유자로부터 위 금액 지출을 승인받았음을 확인합니다.",
    "pay.receipt.title": "결제 완료",
    "pay.receipt.to": "받는 사람",
    "pay.receipt.id": "영수증",
    "pay.receipt.time": "시간",
    "pay.receipt.new_balance": "새 잔액",
    "pay.receipt.done": "완료 · 잔액으로 돌아가기",
    "pay.error.cant_load": "이 카드를 불러올 수 없습니다",
    "pay.error.back_home": "홈으로 돌아가기",
    "pay.error.loading": "카드 불러오는 중…",
    "pay.error.charge_failed": "결제 실패",
    "pay.tap.eyebrow": "탭하여 결제",
    "pay.tap.title": "이 코드를 보여주세요",
    "pay.tap.subtitle": "가맹점 스캐너에",
    "pay.tap.balance": "잔액",
    "pay.tap.paused": "일시 중지됨",
    "pay.tap.qr_unavailable": "이 카드는 QR을 사용할 수 없습니다",
    "pay.tap.refresh_in": "{secs}초 후 갱신",
    "pay.tap.show_digits": "숫자 표시",
    "pay.tap.hide_digits": "숫자 숨기기",
    "pay.tap.card_label": "카드 · ••••",
    "pay.tap.auto_hides": "60초 후 자동 숨김",
    "pay.tap.full_only_image": "전체 번호는 원본 카드 이미지에서만 확인할 수 있습니다",
    "pay.tap.disclaimer": "폐쇄형 카드 · Zcardon 가맹점 어디서나 사용 가능. QR은 재사용 방지를 위해 60초마다 갱신됩니다.",
    "pay.subscribe.eyebrow": "최신 정보 받기",
    "pay.subscribe.title": "모든 거래 시 이메일 알림 받기",
    "pay.subscribe.desc": "무료. 결제 또는 충전 시 이메일 1건 발송. 언제든지 구독 해지 가능. 주소는 절대 공유되지 않습니다.",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "알림 받기",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "구독 완료",
    "pay.subscribe.done.body": "카드 •••• {last4}의 모든 결제 또는 충전 시 {email}로 즉시 이메일을 보내드립니다.",
    "pay.subscribe.toast.already": "이미 구독 중입니다 — 중복 발송되지 않았습니다.",
    "pay.subscribe.toast.success": "모든 거래 시 이메일을 받게 됩니다. 환영합니다!",
    "pay.subscribe.toast.failed": "구독 실패",
    "pay.save.eyebrow": "카드 휴대하기",
    "pay.save.title": "이 카드를 휴대폰에 저장",
    "pay.save.desc": "카드 이미지를 사진에 저장하여 오프라인에서도 사용하세요 — 앱 없이 언제든지 가맹점에 QR을 보여주세요.",
    "pay.save.cta": "카드 이미지 저장",
    "pay.save.preparing": "준비 중…",
    "pay.save.add_home": "홈 화면에 추가",
    "pay.save.toast.opened": "카드 이미지가 열렸습니다 — 길게 눌러 사진에 저장하세요",
    "pay.save.toast.failed": "카드를 저장할 수 없습니다",
    "pay.save.ios.title": "iPhone 홈 화면에 Zcardon을 추가하려면:",
    "pay.save.ios.step1": "Safari 하단의 공유 버튼을 탭하세요",
    "pay.save.ios.step2": "아래로 스크롤하여 홈 화면에 추가를 탭하세요",
    "pay.save.ios.step3": "추가를 탭하여 확인하세요",
    "pay.save.ios.note": "Zcardon이 일반 앱처럼 표시됩니다 — 아이콘을 탭하면 이 카드가 바로 열립니다.",
    "pay.thanks.eyebrow": "감사 인사 보내기",
    "pay.thanks.body": "${amount}이 도착했습니다 — 이 카드를 충전해 준 분께 도움이 됐다고 알려주세요.",
    "pay.thanks.placeholder": "선택 사항 — 한 마디 남겨보세요…",
    "pay.thanks.cta": "{emoji} 감사 인사 보내기",
    "pay.thanks.sending": "전송 중…",
    "pay.thanks.done": "감사 인사가 전송되었습니다 — 곧 확인하실 수 있습니다.",
    "pay.thanks.toast.sent": "감사 인사 전송 완료",
    "pay.thanks.toast.failed": "감사 인사를 전송할 수 없습니다",
    "pay.activity.topup_bank": "계좌 이체 충전",
    "pay.activity.topup_debit": "체크카드 충전",
    "pay.activity.topup_credit": "신용카드 충전",
    "pay.activity.topup_crypto": "암호화폐 충전",
    "pay.activity.topup_received": "충전 완료",
    "pay.activity.merchant_charge": "가맹점 결제",
    "pay.toast.payment_received": "결제 수신 · −{amount}",
    "cash.confirming": "결제 확인 중…",
    "cash.confirming.sub": "Stripe에서 결제를 처리하고 있습니다.",
    "cash.notfound.title": "링크 만료됨",
    "cash.notfound.body": "이 현금 요청을 찾을 수 없습니다. 이전 세션의 링크이거나 카드가 완료되지 않은 것일 수 있습니다. 새 요청을 시작하세요 — 1분이면 충분하며, 이미 결제하신 금액은 백그라운드 정산 시 자동으로 반영됩니다.",
    "cash.notfound.ref": "참조:",
    "cash.notfound.start": "새 요청 시작",
    "cash.notfound.contact": "고객 지원 문의",
    "cash.error.title": "상태를 확인할 수 없습니다",
    "cash.error.return": "홈으로 돌아가기",
    "cash.autopay.opening": "탭 투 페이 열기…",
    "cash.autopay.redirect": "{secs}초 후 이동합니다.",
    "cash.autopay.stay": "여기에 머물기",
    "cash.ready.title": "카드가 준비되었습니다",
    "cash.ready.subtitle": "${amount} USD 충전 완료 · {phone}으로 문자 발송됨",
    "cash.card.label": "카드",
    "cash.card.exp": "유효기간",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "잔액",
    "cash.cta.open_pay": "탭 투 페이 화면 열기",
    "cash.toast.funded": "카드가 충전되었습니다 — SMS를 확인하세요!",
    "cash.pending.title": "Stripe 확인 대기 중",
    "cash.pending.body": "결제 확인을 기다리고 있습니다. 보통 5~15초 정도 걸립니다. 결제 없이 창을 닫으셨다면 홈페이지에서 다시 시작할 수 있습니다.",
    "cash.pending.back": "홈으로 돌아가기",
    "cash.timeout.title": "결제를 확인할 수 없습니다",
    "cash.timeout.body": "Stripe 확인을 3분 동안 기다렸습니다. 대부분의 경우 결제 전에 창을 닫은 것을 의미합니다 — 하지만 실제로 결제하셨다면 카드는 몇 분 내에 자동으로 활성화되며(백그라운드 정산은 5분마다 실행), 활성화 즉시 {phone}으로 문자를 보내드립니다.",
    "cash.timeout.start_over": "처음부터 다시 시작",
    "cash.timeout.try_again": "다시 시도",
    "cash.resend.title": "못 받으셨나요? 다시 보내기",
    "cash.resend.degraded.lead": "현재 미국 번호로의 전송이 원활하지 않습니다.",
    "cash.resend.degraded.sms": "미국 전화로의 문자가 통신사에 의해 차단될 수 있습니다(수신자 부담 번호 인증 대기 중).",
    "cash.resend.degraded.email": "이메일은 테스트 모드로, DNS 인증 중에는 계정 소유자 주소로만 전송됩니다.",
    "cash.resend.degraded.tail": "채널이 작동하지 않으면 다른 방법을 이용하거나, 수신자 기기에서 이 페이지를 열어 카드를 직접 확인하세요.",
    "cash.resend.text": "문자",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "이메일",
    "cash.resend.sending": "전송 중…",
    "cash.resend.email_sandbox": "이메일은 테스트 모드입니다. zcardon.com 도메인이 Resend에서 인증되는 동안 계정 소유자 주소로만 전송할 수 있습니다. 이메일이 수락되지 않으면 문자 또는 WhatsApp을 이용하세요.",
    "cash.resend.email_label": "이메일 주소",
    "cash.resend.email_send": "이메일로 보내기",
    "cash.resend.cancel": "취소",
    "cash.resend.limit": "카드당 최대 5회 재전송. 등록된 전화번호: {phone}",
    "cash.resend.no_phone": "(없음)",
    "cash.wallet.title": "지갑에 추가",
    "cash.wallet.apple": "Apple Wallet에 추가",
    "cash.wallet.google": "Google Wallet에 추가",
    "cash.wallet.generating": "패스 생성 중…",
    "cash.wallet.note": "잠금 화면에 유지 · 잔액 변경 시 자동 업데이트",
    "cash.wallet.toast.apple": "패스 다운로드 완료 — iPhone에서 열어 Apple Wallet에 추가하세요",
    "cash.wallet.toast.apple_failed": "Apple Wallet 패스를 생성할 수 없습니다",
    "cash.wallet.toast.google_failed": "Google Wallet 패스를 생성할 수 없습니다",
    "cash.delivery.fallback_title": "{fallback}(으)로 전송됨",
    "cash.delivery.fallback_body": "통신사가 기존 SMS를 차단하여 {fallback}을 통해 {phone}으로 카드를 재전송했습니다. 해당 채널을 확인하세요.",
    "cash.delivery.toll_blocked": "통신사가 SMS를 차단했습니다",
    "cash.delivery.failed_title": "카드 문자 전송에 실패했습니다",
    "cash.delivery.toll_body": "통신사가 메시지를 거부했습니다(인증 대기 중인 신규 발신자에게 발생할 수 있습니다). 지금 카드를 저장하세요: 탭",
    "cash.delivery.failed_body": "카드가 충전되었습니다 — SMS 전송에 실패했으니 지금 저장하세요. 탭",
    "cash.delivery.cta_label": "탭 투 페이 화면 열기",
    "cash.delivery.body_tail": "위를 탭하거나 스크린샷을 찍거나, 아래 이메일로 사본을 보내세요.",
    "cash.handoff.eyebrow": "직접 전달",
    "cash.handoff.body": "수신자가 카메라로 이것을 스캔하면 SMS나 이메일 없이 바로 카드가 열립니다.",
    "cash.handoff.share_label": "또는 이 링크 공유",
    "cash.handoff.copy": "링크 복사",
    "cash.handoff.copied": "✓ 복사됨",
    "cash.handoff.share": "공유",
    "cash.handoff.toast.copied": "링크가 복사되었습니다 — 어디든 붙여넣기 하세요",
    "cash.handoff.toast.copy_failed": "복사할 수 없습니다. 링크를 길게 눌러 직접 복사하세요.",
    "portal.loading": "영수증을 불러오는 중…",
    "portal.unavailable.title": "링크를 사용할 수 없습니다",
    "portal.unavailable.body": "이 링크는 유효하지 않거나 만료되었습니다.",
    "portal.unavailable.hint": "발신자가 이 링크를 공유했다면 영수증 이메일을 다시 보내달라고 요청하세요.",
    "portal.brand": "영수증 포털",
    "portal.eyebrow": "{sender}님이 보냈습니다",
    "portal.reference": "참조",
    "portal.pending.title": "승인 대기 중",
    "portal.pending.body": "발신자가 이 이체를 기록했으나 Zcardon에서 검토 중입니다. 잠시 후 다시 확인하세요 — 승인되면 영수증을 볼 수 있습니다.",
    "portal.row.bank": "은행",
    "portal.row.account": "계좌",
    "portal.row.routing": "라우팅 번호",
    "portal.row.memo": "메모",
    "portal.confirmed.title": "수령을 확인했습니다",
    "portal.confirmed.body": "{when}에 확인되었습니다. 발신자가 확인할 수 있습니다.",
    "portal.disputed.title": "불일치를 신고했습니다",
    "portal.disputed.body": "{when}에 신고되었습니다. 발신자에게 이메일이 발송되었습니다.",
    "portal.actions.body": "이 금액이 계좌에 입금되었나요? 확인하면 발신자와의 처리가 완료됩니다 — Zcardon을 통해 이동하는 돈은 없으며, 이것은 기록일 뿐입니다.",
    "portal.actions.confirm": "네, 받았습니다",
    "portal.actions.flag": "문제 신고",
    "portal.dispute.placeholder": "금액 오류, 미도착, 잘못된 계좌번호…",
    "portal.dispute.submit": "신고 제출",
    "portal.dispute.too_short": "간단한 이유를 입력하세요(최소 4자)",
    "portal.toast.confirmed": "감사합니다 — 수령이 확인되었습니다.",
    "portal.toast.confirm_failed": "수령을 확인할 수 없습니다",
    "portal.toast.reported": "신고되었습니다. 발신자에게 알림이 전송되었습니다.",
    "portal.toast.report_failed": "이의 제기를 제출할 수 없습니다",
    "portal.toast.pdf_failed": "영수증을 다운로드할 수 없습니다",
    "portal.download_pdf": "PDF 영수증 다운로드",
    "portal.privacy": "이 페이지는 본인만 볼 수 있습니다. 링크를 가진 누구나 이 영수증을 볼 수 있으니 공개적으로 공유하지 마세요. 문의:",
    "detail.back": "대시보드로 돌아가기",
    "detail.eyebrow": "내 카드",
    "detail.confirming_payment": "결제를 확인하는 중…",
    "detail.active_ready": "카드가 활성화되어 공유할 준비가 되었습니다.",
    "detail.balance.label": "현재 잔액",
    "detail.toast.notfound": "카드를 찾을 수 없습니다",
    "detail.toast.loaded": "카드에 금액이 충전되었습니다.",
    "detail.toast.confirmed": "결제가 확인되었습니다.",
    "detail.toast.expired": "결제 세션이 만료되었습니다",
    "detail.delivery.receiver_fallback": "수신자",
    "detail.delivery.received_title": "{name}님이 카드를 열었습니다",
    "detail.delivery.received_when": "수령 확인 · {when}",
    "detail.delivery.waiting_title_on": "수신자가 받는 즉시 이메일로 알려드립니다",
    "detail.delivery.waiting_title_off": "배송 알림이 꺼져 있습니다",
    "detail.delivery.waiting_body_on": "수신자가 이 카드의 QR을 스캔하는 즉시 이메일로 한 번 알려드립니다.",
    "detail.delivery.waiting_body_off": "수신자가 카드를 처음 열 때 이메일을 한 번 받으려면 이 기능을 켜세요.",
    "detail.delivery.toggle_on": "켜짐",
    "detail.delivery.toggle_off": "켜기",
    "detail.delivery.toast_enabled": "카드가 열리면 알림을 받습니다.",
    "detail.delivery.toast_disabled": "배송 알림이 꺼졌습니다.",
    "detail.delivery.toast_failed": "배송 알림을 업데이트할 수 없습니다.",
    "nudge.eyebrow": "수신자에게 알리기",
    "nudge.title": "{receiver}님에게 미리 알려주세요",
    "nudge.subtitle": "{receiver}님이 카드가 활성화된 사실과 사용 방법을 알 수 있도록 짧은 메시지를 작성했습니다.",
    "nudge.receiver_fallback": "수신자",
    "nudge.sender_fallback": "친구",
    "nudge.template": "안녕하세요 {receiver}님 — {sender}님이 {amount}이 충전된 Zcardon 가상 카드를 보냈습니다. 여기서 탭하여 결제하세요: {url}\\n\\n앱이 필요 없습니다. 링크는 모든 기기에서 작동합니다.",
    "nudge.email_subject": "{sender}님이 {amount} Zcardon 카드를 보냈습니다",
    "nudge.action_copy": "메시지 복사",
    "nudge.action_sms": "SMS 열기",
    "nudge.action_email": "이메일 열기",
    "nudge.toast_copied": "메시지가 클립보드에 복사되었습니다.",
    "nudge.toast_copy_failed": "복사할 수 없습니다. 직접 선택하여 복사해 주세요.",
    "ios_install.title": "iPhone에 Zcardon 설치하기",
    "ios_install.step_1": "탭하세요",
    "ios_install.share_label": "공유",
    "ios_install.step_2": " 그런 다음 \\\"홈 화면에 추가\\\"를 선택하세요 — Zcardon이 전체 화면 앱으로 열립니다.",
    "smart_banner.title": "Zcardon 앱에서 열기",
    "smart_banner.subtitle": "더 빠른 결제. 앱을 벗어나지 않고 탭하여 결제하세요.",
    "smart_banner.cta": "열기",
    "smart_banner.dismiss": "닫기",
    "social_proof.eyebrow": "실시간 · 최근 48시간",
    "social_proof.empty": "다음 카드 도착을 기다리는 중…",
    "social_proof.line_opened": "{name}님이 {amount} 카드를 열었습니다.",
    "social_proof.line_issued": "{name}님이 친구로부터 {amount} 카드를 받았습니다.",
    "social_proof.line_deposit": "{name}님이 {amount} 직접 입금을 받았습니다.",
    "social_proof.stat_funded": "오늘 충전된 카드",
    "social_proof.stat_delivered": "오늘 전달된 카드",
    "social_proof.stat_recent": "최근 활동",
    "social_proof.ago_seconds": "{n}초 전",
    "social_proof.ago_minutes": "{n}분 전",
    "social_proof.ago_hours": "{n}시간 전",
    "flight.eyebrow": "이동 중인 카드 · 최근 48시간",
    "flight.title": "Zcardon이 도착하는 곳",
    "flight.subtitle": "실제 수신자 위치를 지역 단위로 익명화했습니다. 국가 및 미국 주만 표시되며 이름, 주소, 금액은 절대 표시되지 않습니다.",
    "flight.row_template": "{label}에서 카드가 열렸습니다",
    "flight.more_count": "+ {n}개 지역 더 보기",
    "flight.ago_seconds": "{n}초 전",
    "flight.ago_minutes": "{n}분 전",
    "flight.ago_hours": "{n}시간 전",
    "trust_strip.eyebrow": "실제 데이터 · 데이터베이스 실시간 조회",
    "trust_strip.title_a": "추천사가 아닙니다.",
    "trust_strip.title_b": "실시간 데이터베이스 조회입니다.",
    "trust_strip.how_link": "수집 방법 보기",
    "trust_strip.modal_eyebrow": "데이터 출처",
    "trust_strip.modal_title": "모두 실제, 모두 익명화됨",
    "trust_strip.modal_close": "닫기",
    "trust_strip.modal_intro": "이 스트립의 모든 수치는 자체 데이터베이스에서 가져오며 — 서드파티 피드나 임의 데이터가 아닙니다. 각 섹션의 출처와 익명화 방식을 안내합니다.",
    "trust_strip.src_carousel_title": "실시간 활동 캐러셀",
    "trust_strip.src_carousel_body": "최근 48시간의 cards 및 direct_deposits 컬렉션에서 가져옵니다. 이름은 이름 + 성 이니셜로 표시되며, 금액은 삼각측량 방지를 위해 $5 단위로 반올림됩니다. 60초마다 새로 고침됩니다.",
    "trust_strip.src_flight_title": "Zcardon이 도착하는 곳",
    "trust_strip.src_flight_body": "card_scans에서 QR 스캔 위치를 읽어 스캔 시점에 지역을 확인합니다. 국가(또는 미국 주) 단위로 집계됩니다. 주 이하의 도시 정보, IP 주소, 카드 식별자는 표시되지 않습니다. 90초마다 새로 고침됩니다.",
    "trust_strip.src_badges_title": "30일 신뢰 지표",
    "trust_strip.src_badges_body": "순수 집계 수치만 표시되며 행별 데이터는 포함되지 않습니다. 각 지표는 최소 샘플 수를 초과한 경우에만 표시되므로, 2건의 확인에서 100%인 경우는 숨겨집니다. 1분마다 새로 고침됩니다.",
    "trust_strip.modal_promise": "이름, 주소, 정확한 금액, IP 주소, 카드 번호는 공개 위젯에 절대 게시하지 않습니다. 약속합니다.",
    "trust_badge.eyebrow": "실제 수치 · 최근 30일",
    "trust_badge.title": "실제로 사용되고 있습니다.",
    "trust_badge.subtitle": "추천사가 아닌 실시간 수치로, 매분 데이터베이스에서 새로 고침됩니다.",
    "trust_badge.confirmed_label": "수신자가 24시간 이내에 수령을 확인",
    "trust_badge.confirmed_sub": "최근 30일 {n}건의 응답 기준",
    "trust_badge.thanks_label": "감사 메시지 전송 건수",
    "trust_badge.thanks_sub": "최근 30일 수신자 기준",
    "trust_badge.cards_label": "충전된 카드 수",
    "trust_badge.cards_sub": "최근 30일 · 실시간 집계",
    "pulse.label": "실시간 PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name}님이 {amount} 카드를 열었습니다",
    "pulse.tooltip_issued": "{name}님이 {amount} 카드를 받았습니다",
    "pulse.tooltip_deposit": "{name}님이 {amount} 입금을 받았습니다",
    "pulse.ago_seconds": "{n}초 전",
    "pulse.ago_minutes": "{n}분 전",
    "pulse.ago_hours": "{n}시간 전",
    "note_cloud.eyebrow": "사람들이 사용하는 용도",
    "note_cloud.title": "진짜 이유. 진짜 사람들.",
    "note_cloud.subtitle": "지난 90일간 {n}개의 카드 메모에서 집계되었습니다. 최소 {min}개 이상의 서로 다른 메모에 등장하는 단어만 표시됩니다 — 내 메시지는 내 것입니다.",
    "note_cloud.footnote": "이름, 금액, 연락처는 표시 전에 필터링됩니다.",
    "note_cloud.lang_eyebrow": "이 메모들은 어떤 언어로 작성되었나요?",
    "note_cloud.lang_other": "기타",
    "download.button": "앱 다운로드",
    "download.close": "닫기",
    "download.modal_eyebrow": "Zcardon 받기",
    "download.modal_title": "Zcardon을 휴대폰에 설치하세요",
    "download.footnote": "아직 앱스토어는 없습니다 — 안전한 웹 앱을 직접 설치합니다. 즉시 업데이트됩니다.",
    "download.installed_title": "Zcardon 설치 완료",
    "download.installed_body": "홈 화면에서 아이콘을 찾아보세요.",
    "download.webview_warning": "설치하려면 휴대폰 브라우저(iPhone은 Safari, Android는 Chrome)에서 이 페이지를 열어주세요 — 인앱 브라우저는 홈 화면에 추가할 수 없습니다.",
    "download.ios_step1_title": "Safari에서",
    "download.ios_step1_body": "Safari를 사용 중이 아니라면 이 링크를 복사해 Safari에서 열어주세요. (Chrome 및 다른 iOS 브라우저는 앱 설치가 불가합니다.)",
    "download.ios_step2_title": "공유 탭하기",
    "download.ios_step2_body": "공유 아이콘은 iPhone의 경우 하단 중앙, iPad의 경우 오른쪽 상단에 있습니다.",
    "download.ios_step3_title": "홈 화면에 추가",
    "download.ios_step3_body": "공유 메뉴를 아래로 스크롤하여 \\\"홈 화면에 추가\\\"를 선택하세요. Zcardon이 전체 화면 앱으로 설치됩니다.",
    "download.android_ready_body": "Chrome이 Zcardon을 감지하여 설치할 준비가 되었습니다. 아래를 탭하세요.",
    "download.android_install": "Zcardon 설치",
    "download.android_installing": "설치 중…",
    "download.android_step1_title": "Chrome 메뉴 열기",
    "download.android_step1_body": "Chrome 오른쪽 상단의 점 세 개를 탭하세요.",
    "download.android_step2_title": "홈 화면에 추가",
    "download.android_step2_body": "\\\"앱 설치\\\" 또는 \\\"홈 화면에 추가\\\"를 선택하세요. Zcardon이 전체 화면 앱으로 설치됩니다.",
    "download.desktop_body": "휴대폰 카메라로 이 QR 코드를 스캔하여 Zcardon을 열고, 휴대폰 브라우저에서 설치하세요.",
    "download.steps_hint": "이 단계는 이 페이지가 아닌 브라우저에서 진행됩니다. 아래 항목들은 탭할 수 없으며 — 브라우저에서 찾아야 할 내용을 설명하는 것입니다.",
    "download.ios_arrow_label": "Safari에서 탭하세요",
    "download.android_arrow_label": "Chrome 상단을 확인하세요",
    "install.eyebrow": "Zcardon 설치",
    "install.title": "Zcardon을 홈 화면에 추가하세요",
    "install.subtitle": "두 번만 탭하면 Zcardon을 앱 아이콘으로 추가할 수 있습니다 — 앱스토어 필요 없음.",
    "install.back_home": "브라우저에서 Zcardon 열기",
    "install.continue": "Zcardon으로 계속 →",
    "install.copy": "복사",
    "install.copied": "복사됨",
    "install.ios_step1_title": "Safari에서 이 페이지 열기",
    "install.ios_step1_body": "다른 브라우저에서 스캔했다면 URL을 복사하여 Safari에서 먼저 열어주세요 — iOS의 Chrome은 앱 설치가 불가합니다.",
    "install.ios_step2_title": "공유 버튼 탭하기",
    "install.ios_step2_body": "위쪽 화살표가 있는 사각형 아이콘입니다 — Safari 하단(또는 iPad의 경우 오른쪽 상단)에 있습니다.",
    "install.ios_step3_title": "\\\"홈 화면에 추가\\\" 탭하기",
    "install.ios_step3_body": "공유 시트를 아래로 스크롤하여 \\\"홈 화면에 추가\\\"를 선택하고 확인하세요. Zcardon이 앱 아이콘으로 나타납니다.",
    "install.ios_arrow_label": "이 아이콘을 찾으세요",
    "install.android_ready_title": "설치 준비 완료",
    "install.android_ready_body": "Chrome이 Zcardon을 설치 가능한 앱으로 감지했습니다. 아래를 탭하면 홈 화면에 앱 아이콘으로 추가됩니다.",
    "install.android_install": "Zcardon 설치",
    "install.android_installing": "설치 중…",
    "install.android_step1_title": "Chrome 메뉴 탭하기",
    "install.android_step1_body": "Chrome 오른쪽 상단 모서리의 점 세 개입니다.",
    "install.android_step2_title": "\\\"앱 설치\\\" 또는 \\\"홈 화면에 추가\\\" 탭하기",
    "install.android_step2_body": "확인을 눌러 Zcardon 아이콘을 휴대폰에 추가하세요. 언제든지 탭하여 실행할 수 있습니다.",
    "install.android_hint": "\\\"앱 설치\\\"가 아직 보이지 않는다면 홈 페이지를 한 번 둘러보세요 — Chrome은 잠깐 방문 후 잠금이 해제됩니다.",
    "install.desktop_title": "휴대폰으로 스캔하세요",
    "install.desktop_body": "Zcardon은 모바일 우선 앱입니다. 휴대폰에서 이 URL을 열어 설치하세요:",
    "install.webview_title": "브라우저에서 열기",
    "install.webview_body": "인앱 브라우저(Instagram, Facebook, TikTok)는 앱 설치가 불가합니다. \\\"…\\\" 또는 공유 메뉴를 탭한 후 \\\"Safari에서 열기\\\" / \\\"Chrome에서 열기\\\"를 선택하세요. 또는 이 링크를 복사하세요:",
    "install.already_title": "이미 앱을 사용 중입니다",
    "install.already_body": "이 기기에 Zcardon이 설치되어 있습니다. 언제든지 홈 화면의 아이콘을 탭하여 여세요.",
    "install.installed_title": "설치 완료",
    "install.installed_body": "홈 화면에서 Zcardon 아이콘을 찾아보세요 — 그게 바로 앱입니다.",
    "install.steps_hint": "이 단계는 이 페이지가 아닌 브라우저에서 진행됩니다. 아래 항목들은 탭할 수 없으며 — 브라우저 메뉴에서 찾아야 할 내용을 설명하는 것입니다.",
    "install.desktop_body_qr": "Zcardon은 iPhone 또는 Android에서 앱 아이콘으로 설치됩니다 — 데스크톱에서는 불가합니다. 휴대폰 카메라를 이 QR 코드에 대세요:",
    "install.desktop_or": "또는 링크 복사",
    "install.android_arrow_label": "Chrome 상단을 확인하세요",
    "install.ios_arrow_caption": "Safari 맨 하단에 있습니다",
    "vietqr.disabled.title": "VietQR로 VND 받기",
    "vietqr.disabled.badge": "베트남 전용",
    "vietqr.disabled.desc": "베트남 은행 계좌를 연결하면 이 카드의 QR이 실제 VietQR이 됩니다 — 모든 베트남 은행 앱(Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank 등)이 이를 스캔하여 NAPAS 네트워크를 통해 VND를 직접 송금할 수 있습니다. Stripe Issuing 승인이 필요하지 않습니다.",
    "vietqr.disabled.btn_enter": "은행 정보 입력",
    "vietqr.disabled.or": "또는",
    "vietqr.disabled.btn_send_link": "수신자에게 설정 링크 보내기",
    "vietqr.disabled.btn_send_link_tip": "수신자가 직접 작성할 수 있는 7일짜리 공개 링크 생성",
    "vietqr.form.warn_title": "활성화 전에 읽어주세요",
    "vietqr.form.warn_body_a": "스캔이 더 이상 Zcardon 추적 페이지로 연결되지 않습니다. 자금은",
    "vietqr.form.warn_body_b": "귀하의 베트남 은행 계좌로 직접",
    "vietqr.form.warn_body_c": "NAPAS 은행 간 네트워크를 통해 흐릅니다 — Zcardon은 자금을 보지 못합니다. VietQR은 베트남 내 VND 송금에만 작동합니다.",
    "vietqr.form.bank": "은행",
    "vietqr.form.account_number": "계좌번호",
    "vietqr.form.account_name": "예금주명 (25자 이내)",
    "vietqr.form.city": "도시 (15자 이내)",
    "vietqr.form.advanced": "고급 — 고정 금액 / 참조 (선택사항)",
    "vietqr.form.amount_vnd": "금액 (VND)",
    "vietqr.form.amount_ph": "가변 금액은 비워두세요",
    "vietqr.form.reference": "참조 (25자 이내)",
    "vietqr.form.confirm": "이 카드를 스캔하면 VND가 위의 은행 계좌로 직접 이체된다는 것을 이해합니다. Zcardon은 이 자금을 처리하거나 보유하지 않습니다.",
    "vietqr.form.btn_attach": "VietQR 활성화",
    "vietqr.form.btn_attaching": "활성화 중…",
    "vietqr.form.btn_cancel": "취소",
    "vietqr.enabled.title": "VietQR 활성",
    "vietqr.enabled.desc": "베트남 은행 앱이 이 카드를 스캔하면 VND가 아래 계좌로 직접 이체됩니다 — Zcardon을 완전히 우회합니다.",
    "vietqr.enabled.bank_label": "은행",
    "vietqr.enabled.account_label": "계좌",
    "vietqr.enabled.fixed_amount": "고정 금액",
    "vietqr.enabled.reference": "참조",
    "vietqr.enabled.btn_detach": "VietQR 해제",
    "vietqr.enabled.btn_detaching": "해제 중…",
    "vietqr.enabled.attached_at": "활성화 일시",
    "vietqr.analytics.live_activity": "실시간 활동",
    "vietqr.analytics.today": "오늘",
    "vietqr.analytics.days_live": "{n}일 가동",
    "vietqr.analytics.phantom_views": "추정 조회",
    "vietqr.analytics.confirmed_pays": "확인된 결제",
    "vietqr.analytics.total_vnd": "총 VND",
    "vietqr.analytics.recent_confirmed": "최근 확인",
    "vietqr.analytics.phantom_explainer": "추정 조회 ≈ 활성화 이후 카드 이미지 다운로드 수. 실제 VietQR 스캔은 당사 서버를 거치지 않으므로 \"카드 표시\" 활동에서 추정합니다. 정확한 데이터를 위해서는 수신자에게 설정 링크로 결제를 확인해달라고 요청하세요.",
    "vietqr.toast.required": "계좌번호와 예금주명은 필수입니다.",
    "vietqr.toast.confirm_first": "VietQR이 Zcardon을 우회하여 VND를 귀하의 은행으로 보낸다는 것을 확인해주세요.",
    "vietqr.toast.attached_default": "VietQR이 활성화되었습니다.",
    "vietqr.toast.attach_failed": "VietQR 활성화 실패.",
    "vietqr.toast.detach_confirm": "VietQR을 해제하시겠습니까? 카드의 QR이 Zcardon 추적 URL로 돌아갑니다.",
    "vietqr.toast.detached": "VietQR이 해제되었습니다 — QR이 Zcardon 추적 URL로 돌아갔습니다.",
    "vietqr.toast.detach_failed": "해제에 실패했습니다.",
    "vietqr.toast.link_copied": "설정 링크 복사됨 · {h}시간 후 만료",
    "vietqr.toast.link_generated": "설정 링크 생성됨 · 아래에서 복사",
    "vietqr.toast.link_failed": "설정 링크 생성 실패.",

    "setpw.toast.too_short": "비밀번호는 최소 8자 이상이어야 합니다",
    "setpw.toast.no_match": "비밀번호가 일치하지 않습니다",
    "setpw.toast.success_signin_again": "비밀번호 설정 완료 — 다시 로그인해 주세요",
    "setpw.toast.welcome": "Zcardon 에이전트 프로그램에 오신 것을 환영합니다",
    "setpw.toast.expired": "링크가 만료되었거나 유효하지 않습니다",
    "setpw.verifying": "초대를 확인하는 중…",
    "setpw.invalid.title": "링크 만료됨",
    "setpw.invalid.desc": "이 환영 링크는 만료되었거나 이미 사용되었습니다. Zcardon 관리자에게 문의하여 새 초대를 받으세요.",
    "setpw.invalid.home_btn": "홈",
    "setpw.invalid.support_btn": "고객 지원 문의",
    "setpw.fallback_name": "에이전트",
    "setpw.eyebrow": "에이전트 환영",
    "setpw.heading": "{name}님, 환영합니다",
    "setpw.subheading": "비밀번호를 설정하여 에이전트 계정을 활성화하세요. 즉시 로그인됩니다.",
    "setpw.code_label": "에이전트 코드",
    "setpw.code_tag": "고객 온보딩 시 공유하세요",
    "setpw.form.title": "비밀번호 선택",
    "setpw.form.new_label": "새 비밀번호",
    "setpw.form.placeholder": "최소 8자",
    "setpw.form.confirm_label": "비밀번호 확인",
    "setpw.form.btn_busy": "활성화 중…",
    "setpw.form.btn_submit": "에이전트 계정 활성화",
    "tx.heading": "거래 내역",
    "tx.kind.direct_deposit": "직접 입금",
    "tx.kind.card_topup": "카드 충전",
    "tx.kind.outbound_payout": "출금 지급",
    "tx.toast.load_failed": "거래 내역을 불러오지 못했습니다",
    "tx.summary.deposits": "입금",
    "tx.summary.topups": "충전",
    "tx.summary.topups_sub": "카드",
    "tx.summary.payouts": "지급",
    "tx.summary.payouts_sub": "출금",
    "tx.filter.all": "전체",
    "tx.filter.deposits": "입금",
    "tx.filter.topups": "충전",
    "tx.filter.payouts": "지급",
    "tx.loading": "거래 내역 불러오는 중…",
    "tx.empty.filtered": "해당 필터에 맞는 거래가 없습니다",
    "tx.empty.none": "거래 내역이 없습니다",
    "tx.empty.cta": "직접 입금으로 시작하기 →",
    "tx.open_title": "열기",
    "terms.load_error_default": "약관을 불러오지 못했습니다",
    "terms.toast.sign_and_check": "동의서를 수락하려면 서명하고 확인해 주세요",
    "terms.toast.recorded": "동의가 기록되었습니다",
    "terms.toast.failed": "제출에 실패했습니다",
    "terms.error.title": "유효하지 않은 링크",
    "terms.back_home": "홈으로 돌아가기",
    "terms.loading": "동의서 불러오는 중…",
    "terms.done.title": "동의 완료",
    "terms.done.meta": "버전 {v} · {ts}",
    "terms.done.next": "이 페이지를 닫아도 됩니다. 카드가 발급되는 즉시 사용 가능합니다.",
    "terms.eyebrow": "카드 소지자 동의서 · v{v}",
    "terms.title_reaccept": "카드 소지자 동의서 재동의",
    "terms.title_accept": "카드 소지자 동의서 동의",
    "terms.desc.sender_sent": "Zcardon 카드를 보냈습니다",
    "terms.desc.you_were_sent": "Zcardon 카드가 발송되었습니다",
    "terms.desc.ending": "끝 번호",
    "terms.desc.read_before": "아래 동의서를 읽은 후 서명하고 확인하여 카드를 활성화하세요.",
    "terms.already_accepted_note": "이전 버전에 이미 동의하셨습니다. 최신 버전으로 업데이트하려면 다시 동의해 주세요.",
    "terms.scroll_hint": "상자 안을 스크롤하여 전체 동의서를 읽어보세요.",
    "terms.view_json": "JSON으로 열기",
    "terms.sign_label": "법적 성명으로 서명하세요",
    "terms.sign_placeholder": "정부 발급 신분증에 표시된 대로",
    "terms.consent": "Zcardon 카드 소지자 계약서 v{v}를 읽고 동의합니다.",
    "terms.btn_recording": "녹음 중…",
    "terms.btn_accept": "계약 수락",
    "hub.eyebrow": "수신 허브",
    "hub.heading.all_done": "모두 완료되었습니다 🎉",
    "hub.heading.greet": "안녕하세요 {name} 👋",
    "hub.fallback_name": "고객님",
    "hub.desc.sender_sent": "님이 Zcardon 카드를 보냈습니다",
    "hub.desc.you_were_sent": "Zcardon 카드가 전송되었습니다",
    "hub.desc.ending": "끝자리",
    "hub.desc.complete_steps": "아래 단계를 완료하여 모든 것을 활성화하세요.",
    "hub.progress.label": "전체 {total}단계 중 {done}단계 완료",
    "hub.blocker.title_one": "필수 단계 1개 남음",
    "hub.blocker.title_many": "필수 단계 {n}개 남음",
    "hub.blocker.subtext": "완료하기 전까지 카드가 완전히 활성화되지 않습니다.",
    "hub.required_pill": "필수",
    "hub.done_pill": "완료",
    "hub.setup_btn": "설정",
    "hub.refresh": "새로고침",
    "hub.loading": "로딩 중…",
    "hub.error.title": "링크 오류",
    "hub.error.default": "수신 허브를 불러올 수 없습니다.",
    "hub.back_home": "홈으로 돌아가기",
    "hub.footer": "이 페이지를 북마크하세요 · 90일간 유효 · 이 링크를 가진 사람만 볼 수 있습니다.",
    "onboarding.eyebrow": "송금 수령하기",
    "onboarding.title": "안녕하세요 {name}",
    "onboarding.fallback_name": "고객님",
    "onboarding.intro_via": "via Zcardon",
    "onboarding.intro_ref_open": "(참조",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "아래에 {country} 은행 계좌를 추가하여 자금을 수령하세요. 귀하의 정보는 결제 파트너인 Stripe에 직접 전달되며 — 발신자는 절대 볼 수 없습니다.",
    "onboarding.invalid.title": "유효하지 않은 링크",
    "onboarding.invalid.hint": "발신자에게 새 링크를 요청하세요.",
    "onboarding.loading": "로딩 중…",
    "onboarding.complete.title": "은행 정보가 이미 등록됨",
    "onboarding.complete.desc": "이미 은행 계좌를 제출하셨습니다. 발신자가 곧 자금을 지급할 것입니다.",
    "onboarding.success.title": "은행 정보 수신 완료 ✓",
    "onboarding.success.auto_paid": "감사합니다 — 은행 계좌가 인증되었으며 자금이 지금 귀하의 계좌로 지급되었습니다. 영업일 기준 1~2일 내에 도착할 예정입니다.",
    "onboarding.success.pending": "감사합니다 — 은행 계좌가 인증되었습니다. 발신자에게 알림이 전송되며, 자금은 영업일 기준 1~2일 내에 계좌에 입금됩니다.",
    "onboarding.success.ref": "참조: {id}",
    "onboarding.beta.title": "베타 미리보기.",
    "onboarding.beta.body": "이 페이지는 배포되었으나 실시간 지급 파이프라인은 Stripe 승인을 기다리고 있습니다. 플랫폼이 활성화될 때까지 제출 시 오류가 반환됩니다. 나중에 다시 확인하거나, 긴급한 경우 고객 지원에 문의하세요.",
    "onboarding.select_placeholder": "선택…",
    "onboarding.submit_error_default": "제출에 실패했습니다. 다시 시도해 주세요.",
    "onboarding.submit_busy": "제출 중…",
    "onboarding.submit": "은행 정보 제출 →",
    "onboarding.secure_note": "Stripe로 보안 처리됩니다. 이 링크는 7일 후 만료됩니다.",
    "onboarding.validate.required": "{field}은(는) 필수 항목입니다",
    "onboarding.validate.format": "{field} 형식이 올바르지 않습니다 ({pattern} 형식이어야 합니다)",
    "onboarding.validate.no_schema": "사용 가능한 양식 스키마가 없습니다",
    "apply.toast.invalid_image": "JPEG, PNG 또는 WEBP 이미지를 선택해 주세요",
    "apply.toast.too_large": "이미지는 6MB 이하여야 합니다",
    "apply.toast.upload_both": "운전면허증 앞뒷면을 모두 업로드해 주세요",
    "apply.toast.failed": "제출에 실패했습니다",
    "apply.toast.submitted": "신청서가 제출되었습니다",
    "apply.success.title": "신청서 접수 완료",
    "apply.success.desc": "감사합니다, {name}님. 영업일 기준 2~3일 내에 신청서를 검토하고 {email}로 다음 단계를 안내해 드리겠습니다.",
    "apply.success.back_home": "홈으로 돌아가기",
    "apply.success.contact_support": "고객 지원 문의",
    "apply.eyebrow": "에이전트 프로그램",
    "apply.heading": "Zcardon 에이전트 신청",
    "apply.intro": "카드 발급, 충전 수납, 고객 대리 현금 입금마다 0.5% 수수료를 받으세요. 지역 사회에서 신뢰받고 신원 확인이 가능한 에이전트를 모집합니다.",
    "apply.bullet1": "고유 에이전트 코드 발급 · 모든 거래에서 추적 가능",
    "apply.bullet2": "주간/월간/연간 수익 요약 대시보드",
    "apply.bullet3": "직접 은행 입금 — 매주 지급",
    "apply.section.your_info": "개인 정보",
    "apply.field.full_name": "법적 성명",
    "apply.field.full_name_ph": "운전면허증에 표시된 이름과 동일하게",
    "apply.field.email": "이메일",
    "apply.field.phone": "전화번호",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "운전면허증 번호",
    "apply.field.address": "주소",
    "apply.field.address_ph": "도로명, 시, 주, 우편번호",
    "apply.field.why_join_label": "에이전트에 지원하는 이유 (선택)",
    "apply.field.why_join_ph": "지역 사회와의 연계 또는 송금 관련 경험을 알려주세요",
    "apply.dl.section": "운전면허증 인증",
    "apply.dl.helper": "양면을 밝고 선명하게 촬영하여 업로드하세요. JPEG, PNG 또는 WEBP · 각 최대 6MB. 이미지는 비공개로 저장되며 Zcardon 관리자만 인증 목적으로 열람합니다.",
    "apply.dl.front": "운전면허증 앞면",
    "apply.dl.back": "운전면허증 뒷면",
    "apply.dl.tap_upload": "탭하여 업로드",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "취소",
    "apply.submit_busy": "제출 중…",
    "apply.submit": "신청서 제출",
    "agent.toast.load_failed": "에이전트 데이터를 불러오지 못했습니다",
    "agent.toast.cust_created": "고객 {name} 생성됨",
    "agent.toast.cust_failed": "고객 생성에 실패했습니다",
    "agent.toast.card_issued": "카드 발급 완료 · {amount} 수익 발생",
    "agent.toast.card_issue_failed": "카드 발급에 실패했습니다",
    "agent.toast.cash_accepted": "현금 입금 수납 완료 · {amount} 수익 발생",
    "agent.toast.cash_failed": "입금 기록에 실패했습니다",
    "agent.loading": "로딩 중…",
    "agent.eyebrow": "에이전트 포털",
    "agent.fallback_name": "에이전트",
    "agent.code_line.code_label": "에이전트 코드",
    "agent.code_line.commission_label": "수수료",
    "agent.earnings.label": "미지급 잔액",
    "agent.earnings.sub": "미지급 거래 {n}건",
    "agent.stat.week": "이번 주",
    "agent.stat.month": "이번 달",
    "agent.stat.year": "올해",
    "agent.stat.txns": "{n}건",
    "agent.stat.customers": "내 고객",
    "agent.stat.active": "활성",
    "agent.customers.title": "내 고객",
    "agent.customers.empty": "아직 고객이 없습니다. \\\"고객 등록\\\"을 탭하여 첫 번째 사용자를 온보딩하세요.",
    "agent.customers.col_customer": "고객",
    "agent.customers.col_phone": "전화번호",
    "agent.customers.col_cards": "카드",
    "agent.customers.col_commissions": "획득 수수료",
    "agent.customers.col_joined": "가입일",
    "agent.customers.col_actions": "작업",
    "agent.customers.txns": "{n}건",
    "agent.commissions.title": "최근 수수료",
    "agent.commissions.empty": "아직 수수료가 없습니다. 첫 번째 카드를 발급하여 수익을 올리세요.",
    "agent.commissions.col_when": "시기",
    "agent.commissions.col_customer": "고객",
    "agent.commissions.col_type": "유형",
    "agent.commissions.col_txn": "거래",
    "agent.commissions.col_commission": "커미션",
    "agent.commissions.col_status": "상태",
    "agent.commissions.paid": "지급됨",
    "agent.commissions.unpaid": "미지급",
    "agent.type.issuance": "발급",
    "agent.type.topup": "충전",
    "agent.type.cash_deposit": "현금 입금",
    "agent.register_btn": "고객 등록",
    "agent.dialog.new_cust.title": "신규 고객",
    "agent.dialog.new_cust.desc": "고객은 에이전트 코드에 연결됩니다. 대신 발급하는 카드마다 0.5% 커미션을 받습니다.",
    "agent.dialog.field.full_name": "이름",
    "agent.dialog.field.email": "이메일",
    "agent.dialog.field.phone": "전화번호",
    "agent.dialog.field.password": "초기 비밀번호 (고객에게 공유)",
    "agent.dialog.cancel": "취소",
    "agent.dialog.creating": "생성 중…",
    "agent.dialog.create_customer": "고객 생성",
    "agent.dialog.issue_card.title": "{name}에게 카드 발급",
    "agent.dialog.issue_card.desc": "직접 수령한 현금으로 충전됩니다. 고객은 3.75% 수수료를 부담하며, 에이전트는 0.5%를 받습니다.",
    "agent.dialog.field.receiver_name": "수령인 이름",
    "agent.dialog.field.receiver_email": "수령인 이메일",
    "agent.dialog.field.receiver_phone": "수령인 전화번호",
    "agent.dialog.field.amount_usd": "금액 (USD)",
    "agent.dialog.field.note": "메모 (선택)",
    "agent.dialog.issuing": "발급 중…",
    "agent.dialog.issue_card_btn": "카드 발급",
    "agent.actions.issue_card": "카드 발급",
    "agent.dialog.cash_deposit.title": "{name} 현금 입금",
    "agent.dialog.cash_deposit.desc": "고객으로부터 수령한 현금 · 수령인에게 전달될 카드를 생성합니다.",
    "agent.dialog.field.receiver_name_opt": "수령인 이름 (선택)",
    "agent.dialog.recording": "기록 중…",
    "agent.dialog.record_deposit": "입금 기록",
    "agent.actions.cash_deposit": "현금 입금",

  },
  pt: {
    "header.nav.home": "Início",
    "header.nav.rates": "Tarifas",
    "header.nav.support": "Suporte",
    "header.nav.admin": "Admin",
    "header.nav.signin": "Entrar",
    "header.nav.signout": "Sair",
    "header.nav.get_started": "Começar",
    "header.nav.account": "Conta",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Alterar idioma",
    "landing.hero.eyebrow": "Uma nova via para gastos digitais",
    "landing.hero.title_a": "Carregue um cartão virtual.",
    "landing.hero.title_b_ship": "Envie",
    "landing.hero.title_b_rest": "para qualquer pessoa,",
    "landing.hero.title_c": "use em",
    "landing.hero.subtitle": "Emita um cartão de débito virtual com tap-to-pay e QR escaneável. Checkout via Stripe, saldo em USD, taxas de serviço e gateway transparentes — entregue a qualquer destinatário por e-mail ou download.",
    "landing.hero.cta_start": "Emitir cartões",
    "landing.hero.cta_request_cash": "Solicitar cartão cash",
    "landing.hero.cta_rates": "Ver tarifas",
    "landing.hero.cta_find_card": "Encontrar meu cartão",
    "landing.hero.stat_currency": "Moeda",
    "landing.hero.stat_settle": "Liquidação",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "Taxa de serviço",
    "landing.hero.image_eyebrow": "SKYLINE · ALCANCE GLOBAL",
    "landing.hero.image_title": "Feito para a economia de cartões moderna",
    "landing.hero.live_pill": "AO VIVO",
    "landing.hero.live_pill_text": "Liquida em USD",
    "landing.hero.marquee": "Emita · Carregue · Gaste em USD",
    "landing.demo.eyebrow": "Veja em 20 segundos",
    "landing.demo.title_a": "Da emissão ao",
    "landing.demo.title_b_spend": "uso",
    "landing.demo.title_c": ". Em quatro toques.",
    "landing.demo.step_issue.label": "Emitir cartão",
    "landing.demo.step_issue.detail": "Escolha um valor. Cartão pronto em ~2 minutos.",
    "landing.demo.step_share.label": "Compartilhar",
    "landing.demo.step_share.detail": "E-mail, SMS, WhatsApp ou baixe como PNG.",
    "landing.demo.step_scan.label": "Destinatário escaneia",
    "landing.demo.step_scan.detail": "A câmera abre a página de rastreio instantaneamente.",
    "landing.demo.step_spend.label": "Gaste em USD",
    "landing.demo.step_spend.detail": "Tap-to-pay, saque em dinheiro, compre em estabelecimentos.",
    "landing.demo.cta_try": "Experimente agora",
    "landing.demo.live_pill": "Demo ao vivo",
    "landing.demo.scene1_caption": "Gerando cartão · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Enviando ao destinatário via WhatsApp",
    "landing.demo.scene3_caption": "Escaneando QR · correspondência encontrada",
    "landing.demo.scene4_eyebrow": "Saldo creditado",
    "landing.demo.scene4_status": "✓ aprovado · tap-to-pay pronto",
    "landing.footer.rates": "Tarifas",
    "landing.footer.support": "Suporte",
    "landing.footer.admin_portal": "Portal admin",
    "landing.footer.copy": "© 2026 Zcardon · Um produto fintech demo",
    "landing.cash.dialog_title": "Solicitar cartão cash",
    "landing.cash.dialog_desc": "Enviaremos um link de checkout Stripe por SMS. Pague com qualquer cartão; o cartão cash é carregado no instante em que o Stripe confirmar.",
    "landing.cash.phone_label": "Número de celular",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Valor (USD)",
    "landing.cash.name_label": "Seu nome (opcional)",
    "landing.cash.submit": "Enviar link de checkout",
    "landing.cash.submitting": "Enviando…",
    "landing.cash.success_title": "Link de checkout a caminho",
    "landing.cash.success_body": "Enviamos um link de checkout Stripe para {phone}. Toque para pagar e seu cartão virtual será ativado imediatamente.",
    "landing.cash.success_open_checkout": "Abrir checkout agora",
    "landing.find.cta": "Já pagou? Encontrar meu cartão",
    "landing.find.dialog_title": "Encontrar meu cartão",
    "landing.find.dialog_desc": "Informe o número de celular usado. Enviaremos um código de 6 dígitos para recuperar qualquer cartão cash ativo cadastrado.",
    "landing.find.phone_label": "Número de celular",
    "landing.find.code_label": "Código de 6 dígitos",
    "landing.find.send_code": "Enviar código",
    "landing.find.verify": "Verificar e encontrar",
    "landing.find.sending": "Enviando…",
    "landing.find.verifying": "Verificando…",
    "landing.find.no_cards": "Nenhum cartão ativo encontrado para esse número.",
    "login.eyebrow.welcome": "Bem-vindo",
    "login.eyebrow.admin": "Administração",
    "login.title": "Entrar",
    "login.title_admin": "Acesso admin",
    "login.admin_badge": "Portal admin",
    "login.email_label": "E-mail",
    "login.password_label": "Senha",
    "login.submit": "Entrar",
    "login.submitting": "Entrando…",
    "login.toast_welcome": "Bem-vindo de volta, {name}",
    "login.toast_failed": "Falha no login",
    "login.gate_title": "Os acessos de clientes estão temporariamente suspensos",
    "login.gate_default_message": "Estamos fazendo melhorias na plataforma. Volte em breve.",
    "login.gate_admin_note": "Administradores e gerentes ainda podem entrar abaixo.",
    "login.new_here": "Novo por aqui?",
    "login.create_account": "Criar uma conta",
    "login.customer_q": "Cliente?",
    "login.regular_signin": "Acesso padrão",
    "login.demo_title": "Contas de demonstração",
    "login.demo_customers_note": "Clientes: cadastre-se via /register ou crie pelo painel Admin",
    "login.forgot_password": "Esqueceu sua senha?",
    "forgot.eyebrow": "Recuperação de conta",
    "forgot.title": "Redefina sua senha",
    "forgot.subtitle": "Informe o e-mail vinculado à sua conta Zcardon e enviaremos um link seguro para você escolher uma nova senha.",
    "forgot.email_label": "E-mail",
    "forgot.email_placeholder": "voce@exemplo.com",
    "forgot.submit": "Enviar link de redefinição",
    "forgot.submitting": "Enviando…",
    "forgot.toast_failed": "Não foi possível enviar o link de redefinição. Tente novamente.",
    "forgot.sent_title": "Verifique sua caixa de entrada",
    "forgot.sent_message": "Se houver uma conta para {email}, enviamos um link de redefinição de senha.",
    "forgot.sent_expiry_note": "O link é válido por 15 minutos. Não recebeu? Verifique o spam ou envie outro.",
    "forgot.back_to_login": "Voltar para o login",
    "forgot.resend_link": "Enviar outro link",
    "forgot.remember_q": "Lembrou sua senha?",
    "forgot.signin_link": "Entrar",
    "reset.eyebrow": "Recuperação de conta",
    "reset.title": "Escolha uma nova senha",
    "reset.subtitle": "Crie uma senha forte que você não usou antes. Você será conectado automaticamente após defini-la.",
    "reset.verifying": "Verificando seu link de redefinição…",
    "reset.invalid_title": "Este link de redefinição é inválido ou expirou",
    "reset.invalid_message": "Os links de redefinição são válidos por 15 minutos e podem ser usados uma vez. Solicite um novo para continuar.",
    "reset.request_new_link": "Solicitar um novo link de redefinição",
    "reset.new_password_label": "Nova senha",
    "reset.confirm_password_label": "Confirmar senha",
    "reset.password_hint": "Mínimo de 8 caracteres. Evite senhas usadas em outros lugares.",
    "reset.submit": "Atualizar senha",
    "reset.submitting": "Atualizando…",
    "reset.toast_success": "Senha atualizada. Você está conectado.",
    "reset.toast_failed": "Não foi possível redefinir a senha. Tente novamente.",
    "reset.error_min_length": "A senha deve ter pelo menos 8 caracteres.",
    "reset.error_mismatch": "As senhas não coincidem.",
    "register.eyebrow": "Cadastro",
    "register.title": "Criar conta",
    "register.full_name_label": "Nome completo",
    "register.email_label": "E-mail",
    "register.password_label": "Senha",
    "register.submit": "Criar conta",
    "register.submitting": "Criando…",
    "register.gate_title": "Os cadastros estão temporariamente suspensos",
    "register.gate_default_message": "Estamos fazendo melhorias na plataforma. Volte em breve.",
    "register.gate_already_member": "Já tem uma conta?",
    "register.toast_created": "Conta criada",
    "register.toast_failed": "Falha no cadastro",
    "register.already_member": "Já é membro?",
    "register.signin_link": "Entrar",
    "dash.eyebrow": "Painel",
    "dash.greeting": "Bem-vindo de volta, {name}",
    "dash.subtitle": "Emita um cartão virtual ou envie um depósito por transferência — seu centro de controle está abaixo.",
    "dash.action.issue_card": "Emitir cartão",
    "dash.action.direct_deposit": "Depósito direto (transferência)",
    "dash.action.support": "Suporte",
    "dash.section.cards": "Seus cartões",
    "dash.section.activity": "Atividade recente",
    "dash.section.deposits": "Depósitos diretos",
    "dash.empty.cards": "Nenhum cartão ainda — emita o primeiro para começar.",
    "dash.empty.activity": "Assim que você emitir cartões ou enviar transferências, a atividade recente aparecerá aqui.",
    "ref.eyebrow": "Bem-vindo ao Zcardon",
    "ref.greeting": "{name} te convidou.",
    "ref.loading": "Carregando seu convite…",
    "ref.resolved_redirect": "Um momento — redirecionando você.",
    "ref.resolving": "Processando seu link de convite…",
    "support.eyebrow": "Aqui por você",
    "support.title": "Suporte, não tickets.",
    "support.subtitle": "Fale com uma pessoa. Respostas por e-mail em até uma hora útil nos dias de semana.",
    "support.card.email": "E-mail",
    "support.card.phone": "Telefone",
    "support.card.live_chat": "Chat ao vivo",
    "support.card.live_chat_value": "No seu painel",
    "support.card.hours": "Horários",
    "support.faq.title": "Perguntas frequentes",
    "support.faq.q1": "Quanto tempo até o destinatário receber o cartão?",
    "support.faq.a1": "Geralmente em até 2 minutos após o financiamento bem-sucedido. A entrega por e-mail (Resend) e MMS (Twilio) acontece automaticamente.",
    "support.faq.q2": "Posso cancelar ou congelar um cartão após o envio?",
    "support.faq.a2": "Sim — abra a página de detalhes do cartão e toque em Congelar. Você também pode congelar diretamente pelo e-mail de alerta enviado ao destinatário.",
    "support.faq.q3": "Em quais moedas o cartão pode ser usado?",
    "support.faq.a3": "Todos os cartões liquidam em USD. O destinatário pode gastar em qualquer lugar onde Visa/Mastercard seja aceito — o câmbio é gerenciado pela rede do cartão.",
    "support.faq.q4": "Quais tarifas se aplicam?",
    "support.faq.a4": "Uma taxa de serviço transparente mais o custo do gateway Stripe. Veja a cotação ao vivo na página de Tarifas antes de pagar.",
    "rates.eyebrow": "Transparente",
    "rates.title_a": "Sem spreads",
    "rates.title_b": "ocultos",
    "rates.subtitle": "Você vê a taxa de serviço e o custo do gateway antes de pagar. O principal sempre é creditado no saldo do cartão virtual em USD.",
    "rates.row.service_fee": "Taxa de serviço",
    "rates.row.service_fee_value": "{pct} do valor enviado",
    "rates.row.gateway": "Gateway (Stripe)",
    "rates.row.settlement": "Liquidação",
    "rates.row.settlement_value": "Saldo em USD no cartão virtual",
    "rates.row.topup": "Métodos de recarga",
    "rates.row.topup_value": "Cartão · Débito · Banco (ACH)",
    "rates.sim.eyebrow": "Cotação ao vivo",
    "rates.sim.title_a": "Envie qualquer valor,",
    "rates.sim.title_b": "veja o detalhamento na hora.",
    "rates.sim.amount_label": "Valor a enviar",
    "rates.sim.line.principal": "No cartão (principal)",
    "rates.sim.line.service": "Taxa de serviço · {pct}",
    "rates.sim.line.gateway": "Gateway · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Você paga",
    "rates.sim.cta": "Começar com ${amount} →",
    "rates.sim.note": "As tarifas aplicadas acima são configuradas pelo administrador. O destinatário sempre recebe o principal integral.",
    "dd.title": "Depósito Direto",
    "dd.intro": "Envie dinheiro para a conta bancária de um destinatário em qualquer lugar do mundo. Pague com cartão (Stripe Checkout) ou com seu saldo Zcardon — nós realizamos a transferência para o banco do destinatário automaticamente onde houver suporte, ou manualmente para países que ainda não roteamos automaticamente. Cada transação é registrada com um recibo imprimível.",
    "dd.new_btn": "Registrar novo depósito direto",
    "dd.close": "Fechar",
    "dd.history_eyebrow": "Seus depósitos",
    "dd.loading": "Carregando…",
    "dd.empty": "Nenhum depósito direto ainda.",
    "dd.status.approved": "aprovado",
    "dd.status.pending": "aguardando aprovação",
    "dd.status.rejected": "rejeitado",
    "dd.row.confirmed": "✓ Destinatário confirmou o recebimento",
    "dd.row.disputed": "⚠ Destinatário sinalizou uma discrepância",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Não foi possível carregar os depósitos",
    "dd.error.pdf": "Não foi possível baixar o recibo",
    "pay.mode.view": "Seu cartão Zcardon",
    "pay.mode.charge": "Pagar com QR",
    "pay.heading.view": "Olá, {name}.",
    "pay.heading.charge": "Cobrar neste cartão.",
    "pay.cardholder": "Titular",
    "pay.paused.label": "Cartão pausado",
    "pay.paused.body": "Este cartão foi congelado e não pode receber novas cobranças até que o titular o reative.",
    "pay.balance.label": "Saldo disponível",
    "pay.charges_count": "{count} cobrança",
    "pay.charges_count_plural": "{count} cobranças",
    "pay.topups_count": "{count} recarga",
    "pay.topups_count_plural": "{count} recargas",
    "pay.activity.label": "Atividade recente",
    "pay.activity.empty": "Nenhuma atividade ainda",
    "pay.cta.charge": "Cobrar neste cartão (lojista)",
    "pay.cta.charge_paused": "Cartão pausado",
    "pay.cta.note_active": "Toque acima somente se você é o lojista aceitando pagamento deste cartão.",
    "pay.cta.note_paused": "Este cartão foi pausado pelo remetente. Peça a ele que o reative.",
    "pay.back_to_balance": "Voltar ao saldo",
    "pay.form.amount_label": "Valor (USD)",
    "pay.form.amount_max": "Máx {amount}",
    "pay.form.merchant_label": "Lojista / motivo",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Memo (opcional)",
    "pay.form.memo_placeholder": "Dois lattês",
    "pay.form.submit_processing": "Processando…",
    "pay.form.submit_paused": "Cartão pausado",
    "pay.form.submit_charge": "Cobrar {amount}",
    "pay.form.submit_charge_card": "Cobrar cartão",
    "pay.form.disclaimer": "Ao cobrar este cartão, você confirma que está autorizado pelo titular a gastar o valor acima.",
    "pay.receipt.title": "Pagamento concluído",
    "pay.receipt.to": "para",
    "pay.receipt.id": "recibo",
    "pay.receipt.time": "hora",
    "pay.receipt.new_balance": "novo saldo",
    "pay.receipt.done": "Concluído · voltar ao saldo",
    "pay.error.cant_load": "Não foi possível carregar este cartão",
    "pay.error.back_home": "Voltar ao início",
    "pay.error.loading": "Carregando cartão…",
    "pay.error.charge_failed": "Cobrança falhou",
    "pay.tap.eyebrow": "Toque para pagar",
    "pay.tap.title": "Mostre este código",
    "pay.tap.subtitle": "ao leitor do estabelecimento",
    "pay.tap.balance": "Saldo",
    "pay.tap.paused": "Pausado",
    "pay.tap.qr_unavailable": "QR indisponível para este cartão",
    "pay.tap.refresh_in": "Atualiza em {secs}s",
    "pay.tap.show_digits": "Mostrar dígitos",
    "pay.tap.hide_digits": "Ocultar dígitos",
    "pay.tap.card_label": "Cartão · ••••",
    "pay.tap.auto_hides": "Oculta automaticamente em 60s",
    "pay.tap.full_only_image": "O número completo é exibido apenas na imagem original do cartão",
    "pay.tap.disclaimer": "Cartão de circuito fechado · utilizável em qualquer estabelecimento habilitado para Zcardon. O QR é rotacionado a cada 60s para proteção antirreplay.",
    "pay.subscribe.eyebrow": "Fique por dentro",
    "pay.subscribe.title": "Receba um e-mail a cada transação",
    "pay.subscribe.desc": "Grátis. Um e-mail por cobrança ou recarga. Cancele quando quiser. Nunca compartilhamos seu endereço.",
    "pay.subscribe.placeholder": "voce@exemplo.com",
    "pay.subscribe.cta": "Notificar-me",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Você está inscrito",
    "pay.subscribe.done.body": "Enviaremos um e-mail para {email} instantaneamente a cada cobrança ou recarga no cartão •••• {last4}.",
    "pay.subscribe.toast.already": "Você já estava inscrito — nenhum duplicado enviado.",
    "pay.subscribe.toast.success": "Você receberá um e-mail a cada transação. Bem-vindo!",
    "pay.subscribe.toast.failed": "Falha na inscrição",
    "pay.save.eyebrow": "Leve seu cartão",
    "pay.save.title": "Salve este cartão no seu celular",
    "pay.save.desc": "Salve a imagem do cartão nas suas fotos para acesso offline — mostre o QR aos estabelecimentos a qualquer momento, sem precisar de app.",
    "pay.save.cta": "Salvar imagem do cartão",
    "pay.save.preparing": "Preparando…",
    "pay.save.add_home": "Adicionar à tela inicial",
    "pay.save.toast.opened": "Imagem do cartão aberta — pressione e segure para salvar nas Fotos",
    "pay.save.toast.failed": "Não foi possível salvar o cartão",
    "pay.save.ios.title": "Para adicionar o Zcardon à tela inicial do seu iPhone:",
    "pay.save.ios.step1": "Toque no botão Compartilhar na parte inferior do Safari",
    "pay.save.ios.step2": "Role para baixo e toque em Adicionar à Tela de Início",
    "pay.save.ios.step3": "Toque em Adicionar para confirmar",
    "pay.save.ios.note": "O Zcardon aparecerá como qualquer outro app — toque no ícone para abrir este cartão instantaneamente.",
    "pay.thanks.eyebrow": "Envie um agradecimento",
    "pay.thanks.body": "${amount} acabou de chegar — avise a pessoa que financiou este cartão que foi de grande ajuda.",
    "pay.thanks.placeholder": "Opcional — diga algo especial…",
    "pay.thanks.cta": "Enviar agradecimento {emoji}",
    "pay.thanks.sending": "Enviando…",
    "pay.thanks.done": "Agradecimento enviado — eles verão em segundos.",
    "pay.thanks.toast.sent": "Agradecimento enviado",
    "pay.thanks.toast.failed": "Não foi possível enviar o agradecimento",
    "pay.activity.topup_bank": "Recarga por transferência bancária",
    "pay.activity.topup_debit": "Recarga por cartão de débito",
    "pay.activity.topup_credit": "Recarga por cartão de crédito",
    "pay.activity.topup_crypto": "Recarga por cripto",
    "pay.activity.topup_received": "Recarga recebida",
    "pay.activity.merchant_charge": "Cobrança do estabelecimento",
    "pay.toast.payment_received": "Pagamento recebido · −{amount}",
    "cash.confirming": "Confirmando pagamento…",
    "cash.confirming.sub": "O Stripe está finalizando a cobrança.",
    "cash.notfound.title": "Link expirado",
    "cash.notfound.body": "Não encontramos esta solicitação de pagamento. O link pode ser de uma sessão anterior, ou o cartão nunca foi finalizado. Inicie uma nova solicitação — leva apenas um minuto, e qualquer pagamento já realizado será creditado automaticamente assim que o reconciliador processar.",
    "cash.notfound.ref": "ref:",
    "cash.notfound.start": "Iniciar nova solicitação",
    "cash.notfound.contact": "Falar com o suporte",
    "cash.error.title": "Não foi possível verificar o status",
    "cash.error.return": "Voltar ao início",
    "cash.autopay.opening": "Abrindo Toque para Pagar…",
    "cash.autopay.redirect": "Redirecionando em {secs}s.",
    "cash.autopay.stay": "Ficar aqui",
    "cash.ready.title": "Seu cartão está pronto",
    "cash.ready.subtitle": "Carregado com ${amount} USD · também enviado por SMS para {phone}",
    "cash.card.label": "Cartão",
    "cash.card.exp": "Val.",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Saldo",
    "cash.cta.open_pay": "Abrir tela Toque para Pagar",
    "cash.toast.funded": "Seu cartão foi carregado — verifique seu celular pelo SMS!",
    "cash.pending.title": "Aguardando o Stripe",
    "cash.pending.body": "Estamos verificando a confirmação. Isso geralmente leva 5 a 15 segundos. Se você fechou o checkout sem pagar, pode recomeçar pela página inicial.",
    "cash.pending.back": "Voltar ao início",
    "cash.timeout.title": "Não foi possível confirmar o pagamento",
    "cash.timeout.body": "Aguardamos 3 minutos pelo Stripe confirmar. Na maioria das vezes, isso significa que o checkout foi fechado antes do pagamento — mas se você PAGOU, seu cartão ainda será ativado automaticamente nos próximos minutos (nosso reconciliador em segundo plano roda a cada 5 min) e enviaremos para {phone} assim que isso acontecer.",
    "cash.timeout.start_over": "Recomeçar",
    "cash.timeout.try_again": "Tentar novamente",
    "cash.resend.title": "Não recebeu? Enviar novamente",
    "cash.resend.degraded.lead": "A entrega para números dos EUA está degradada agora.",
    "cash.resend.degraded.sms": "Mensagens para celulares dos EUA podem ser bloqueadas silenciosamente pela operadora (verificação toll-free ainda pendente).",
    "cash.resend.degraded.email": "O e-mail está em modo de teste e só entrega para o endereço do proprietário da conta enquanto o DNS está sendo validado.",
    "cash.resend.degraded.tail": "Se um canal não funcionar, tente o outro — ou abra esta página no dispositivo do destinatário para acessar o cartão diretamente.",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "E-mail",
    "cash.resend.sending": "Enviando…",
    "cash.resend.email_sandbox": "O e-mail está em modo de teste. Enquanto o domínio zcardon.com está sendo verificado no Resend, só podemos enviar para o endereço do proprietário da conta. Se seu e-mail não for aceito, use SMS ou WhatsApp.",
    "cash.resend.email_label": "Endereço de e-mail",
    "cash.resend.email_send": "Enviar por e-mail",
    "cash.resend.cancel": "Cancelar",
    "cash.resend.limit": "Limite de 5 reenvios por cartão. Telefone cadastrado: {phone}",
    "cash.resend.no_phone": "(nenhum)",
    "cash.wallet.title": "Adicionar à Carteira",
    "cash.wallet.apple": "Adicionar à Apple Wallet",
    "cash.wallet.google": "Adicionar à Google Wallet",
    "cash.wallet.generating": "Gerando pass…",
    "cash.wallet.note": "Fica na tela de bloqueio · Atualiza automaticamente quando o saldo muda",
    "cash.wallet.toast.apple": "Pass baixado — abra no seu iPhone para adicionar à Apple Wallet",
    "cash.wallet.toast.apple_failed": "Não foi possível gerar o pass da Apple Wallet",
    "cash.wallet.toast.google_failed": "Não foi possível gerar o pass da Google Wallet",
    "cash.delivery.fallback_title": "Enviado via {fallback}",
    "cash.delivery.fallback_body": "Sua operadora bloqueou o SMS original, então reenviamos seu cartão via {fallback} para {phone}. Verifique esse canal.",
    "cash.delivery.toll_blocked": "Operadora bloqueou o SMS",
    "cash.delivery.failed_title": "Não conseguimos enviar seu cartão por SMS",
    "cash.delivery.toll_body": "Sua operadora rejeitou a mensagem (isso acontece com novos remetentes pendentes de verificação). Salve seu cartão agora: toque em",
    "cash.delivery.failed_body": "Seu cartão foi carregado — salve-o agora, pois o SMS não foi entregue. Toque em",
    "cash.delivery.cta_label": "Abrir tela Tap-to-Pay",
    "cash.delivery.body_tail": "acima, tire um print ou use o E-mail abaixo para enviar uma cópia para você mesmo.",
    "cash.handoff.eyebrow": "Entregar pessoalmente",
    "cash.handoff.body": "Peça ao destinatário para escanear isso com a câmera — o cartão abre diretamente no celular dele, sem precisar de SMS ou e-mail.",
    "cash.handoff.share_label": "Ou compartilhe este link",
    "cash.handoff.copy": "Copiar link",
    "cash.handoff.copied": "✓ Copiado",
    "cash.handoff.share": "Compartilhar",
    "cash.handoff.toast.copied": "Link copiado — cole onde quiser",
    "cash.handoff.toast.copy_failed": "Não foi possível copiar. Pressione e segure o link para copiar manualmente.",
    "portal.loading": "Carregando seu comprovante…",
    "portal.unavailable.title": "Link indisponível",
    "portal.unavailable.body": "Este link é inválido ou expirou.",
    "portal.unavailable.hint": "Se o remetente compartilhou este link com você, peça que reenvie o e-mail do comprovante.",
    "portal.brand": "Portal de comprovantes",
    "portal.eyebrow": "{sender} enviou para você",
    "portal.reference": "Referência",
    "portal.pending.title": "Aprovação pendente",
    "portal.pending.body": "O remetente registrou esta transferência, mas ela ainda está sendo analisada pelo Zcardon. Volte em breve — o comprovante ficará disponível após a aprovação.",
    "portal.row.bank": "Banco",
    "portal.row.account": "Conta",
    "portal.row.routing": "Agência",
    "portal.row.memo": "Memo",
    "portal.confirmed.title": "Você confirmou o recebimento",
    "portal.confirmed.body": "Confirmado em {when}. O remetente pode ver isso.",
    "portal.disputed.title": "Você sinalizou uma divergência",
    "portal.disputed.body": "Reportado em {when}. O remetente foi notificado por e-mail.",
    "portal.actions.body": "Esse valor chegou na sua conta? Confirmar fecha o ciclo com o remetente — nenhum dinheiro passa pelo Zcardon, isso é apenas um registro.",
    "portal.actions.confirm": "Sim, recebi",
    "portal.actions.flag": "Sinalizar um problema",
    "portal.dispute.placeholder": "Valor incorreto, não chegou, número de conta errado…",
    "portal.dispute.submit": "Enviar relatório",
    "portal.dispute.too_short": "Adicione um motivo curto (mínimo 4 caracteres)",
    "portal.toast.confirmed": "Obrigado — recebimento confirmado.",
    "portal.toast.confirm_failed": "Não foi possível confirmar o recebimento",
    "portal.toast.reported": "Reportado. O remetente foi notificado.",
    "portal.toast.report_failed": "Não foi possível enviar a contestação",
    "portal.toast.pdf_failed": "Não foi possível baixar o comprovante",
    "portal.download_pdf": "Baixar comprovante em PDF",
    "portal.privacy": "Esta página é privada para você. Qualquer pessoa com o link pode ver este comprovante — não o compartilhe publicamente. Dúvidas:",
    "detail.back": "Voltar ao painel",
    "detail.eyebrow": "Seu cartão",
    "detail.confirming_payment": "Confirmando seu pagamento…",
    "detail.active_ready": "Cartão ativo e pronto para compartilhar.",
    "detail.balance.label": "Saldo atual",
    "detail.toast.notfound": "Cartão não encontrado",
    "detail.toast.loaded": "Fundos carregados no cartão.",
    "detail.toast.confirmed": "Pagamento confirmado.",
    "detail.toast.expired": "Sessão de pagamento expirada",
    "detail.delivery.receiver_fallback": "seu destinatário",
    "detail.delivery.received_title": "{name} abriu o cartão",
    "detail.delivery.received_when": "Entrega confirmada · {when}",
    "detail.delivery.waiting_title_on": "Você receberá um e-mail assim que for recebido",
    "detail.delivery.waiting_title_off": "Notificações de entrega estão desativadas",
    "detail.delivery.waiting_body_on": "Assim que seu destinatário escanear o QR deste cartão, enviaremos um e-mail para você — apenas uma vez.",
    "detail.delivery.waiting_body_off": "Ative para receber um e-mail único no momento em que seu destinatário abrir o cartão pela primeira vez.",
    "detail.delivery.toggle_on": "Ativado",
    "detail.delivery.toggle_off": "Ativar",
    "detail.delivery.toast_enabled": "Você será notificado quando o cartão for aberto.",
    "detail.delivery.toast_disabled": "Notificações de entrega desativadas.",
    "detail.delivery.toast_failed": "Não foi possível atualizar as notificações de entrega.",
    "nudge.eyebrow": "Avise seu destinatário",
    "nudge.title": "Mande um aviso para {receiver}",
    "nudge.subtitle": "Preparamos uma mensagem curta para que {receiver} saiba que o cartão está ativo e como usá-lo.",
    "nudge.receiver_fallback": "seu destinatário",
    "nudge.sender_fallback": "um amigo",
    "nudge.template": "Oi {receiver} — {sender} acabou de te enviar um cartão virtual Zcardon com {amount}. Toque para pagar aqui: {url}\\n\\nNenhum app necessário. O link funciona em qualquer celular.",
    "nudge.email_subject": "{sender} te enviou um cartão Zcardon de {amount}",
    "nudge.action_copy": "Copiar mensagem",
    "nudge.action_sms": "Abrir SMS",
    "nudge.action_email": "Abrir e-mail",
    "nudge.toast_copied": "Mensagem copiada para a área de transferência.",
    "nudge.toast_copy_failed": "Não foi possível copiar. Selecione e copie manualmente.",
    "ios_install.title": "Instale o Zcardon no seu iPhone",
    "ios_install.step_1": "Toque em",
    "ios_install.share_label": "Compartilhar",
    "ios_install.step_2": " e escolha \\\"Adicionar à Tela de Início\\\" — o Zcardon abrirá como um app em tela cheia.",
    "smart_banner.title": "Abrir no app Zcardon",
    "smart_banner.subtitle": "Checkout mais rápido. Toque para pagar sem sair do app.",
    "smart_banner.cta": "Abrir",
    "smart_banner.dismiss": "Dispensar",
    "social_proof.eyebrow": "Ao vivo · últimas 48 horas",
    "social_proof.empty": "Aguardando o próximo cartão chegar…",
    "social_proof.line_opened": "{name} acabou de abrir um cartão de {amount}.",
    "social_proof.line_issued": "{name} recebeu um cartão de {amount} de um amigo.",
    "social_proof.line_deposit": "{name} recebeu um depósito direto de {amount}.",
    "social_proof.stat_funded": "Cartões financiados hoje",
    "social_proof.stat_delivered": "Cartões entregues hoje",
    "social_proof.stat_recent": "Atividade recente",
    "social_proof.ago_seconds": "há {n}s",
    "social_proof.ago_minutes": "há {n}min",
    "social_proof.ago_hours": "há {n}h",
    "flight.eyebrow": "Cartões em trânsito · últimas 48h",
    "flight.title": "Onde o Zcardon chega",
    "flight.subtitle": "Localizações reais dos destinatários, anonimizadas por região. Exibimos apenas países e estados dos EUA — nunca nomes, endereços ou valores.",
    "flight.row_template": "Cartão aberto em {label}",
    "flight.more_count": "+ {n} regiões",
    "flight.ago_seconds": "há {n}s",
    "flight.ago_minutes": "há {n}min",
    "flight.ago_hours": "há {n}h",
    "trust_strip.eyebrow": "Sinal real · ao vivo do banco de dados",
    "trust_strip.title_a": "Não são depoimentos.",
    "trust_strip.title_b": "Leituras ao vivo do banco de dados.",
    "trust_strip.how_link": "Como coletamos isso",
    "trust_strip.modal_eyebrow": "Fontes de dados",
    "trust_strip.modal_title": "Tudo real, tudo anonimizado",
    "trust_strip.modal_close": "Fechar",
    "trust_strip.modal_intro": "Cada número nesta faixa vem do nosso próprio banco de dados — nunca de um feed de terceiros, nunca inventado. Veja de onde cada seção extrai os dados e como os anonimizamos antes de exibi-los.",
    "trust_strip.src_carousel_title": "Carrossel de atividade ao vivo",
    "trust_strip.src_carousel_body": "Extrai das coleções cards e direct_deposits, últimas 48h. Nomes exibidos como primeiro nome + inicial do sobrenome; valores arredondados para o múltiplo de $5 mais próximo para evitar triangulação. Atualizado a cada 60 segundos.",
    "trust_strip.src_flight_title": "Onde o Zcardon chega",
    "trust_strip.src_flight_body": "Lê os acessos por QR de card_scans, com geolocalização no momento do scan. Agrupado por país (ou estado dos EUA). Sem precisão abaixo do nível estadual, sem exibição de IPs, sem identificadores de cartão. Atualizado a cada 90 segundos.",
    "trust_strip.src_badges_title": "Números de confiança dos últimos 30 dias",
    "trust_strip.src_badges_body": "Apenas contagens agregadas — nunca dados por linha. Cada métrica só aparece após atingir um tamanho mínimo de amostra, portanto uma taxa de 100% com apenas 2 confirmações ficaria oculta. Atualizado a cada minuto.",
    "trust_strip.modal_promise": "Nunca publicaremos nomes, endereços, valores exatos, IPs ou números de cartão em widgets públicos. Prometemos.",
    "trust_badge.eyebrow": "Números reais · últimos 30 dias",
    "trust_badge.title": "As pessoas estão realmente usando isso.",
    "trust_badge.subtitle": "Não é depoimento — é uma contagem ao vivo, atualizada do banco de dados a cada minuto.",
    "trust_badge.confirmed_label": "destinatários confirmam o recebimento em até 24h",
    "trust_badge.confirmed_sub": "de {n} respostas nos últimos 30 dias",
    "trust_badge.thanks_label": "mensagens de agradecimento enviadas",
    "trust_badge.thanks_sub": "de destinatários nos últimos 30 dias",
    "trust_badge.cards_label": "cartões financiados",
    "trust_badge.cards_sub": "últimos 30 dias · contagem ao vivo",
    "pulse.label": "AO VIVO",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} abriu um cartão de {amount}",
    "pulse.tooltip_issued": "{name} recebeu um cartão de {amount}",
    "pulse.tooltip_deposit": "{name} recebeu um depósito de {amount}",
    "pulse.ago_seconds": "há {n}s",
    "pulse.ago_minutes": "há {n}m",
    "pulse.ago_hours": "há {n}h",
    "note_cloud.eyebrow": "Para que as pessoas usam",
    "note_cloud.title": "Motivos reais. Pessoas reais.",
    "note_cloud.subtitle": "Agregado de {n} notas de cartão nos últimos 90 dias. Mostramos apenas palavras que aparecem em pelo menos {min} notas diferentes — sua mensagem continua sendo sua.",
    "note_cloud.footnote": "Nomes, valores e informações de contato são filtrados antes da exibição.",
    "note_cloud.lang_eyebrow": "Em que idioma estão essas notas?",
    "note_cloud.lang_other": "Outro",
    "download.button": "Baixar app",
    "download.close": "Fechar",
    "download.modal_eyebrow": "Obtenha o Zcardon",
    "download.modal_title": "Instale o Zcardon no seu celular",
    "download.footnote": "Sem App Store ainda — você está instalando o web app seguro diretamente. Atualiza instantaneamente.",
    "download.installed_title": "Zcardon instalado",
    "download.installed_body": "Procure o ícone na sua tela inicial.",
    "download.webview_warning": "Abra esta página no navegador do seu celular (Safari no iPhone, Chrome no Android) para instalar — navegadores dentro de apps não podem adicionar à tela inicial.",
    "download.ios_step1_title": "No Safari",
    "download.ios_step1_body": "Se você ainda não está no Safari, copie este link e abra lá. (Chrome e outros navegadores iOS não conseguem instalar apps.)",
    "download.ios_step2_title": "Toque em Compartilhar",
    "download.ios_step2_body": "O ícone de Compartilhar fica na parte inferior central do iPhone e no canto superior direito do iPad.",
    "download.ios_step3_title": "Adicionar à Tela de Início",
    "download.ios_step3_body": "Role para baixo no menu de compartilhamento e escolha \\\"Adicionar à Tela de Início\\\". O Zcardon é instalado como um app em tela cheia.",
    "download.android_ready_body": "O Chrome detectou o Zcardon e está pronto para instalar. Toque abaixo.",
    "download.android_install": "Instalar Zcardon",
    "download.android_installing": "Instalando…",
    "download.android_step1_title": "Abra o menu do Chrome",
    "download.android_step1_body": "Toque nos três pontos no canto superior direito do Chrome.",
    "download.android_step2_title": "Adicionar à tela inicial",
    "download.android_step2_body": "Escolha \\\"Instalar app\\\" ou \\\"Adicionar à tela inicial\\\". O Zcardon é instalado como um app em tela cheia.",
    "download.desktop_body": "Escaneie este QR code com a câmera do seu celular para abrir o Zcardon lá e instale pelo navegador do celular.",
    "download.steps_hint": "Essas etapas acontecem no seu navegador, não nesta página. Nenhuma das linhas abaixo é tocável — elas descrevem o que procurar.",
    "download.ios_arrow_label": "Toque neste ícone no Safari",
    "download.android_arrow_label": "Procure aqui no Chrome",
    "install.eyebrow": "Instalar Zcardon",
    "install.title": "Adicione o Zcardon à sua tela inicial",
    "install.subtitle": "Dois toques e você terá o Zcardon como um ícone de app de verdade — sem App Store.",
    "install.back_home": "Abrir Zcardon no navegador",
    "install.continue": "Continuar para o Zcardon →",
    "install.copy": "Copiar",
    "install.copied": "Copiado",
    "install.ios_step1_title": "Abra esta página no Safari",
    "install.ios_step1_body": "Se você escaneou em outro navegador, copie a URL e abra no Safari primeiro — o Chrome no iOS não consegue instalar apps.",
    "install.ios_step2_title": "Toque no botão Compartilhar",
    "install.ios_step2_body": "É o quadrado com uma seta apontando para cima — na parte inferior do Safari (ou canto superior direito no iPad).",
    "install.ios_step3_title": "Toque em \\\"Adicionar à Tela de Início\\\"",
    "install.ios_step3_body": "Role para baixo na aba de compartilhamento e escolha \\\"Adicionar à Tela de Início\\\", depois confirme. O Zcardon aparece como um ícone de app.",
    "install.ios_arrow_label": "Procure este ícone",
    "install.android_ready_title": "Pronto para instalar",
    "install.android_ready_body": "O Chrome detectou o Zcardon como instalável. Toque abaixo e apareceremos como um ícone de app na sua tela inicial.",
    "install.android_install": "Instalar Zcardon",
    "install.android_installing": "Instalando…",
    "install.android_step1_title": "Toque no menu do Chrome",
    "install.android_step1_body": "São os três pontos no canto superior direito do Chrome.",
    "install.android_step2_title": "Toque em \\\"Instalar app\\\" ou \\\"Adicionar à tela inicial\\\"",
    "install.android_step2_body": "Confirme para adicionar o ícone do Zcardon ao seu celular. Toque a qualquer momento para abrir.",
    "install.android_hint": "Se você ainda não vê \\\"Instalar app\\\", navegue pela página inicial uma vez — o Chrome libera após uma breve visita.",
    "install.desktop_title": "Escaneie com seu celular",
    "install.desktop_body": "O Zcardon é um app mobile. Abra esta URL no seu celular para instalar:",
    "install.webview_title": "Abra no seu navegador",
    "install.webview_body": "Navegadores dentro de apps (Instagram, Facebook, TikTok) não conseguem instalar apps. Toque em \\\"…\\\" ou no menu de compartilhamento e escolha \\\"Abrir no Safari\\\" / \\\"Abrir no Chrome\\\". Ou copie este link:",
    "install.already_title": "Você já está no app",
    "install.already_body": "O Zcardon está instalado neste dispositivo. Toque no ícone da sua tela inicial para abrir a qualquer momento.",
    "install.installed_title": "Instalado",
    "install.installed_body": "Procure o ícone do Zcardon na sua tela inicial — esse é o seu app.",
    "install.steps_hint": "Essas etapas acontecem no seu navegador, não nesta página. Nenhuma das linhas abaixo é tocável — elas descrevem o que procurar no menu do navegador.",
    "install.desktop_body_qr": "O Zcardon é instalado como ícone de app no iPhone ou Android — não no desktop. Aponte a câmera do seu celular para este QR code:",
    "install.desktop_or": "Ou copie o link",
    "install.android_arrow_label": "Procure aqui no Chrome",
    "install.ios_arrow_caption": "Fica bem na parte inferior do Safari",
    "vietqr.disabled.title": "Receba VND via VietQR",
    "vietqr.disabled.badge": "APENAS VIETNÃ",
    "vietqr.disabled.desc": "Vincule uma conta bancária vietnamita e o QR deste cartão se tornará um VietQR real — qualquer app bancário vietnamita (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) poderá escaneá-lo e pagar-lhe em VND diretamente pela rede NAPAS. Não requer aprovação do Stripe Issuing.",
    "vietqr.disabled.btn_enter": "Inserir dados bancários",
    "vietqr.disabled.or": "ou",
    "vietqr.disabled.btn_send_link": "Enviar link de configuração ao destinatário",
    "vietqr.disabled.btn_send_link_tip": "Gera um link público válido por 7 dias para o destinatário preencher",
    "vietqr.form.warn_title": "Leia antes de ativar",
    "vietqr.form.warn_body_a": "As leituras não levarão mais à sua página de rastreio Zcardon. Os fundos vão",
    "vietqr.form.warn_body_b": "diretamente para sua conta bancária vietnamita",
    "vietqr.form.warn_body_c": "via interbancário NAPAS — o Zcardon nunca vê o dinheiro. O VietQR só funciona dentro do Vietnã para transferências em VND.",
    "vietqr.form.bank": "Banco",
    "vietqr.form.account_number": "Número da conta",
    "vietqr.form.account_name": "Titular da conta (25 caracteres)",
    "vietqr.form.city": "Cidade (15 caracteres)",
    "vietqr.form.advanced": "Avançado — valor fixo / referência (opcional)",
    "vietqr.form.amount_vnd": "Valor (VND)",
    "vietqr.form.amount_ph": "deixe em branco para valor variável",
    "vietqr.form.reference": "Referência (25 caracteres)",
    "vietqr.form.confirm": "Entendo que ao escanear este cartão os VND serão transferidos diretamente para a conta bancária acima. O Zcardon não processa nem retém esses fundos.",
    "vietqr.form.btn_attach": "Ativar VietQR",
    "vietqr.form.btn_attaching": "Ativando…",
    "vietqr.form.btn_cancel": "Cancelar",
    "vietqr.enabled.title": "VietQR ativo",
    "vietqr.enabled.desc": "Quando um app bancário vietnamita escanear este cartão, transferirá VND diretamente para a conta abaixo — sem passar pelo Zcardon.",
    "vietqr.enabled.bank_label": "Banco",
    "vietqr.enabled.account_label": "Conta",
    "vietqr.enabled.fixed_amount": "Valor fixo",
    "vietqr.enabled.reference": "Referência",
    "vietqr.enabled.btn_detach": "Desvincular VietQR",
    "vietqr.enabled.btn_detaching": "Desvinculando…",
    "vietqr.enabled.attached_at": "Ativado em",
    "vietqr.analytics.live_activity": "Atividade ao vivo",
    "vietqr.analytics.today": "hoje",
    "vietqr.analytics.days_live": "{n}d ativo",
    "vietqr.analytics.phantom_views": "Visualizações estimadas",
    "vietqr.analytics.confirmed_pays": "Pagamentos confirmados",
    "vietqr.analytics.total_vnd": "Total VND",
    "vietqr.analytics.recent_confirmed": "Confirmações recentes",
    "vietqr.analytics.phantom_explainer": "Visualizações estimadas ≈ downloads da imagem do cartão desde a ativação. As leituras reais do VietQR não passam pelos nossos servidores, então estimamos pela atividade de \"cartão exibido\". Peça ao destinatário para confirmar os pagamentos pelo link de configuração para dados precisos.",
    "vietqr.toast.required": "Número da conta e titular são obrigatórios.",
    "vietqr.toast.confirm_first": "Confirme que entendeu que o VietQR envia VND ao seu banco, bypassando o Zcardon.",
    "vietqr.toast.attached_default": "VietQR ativado.",
    "vietqr.toast.attach_failed": "Falha ao ativar o VietQR.",
    "vietqr.toast.detach_confirm": "Desvincular VietQR? O QR voltará ao link de rastreio Zcardon.",
    "vietqr.toast.detached": "VietQR desvinculado — o QR volta ao link de rastreio Zcardon.",
    "vietqr.toast.detach_failed": "Falha ao desvincular.",
    "vietqr.toast.link_copied": "Link copiado · expira em {h}h",
    "vietqr.toast.link_generated": "Link gerado · copie abaixo",
    "vietqr.toast.link_failed": "Falha ao gerar o link.",

    "setpw.toast.too_short": "A senha deve ter pelo menos 8 caracteres",
    "setpw.toast.no_match": "As senhas não coincidem",
    "setpw.toast.success_signin_again": "Senha definida — faça login",
    "setpw.toast.welcome": "Bem-vindo ao programa de agentes Zcardon",
    "setpw.toast.expired": "Link expirado ou inválido",
    "setpw.verifying": "Verificando seu convite…",
    "setpw.invalid.title": "Link expirado",
    "setpw.invalid.desc": "Este link de boas-vindas expirou ou já foi usado. Entre em contato com seu administrador Zcardon para receber um novo convite.",
    "setpw.invalid.home_btn": "Início",
    "setpw.invalid.support_btn": "Contatar suporte",
    "setpw.fallback_name": "agente",
    "setpw.eyebrow": "Boas-vindas ao agente",
    "setpw.heading": "Bem-vindo, {name}",
    "setpw.subheading": "Defina sua senha para ativar sua conta de agente. Você será conectado imediatamente.",
    "setpw.code_label": "Seu código de agente",
    "setpw.code_tag": "Compartilhe ao integrar clientes",
    "setpw.form.title": "Escolha uma senha",
    "setpw.form.new_label": "Nova senha",
    "setpw.form.placeholder": "Mínimo de 8 caracteres",
    "setpw.form.confirm_label": "Confirmar senha",
    "setpw.form.btn_busy": "Ativando…",
    "setpw.form.btn_submit": "Ativar minha conta de agente",
    "tx.heading": "Transações",
    "tx.kind.direct_deposit": "Depósito direto",
    "tx.kind.card_topup": "Recarga de cartão",
    "tx.kind.outbound_payout": "Pagamento de saída",
    "tx.toast.load_failed": "Falha ao carregar transações",
    "tx.summary.deposits": "Depósitos",
    "tx.summary.topups": "Recargas",
    "tx.summary.topups_sub": "cartão",
    "tx.summary.payouts": "Pagamentos",
    "tx.summary.payouts_sub": "saída",
    "tx.filter.all": "Todos",
    "tx.filter.deposits": "Depósitos",
    "tx.filter.topups": "Recargas",
    "tx.filter.payouts": "Pagamentos",
    "tx.loading": "Carregando transações…",
    "tx.empty.filtered": "Nenhuma transação corresponde a este filtro",
    "tx.empty.none": "Nenhuma transação ainda",
    "tx.empty.cta": "Comece com um depósito direto →",
    "tx.open_title": "Abrir",
    "terms.load_error_default": "Falha ao carregar os termos",
    "terms.toast.sign_and_check": "Por favor, assine e confirme para aceitar o contrato",
    "terms.toast.recorded": "Contrato registrado",
    "terms.toast.failed": "Falha no envio",
    "terms.error.title": "Link inválido",
    "terms.back_home": "Voltar ao início",
    "terms.loading": "Carregando contrato…",
    "terms.done.title": "Contrato aceito",
    "terms.done.meta": "Versão {v} · {ts}",
    "terms.done.next": "Você pode fechar esta página. Seu cartão estará pronto para uso assim que for emitido.",
    "terms.eyebrow": "Contrato do titular · v{v}",
    "terms.title_reaccept": "Aceite novamente o contrato do titular",
    "terms.title_accept": "Aceite o contrato do titular",
    "terms.desc.sender_sent": "enviou um cartão Zcardon para você",
    "terms.desc.you_were_sent": "Você recebeu um cartão Zcardon",
    "terms.desc.ending": "final",
    "terms.desc.read_before": "Leia o contrato abaixo, depois assine e confirme para ativar o cartão.",
    "terms.already_accepted_note": "Você já aceitou uma versão anterior. Aceite novamente para atualizar para a mais recente.",
    "terms.scroll_hint": "Role dentro da caixa para ler o contrato completo.",
    "terms.view_json": "Abrir como JSON",
    "terms.sign_label": "Assine com seu nome legal completo",
    "terms.sign_placeholder": "Conforme consta no seu documento de identidade",
    "terms.consent": "Li e concordo com o Contrato de Titular de Cartão Zcardon v{v}.",
    "terms.btn_recording": "Gravando…",
    "terms.btn_accept": "Aceitar contrato",
    "hub.eyebrow": "Seu hub de recebimento",
    "hub.heading.all_done": "Tudo pronto 🎉",
    "hub.heading.greet": "Olá, {name} 👋",
    "hub.fallback_name": "você",
    "hub.desc.sender_sent": "enviou um cartão Zcardon para você",
    "hub.desc.you_were_sent": "Você recebeu um cartão Zcardon",
    "hub.desc.ending": "final",
    "hub.desc.complete_steps": "Conclua as etapas abaixo para ativar tudo.",
    "hub.progress.label": "{done} de {total} etapas concluídas",
    "hub.blocker.title_one": "1 etapa obrigatória restante",
    "hub.blocker.title_many": "{n} etapas obrigatórias restantes",
    "hub.blocker.subtext": "Seu cartão não será totalmente ativado até que estas etapas sejam concluídas.",
    "hub.required_pill": "Obrigatório",
    "hub.done_pill": "Concluído",
    "hub.setup_btn": "Configurar",
    "hub.refresh": "Atualizar",
    "hub.loading": "Carregando…",
    "hub.error.title": "Problema com o link",
    "hub.error.default": "Não foi possível carregar seu hub de recebimento.",
    "hub.back_home": "Voltar para início",
    "hub.footer": "Salve esta página nos favoritos · fica ativa por 90 dias · somente pessoas com este link podem vê-la.",
    "onboarding.eyebrow": "Receba sua transferência",
    "onboarding.title": "Olá, {name}",
    "onboarding.fallback_name": "você",
    "onboarding.intro_via": "via Zcardon",
    "onboarding.intro_ref_open": "(referência",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Adicione sua conta bancária do {country} abaixo para receber os fundos. Seus dados vão diretamente para o Stripe (nosso parceiro de pagamentos) — o remetente nunca os verá.",
    "onboarding.invalid.title": "Link inválido",
    "onboarding.invalid.hint": "Peça ao remetente que envie um novo link.",
    "onboarding.loading": "Carregando…",
    "onboarding.complete.title": "Dados bancários já cadastrados",
    "onboarding.complete.desc": "Você já enviou sua conta bancária. O remetente liberará os fundos em breve.",
    "onboarding.success.title": "Dados bancários recebidos ✓",
    "onboarding.success.auto_paid": "Obrigado — sua conta bancária foi verificada e os fundos foram liberados agora. Eles devem chegar em 1 a 2 dias úteis.",
    "onboarding.success.pending": "Obrigado — sua conta bancária foi verificada. O remetente será notificado e os fundos chegarão à sua conta em 1 a 2 dias úteis.",
    "onboarding.success.ref": "Referência: {id}",
    "onboarding.beta.title": "Pré-visualização beta.",
    "onboarding.beta.body": "Esta página está publicada, mas o pipeline de pagamentos ao vivo aguarda aprovação do Stripe. O envio retornará um erro até que a plataforma seja habilitada. Verifique novamente mais tarde ou entre em contato com o suporte se for urgente.",
    "onboarding.select_placeholder": "Selecionar…",
    "onboarding.submit_error_default": "Falha no envio. Tente novamente.",
    "onboarding.submit_busy": "Enviando…",
    "onboarding.submit": "Enviar dados bancários →",
    "onboarding.secure_note": "Protegido via Stripe. Este link expira em 7 dias.",
    "onboarding.validate.required": "{field} é obrigatório",
    "onboarding.validate.format": "O formato de {field} parece incorreto (esperado: {pattern})",
    "onboarding.validate.no_schema": "Nenhum esquema de formulário disponível",
    "apply.toast.invalid_image": "Escolha uma imagem JPEG, PNG ou WEBP",
    "apply.toast.too_large": "A imagem deve ter no máximo 6 MB",
    "apply.toast.upload_both": "Envie os dois lados da sua carteira de motorista",
    "apply.toast.failed": "Falha no envio",
    "apply.toast.submitted": "Solicitação enviada",
    "apply.success.title": "Solicitação recebida",
    "apply.success.desc": "Obrigado, {name}. Analisaremos sua solicitação em 2 a 3 dias úteis e enviaremos os próximos passos para {email}.",
    "apply.success.back_home": "Voltar para o início",
    "apply.success.contact_support": "Contatar suporte",
    "apply.eyebrow": "Programa de agentes",
    "apply.heading": "Candidate-se para ser um agente Zcardon",
    "apply.intro": "Ganhe 0,5% de comissão em cada cartão que ajudar a emitir, em cada recarga que coletar e em cada depósito em dinheiro que aceitar em nome dos clientes. Aceitamos agentes de confiança em sua comunidade e que possam verificar sua identidade.",
    "apply.bullet1": "Receba seu próprio código de agente exclusivo · rastreável em todas as transações",
    "apply.bullet2": "Painel com resumos de ganhos semanais/mensais/anuais",
    "apply.bullet3": "Depósitos bancários diretos — pagos semanalmente",
    "apply.section.your_info": "Suas informações",
    "apply.field.full_name": "Nome legal completo",
    "apply.field.full_name_ph": "Conforme consta em sua carteira de motorista",
    "apply.field.email": "E-mail",
    "apply.field.phone": "Telefone",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Número da carteira de motorista",
    "apply.field.address": "Endereço",
    "apply.field.address_ph": "Rua, Cidade, Estado, CEP",
    "apply.field.why_join_label": "Por que você quer se tornar um agente? (opcional)",
    "apply.field.why_join_ph": "Conte um pouco sobre seus vínculos com a comunidade ou experiência com remessas",
    "apply.dl.section": "Verificação da carteira de motorista",
    "apply.dl.helper": "Envie fotos nítidas e bem iluminadas de ambos os lados. JPEG, PNG ou WEBP · máx. 6 MB cada. As imagens são armazenadas com privacidade e visualizadas apenas pelos administradores da Zcardon para verificação.",
    "apply.dl.front": "Frente da carteira de motorista",
    "apply.dl.back": "Verso da carteira de motorista",
    "apply.dl.tap_upload": "Toque para enviar",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "Cancelar",
    "apply.submit_busy": "Enviando…",
    "apply.submit": "Enviar candidatura",
    "agent.toast.load_failed": "Falha ao carregar dados do agente",
    "agent.toast.cust_created": "Cliente {name} criado",
    "agent.toast.cust_failed": "Falha ao criar cliente",
    "agent.toast.card_issued": "Cartão emitido · você ganhou {amount}",
    "agent.toast.card_issue_failed": "Falha ao emitir cartão",
    "agent.toast.cash_accepted": "Depósito em dinheiro aceito · você ganhou {amount}",
    "agent.toast.cash_failed": "Falha ao registrar depósito",
    "agent.loading": "Carregando…",
    "agent.eyebrow": "Portal do agente",
    "agent.fallback_name": "Agente",
    "agent.code_line.code_label": "Código do agente",
    "agent.code_line.commission_label": "comissão",
    "agent.earnings.label": "Saldo a receber",
    "agent.earnings.sub": "{n} transações não pagas",
    "agent.stat.week": "Esta semana",
    "agent.stat.month": "Este mês",
    "agent.stat.year": "Este ano",
    "agent.stat.txns": "{n} transações",
    "agent.stat.customers": "Seus clientes",
    "agent.stat.active": "ativo",
    "agent.customers.title": "Seus clientes",
    "agent.customers.empty": "Nenhum cliente ainda. Toque em \\\"Cadastrar cliente\\\" para começar.",
    "agent.customers.col_customer": "Cliente",
    "agent.customers.col_phone": "Telefone",
    "agent.customers.col_cards": "Cartões",
    "agent.customers.col_commissions": "Comissões ganhas",
    "agent.customers.col_joined": "Cadastro",
    "agent.customers.col_actions": "Ações",
    "agent.customers.txns": "{n} transações",
    "agent.commissions.title": "Comissões recentes",
    "agent.commissions.empty": "Nenhuma comissão ainda. Emita seu primeiro cartão para ganhar.",
    "agent.commissions.col_when": "Quando",
    "agent.commissions.col_customer": "Cliente",
    "agent.commissions.col_type": "Tipo",
    "agent.commissions.col_txn": "Transação",
    "agent.commissions.col_commission": "Comissão",
    "agent.commissions.col_status": "Status",
    "agent.commissions.paid": "pago",
    "agent.commissions.unpaid": "não pago",
    "agent.type.issuance": "Emissão",
    "agent.type.topup": "Recarga",
    "agent.type.cash_deposit": "Depósito em dinheiro",
    "agent.register_btn": "Cadastrar cliente",
    "agent.dialog.new_cust.title": "Novo cliente",
    "agent.dialog.new_cust.desc": "Ele será vinculado ao seu código de agente. Cada cartão emitido em nome dele gera 0,5% de comissão para você.",
    "agent.dialog.field.full_name": "Nome completo",
    "agent.dialog.field.email": "E-mail",
    "agent.dialog.field.phone": "Telefone",
    "agent.dialog.field.password": "Senha inicial (compartilhe com o cliente)",
    "agent.dialog.cancel": "Cancelar",
    "agent.dialog.creating": "Criando…",
    "agent.dialog.create_customer": "Criar cliente",
    "agent.dialog.issue_card.title": "Emitir cartão para {name}",
    "agent.dialog.issue_card.desc": "Financiado com dinheiro recebido em mãos. O cliente paga uma taxa de 3,75% — você fica com 0,5%.",
    "agent.dialog.field.receiver_name": "Nome do destinatário",
    "agent.dialog.field.receiver_email": "E-mail do destinatário",
    "agent.dialog.field.receiver_phone": "Telefone do destinatário",
    "agent.dialog.field.amount_usd": "Valor (USD)",
    "agent.dialog.field.note": "Observação (opcional)",
    "agent.dialog.issuing": "Emitindo…",
    "agent.dialog.issue_card_btn": "Emitir cartão",
    "agent.actions.issue_card": "Emitir cartão",
    "agent.dialog.cash_deposit.title": "Depósito em dinheiro para {name}",
    "agent.dialog.cash_deposit.desc": "Dinheiro recebido do cliente · cria um cartão entregue ao destinatário.",
    "agent.dialog.field.receiver_name_opt": "Nome do destinatário (opcional)",
    "agent.dialog.recording": "Registrando…",
    "agent.dialog.record_deposit": "Registrar depósito",
    "agent.actions.cash_deposit": "Depósito em dinheiro",

  },
  ru: {
    "header.nav.home": "Главная",
    "header.nav.rates": "Тарифы",
    "header.nav.support": "Поддержка",
    "header.nav.admin": "Админ",
    "header.nav.signin": "Войти",
    "header.nav.signout": "Выйти",
    "header.nav.get_started": "Начать",
    "header.nav.account": "Аккаунт",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "Сменить язык",
    "landing.hero.eyebrow": "Новый уровень цифровых расходов",
    "landing.hero.title_a": "Пополните виртуальную карту.",
    "landing.hero.title_b_ship": "Отправьте",
    "landing.hero.title_b_rest": "её кому угодно,",
    "landing.hero.title_c": "тратьте в",
    "landing.hero.subtitle": "Выпустите виртуальную дебетовую карту с поддержкой tap-and-pay и сканируемым QR-кодом. Оплата через Stripe, баланс в USD, прозрачные сервисные и шлюзовые комиссии — получатель получает карту по email или скачивает напрямую.",
    "landing.hero.cta_start": "Выпустить карты",
    "landing.hero.cta_request_cash": "Запросить кэш-карту",
    "landing.hero.cta_rates": "Смотреть тарифы",
    "landing.hero.cta_find_card": "Найти мою карту",
    "landing.hero.stat_currency": "Валюта",
    "landing.hero.stat_settle": "Время расчёта",
    "landing.hero.stat_settle_value": "~2 мин",
    "landing.hero.stat_service_fee": "Сервисный сбор",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "Создано для современной карточной экономики",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "Расчёт в USD",
    "landing.hero.marquee": "Выпуск · Пополнение · Расходы в USD",
    "landing.demo.eyebrow": "Смотрите за 20 секунд",
    "landing.demo.title_a": "От выпуска до",
    "landing.demo.title_b_spend": "оплаты",
    "landing.demo.title_c": ". За четыре касания.",
    "landing.demo.step_issue.label": "Выпустить карту",
    "landing.demo.step_issue.detail": "Выберите сумму. Карта готова за ~2 минуты.",
    "landing.demo.step_share.label": "Поделиться",
    "landing.demo.step_share.detail": "Email, SMS, WhatsApp или скачать как PNG.",
    "landing.demo.step_scan.label": "Получатель сканирует",
    "landing.demo.step_scan.detail": "Камера телефона мгновенно открывает страницу отслеживания.",
    "landing.demo.step_spend.label": "Тратить в USD",
    "landing.demo.step_spend.detail": "Tap-to-pay, снятие наличных, оплата у продавцов.",
    "landing.demo.cta_try": "Попробовать",
    "landing.demo.live_pill": "Демо в реальном времени",
    "landing.demo.scene1_caption": "Генерация карты · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "Отправка получателю через WhatsApp",
    "landing.demo.scene3_caption": "Сканирование QR · совпадение найдено",
    "landing.demo.scene4_eyebrow": "Баланс зачислен",
    "landing.demo.scene4_status": "✓ одобрено · tap-to-pay готов",
    "landing.footer.rates": "Тарифы",
    "landing.footer.support": "Поддержка",
    "landing.footer.admin_portal": "Портал администратора",
    "landing.footer.copy": "© 2026 Zcardon · Демо-продукт в сфере финтех",
    "landing.cash.dialog_title": "Запросить кэш-карту",
    "landing.cash.dialog_desc": "Мы отправим вам ссылку на оплату через Stripe по SMS. Оплатите любой картой — кэш-карта пополняется сразу после подтверждения от Stripe.",
    "landing.cash.phone_label": "Номер телефона",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "Сумма (USD)",
    "landing.cash.name_label": "Ваше имя (необязательно)",
    "landing.cash.submit": "Отправить ссылку на оплату",
    "landing.cash.submitting": "Отправка…",
    "landing.cash.success_title": "Ссылка на оплату отправлена",
    "landing.cash.success_body": "Мы отправили ссылку Stripe на {phone}. Нажмите на неё для оплаты — виртуальная карта активируется сразу.",
    "landing.cash.success_open_checkout": "Открыть оплату",
    "landing.find.cta": "Уже оплатили? Найти мою карту",
    "landing.find.dialog_title": "Найти мою карту",
    "landing.find.dialog_desc": "Введите номер телефона, который вы использовали. Мы отправим 6-значный код для восстановления активной кэш-карты.",
    "landing.find.phone_label": "Номер телефона",
    "landing.find.code_label": "6-значный код",
    "landing.find.send_code": "Отправить код",
    "landing.find.verify": "Подтвердить и найти",
    "landing.find.sending": "Отправка…",
    "landing.find.verifying": "Проверка…",
    "landing.find.no_cards": "Активных карт для этого номера не найдено.",
    "login.eyebrow.welcome": "Добро пожаловать",
    "login.eyebrow.admin": "Администрирование",
    "login.title": "Войти",
    "login.title_admin": "Вход для администратора",
    "login.admin_badge": "Портал администратора",
    "login.email_label": "Email",
    "login.password_label": "Пароль",
    "login.submit": "Войти",
    "login.submitting": "Вход…",
    "login.toast_welcome": "С возвращением, {name}",
    "login.toast_failed": "Ошибка входа",
    "login.gate_title": "Вход для клиентов временно приостановлен",
    "login.gate_default_message": "Мы улучшаем платформу. Заходите позже.",
    "login.gate_admin_note": "Администраторы и менеджеры могут войти ниже.",
    "login.new_here": "Впервые здесь?",
    "login.create_account": "Создать аккаунт",
    "login.customer_q": "Клиент?",
    "login.regular_signin": "Обычный вход",
    "login.demo_title": "Демо-аккаунты",
    "login.demo_customers_note": "Клиенты: зарегистрируйтесь через /register или создайте через панель администратора",
    "login.forgot_password": "Забыли пароль?",
    "forgot.eyebrow": "Восстановление аккаунта",
    "forgot.title": "Сброс пароля",
    "forgot.subtitle": "Введите email, привязанный к вашему аккаунту Zcardon, и мы отправим вам защищённую ссылку для выбора нового пароля.",
    "forgot.email_label": "Email",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "Отправить ссылку",
    "forgot.submitting": "Отправка…",
    "forgot.toast_failed": "Не удалось отправить ссылку. Попробуйте ещё раз.",
    "forgot.sent_title": "Проверьте почту",
    "forgot.sent_message": "Если аккаунт с адресом {email} существует, мы отправили ссылку для сброса пароля.",
    "forgot.sent_expiry_note": "Ссылка действительна 15 минут. Не получили? Проверьте спам или отправьте ещё раз.",
    "forgot.back_to_login": "Назад ко входу",
    "forgot.resend_link": "Отправить ещё раз",
    "forgot.remember_q": "Вспомнили пароль?",
    "forgot.signin_link": "Войти",
    "reset.eyebrow": "Восстановление аккаунта",
    "reset.title": "Выберите новый пароль",
    "reset.subtitle": "Придумайте надёжный пароль, который вы ещё не использовали. После установки вы войдёте автоматически.",
    "reset.verifying": "Проверка ссылки для сброса…",
    "reset.invalid_title": "Ссылка недействительна или устарела",
    "reset.invalid_message": "Ссылки действительны 15 минут и могут быть использованы однократно. Запросите новую, чтобы продолжить.",
    "reset.request_new_link": "Запросить новую ссылку",
    "reset.new_password_label": "Новый пароль",
    "reset.confirm_password_label": "Подтвердите пароль",
    "reset.password_hint": "Не менее 8 символов. Не используйте пароли от других сервисов.",
    "reset.submit": "Обновить пароль",
    "reset.submitting": "Обновление…",
    "reset.toast_success": "Пароль обновлён. Вы вошли в систему.",
    "reset.toast_failed": "Не удалось сбросить пароль. Попробуйте ещё раз.",
    "reset.error_min_length": "Пароль должен содержать не менее 8 символов.",
    "reset.error_mismatch": "Пароли не совпадают.",
    "register.eyebrow": "Регистрация",
    "register.title": "Создать аккаунт",
    "register.full_name_label": "Полное имя",
    "register.email_label": "Email",
    "register.password_label": "Пароль",
    "register.submit": "Создать аккаунт",
    "register.submitting": "Создание…",
    "register.gate_title": "Регистрация временно приостановлена",
    "register.gate_default_message": "Мы улучшаем платформу. Заходите позже.",
    "register.gate_already_member": "Уже есть аккаунт?",
    "register.toast_created": "Аккаунт создан",
    "register.toast_failed": "Ошибка регистрации",
    "register.already_member": "Уже зарегистрированы?",
    "register.signin_link": "Войти",
    "dash.eyebrow": "Дашборд",
    "dash.greeting": "С возвращением, {name}",
    "dash.subtitle": "Выпустите виртуальную карту или отправьте банковский перевод — ваш центр управления ниже.",
    "dash.action.issue_card": "Выпустить карту",
    "dash.action.direct_deposit": "Прямой депозит (перевод)",
    "dash.action.support": "Поддержка",
    "dash.section.cards": "Ваши карты",
    "dash.section.activity": "Последние операции",
    "dash.section.deposits": "Прямые депозиты",
    "dash.empty.cards": "Карт пока нет — выпустите первую, чтобы начать.",
    "dash.empty.activity": "Как только вы начнёте выпускать карты или отправлять переводы, последние операции появятся здесь.",
    "ref.eyebrow": "Добро пожаловать в Zcardon",
    "ref.greeting": "{name} пригласил вас.",
    "ref.loading": "Загрузка приглашения…",
    "ref.resolved_redirect": "Секунду — перенаправляем вас.",
    "ref.resolving": "Обработка пригласительной ссылки…",
    "support.eyebrow": "Мы рядом",
    "support.title": "Поддержка, а не заявки.",
    "support.subtitle": "Общайтесь с живым человеком. Ответ на email в течение одного рабочего часа по будням.",
    "support.card.email": "Email",
    "support.card.phone": "Телефон",
    "support.card.live_chat": "Онлайн-чат",
    "support.card.live_chat_value": "В вашем дашборде",
    "support.card.hours": "Часы работы",
    "support.faq.title": "Частые вопросы",
    "support.faq.q1": "Когда получатель получит карту?",
    "support.faq.a1": "Как правило, в течение 2 минут после успешного пополнения. Доставка по email (Resend) и MMS (Twilio) происходит автоматически.",
    "support.faq.q2": "Можно ли отменить или заморозить карту после отправки?",
    "support.faq.a2": "Да — откройте страницу карты и нажмите «Заморозить». Также можно заморозить прямо из письма-уведомления получателю.",
    "support.faq.q3": "В каких валютах можно использовать карту?",
    "support.faq.a3": "Все карты рассчитываются в USD. Получатель может совершать покупки везде, где принимается Visa/Mastercard — конвертация валют выполняется карточной сетью.",
    "support.faq.q4": "Какие комиссии взимаются?",
    "support.faq.a4": "Прозрачная сервисная комиссия плюс стоимость шлюза Stripe. Актуальная стоимость отображается на странице тарифов до оплаты.",
    "rates.eyebrow": "Прозрачно",
    "rates.title_a": "Никаких скрытых",
    "rates.title_b": "наценок",
    "rates.subtitle": "Вы видите сервисную комиссию и стоимость шлюза до оплаты. Основная сумма всегда зачисляется на баланс виртуальной карты в USD.",
    "rates.row.service_fee": "Сервисная комиссия",
    "rates.row.service_fee_value": "{pct} от отправленной суммы",
    "rates.row.gateway": "Шлюз (Stripe)",
    "rates.row.settlement": "Расчёт",
    "rates.row.settlement_value": "Баланс в USD на виртуальной карте",
    "rates.row.topup": "Способы пополнения",
    "rates.row.topup_value": "Карта · Дебет · Банк (ACH)",
    "rates.sim.eyebrow": "Актуальный расчёт",
    "rates.sim.title_a": "Отправьте любую сумму —",
    "rates.sim.title_b": "увидьте разбивку мгновенно.",
    "rates.sim.amount_label": "Сумма отправки",
    "rates.sim.line.principal": "На карту (основная сумма)",
    "rates.sim.line.service": "Сервисная комиссия · {pct}",
    "rates.sim.line.gateway": "Шлюз · {pct} + {fixed}",
    "rates.sim.line.you_pay": "Вы платите",
    "rates.sim.cta": "Начать с ${amount} →",
    "rates.sim.note": "Тарифы применяются согласно настройкам администратора. Получатель всегда получает полную основную сумму.",
    "dd.title": "Прямой перевод",
    "dd.intro": "Отправляйте деньги на банковский счёт получателя в любой точке мира. Оплачивайте картой (Stripe Checkout) или балансом Zcardon — мы автоматически выполняем перевод в банк получателя там, где это поддерживается, или вручную для стран без автоматической маршрутизации. Каждая транзакция сохраняется с распечатываемой квитанцией.",
    "dd.new_btn": "Создать новый прямой перевод",
    "dd.close": "Закрыть",
    "dd.history_eyebrow": "Ваши переводы",
    "dd.loading": "Загрузка…",
    "dd.empty": "Переводов пока нет.",
    "dd.status.approved": "одобрено",
    "dd.status.pending": "ожидает одобрения",
    "dd.status.rejected": "отклонено",
    "dd.row.confirmed": "✓ Получатель подтвердил получение",
    "dd.row.disputed": "⚠ Получатель сообщил о расхождении",
    "dd.row.pdf": "PDF",
    "dd.error.load": "Не удалось загрузить переводы",
    "dd.error.pdf": "Не удалось скачать квитанцию",
    "pay.mode.view": "Ваша карта Zcardon",
    "pay.mode.charge": "Оплата по QR",
    "pay.heading.view": "Привет, {name}.",
    "pay.heading.charge": "Списать с этой карты.",
    "pay.cardholder": "Владелец карты",
    "pay.paused.label": "Карта приостановлена",
    "pay.paused.body": "Эта карта заморожена и не может принимать новые платежи, пока владелец не возобновит её.",
    "pay.balance.label": "Доступный баланс",
    "pay.charges_count": "{count} платёж",
    "pay.charges_count_plural": "{count} платежей",
    "pay.topups_count": "{count} пополнение",
    "pay.topups_count_plural": "{count} пополнений",
    "pay.activity.label": "Последние операции",
    "pay.activity.empty": "Операций пока нет",
    "pay.cta.charge": "Списать с карты (продавец)",
    "pay.cta.charge_paused": "Карта приостановлена",
    "pay.cta.note_active": "Нажмите выше, только если вы являетесь продавцом, принимающим оплату с этой карты.",
    "pay.cta.note_paused": "Эта карта приостановлена отправителем. Попросите его возобновить её.",
    "pay.back_to_balance": "Вернуться к балансу",
    "pay.form.amount_label": "Сумма (USD)",
    "pay.form.amount_max": "Макс. {amount}",
    "pay.form.merchant_label": "Продавец / причина",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "Примечание (необязательно)",
    "pay.form.memo_placeholder": "Two lattes",
    "pay.form.submit_processing": "Обработка…",
    "pay.form.submit_paused": "Карта приостановлена",
    "pay.form.submit_charge": "Списать {amount}",
    "pay.form.submit_charge_card": "Списать с карты",
    "pay.form.disclaimer": "Списывая средства с этой карты, вы подтверждаете, что авторизованы владельцем карты на проведение указанной суммы.",
    "pay.receipt.title": "Платёж выполнен",
    "pay.receipt.to": "кому",
    "pay.receipt.id": "чек",
    "pay.receipt.time": "время",
    "pay.receipt.new_balance": "новый баланс",
    "pay.receipt.done": "Готово · вернуться к балансу",
    "pay.error.cant_load": "Не удалось загрузить карту",
    "pay.error.back_home": "На главную",
    "pay.error.loading": "Загрузка карты…",
    "pay.error.charge_failed": "Ошибка списания",
    "pay.tap.eyebrow": "Нажмите для оплаты",
    "pay.tap.title": "Покажите этот код",
    "pay.tap.subtitle": "сканеру кассира",
    "pay.tap.balance": "Баланс",
    "pay.tap.paused": "Приостановлено",
    "pay.tap.qr_unavailable": "QR недоступен для этой карты",
    "pay.tap.refresh_in": "Обновится через {secs}с",
    "pay.tap.show_digits": "Показать цифры",
    "pay.tap.hide_digits": "Скрыть цифры",
    "pay.tap.card_label": "Карта · ••••",
    "pay.tap.auto_hides": "Автоскрытие через 60с",
    "pay.tap.full_only_image": "Полный номер отображается только на оригинальном изображении карты",
    "pay.tap.disclaimer": "Карта замкнутого контура · принимается у любого продавца с поддержкой Zcardon. QR обновляется каждые 60с для защиты от повторного использования.",
    "pay.subscribe.eyebrow": "Будьте в курсе",
    "pay.subscribe.title": "Получайте письмо о каждой транзакции",
    "pay.subscribe.desc": "Бесплатно. Одно письмо на каждое списание или пополнение. Отписаться можно в любой момент. Мы никому не передаём ваш адрес.",
    "pay.subscribe.placeholder": "вы@пример.com",
    "pay.subscribe.cta": "Уведомлять меня",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "Вы подписаны",
    "pay.subscribe.done.body": "Мы будем мгновенно отправлять письма на {email} при каждом списании или пополнении карты •••• {last4}.",
    "pay.subscribe.toast.already": "Вы уже были подписаны — дубликат не отправлен.",
    "pay.subscribe.toast.success": "Вы будете получать письмо о каждой транзакции. Добро пожаловать!",
    "pay.subscribe.toast.failed": "Не удалось подписаться",
    "pay.save.eyebrow": "Карта всегда под рукой",
    "pay.save.title": "Сохраните карту на телефон",
    "pay.save.desc": "Сохраните изображение карты в фото для доступа офлайн — показывайте QR кассирам в любое время, без приложения.",
    "pay.save.cta": "Сохранить изображение карты",
    "pay.save.preparing": "Подготовка…",
    "pay.save.add_home": "Добавить на главный экран",
    "pay.save.toast.opened": "Изображение карты открыто — нажмите и удерживайте, чтобы сохранить в Фото",
    "pay.save.toast.failed": "Не удалось сохранить карту",
    "pay.save.ios.title": "Чтобы добавить Zcardon на главный экран iPhone:",
    "pay.save.ios.step1": "Нажмите кнопку «Поделиться» внизу Safari",
    "pay.save.ios.step2": "Прокрутите вниз и нажмите «На экран «Домой»»",
    "pay.save.ios.step3": "Нажмите «Добавить» для подтверждения",
    "pay.save.ios.note": "Zcardon появится как обычное приложение — нажмите на значок, чтобы мгновенно открыть карту.",
    "pay.thanks.eyebrow": "Отправить благодарность",
    "pay.thanks.body": "${amount} уже зачислено — дайте знать человеку, пополнившему эту карту, что это помогло.",
    "pay.thanks.placeholder": "Необязательно — напишите пару слов…",
    "pay.thanks.cta": "Отправить {emoji} благодарность",
    "pay.thanks.sending": "Отправка…",
    "pay.thanks.done": "Благодарность отправлена — они увидят её через несколько секунд.",
    "pay.thanks.toast.sent": "Благодарность отправлена",
    "pay.thanks.toast.failed": "Не удалось отправить благодарность",
    "pay.activity.topup_bank": "Пополнение банковским переводом",
    "pay.activity.topup_debit": "Пополнение дебетовой картой",
    "pay.activity.topup_credit": "Пополнение кредитной картой",
    "pay.activity.topup_crypto": "Пополнение криптовалютой",
    "pay.activity.topup_received": "Пополнение получено",
    "pay.activity.merchant_charge": "Списание у продавца",
    "pay.toast.payment_received": "Платёж получен · −{amount}",
    "cash.confirming": "Подтверждение платежа…",
    "cash.confirming.sub": "Stripe завершает списание.",
    "cash.notfound.title": "Ссылка устарела",
    "cash.notfound.body": "Не удалось найти этот запрос на пополнение. Ссылка могла быть из старой сессии или карта не была оформлена. Создайте новый запрос — это займёт минуту, а любой уже выполненный платёж будет автоматически зачислен, как только это обнаружит фоновый сервис.",
    "cash.notfound.ref": "ref:",
    "cash.notfound.start": "Создать новый запрос",
    "cash.notfound.contact": "Связаться с поддержкой",
    "cash.error.title": "Не удалось проверить статус",
    "cash.error.return": "На главную",
    "cash.autopay.opening": "Открытие режима оплаты…",
    "cash.autopay.redirect": "Перенаправление через {secs}с.",
    "cash.autopay.stay": "Остаться здесь",
    "cash.ready.title": "Ваша карта готова",
    "cash.ready.subtitle": "Пополнена на ${amount} USD · также отправлено SMS на {phone}",
    "cash.card.label": "Карта",
    "cash.card.exp": "Срок",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "Баланс",
    "cash.cta.open_pay": "Открыть экран оплаты",
    "cash.toast.funded": "Ваша карта пополнена — проверьте телефон, вам пришло SMS!",
    "cash.pending.title": "Ожидаем ответа от Stripe",
    "cash.pending.body": "Мы ожидаем подтверждения. Обычно это занимает 5–15 секунд. Если вы закрыли форму оплаты, не завершив платёж, вы можете начать заново с главной страницы.",
    "cash.pending.back": "На главную",
    "cash.timeout.title": "Не удалось подтвердить платёж",
    "cash.timeout.body": "Мы ждали 3 минуты подтверждения от Stripe. Чаще всего это означает, что форма оплаты была закрыта до завершения платежа — но если вы ВСЁ ЖЕ оплатили, ваша карта будет активирована автоматически в течение нескольких минут (фоновая сверка запускается каждые 5 мин), и мы сразу отправим данные на {phone}.",
    "cash.timeout.start_over": "Начать заново",
    "cash.timeout.try_again": "Попробовать снова",
    "cash.resend.title": "Не получили? Отправить повторно",
    "cash.resend.degraded.lead": "Доставка на номера США сейчас нарушена.",
    "cash.resend.degraded.sms": "SMS на американские номера могут тихо блокироваться оператором (верификация toll-free ещё не завершена).",
    "cash.resend.degraded.email": "Email работает в тестовом режиме и доставляется только на адрес владельца аккаунта, пока проходит проверка DNS.",
    "cash.resend.degraded.tail": "Если один канал не работает, попробуйте другой — или откройте эту страницу на устройстве получателя, чтобы получить карту напрямую.",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "Отправка…",
    "cash.resend.email_sandbox": "Email в тестовом режиме. Пока домен zcardon.com проходит верификацию в Resend, отправка возможна только на адрес владельца аккаунта. Если ваш email не принимается, используйте SMS или WhatsApp.",
    "cash.resend.email_label": "Адрес электронной почты",
    "cash.resend.email_send": "Отправить на email",
    "cash.resend.cancel": "Отмена",
    "cash.resend.limit": "Лимит: 5 повторных отправок на карту. Номер телефона: {phone}",
    "cash.resend.no_phone": "(не указан)",
    "cash.wallet.title": "Добавить в Wallet",
    "cash.wallet.apple": "Добавить в Apple Wallet",
    "cash.wallet.google": "Добавить в Google Wallet",
    "cash.wallet.generating": "Создание пропуска…",
    "cash.wallet.note": "Отображается на экране блокировки · Обновляется автоматически при изменении баланса",
    "cash.wallet.toast.apple": "Пропуск загружен — откройте его на iPhone, чтобы добавить в Apple Wallet",
    "cash.wallet.toast.apple_failed": "Не удалось создать пропуск Apple Wallet",
    "cash.wallet.toast.google_failed": "Не удалось создать пропуск Google Wallet",
    "cash.delivery.fallback_title": "Отправлено через {fallback}",
    "cash.delivery.fallback_body": "Ваш оператор заблокировал исходное SMS, поэтому мы повторно отправили карту через {fallback} на {phone}. Проверьте этот канал.",
    "cash.delivery.toll_blocked": "Оператор заблокировал SMS",
    "cash.delivery.failed_title": "Не удалось отправить карту по SMS",
    "cash.delivery.toll_body": "Ваш мобильный оператор отклонил сообщение (это происходит с новыми отправителями, ожидающими верификации). Сохраните карту сейчас: нажмите",
    "cash.delivery.failed_body": "Ваша карта пополнена — сохраните её сейчас, так как SMS не было доставлено. Нажмите",
    "cash.delivery.cta_label": "Открыть экран оплаты касанием",
    "cash.delivery.body_tail": "выше, сделайте скриншот или воспользуйтесь Email ниже, чтобы отправить копию себе.",
    "cash.handoff.eyebrow": "Передать лично",
    "cash.handoff.body": "Попросите получателя отсканировать это камерой — карта откроется напрямую на его телефоне, без SMS или email.",
    "cash.handoff.share_label": "Или поделитесь ссылкой",
    "cash.handoff.copy": "Скопировать ссылку",
    "cash.handoff.copied": "✓ Скопировано",
    "cash.handoff.share": "Поделиться",
    "cash.handoff.toast.copied": "Ссылка скопирована — вставьте её куда нужно",
    "cash.handoff.toast.copy_failed": "Не удалось скопировать. Удерживайте ссылку, чтобы скопировать вручную.",
    "portal.loading": "Загрузка квитанции…",
    "portal.unavailable.title": "Ссылка недоступна",
    "portal.unavailable.body": "Эта ссылка недействительна или устарела.",
    "portal.unavailable.hint": "Если отправитель поделился с вами этой ссылкой, попросите его повторно отправить квитанцию по email.",
    "portal.brand": "Портал квитанций",
    "portal.eyebrow": "{sender} отправил вам",
    "portal.reference": "Референс",
    "portal.pending.title": "Ожидает подтверждения",
    "portal.pending.body": "Отправитель зафиксировал этот перевод, но Zcardon ещё проверяет его. Пожалуйста, загляните позже — квитанция станет доступна после одобрения.",
    "portal.row.bank": "Банк",
    "portal.row.account": "Счёт",
    "portal.row.routing": "Маршрутный номер",
    "portal.row.memo": "Примечание",
    "portal.confirmed.title": "Вы подтвердили получение",
    "portal.confirmed.body": "Подтверждено {when}. Отправитель видит это.",
    "portal.disputed.title": "Вы сообщили о расхождении",
    "portal.disputed.body": "Сообщено {when}. Отправителю отправлено письмо.",
    "portal.actions.body": "Средства поступили на ваш счёт? Подтверждение закрывает цикл с отправителем — деньги не проходят через Zcardon, это лишь запись.",
    "portal.actions.confirm": "Да, я получил",
    "portal.actions.flag": "Сообщить о проблеме",
    "portal.dispute.placeholder": "Неверная сумма, не поступило, неверный номер счёта…",
    "portal.dispute.submit": "Отправить жалобу",
    "portal.dispute.too_short": "Добавьте краткое описание (не менее 4 символов)",
    "portal.toast.confirmed": "Спасибо — получение подтверждено.",
    "portal.toast.confirm_failed": "Не удалось подтвердить получение",
    "portal.toast.reported": "Жалоба отправлена. Отправитель уведомлён.",
    "portal.toast.report_failed": "Не удалось отправить жалобу",
    "portal.toast.pdf_failed": "Не удалось скачать квитанцию",
    "portal.download_pdf": "Скачать квитанцию в PDF",
    "portal.privacy": "Эта страница доступна только вам. Любой, у кого есть ссылка, может просмотреть квитанцию — не публикуйте её. Вопросы:",
    "detail.back": "Назад к панели управления",
    "detail.eyebrow": "Ваша карта",
    "detail.confirming_payment": "Подтверждение платежа…",
    "detail.active_ready": "Карта активна и готова к отправке.",
    "detail.balance.label": "Текущий баланс",
    "detail.toast.notfound": "Карта не найдена",
    "detail.toast.loaded": "Средства зачислены на карту.",
    "detail.toast.confirmed": "Платёж подтверждён.",
    "detail.toast.expired": "Сессия оплаты истекла",
    "detail.delivery.receiver_fallback": "получатель",
    "detail.delivery.received_title": "{name} открыл карту",
    "detail.delivery.received_when": "Доставка подтверждена · {when}",
    "detail.delivery.waiting_title_on": "Вы получите письмо, как только карту откроют",
    "detail.delivery.waiting_title_off": "Уведомления о доставке отключены",
    "detail.delivery.waiting_body_on": "Как только получатель отсканирует QR-код на карте, мы отправим вам письмо — только один раз.",
    "detail.delivery.waiting_body_off": "Включите, чтобы получить одноразовое письмо в момент первого открытия карты получателем.",
    "detail.delivery.toggle_on": "Вкл.",
    "detail.delivery.toggle_off": "Включить",
    "detail.delivery.toast_enabled": "Вы получите уведомление, когда карту откроют.",
    "detail.delivery.toast_disabled": "Уведомления о доставке отключены.",
    "detail.delivery.toast_failed": "Не удалось обновить уведомления о доставке.",
    "nudge.eyebrow": "Сообщите получателю",
    "nudge.title": "Предупредите {receiver}",
    "nudge.subtitle": "Мы составили короткое сообщение, чтобы {receiver} знал, что карта активна и как ею воспользоваться.",
    "nudge.receiver_fallback": "получателя",
    "nudge.sender_fallback": "друг",
    "nudge.template": "Привет, {receiver} — {sender} только что отправил тебе виртуальную карту Zcardon на {amount}. Нажми, чтобы оплатить: {url}\\n\\nПриложение не нужно. Ссылка работает на любом телефоне.",
    "nudge.email_subject": "{sender} отправил тебе карту Zcardon на {amount}",
    "nudge.action_copy": "Скопировать сообщение",
    "nudge.action_sms": "Открыть SMS",
    "nudge.action_email": "Открыть почту",
    "nudge.toast_copied": "Сообщение скопировано в буфер обмена.",
    "nudge.toast_copy_failed": "Не удалось скопировать. Выделите и скопируйте вручную.",
    "ios_install.title": "Установите Zcardon на iPhone",
    "ios_install.step_1": "Нажмите",
    "ios_install.share_label": "Поделиться",
    "ios_install.step_2": " и выберите \\\"На экран «Домой»\\\" — Zcardon откроется как полноэкранное приложение.",
    "smart_banner.title": "Открыть в приложении Zcardon",
    "smart_banner.subtitle": "Быстрая оплата. Платите, не выходя из приложения.",
    "smart_banner.cta": "Открыть",
    "smart_banner.dismiss": "Закрыть",
    "social_proof.eyebrow": "В эфире · последние 48 часов",
    "social_proof.empty": "Ждём следующую карту…",
    "social_proof.line_opened": "{name} только что открыл карту на {amount}.",
    "social_proof.line_issued": "{name} получил карту на {amount} от друга.",
    "social_proof.line_deposit": "{name} получил прямой депозит на {amount}.",
    "social_proof.stat_funded": "Карт пополнено сегодня",
    "social_proof.stat_delivered": "Карт доставлено сегодня",
    "social_proof.stat_recent": "Недавняя активность",
    "social_proof.ago_seconds": "{n}с назад",
    "social_proof.ago_minutes": "{n}м назад",
    "social_proof.ago_hours": "{n}ч назад",
    "flight.eyebrow": "Карты в пути · последние 48ч",
    "flight.title": "Где приземляется Zcardon",
    "flight.subtitle": "Реальные местоположения получателей, анонимизированные до региона. Мы показываем только страны и штаты США — никаких имён, адресов или сумм.",
    "flight.row_template": "Карта открыта в {label}",
    "flight.more_count": "+ ещё {n} регионов",
    "flight.ago_seconds": "{n}с назад",
    "flight.ago_minutes": "{n}м назад",
    "flight.ago_hours": "{n}ч назад",
    "trust_strip.eyebrow": "Реальные данные · в прямом эфире из базы",
    "trust_strip.title_a": "Не отзывы.",
    "trust_strip.title_b": "Живые данные из базы.",
    "trust_strip.how_link": "Как мы это собираем",
    "trust_strip.modal_eyebrow": "Источники данных",
    "trust_strip.modal_title": "Всё реально, всё анонимизировано",
    "trust_strip.modal_close": "Закрыть",
    "trust_strip.modal_intro": "Каждое число в этой полосе берётся из нашей собственной базы данных — никаких сторонних источников, ничего выдуманного. Ниже — откуда берётся каждый раздел и как мы анонимизируем данные перед показом.",
    "trust_strip.src_carousel_title": "Карусель активности в реальном времени",
    "trust_strip.src_carousel_body": "Данные из коллекций cards и direct_deposits за последние 48ч. Имена отображаются как имя + первая буква фамилии; суммы округляются до $5 для защиты от деанонимизации. Обновляется каждые 60 секунд.",
    "trust_strip.src_flight_title": "Где приземляется Zcardon",
    "trust_strip.src_flight_body": "Читает данные о сканированиях QR из card_scans, геолоцированных в момент сканирования. Сворачивается до страны (или штата США). Точность ниже уровня штата не используется, IP-адреса не отображаются, идентификаторы карт не раскрываются. Обновляется каждые 90 секунд.",
    "trust_strip.src_badges_title": "Показатели доверия за 30 дней",
    "trust_strip.src_badges_body": "Только агрегированные данные — никаких отдельных строк. Каждая метрика отображается только при достижении минимального объёма выборки: 100% при 2 подтверждениях показано не будет. Обновляется каждую минуту.",
    "trust_strip.modal_promise": "Мы никогда не публикуем имена, адреса, точные суммы, IP-адреса или номера карт в публичных виджетах. Обещаем.",
    "trust_badge.eyebrow": "Реальные цифры · последние 30 дней",
    "trust_badge.title": "Этим реально пользуются.",
    "trust_badge.subtitle": "Не отзыв — живой счётчик, обновляемый из базы каждую минуту.",
    "trust_badge.confirmed_label": "получателей подтверждают получение в течение 24ч",
    "trust_badge.confirmed_sub": "из {n} ответов за последние 30 дней",
    "trust_badge.thanks_label": "благодарственных сообщений отправлено",
    "trust_badge.thanks_sub": "от получателей за последние 30 дней",
    "trust_badge.cards_label": "карт пополнено",
    "trust_badge.cards_sub": "последние 30 дней · live-счёт",
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} открыл карту на {amount}",
    "pulse.tooltip_issued": "{name} получил карту на {amount}",
    "pulse.tooltip_deposit": "{name} получил депозит {amount}",
    "pulse.ago_seconds": "{n}с назад",
    "pulse.ago_minutes": "{n}м назад",
    "pulse.ago_hours": "{n}ч назад",
    "note_cloud.eyebrow": "Для чего люди используют",
    "note_cloud.title": "Реальные причины. Реальные люди.",
    "note_cloud.subtitle": "Собрано из {n} заметок к картам за последние 90 дней. Мы показываем только слова, встречающиеся минимум в {min} разных заметках — ваше сообщение остаётся вашим.",
    "note_cloud.footnote": "Имена, суммы и контактные данные скрываются перед отображением.",
    "note_cloud.lang_eyebrow": "На каком языке эти заметки?",
    "note_cloud.lang_other": "Другой",
    "download.button": "Скачать приложение",
    "download.close": "Закрыть",
    "download.modal_eyebrow": "Получить Zcardon",
    "download.modal_title": "Установите Zcardon на телефон",
    "download.footnote": "App Store пока нет — вы устанавливаете защищённое веб-приложение напрямую. Обновляется мгновенно.",
    "download.installed_title": "Zcardon установлен",
    "download.installed_body": "Найдите иконку на главном экране.",
    "download.webview_warning": "Откройте эту страницу в браузере телефона (Safari на iPhone, Chrome на Android) для установки — встроенные браузеры приложений не могут добавить на главный экран.",
    "download.ios_step1_title": "В Safari",
    "download.ios_step1_body": "Если вы не в Safari, скопируйте ссылку и откройте её там. (Chrome и другие браузеры на iOS не могут устанавливать приложения.)",
    "download.ios_step2_title": "Нажмите «Поделиться»",
    "download.ios_step2_body": "Кнопка «Поделиться» — внизу по центру на iPhone, вверху справа на iPad.",
    "download.ios_step3_title": "На главный экран",
    "download.ios_step3_body": "Прокрутите меню «Поделиться» вниз и выберите \\\"На экран «Домой»\\\". Zcardon установится как полноэкранное приложение.",
    "download.android_ready_body": "Chrome обнаружил Zcardon и готов к установке. Нажмите ниже.",
    "download.android_install": "Установить Zcardon",
    "download.android_installing": "Установка…",
    "download.android_step1_title": "Откройте меню Chrome",
    "download.android_step1_body": "Нажмите три точки в правом верхнем углу Chrome.",
    "download.android_step2_title": "На главный экран",
    "download.android_step2_body": "Выберите \\\"Установить приложение\\\" или \\\"На главный экран\\\". Zcardon установится как полноэкранное приложение.",
    "download.desktop_body": "Отсканируйте этот QR-код камерой телефона, чтобы открыть Zcardon, а затем установите его через браузер телефона.",
    "download.steps_hint": "Эти шаги выполняются в вашем браузере, а не на этой странице. Ни одна строка ниже не нажимается — они описывают, что нужно найти.",
    "download.ios_arrow_label": "Нажмите это в Safari",
    "download.android_arrow_label": "Ищите вверху в Chrome",
    "install.eyebrow": "Установить Zcardon",
    "install.title": "Добавьте Zcardon на главный экран",
    "install.subtitle": "Два касания — и Zcardon станет настоящей иконкой приложения. App Store не нужен.",
    "install.back_home": "Открыть Zcardon в браузере",
    "install.continue": "Перейти в Zcardon →",
    "install.copy": "Копировать",
    "install.copied": "Скопировано",
    "install.ios_step1_title": "Откройте страницу в Safari",
    "install.ios_step1_body": "Если вы перешли из другого браузера, скопируйте URL и откройте его в Safari — Chrome на iOS не может устанавливать приложения.",
    "install.ios_step2_title": "Нажмите кнопку «Поделиться»",
    "install.ios_step2_body": "Это квадрат со стрелкой вверх — внизу Safari (или вверху справа на iPad).",
    "install.ios_step3_title": "Нажмите \\\"На экран «Домой»\\\"",
    "install.ios_step3_body": "Прокрутите панель «Поделиться» вниз, выберите \\\"На экран «Домой»\\\" и подтвердите. Zcardon появится как иконка приложения.",
    "install.ios_arrow_label": "Ищите эту иконку",
    "install.android_ready_title": "Готово к установке",
    "install.android_ready_body": "Chrome обнаружил, что Zcardon можно установить. Нажмите ниже — мы появимся как иконка приложения на главном экране.",
    "install.android_install": "Установить Zcardon",
    "install.android_installing": "Установка…",
    "install.android_step1_title": "Откройте меню Chrome",
    "install.android_step1_body": "Три точки в правом верхнем углу Chrome.",
    "install.android_step2_title": "Нажмите \\\"Установить приложение\\\" или \\\"На главный экран\\\"",
    "install.android_step2_body": "Подтвердите добавление иконки Zcardon на телефон. Нажимайте её в любое время для запуска.",
    "install.android_hint": "Если \\\"Установить приложение\\\" не отображается, зайдите на главную страницу — Chrome разблокирует опцию после короткого визита.",
    "install.desktop_title": "Отсканируйте телефоном",
    "install.desktop_body": "Zcardon — мобильное приложение. Откройте этот URL на телефоне для установки:",
    "install.webview_title": "Откройте в браузере",
    "install.webview_body": "Встроенные браузеры (Instagram, Facebook, TikTok) не могут устанавливать приложения. Нажмите \\\"…\\\" или меню «Поделиться», затем выберите \\\"Открыть в Safari\\\" / \\\"Открыть в Chrome\\\". Или скопируйте ссылку:",
    "install.already_title": "Вы уже в приложении",
    "install.already_body": "Zcardon установлен на этом устройстве. Нажмите иконку на главном экране, чтобы открыть в любое время.",
    "install.installed_title": "Установлено",
    "install.installed_body": "Найдите иконку Zcardon на главном экране — это ваше приложение.",
    "install.steps_hint": "Эти шаги выполняются в вашем браузере, а не на этой странице. Ни одна строка ниже не нажимается — они описывают, что искать в меню браузера.",
    "install.desktop_body_qr": "Zcardon устанавливается как иконка приложения на iPhone или Android — не на компьютере. Наведите камеру телефона на этот QR-код:",
    "install.desktop_or": "Или скопируйте ссылку",
    "install.android_arrow_label": "Ищите вверху в Chrome",
    "install.ios_arrow_caption": "Находится в самом низу Safari",
    "vietqr.disabled.title": "Принимать VND через VietQR",
    "vietqr.disabled.badge": "ТОЛЬКО ВЬЕТНАМ",
    "vietqr.disabled.desc": "Привяжите вьетнамский банковский счёт, и QR-код на этой карте станет настоящим VietQR — любое вьетнамское банковское приложение (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) сможет отсканировать его и перевести VND напрямую через сеть NAPAS. Одобрение Stripe Issuing не требуется.",
    "vietqr.disabled.btn_enter": "Ввести банковские реквизиты",
    "vietqr.disabled.or": "или",
    "vietqr.disabled.btn_send_link": "Отправить ссылку настройки получателю",
    "vietqr.disabled.btn_send_link_tip": "Создаёт публичную ссылку на 7 дней, чтобы получатель сам заполнил данные",
    "vietqr.form.warn_title": "Прочитайте перед активацией",
    "vietqr.form.warn_body_a": "Сканирования больше не будут направляться на страницу отслеживания Zcardon. Средства поступают",
    "vietqr.form.warn_body_b": "напрямую на ваш вьетнамский банковский счёт",
    "vietqr.form.warn_body_c": "через межбанковскую сеть NAPAS — Zcardon никогда не видит эти деньги. VietQR работает только внутри Вьетнама для переводов в VND.",
    "vietqr.form.bank": "Банк",
    "vietqr.form.account_number": "Номер счёта",
    "vietqr.form.account_name": "Имя владельца счёта (до 25 символов)",
    "vietqr.form.city": "Город (до 15 символов)",
    "vietqr.form.advanced": "Расширенно — фиксированная сумма / назначение (необязательно)",
    "vietqr.form.amount_vnd": "Сумма (VND)",
    "vietqr.form.amount_ph": "оставьте пустым для произвольной суммы",
    "vietqr.form.reference": "Назначение (до 25 символов)",
    "vietqr.form.confirm": "Я понимаю, что сканирование этой карты переведёт VND напрямую на указанный выше банковский счёт. Zcardon не обрабатывает и не удерживает эти средства.",
    "vietqr.form.btn_attach": "Активировать VietQR",
    "vietqr.form.btn_attaching": "Активация…",
    "vietqr.form.btn_cancel": "Отмена",
    "vietqr.enabled.title": "VietQR активен",
    "vietqr.enabled.desc": "Когда вьетнамское банковское приложение отсканирует эту карту, VND будут переведены напрямую на счёт ниже — минуя Zcardon полностью.",
    "vietqr.enabled.bank_label": "Банк",
    "vietqr.enabled.account_label": "Счёт",
    "vietqr.enabled.fixed_amount": "Фиксированная сумма",
    "vietqr.enabled.reference": "Назначение",
    "vietqr.enabled.btn_detach": "Отключить VietQR",
    "vietqr.enabled.btn_detaching": "Отключение…",
    "vietqr.enabled.attached_at": "Активирован",
    "vietqr.analytics.live_activity": "Активность в реальном времени",
    "vietqr.analytics.today": "сегодня",
    "vietqr.analytics.days_live": "{n} д. активно",
    "vietqr.analytics.phantom_views": "Оценочные просмотры",
    "vietqr.analytics.confirmed_pays": "Подтверждённые оплаты",
    "vietqr.analytics.total_vnd": "Всего VND",
    "vietqr.analytics.recent_confirmed": "Недавние подтверждения",
    "vietqr.analytics.phantom_explainer": "Оценочные просмотры ≈ загрузки изображения карты с момента активации. Настоящие сканирования VietQR не проходят через наши серверы, поэтому мы оцениваем по активности \"карта показана\". Попросите получателя подтверждать платежи через свою ссылку настройки для точных данных.",
    "vietqr.toast.required": "Номер счёта и имя владельца обязательны.",
    "vietqr.toast.confirm_first": "Подтвердите, что вы понимаете: VietQR направляет VND в ваш банк, минуя Zcardon.",
    "vietqr.toast.attached_default": "VietQR активирован.",
    "vietqr.toast.attach_failed": "Не удалось активировать VietQR.",
    "vietqr.toast.detach_confirm": "Отключить VietQR? QR-код вернётся к ссылке отслеживания Zcardon.",
    "vietqr.toast.detached": "VietQR отключён — QR-код вернулся к ссылке отслеживания Zcardon.",
    "vietqr.toast.detach_failed": "Не удалось отключить.",
    "vietqr.toast.link_copied": "Ссылка скопирована · истекает через {h} ч",
    "vietqr.toast.link_generated": "Ссылка создана · скопируйте ниже",
    "vietqr.toast.link_failed": "Не удалось создать ссылку.",

    "setpw.toast.too_short": "Пароль должен содержать не менее 8 символов",
    "setpw.toast.no_match": "Пароли не совпадают",
    "setpw.toast.success_signin_again": "Пароль установлен — войдите в систему",
    "setpw.toast.welcome": "Добро пожаловать в агентскую программу Zcardon",
    "setpw.toast.expired": "Ссылка устарела или недействительна",
    "setpw.verifying": "Проверка приглашения…",
    "setpw.invalid.title": "Ссылка устарела",
    "setpw.invalid.desc": "Эта приветственная ссылка устарела или уже использована. Обратитесь к администратору Zcardon за новым приглашением.",
    "setpw.invalid.home_btn": "Главная",
    "setpw.invalid.support_btn": "Связаться с поддержкой",
    "setpw.fallback_name": "агент",
    "setpw.eyebrow": "Приветствие агента",
    "setpw.heading": "Добро пожаловать, {name}",
    "setpw.subheading": "Установите пароль для активации агентского аккаунта. Вход выполнится автоматически.",
    "setpw.code_label": "Ваш агентский код",
    "setpw.code_tag": "Используйте при подключении клиентов",
    "setpw.form.title": "Придумайте пароль",
    "setpw.form.new_label": "Новый пароль",
    "setpw.form.placeholder": "Минимум 8 символов",
    "setpw.form.confirm_label": "Подтвердите пароль",
    "setpw.form.btn_busy": "Активация…",
    "setpw.form.btn_submit": "Активировать агентский аккаунт",
    "tx.heading": "Транзакции",
    "tx.kind.direct_deposit": "Прямой депозит",
    "tx.kind.card_topup": "Пополнение карты",
    "tx.kind.outbound_payout": "Исходящий платёж",
    "tx.toast.load_failed": "Не удалось загрузить транзакции",
    "tx.summary.deposits": "Депозиты",
    "tx.summary.topups": "Пополнения",
    "tx.summary.topups_sub": "карта",
    "tx.summary.payouts": "Выплаты",
    "tx.summary.payouts_sub": "исходящие",
    "tx.filter.all": "Все",
    "tx.filter.deposits": "Депозиты",
    "tx.filter.topups": "Пополнения",
    "tx.filter.payouts": "Выплаты",
    "tx.loading": "Загрузка транзакций…",
    "tx.empty.filtered": "Транзакции не найдены",
    "tx.empty.none": "Транзакций пока нет",
    "tx.empty.cta": "Начните с прямого депозита →",
    "tx.open_title": "Открыть",
    "terms.load_error_default": "Не удалось загрузить условия",
    "terms.toast.sign_and_check": "Подпишите и подтвердите принятие соглашения",
    "terms.toast.recorded": "Соглашение зафиксировано",
    "terms.toast.failed": "Ошибка отправки",
    "terms.error.title": "Ссылка недействительна",
    "terms.back_home": "На главную",
    "terms.loading": "Загрузка соглашения…",
    "terms.done.title": "Соглашение принято",
    "terms.done.meta": "Версия {v} · {ts}",
    "terms.done.next": "Можете закрыть эту страницу. Карта будет готова к использованию сразу после выпуска.",
    "terms.eyebrow": "Соглашение держателя карты · v{v}",
    "terms.title_reaccept": "Повторно принять соглашение держателя карты",
    "terms.title_accept": "Принять соглашение держателя карты",
    "terms.desc.sender_sent": "отправил вам карту Zcardon",
    "terms.desc.you_were_sent": "Вам выслана карта Zcardon",
    "terms.desc.ending": "заканчивается на",
    "terms.desc.read_before": "Прочитайте соглашение ниже, затем подпишите и подтвердите для активации карты.",
    "terms.already_accepted_note": "Вы уже приняли предыдущую версию. Примите снова, чтобы перейти на актуальную.",
    "terms.scroll_hint": "Прокрутите поле, чтобы прочитать соглашение полностью.",
    "terms.view_json": "Открыть как JSON",
    "terms.sign_label": "Подпишите полным юридическим именем",
    "terms.sign_placeholder": "Как указано в вашем удостоверении личности",
    "terms.consent": "Я прочитал(-а) и согласен(-на) с Соглашением держателя карты Zcardon v{v}.",
    "terms.btn_recording": "Запись…",
    "terms.btn_accept": "Принять соглашение",
    "hub.eyebrow": "Ваш хаб получения",
    "hub.heading.all_done": "Всё готово 🎉",
    "hub.heading.greet": "Привет, {name} 👋",
    "hub.fallback_name": "там",
    "hub.desc.sender_sent": "отправил(-а) вам карту Zcardon",
    "hub.desc.you_were_sent": "Вам была отправлена карта Zcardon",
    "hub.desc.ending": "оканчивающаяся на",
    "hub.desc.complete_steps": "Выполните шаги ниже, чтобы активировать всё.",
    "hub.progress.label": "{done} из {total} шагов выполнено",
    "hub.blocker.title_one": "Остался 1 обязательный шаг",
    "hub.blocker.title_many": "Осталось обязательных шагов: {n}",
    "hub.blocker.subtext": "Карта не будет полностью активирована до их завершения.",
    "hub.required_pill": "Обязательно",
    "hub.done_pill": "Готово",
    "hub.setup_btn": "Настроить",
    "hub.refresh": "Обновить",
    "hub.loading": "Загрузка…",
    "hub.error.title": "Проблема со ссылкой",
    "hub.error.default": "Не удалось загрузить хаб получения.",
    "hub.back_home": "На главную",
    "hub.footer": "Добавьте страницу в закладки · действует 90 дней · видна только тем, у кого есть эта ссылка.",
    "onboarding.eyebrow": "Получите ваш перевод",
    "onboarding.title": "Привет, {name}",
    "onboarding.fallback_name": "там",
    "onboarding.intro_via": "через Zcardon",
    "onboarding.intro_ref_open": "(ссылка",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "Добавьте ваш банковский счёт в {country} ниже, чтобы получить средства. Ваши данные передаются напрямую в Stripe (наш платёжный партнёр) — отправитель их не видит.",
    "onboarding.invalid.title": "Ссылка недействительна",
    "onboarding.invalid.hint": "Попросите отправителя прислать новую ссылку.",
    "onboarding.loading": "Загрузка…",
    "onboarding.complete.title": "Банковские данные уже сохранены",
    "onboarding.complete.desc": "Вы уже предоставили данные банковского счёта. Отправитель скоро переведёт средства.",
    "onboarding.success.title": "Банковские данные получены ✓",
    "onboarding.success.auto_paid": "Спасибо — ваш банковский счёт подтверждён, и средства уже переведены. Они поступят в течение 1–2 рабочих дней.",
    "onboarding.success.pending": "Спасибо — ваш банковский счёт подтверждён. Отправитель будет уведомлён, и средства поступят на ваш счёт в течение 1–2 рабочих дней.",
    "onboarding.success.ref": "Ссылка: {id}",
    "onboarding.beta.title": "Бета-версия.",
    "onboarding.beta.body": "Страница запущена, но конвейер выплат ожидает одобрения Stripe. До включения платформы отправка будет возвращать ошибку. Зайдите позже или свяжитесь с поддержкой, если срочно.",
    "onboarding.select_placeholder": "Выбрать…",
    "onboarding.submit_error_default": "Ошибка отправки. Попробуйте снова.",
    "onboarding.submit_busy": "Отправка…",
    "onboarding.submit": "Отправить банковские данные →",
    "onboarding.secure_note": "Защищено через Stripe. Ссылка действует 7 дней.",
    "onboarding.validate.required": "{field} обязательно",
    "onboarding.validate.format": "Неверный формат {field} (ожидается {pattern})",
    "onboarding.validate.no_schema": "Схема формы недоступна",
    "apply.toast.invalid_image": "Выберите изображение в формате JPEG, PNG или WEBP",
    "apply.toast.too_large": "Размер изображения не должен превышать 6 МБ",
    "apply.toast.upload_both": "Загрузите обе стороны водительского удостоверения",
    "apply.toast.failed": "Ошибка отправки",
    "apply.toast.submitted": "Заявка отправлена",
    "apply.success.title": "Заявка получена",
    "apply.success.desc": "Спасибо, {name}. Мы рассмотрим вашу заявку в течение 2–3 рабочих дней и напишем на {email} с дальнейшими инструкциями.",
    "apply.success.back_home": "На главную",
    "apply.success.contact_support": "Связаться с поддержкой",
    "apply.eyebrow": "Агентская программа",
    "apply.heading": "Подать заявку на роль агента Zcardon",
    "apply.intro": "Зарабатывайте 0.5% комиссии с каждой выпущенной карты, каждого пополнения и каждого принятого наличного депозита от клиентов. Мы подключаем агентов, которым доверяют в их сообществе и которые могут подтвердить свою личность.",
    "apply.bullet1": "Получите уникальный агентский код · отслеживается по всем транзакциям",
    "apply.bullet2": "Панель с еженедельной/ежемесячной/годовой статистикой заработка",
    "apply.bullet3": "Прямые банковские выплаты — еженедельно",
    "apply.section.your_info": "Ваши данные",
    "apply.field.full_name": "Полное юридическое имя",
    "apply.field.full_name_ph": "Как указано в водительском удостоверении",
    "apply.field.email": "Email",
    "apply.field.phone": "Телефон",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "Номер водительского удостоверения",
    "apply.field.address": "Адрес",
    "apply.field.address_ph": "Улица, город, штат, ZIP",
    "apply.field.why_join_label": "Почему вы хотите стать агентом? (необязательно)",
    "apply.field.why_join_ph": "Расскажите немного о вашей связи с сообществом или опыте денежных переводов",
    "apply.dl.section": "Верификация водительского удостоверения",
    "apply.dl.helper": "Загрузите чёткие, хорошо освещённые фото обеих сторон. JPEG, PNG или WEBP · не более 6 МБ каждое. Изображения хранятся в защищённом виде и просматриваются только администраторами Zcardon для верификации.",
    "apply.dl.front": "Лицевая сторона водительского удостоверения",
    "apply.dl.back": "Обратная сторона водительского удостоверения",
    "apply.dl.tap_upload": "Нажмите для загрузки",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 МБ",
    "apply.cancel": "Отмена",
    "apply.submit_busy": "Отправка…",
    "apply.submit": "Отправить заявку",
    "agent.toast.load_failed": "Не удалось загрузить данные агента",
    "agent.toast.cust_created": "Клиент {name} создан",
    "agent.toast.cust_failed": "Не удалось создать клиента",
    "agent.toast.card_issued": "Карта выпущена · вы заработали {amount}",
    "agent.toast.card_issue_failed": "Не удалось выпустить карту",
    "agent.toast.cash_accepted": "Наличный депозит принят · вы заработали {amount}",
    "agent.toast.cash_failed": "Не удалось записать депозит",
    "agent.loading": "Загрузка…",
    "agent.eyebrow": "Портал агента",
    "agent.fallback_name": "Агент",
    "agent.code_line.code_label": "Агентский код",
    "agent.code_line.commission_label": "комиссия",
    "agent.earnings.label": "Неоплаченный баланс",
    "agent.earnings.sub": "{n} неоплаченных транзакций",
    "agent.stat.week": "На этой неделе",
    "agent.stat.month": "В этом месяце",
    "agent.stat.year": "В этом году",
    "agent.stat.txns": "{n} тр.",
    "agent.stat.customers": "Ваши клиенты",
    "agent.stat.active": "активных",
    "agent.customers.title": "Ваши клиенты",
    "agent.customers.empty": "Клиентов пока нет. Нажмите \\\"Зарегистрировать клиента\\\", чтобы добавить первого пользователя.",
    "agent.customers.col_customer": "Клиент",
    "agent.customers.col_phone": "Телефон",
    "agent.customers.col_cards": "Карты",
    "agent.customers.col_commissions": "Заработано комиссий",
    "agent.customers.col_joined": "Дата регистрации",
    "agent.customers.col_actions": "Действия",
    "agent.customers.txns": "{n} тр.",
    "agent.commissions.title": "Последние комиссии",
    "agent.commissions.empty": "Комиссий пока нет. Выпустите первую карту, чтобы начать зарабатывать.",
    "agent.commissions.col_when": "Когда",
    "agent.commissions.col_customer": "Клиент",
    "agent.commissions.col_type": "Тип",
    "agent.commissions.col_txn": "Транзакция",
    "agent.commissions.col_commission": "Комиссия",
    "agent.commissions.col_status": "Статус",
    "agent.commissions.paid": "оплачено",
    "agent.commissions.unpaid": "не оплачено",
    "agent.type.issuance": "Выпуск",
    "agent.type.topup": "Пополнение",
    "agent.type.cash_deposit": "Наличный депозит",
    "agent.register_btn": "Зарегистрировать клиента",
    "agent.dialog.new_cust.title": "Новый клиент",
    "agent.dialog.new_cust.desc": "Клиент будет привязан к вашему агентскому коду. За каждую выпущенную от его имени карту вы получаете комиссию 0.5%.",
    "agent.dialog.field.full_name": "Полное имя",
    "agent.dialog.field.email": "Email",
    "agent.dialog.field.phone": "Телефон",
    "agent.dialog.field.password": "Начальный пароль (передайте клиенту)",
    "agent.dialog.cancel": "Отмена",
    "agent.dialog.creating": "Создание…",
    "agent.dialog.create_customer": "Создать клиента",
    "agent.dialog.issue_card.title": "Выпустить карту для {name}",
    "agent.dialog.issue_card.desc": "Финансируется из наличных, полученных на руки. Клиент платит комиссию 3.75% — вы получаете 0.5%.",
    "agent.dialog.field.receiver_name": "Имя получателя",
    "agent.dialog.field.receiver_email": "Email получателя",
    "agent.dialog.field.receiver_phone": "Телефон получателя",
    "agent.dialog.field.amount_usd": "Сумма (USD)",
    "agent.dialog.field.note": "Примечание (необязательно)",
    "agent.dialog.issuing": "Выпуск…",
    "agent.dialog.issue_card_btn": "Выпустить карту",
    "agent.actions.issue_card": "Выпустить карту",
    "agent.dialog.cash_deposit.title": "Наличный депозит для {name}",
    "agent.dialog.cash_deposit.desc": "Наличные получены от клиента · создаётся карта для получателя.",
    "agent.dialog.field.receiver_name_opt": "Имя получателя (необязательно)",
    "agent.dialog.recording": "Запись…",
    "agent.dialog.record_deposit": "Записать депозит",
    "agent.actions.cash_deposit": "Наличный депозит",

  },
  th: {
    "header.nav.home": "หน้าหลัก",
    "header.nav.rates": "อัตราค่าบริการ",
    "header.nav.support": "ช่วยเหลือ",
    "header.nav.admin": "แอดมิน",
    "header.nav.signin": "เข้าสู่ระบบ",
    "header.nav.signout": "ออกจากระบบ",
    "header.nav.get_started": "เริ่มต้นใช้งาน",
    "header.nav.account": "บัญชี",
    "header.lang.en": "EN",
    "header.lang.vi": "VI",
    "header.lang.aria": "เปลี่ยนภาษา",
    "landing.hero.eyebrow": "ระบบใหม่สำหรับการใช้จ่ายดิจิทัล",
    "landing.hero.title_a": "เติมเงินบัตรเวอร์ชวล",
    "landing.hero.title_b_ship": "ส่ง",
    "landing.hero.title_b_rest": "ให้ใครก็ได้",
    "landing.hero.title_c": "ใช้จ่ายได้ใน",
    "landing.hero.subtitle": "ออกบัตรเดบิตเวอร์ชวลแบบแตะจ่ายพร้อม QR สแกนได้ ชำระเงินผ่าน Stripe ยอดเงิน USD ค่าบริการและค่าเกตเวย์โปร่งใส — ส่งถึงผู้รับทางอีเมลหรือดาวน์โหลด",
    "landing.hero.cta_start": "เริ่มออกบัตร",
    "landing.hero.cta_request_cash": "ขอบัตรเงินสด",
    "landing.hero.cta_rates": "ดูอัตราค่าบริการ",
    "landing.hero.cta_find_card": "ค้นหาบัตรของฉัน",
    "landing.hero.stat_currency": "สกุลเงิน",
    "landing.hero.stat_settle": "เวลาชำระ",
    "landing.hero.stat_settle_value": "~2m",
    "landing.hero.stat_service_fee": "ค่าบริการ",
    "landing.hero.image_eyebrow": "SKYLINE · GLOBAL REACH",
    "landing.hero.image_title": "สร้างมาเพื่อเศรษฐกิจบัตรยุคใหม่",
    "landing.hero.live_pill": "LIVE",
    "landing.hero.live_pill_text": "ชำระเป็น USD",
    "landing.hero.marquee": "ออกบัตร · เติมเงิน · ใช้จ่ายเป็น USD",
    "landing.demo.eyebrow": "ดูได้ใน 20 วินาที",
    "landing.demo.title_a": "จากการออกบัตรสู่การ",
    "landing.demo.title_b_spend": "ใช้จ่าย",
    "landing.demo.title_c": "ใน 4 ขั้นตอน",
    "landing.demo.step_issue.label": "ออกบัตร",
    "landing.demo.step_issue.detail": "เลือกจำนวนเงิน บัตรพร้อมใน ~2 นาที",
    "landing.demo.step_share.label": "แชร์บัตร",
    "landing.demo.step_share.detail": "ทางอีเมล SMS WhatsApp หรือดาวน์โหลดเป็น PNG",
    "landing.demo.step_scan.label": "ผู้รับสแกน",
    "landing.demo.step_scan.detail": "กล้องโทรศัพท์เปิดหน้า Track ทันที",
    "landing.demo.step_spend.label": "ใช้จ่ายเป็น USD",
    "landing.demo.step_spend.detail": "แตะจ่าย ถอนเงินสด หรือใช้จ่ายกับร้านค้า",
    "landing.demo.cta_try": "ลองเลย",
    "landing.demo.live_pill": "เดโมสด",
    "landing.demo.scene1_caption": "กำลังสร้างบัตร · 4989 0168 2598 3196",
    "landing.demo.scene2_caption": "ส่งให้ผู้รับทาง WhatsApp",
    "landing.demo.scene3_caption": "กำลังสแกน QR · พบข้อมูลที่ตรงกัน",
    "landing.demo.scene4_eyebrow": "เครดิตยอดเงินแล้ว",
    "landing.demo.scene4_status": "✓ อนุมัติแล้ว · พร้อมแตะจ่าย",
    "landing.footer.rates": "อัตราค่าบริการ",
    "landing.footer.support": "ช่วยเหลือ",
    "landing.footer.admin_portal": "พอร์ทัลแอดมิน",
    "landing.footer.copy": "© 2026 Zcardon · ผลิตภัณฑ์ฟินเทคสาธิต",
    "landing.cash.dialog_title": "ขอบัตรเงินสด",
    "landing.cash.dialog_desc": "เราจะส่งลิงก์ชำระเงิน Stripe ให้ทาง SMS ชำระด้วยบัตรใดก็ได้ บัตรเงินสดจะถูกเติมเงินทันทีที่ Stripe ยืนยัน",
    "landing.cash.phone_label": "หมายเลขโทรศัพท์",
    "landing.cash.phone_placeholder": "+1 415 555 2671",
    "landing.cash.amount_label": "จำนวนเงิน (USD)",
    "landing.cash.name_label": "ชื่อของคุณ (ไม่บังคับ)",
    "landing.cash.submit": "ส่งลิงก์ชำระเงิน",
    "landing.cash.submitting": "กำลังส่ง…",
    "landing.cash.success_title": "ลิงก์ชำระเงินกำลังมา",
    "landing.cash.success_body": "เราส่งลิงก์ชำระเงิน Stripe ไปที่ {phone} แล้ว แตะเพื่อชำระและบัตรเวอร์ชวลของคุณจะเปิดใช้งานทันที",
    "landing.cash.success_open_checkout": "เปิดหน้าชำระเงิน",
    "landing.find.cta": "ชำระแล้ว? ค้นหาบัตรของฉัน",
    "landing.find.dialog_title": "ค้นหาบัตรที่มีอยู่",
    "landing.find.dialog_desc": "กรอกหมายเลขโทรศัพท์ที่ใช้สมัคร เราจะส่งรหัส 6 หลักเพื่อกู้คืนบัตรเงินสดที่ยังใช้งานได้",
    "landing.find.phone_label": "หมายเลขโทรศัพท์",
    "landing.find.code_label": "รหัส 6 หลัก",
    "landing.find.send_code": "ส่งรหัส",
    "landing.find.verify": "ยืนยันและค้นหา",
    "landing.find.sending": "กำลังส่ง…",
    "landing.find.verifying": "กำลังตรวจสอบ…",
    "landing.find.no_cards": "ไม่พบบัตรที่ยังใช้งานได้สำหรับหมายเลขนี้",
    "login.eyebrow.welcome": "ยินดีต้อนรับ",
    "login.eyebrow.admin": "การจัดการระบบ",
    "login.title": "เข้าสู่ระบบ",
    "login.title_admin": "เข้าสู่ระบบแอดมิน",
    "login.admin_badge": "พอร์ทัลแอดมิน",
    "login.email_label": "อีเมล",
    "login.password_label": "รหัสผ่าน",
    "login.submit": "เข้าสู่ระบบ",
    "login.submitting": "กำลังเข้าสู่ระบบ…",
    "login.toast_welcome": "ยินดีต้อนรับกลับ, {name}",
    "login.toast_failed": "เข้าสู่ระบบไม่สำเร็จ",
    "login.gate_title": "การเข้าสู่ระบบของลูกค้าถูกระงับชั่วคราว",
    "login.gate_default_message": "เรากำลังปรับปรุงแพลตฟอร์ม โปรดกลับมาใหม่ในภายหลัง",
    "login.gate_admin_note": "ผู้ดูแลระบบและผู้จัดการยังสามารถเข้าสู่ระบบได้ด้านล่าง",
    "login.new_here": "มาใหม่?",
    "login.create_account": "สร้างบัญชี",
    "login.customer_q": "เป็นลูกค้า?",
    "login.regular_signin": "เข้าสู่ระบบปกติ",
    "login.demo_title": "บัญชีสาธิต",
    "login.demo_customers_note": "ลูกค้า: ลงทะเบียนผ่าน /register หรือสร้างจากแผงผู้ดูแลระบบ",
    "login.forgot_password": "ลืมรหัสผ่าน?",
    "forgot.eyebrow": "การกู้คืนบัญชี",
    "forgot.title": "รีเซ็ตรหัสผ่านของคุณ",
    "forgot.subtitle": "ป้อนอีเมลที่ผูกกับบัญชี Zcardon ของคุณ แล้วเราจะส่งลิงก์ที่ปลอดภัยเพื่อตั้งรหัสผ่านใหม่",
    "forgot.email_label": "อีเมล",
    "forgot.email_placeholder": "you@example.com",
    "forgot.submit": "ส่งลิงก์รีเซ็ต",
    "forgot.submitting": "กำลังส่ง…",
    "forgot.toast_failed": "ไม่สามารถส่งลิงก์รีเซ็ตได้ โปรดลองอีกครั้ง",
    "forgot.sent_title": "ตรวจสอบกล่องข้อความของคุณ",
    "forgot.sent_message": "หากมีบัญชีสำหรับ {email} เราได้ส่งลิงก์รีเซ็ตรหัสผ่านแล้ว",
    "forgot.sent_expiry_note": "ลิงก์มีอายุ 15 นาที ไม่ได้รับ? ตรวจสอบสแปม หรือส่งใหม่อีกครั้ง",
    "forgot.back_to_login": "กลับไปเข้าสู่ระบบ",
    "forgot.resend_link": "ส่งลิงก์อีกครั้ง",
    "forgot.remember_q": "จำรหัสผ่านได้?",
    "forgot.signin_link": "เข้าสู่ระบบ",
    "reset.eyebrow": "การกู้คืนบัญชี",
    "reset.title": "ตั้งรหัสผ่านใหม่",
    "reset.subtitle": "เลือกรหัสผ่านที่แข็งแกร่งที่ยังไม่เคยใช้มาก่อน ระบบจะลงชื่อเข้าให้อัตโนมัติเมื่อตั้งเสร็จ",
    "reset.verifying": "กำลังตรวจสอบลิงก์รีเซ็ต…",
    "reset.invalid_title": "ลิงก์รีเซ็ตนี้ไม่ถูกต้องหรือหมดอายุแล้ว",
    "reset.invalid_message": "ลิงก์รีเซ็ตมีอายุ 15 นาทีและใช้ได้เพียงครั้งเดียว ขอลิงก์ใหม่เพื่อดำเนินการต่อ",
    "reset.request_new_link": "ขอลิงก์รีเซ็ตใหม่",
    "reset.new_password_label": "รหัสผ่านใหม่",
    "reset.confirm_password_label": "ยืนยันรหัสผ่าน",
    "reset.password_hint": "อย่างน้อย 8 ตัวอักษร หลีกเลี่ยงรหัสผ่านที่ใช้ที่อื่น",
    "reset.submit": "อัปเดตรหัสผ่าน",
    "reset.submitting": "กำลังอัปเดต…",
    "reset.toast_success": "อัปเดตรหัสผ่านแล้ว คุณเข้าสู่ระบบเรียบร้อย",
    "reset.toast_failed": "ไม่สามารถรีเซ็ตรหัสผ่านได้ โปรดลองอีกครั้ง",
    "reset.error_min_length": "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
    "reset.error_mismatch": "รหัสผ่านไม่ตรงกัน",
    "register.eyebrow": "เข้าร่วม",
    "register.title": "สร้างบัญชี",
    "register.full_name_label": "ชื่อ-นามสกุล",
    "register.email_label": "อีเมล",
    "register.password_label": "รหัสผ่าน",
    "register.submit": "สร้างบัญชี",
    "register.submitting": "กำลังสร้าง…",
    "register.gate_title": "การสมัครสมาชิกถูกระงับชั่วคราว",
    "register.gate_default_message": "เรากำลังปรับปรุงแพลตฟอร์ม โปรดกลับมาใหม่ในภายหลัง",
    "register.gate_already_member": "มีบัญชีอยู่แล้ว?",
    "register.toast_created": "สร้างบัญชีแล้ว",
    "register.toast_failed": "การลงทะเบียนไม่สำเร็จ",
    "register.already_member": "เป็นสมาชิกอยู่แล้ว?",
    "register.signin_link": "เข้าสู่ระบบ",
    "dash.eyebrow": "แดชบอร์ด",
    "dash.greeting": "ยินดีต้อนรับกลับ, {name}",
    "dash.subtitle": "ออกบัตรเสมือนหรือส่งการโอนเงิน — ศูนย์ควบคุมของคุณอยู่ด้านล่าง",
    "dash.action.issue_card": "ออกบัตร",
    "dash.action.direct_deposit": "โอนเงินตรง (wire)",
    "dash.action.support": "ฝ่ายสนับสนุน",
    "dash.section.cards": "บัตรของคุณ",
    "dash.section.activity": "กิจกรรมล่าสุด",
    "dash.section.deposits": "การโอนเงินตรง",
    "dash.empty.cards": "ยังไม่มีบัตร — ออกบัตรใบแรกเพื่อเริ่มต้น",
    "dash.empty.activity": "เมื่อคุณเริ่มออกบัตรหรือส่งการโอนเงิน กิจกรรมล่าสุดจะปรากฏที่นี่",
    "ref.eyebrow": "ยินดีต้อนรับสู่ Zcardon",
    "ref.greeting": "{name} ส่งคุณมา",
    "ref.loading": "กำลังโหลดคำเชิญของคุณ…",
    "ref.resolved_redirect": "รอสักครู่ — กำลังพาคุณเข้าสู่ระบบ",
    "ref.resolving": "กำลังแก้ไขลิงก์คำเชิญของคุณ…",
    "support.eyebrow": "เราพร้อมช่วยเหลือ",
    "support.title": "บริการสนับสนุน ไม่ใช่ตั๋วรอ",
    "support.subtitle": "คุยกับเจ้าหน้าที่จริง ตอบกลับทางอีเมลภายในหนึ่งชั่วโมงทำการในวันทำงาน",
    "support.card.email": "อีเมล",
    "support.card.phone": "โทรศัพท์",
    "support.card.live_chat": "แชทสด",
    "support.card.live_chat_value": "ในแดชบอร์ดของคุณ",
    "support.card.hours": "เวลาทำการ",
    "support.faq.title": "คำถามที่พบบ่อย",
    "support.faq.q1": "ผู้รับจะได้รับบัตรนานแค่ไหน?",
    "support.faq.a1": "โดยทั่วไปภายใน 2 นาทีหลังชำระเงินสำเร็จ จัดส่งผ่านอีเมล (Resend) และ MMS (Twilio) โดยอัตโนมัติ",
    "support.faq.q2": "ฉันยกเลิกหรืออายัดบัตรหลังส่งแล้วได้ไหม?",
    "support.faq.a2": "ได้ — เปิดหน้ารายละเอียดบัตรแล้วแตะ Freeze หรืออายัดโดยตรงจากอีเมลแจ้งเตือนผู้รับก็ได้",
    "support.faq.q3": "บัตรใช้จ่ายสกุลเงินอะไรได้บ้าง?",
    "support.faq.a3": "บัตรทุกใบชำระเป็น USD ผู้รับใช้จ่ายได้ทุกที่ที่รับ Visa/Mastercard — การแลกเปลี่ยนเงินตราจัดการโดยเครือข่ายบัตร",
    "support.faq.q4": "มีค่าธรรมเนียมอะไรบ้าง?",
    "support.faq.a4": "ค่าบริการที่โปร่งใสบวกค่าเกตเวย์ Stripe ดูใบเสนอราคาสดได้ที่หน้า Rates ก่อนชำระ",
    "rates.eyebrow": "โปร่งใส",
    "rates.title_a": "ไม่มีส่วนต่าง",
    "rates.title_b": "ซ่อนเร้น",
    "rates.subtitle": "คุณเห็นค่าบริการและค่าเกตเวย์ก่อนชำระ เงินต้นเครดิตเข้ายอดบัตรเสมือนเป็น USD เสมอ",
    "rates.row.service_fee": "ค่าบริการ",
    "rates.row.service_fee_value": "{pct} ของยอดที่ส่ง",
    "rates.row.gateway": "เกตเวย์ (Stripe)",
    "rates.row.settlement": "การชำระ",
    "rates.row.settlement_value": "ยอด USD บนบัตรเสมือน",
    "rates.row.topup": "วิธีเติมเงิน",
    "rates.row.topup_value": "บัตรเครดิต · เดบิต · ธนาคาร (ACH)",
    "rates.sim.eyebrow": "ใบเสนอราคาสด",
    "rates.sim.title_a": "ส่งจำนวนเท่าไหร่ก็ได้,",
    "rates.sim.title_b": "ดูรายละเอียดได้ทันที",
    "rates.sim.amount_label": "จำนวนที่ต้องการส่ง",
    "rates.sim.line.principal": "บนบัตร (เงินต้น)",
    "rates.sim.line.service": "ค่าบริการ · {pct}",
    "rates.sim.line.gateway": "เกตเวย์ · {pct} + {fixed}",
    "rates.sim.line.you_pay": "คุณจ่าย",
    "rates.sim.cta": "เริ่มด้วย ${amount} →",
    "rates.sim.note": "อัตราที่ใช้ข้างต้นกำหนดโดยผู้ดูแลระบบ ผู้รับได้รับเงินต้นเต็มจำนวนเสมอ",
    "dd.title": "Direct Deposit",
    "dd.intro": "ส่งเงินเข้าบัญชีธนาคารของผู้รับทั่วโลก ชำระด้วยบัตร (Stripe Checkout) หรือยอด Zcardon ของคุณ — เราจัดการโอนเงินไปยังธนาคารของผู้รับโดยอัตโนมัติสำหรับประเทศที่รองรับ หรือด้วยตนเองสำหรับประเทศที่ยังไม่รองรับ ทุกรายการมีใบเสร็จที่พิมพ์ได้",
    "dd.new_btn": "บันทึก Direct Deposit ใหม่",
    "dd.close": "ปิด",
    "dd.history_eyebrow": "การฝากของคุณ",
    "dd.loading": "กำลังโหลด…",
    "dd.empty": "ยังไม่มี Direct Deposit",
    "dd.status.approved": "อนุมัติแล้ว",
    "dd.status.pending": "รอการอนุมัติ",
    "dd.status.rejected": "ถูกปฏิเสธ",
    "dd.row.confirmed": "✓ ผู้รับยืนยันการรับแล้ว",
    "dd.row.disputed": "⚠ ผู้รับแจ้งพบความคลาดเคลื่อน",
    "dd.row.pdf": "PDF",
    "dd.error.load": "ไม่สามารถโหลดรายการฝากได้",
    "dd.error.pdf": "ไม่สามารถดาวน์โหลดใบเสร็จได้",
    "pay.mode.view": "บัตร Zcardon ของคุณ",
    "pay.mode.charge": "ชำระด้วย QR",
    "pay.heading.view": "สวัสดี, {name}",
    "pay.heading.charge": "เรียกเก็บบัตรนี้",
    "pay.cardholder": "เจ้าของบัตร",
    "pay.paused.label": "บัตรถูกหยุดชั่วคราว",
    "pay.paused.body": "บัตรนี้ถูกอายัดและไม่สามารถรับการเรียกเก็บใหม่ได้จนกว่าเจ้าของจะเปิดใช้งานอีกครั้ง",
    "pay.balance.label": "ยอดคงเหลือ",
    "pay.charges_count": "{count} รายการเรียกเก็บ",
    "pay.charges_count_plural": "{count} รายการเรียกเก็บ",
    "pay.topups_count": "{count} การเติมเงิน",
    "pay.topups_count_plural": "{count} การเติมเงิน",
    "pay.activity.label": "กิจกรรมล่าสุด",
    "pay.activity.empty": "ยังไม่มีกิจกรรม",
    "pay.cta.charge": "เรียกเก็บบัตรนี้ (ร้านค้า)",
    "pay.cta.charge_paused": "บัตรถูกหยุดชั่วคราว",
    "pay.cta.note_active": "แตะด้านบนเฉพาะเมื่อคุณเป็นร้านค้าที่รับชำระจากบัตรนี้เท่านั้น",
    "pay.cta.note_paused": "บัตรนี้ถูกหยุดชั่วคราวโดยผู้ส่ง กรุณาติดต่อให้เปิดใช้งานอีกครั้ง",
    "pay.back_to_balance": "กลับไปยังยอดคงเหลือ",
    "pay.form.amount_label": "จำนวน (USD)",
    "pay.form.amount_max": "สูงสุด {amount}",
    "pay.form.merchant_label": "ร้านค้า / เหตุผล",
    "pay.form.merchant_placeholder": "Starbucks Hanoi",
    "pay.form.memo_label": "บันทึก (ไม่บังคับ)",
    "pay.form.memo_placeholder": "ลาเต้สองแก้ว",
    "pay.form.submit_processing": "กำลังดำเนินการ…",
    "pay.form.submit_paused": "บัตรถูกหยุดชั่วคราว",
    "pay.form.submit_charge": "เรียกเก็บ {amount}",
    "pay.form.submit_charge_card": "เรียกเก็บบัตร",
    "pay.form.disclaimer": "การเรียกเก็บบัตรนี้ถือว่าคุณได้รับอนุญาตจากเจ้าของบัตรให้ใช้จ่ายตามจำนวนข้างต้น",
    "pay.receipt.title": "ชำระเงินสำเร็จ",
    "pay.receipt.to": "ถึง",
    "pay.receipt.id": "ใบเสร็จ",
    "pay.receipt.time": "เวลา",
    "pay.receipt.new_balance": "ยอดคงเหลือใหม่",
    "pay.receipt.done": "เสร็จสิ้น · กลับสู่ยอดเงิน",
    "pay.error.cant_load": "ไม่สามารถโหลดบัตรนี้ได้",
    "pay.error.back_home": "กลับหน้าหลัก",
    "pay.error.loading": "กำลังโหลดบัตร…",
    "pay.error.charge_failed": "การชำระเงินล้มเหลว",
    "pay.tap.eyebrow": "แตะเพื่อชำระเงิน",
    "pay.tap.title": "แสดงรหัสนี้",
    "pay.tap.subtitle": "ต่อเครื่องสแกนของร้านค้า",
    "pay.tap.balance": "ยอดเงิน",
    "pay.tap.paused": "หยุดชั่วคราว",
    "pay.tap.qr_unavailable": "ไม่มี QR สำหรับบัตรนี้",
    "pay.tap.refresh_in": "รีเฟรชใน {secs} วินาที",
    "pay.tap.show_digits": "แสดงตัวเลข",
    "pay.tap.hide_digits": "ซ่อนตัวเลข",
    "pay.tap.card_label": "บัตร · ••••",
    "pay.tap.auto_hides": "ซ่อนอัตโนมัติใน 60 วินาที",
    "pay.tap.full_only_image": "หมายเลขเต็มแสดงเฉพาะบนภาพบัตรต้นฉบับเท่านั้น",
    "pay.tap.disclaimer": "บัตร Closed-loop · ใช้จ่ายได้กับร้านค้าที่รองรับ Zcardon QR หมุนเวียนทุก 60 วินาทีเพื่อป้องกันการซ้ำ",
    "pay.subscribe.eyebrow": "ติดตามทุกรายการ",
    "pay.subscribe.title": "รับอีเมลทุกครั้งที่มีธุรกรรม",
    "pay.subscribe.desc": "ฟรี อีเมลหนึ่งฉบับต่อการชำระหรือเติมเงิน ยกเลิกได้ตลอดเวลา เราไม่เปิดเผยที่อยู่ของคุณ",
    "pay.subscribe.placeholder": "you@example.com",
    "pay.subscribe.cta": "แจ้งเตือนฉัน",
    "pay.subscribe.sending": "…",
    "pay.subscribe.done.title": "สมัครสมาชิกแล้ว",
    "pay.subscribe.done.body": "เราจะส่งอีเมลไปยัง {email} ทันทีทุกครั้งที่มีการชำระหรือเติมเงินในบัตร •••• {last4}",
    "pay.subscribe.toast.already": "คุณสมัครสมาชิกอยู่แล้ว — ไม่มีการส่งซ้ำ",
    "pay.subscribe.toast.success": "คุณจะได้รับอีเมลทุกครั้งที่มีธุรกรรม ยินดีต้อนรับ!",
    "pay.subscribe.toast.failed": "การสมัครสมาชิกล้มเหลว",
    "pay.save.eyebrow": "พกบัตรติดตัว",
    "pay.save.title": "บันทึกบัตรนี้ลงโทรศัพท์",
    "pay.save.desc": "บันทึกภาพบัตรลงคลังรูปเพื่อใช้แบบออฟไลน์ — แสดง QR ให้ร้านค้าได้ทุกเมื่อ ไม่ต้องติดตั้งแอป",
    "pay.save.cta": "บันทึกภาพบัตร",
    "pay.save.preparing": "กำลังเตรียม…",
    "pay.save.add_home": "เพิ่มในหน้าจอหลัก",
    "pay.save.toast.opened": "เปิดภาพบัตรแล้ว — กดค้างเพื่อบันทึกลงคลังรูป",
    "pay.save.toast.failed": "ไม่สามารถบันทึกบัตรได้",
    "pay.save.ios.title": "วิธีเพิ่ม Zcardon ในหน้าจอหลักของ iPhone:",
    "pay.save.ios.step1": "แตะปุ่มแชร์ที่ด้านล่างของ Safari",
    "pay.save.ios.step2": "เลื่อนลงแล้วแตะ เพิ่มในหน้าจอหลัก",
    "pay.save.ios.step3": "แตะ เพิ่ม เพื่อยืนยัน",
    "pay.save.ios.note": "Zcardon จะปรากฏเหมือนแอปทั่วไป — แตะไอคอนเพื่อเปิดบัตรนี้ทันที",
    "pay.thanks.eyebrow": "ส่งคำขอบคุณ",
    "pay.thanks.body": "${amount} ได้รับแล้ว — แจ้งให้ผู้ที่เติมเงินบัตรนี้ทราบว่ามีประโยชน์",
    "pay.thanks.placeholder": "ไม่บังคับ — พูดอะไรสักอย่าง…",
    "pay.thanks.cta": "ส่งคำขอบคุณ {emoji}",
    "pay.thanks.sending": "กำลังส่ง…",
    "pay.thanks.done": "ส่งคำขอบคุณแล้ว — พวกเขาจะเห็นในไม่กี่วินาที",
    "pay.thanks.toast.sent": "ส่งคำขอบคุณแล้ว",
    "pay.thanks.toast.failed": "ไม่สามารถส่งคำขอบคุณได้",
    "pay.activity.topup_bank": "เติมเงินโอนผ่านธนาคาร",
    "pay.activity.topup_debit": "เติมเงินด้วยบัตรเดบิต",
    "pay.activity.topup_credit": "เติมเงินด้วยบัตรเครดิต",
    "pay.activity.topup_crypto": "เติมเงินด้วยคริปโต",
    "pay.activity.topup_received": "รับการเติมเงินแล้ว",
    "pay.activity.merchant_charge": "ชำระให้ร้านค้า",
    "pay.toast.payment_received": "ได้รับการชำระเงิน · −{amount}",
    "cash.confirming": "กำลังยืนยันการชำระเงิน…",
    "cash.confirming.sub": "Stripe กำลังดำเนินการชำระเงินให้เสร็จสิ้น",
    "cash.notfound.title": "ลิงก์หมดอายุ",
    "cash.notfound.body": "ไม่พบคำขอเงินสดนี้ ลิงก์อาจมาจากเซสชันเก่า หรือบัตรไม่ได้ถูกยืนยันสำเร็จ เริ่มคำขอใหม่ได้เลย ใช้เวลาไม่กี่นาที และการชำระที่ทำไปแล้วจะได้รับเครดิตอัตโนมัติเมื่อระบบตรวจสอบในเบื้องหลัง",
    "cash.notfound.ref": "อ้างอิง:",
    "cash.notfound.start": "เริ่มคำขอใหม่",
    "cash.notfound.contact": "ติดต่อฝ่ายสนับสนุน",
    "cash.error.title": "ไม่สามารถตรวจสอบสถานะได้",
    "cash.error.return": "กลับหน้าหลัก",
    "cash.autopay.opening": "กำลังเปิด Tap-to-Pay…",
    "cash.autopay.redirect": "กำลังเปลี่ยนเส้นทางใน {secs} วินาที",
    "cash.autopay.stay": "อยู่ที่นี่แทน",
    "cash.ready.title": "บัตรของคุณพร้อมใช้งานแล้ว",
    "cash.ready.subtitle": "เติมเงิน ${amount} USD · ส่ง SMS ไปยัง {phone} แล้ว",
    "cash.card.label": "บัตร",
    "cash.card.exp": "หมดอายุ",
    "cash.card.cvv": "CVV",
    "cash.card.balance": "ยอดเงิน",
    "cash.cta.open_pay": "เปิดหน้าจอ Tap-to-Pay",
    "cash.toast.funded": "บัตรของคุณได้รับเงินแล้ว — ตรวจสอบ SMS ในโทรศัพท์ของคุณ!",
    "cash.pending.title": "รอการยืนยันจาก Stripe",
    "cash.pending.body": "กำลังรอการยืนยัน โดยปกติใช้เวลา 5-15 วินาที หากคุณปิดหน้าชำระเงินโดยไม่ได้ชำระ คุณสามารถเริ่มใหม่ได้จากหน้าแรก",
    "cash.pending.back": "กลับหน้าแรก",
    "cash.timeout.title": "ไม่สามารถยืนยันการชำระเงินได้",
    "cash.timeout.body": "รอ Stripe ยืนยันนาน 3 นาทีแล้ว ส่วนใหญ่หมายความว่าปิดหน้าชำระเงินก่อนชำระ — แต่หากคุณชำระเงินแล้ว บัตรจะถูกเปิดใช้งานโดยอัตโนมัติภายในไม่กี่นาที (ระบบตรวจสอบเบื้องหลังทำงานทุก 5 นาที) และเราจะส่ง SMS แจ้งไปที่ {phone} ทันทีที่ดำเนินการเสร็จ",
    "cash.timeout.start_over": "เริ่มใหม่",
    "cash.timeout.try_again": "ลองอีกครั้ง",
    "cash.resend.title": "ไม่ได้รับ? ส่งอีกครั้ง",
    "cash.resend.degraded.lead": "การส่งไปยังหมายเลขในสหรัฐฯ มีปัญหาขณะนี้",
    "cash.resend.degraded.sms": "SMS ไปยังโทรศัพท์ในสหรัฐฯ อาจถูกผู้ให้บริการบล็อกโดยไม่มีการแจ้งเตือน (การยืนยันหมายเลขฟรียังอยู่ระหว่างดำเนินการ)",
    "cash.resend.degraded.email": "Email อยู่ในโหมดทดสอบและส่งได้เฉพาะไปยังที่อยู่ของเจ้าของบัญชีเท่านั้น ขณะที่ DNS กำลังได้รับการยืนยัน",
    "cash.resend.degraded.tail": "หากช่องทางหนึ่งใช้งานไม่ได้ กรุณาลองช่องทางอื่น — หรือเปิดหน้านี้บนอุปกรณ์ของผู้รับเพื่อดึงบัตรโดยตรง",
    "cash.resend.text": "SMS",
    "cash.resend.whatsapp": "WhatsApp",
    "cash.resend.email": "Email",
    "cash.resend.sending": "กำลังส่ง…",
    "cash.resend.email_sandbox": "Email อยู่ในโหมดทดสอบ ขณะที่โดเมน zcardon.com กำลังได้รับการยืนยันที่ Resend เราส่งได้เฉพาะไปยังที่อยู่ของเจ้าของบัญชีเท่านั้น หาก Email ของคุณไม่ได้รับการยอมรับ ให้ใช้ SMS หรือ WhatsApp แทน",
    "cash.resend.email_label": "ที่อยู่ Email",
    "cash.resend.email_send": "ส่งทาง Email",
    "cash.resend.cancel": "ยกเลิก",
    "cash.resend.limit": "จำกัด 5 ครั้งต่อบัตร หมายเลขโทรศัพท์ที่บันทึกไว้: {phone}",
    "cash.resend.no_phone": "(ไม่มี)",
    "cash.wallet.title": "เพิ่มลงใน Wallet",
    "cash.wallet.apple": "เพิ่มลงใน Apple Wallet",
    "cash.wallet.google": "เพิ่มลงใน Google Wallet",
    "cash.wallet.generating": "กำลังสร้าง pass…",
    "cash.wallet.note": "แสดงบนหน้าจอล็อก · อัปเดตอัตโนมัติเมื่อยอดเงินเปลี่ยนแปลง",
    "cash.wallet.toast.apple": "ดาวน์โหลด pass แล้ว — เปิดบน iPhone เพื่อเพิ่มลงใน Apple Wallet",
    "cash.wallet.toast.apple_failed": "ไม่สามารถสร้าง Apple Wallet pass ได้",
    "cash.wallet.toast.google_failed": "ไม่สามารถสร้าง Google Wallet pass ได้",
    "cash.delivery.fallback_title": "ส่งผ่าน {fallback} แล้ว",
    "cash.delivery.fallback_body": "ผู้ให้บริการบล็อก SMS เดิม เราจึงส่งบัตรของคุณใหม่ผ่าน {fallback} ไปที่ {phone} กรุณาตรวจสอบช่องทางนั้น",
    "cash.delivery.toll_blocked": "ผู้ให้บริการบล็อก SMS",
    "cash.delivery.failed_title": "เราไม่สามารถส่ง SMS บัตรของคุณได้",
    "cash.delivery.toll_body": "ผู้ให้บริการมือถือปฏิเสธข้อความ (เกิดขึ้นกับผู้ส่งรายใหม่ที่รอการยืนยัน) บันทึกบัตรของคุณตอนนี้: แตะ",
    "cash.delivery.failed_body": "บัตรของคุณได้รับเงินแล้ว — กรุณาบันทึกทันทีเนื่องจาก SMS ส่งไม่สำเร็จ แตะ",
    "cash.delivery.cta_label": "เปิดหน้า Tap-to-Pay",
    "cash.delivery.body_tail": "ด้านบน ถ่ายภาพหน้าจอ หรือใช้ Email ด้านล่างเพื่อส่งสำเนาให้ตัวเอง",
    "cash.handoff.eyebrow": "ส่งต่อแบบพบหน้า",
    "cash.handoff.body": "ให้ผู้รับสแกน QR นี้ด้วยกล้อง — บัตรจะเปิดโดยตรงบนโทรศัพท์ของพวกเขา ไม่ต้องใช้ SMS หรือ Email",
    "cash.handoff.share_label": "หรือแชร์ลิงก์นี้",
    "cash.handoff.copy": "คัดลอกลิงก์",
    "cash.handoff.copied": "✓ คัดลอกแล้ว",
    "cash.handoff.share": "แชร์",
    "cash.handoff.toast.copied": "คัดลอกลิงก์แล้ว — วางได้เลย",
    "cash.handoff.toast.copy_failed": "ไม่สามารถคัดลอกได้ กดค้างที่ลิงก์เพื่อคัดลอกด้วยตนเอง",
    "portal.loading": "กำลังโหลดใบเสร็จของคุณ…",
    "portal.unavailable.title": "ลิงก์ไม่พร้อมใช้งาน",
    "portal.unavailable.body": "ลิงก์นี้ไม่ถูกต้องหรือหมดอายุแล้ว",
    "portal.unavailable.hint": "หากผู้ส่งแชร์ลิงก์นี้ให้คุณ ขอให้พวกเขาส่ง Email ใบเสร็จอีกครั้ง",
    "portal.brand": "พอร์ทัลใบเสร็จ",
    "portal.eyebrow": "{sender} ส่งให้คุณ",
    "portal.reference": "อ้างอิง",
    "portal.pending.title": "รออนุมัติ",
    "portal.pending.body": "ผู้ส่งบันทึกการโอนนี้แล้ว แต่ยังอยู่ระหว่างการตรวจสอบโดย Zcardon กรุณาตรวจสอบใหม่ในภายหลัง — ใบเสร็จจะพร้อมใช้งานเมื่อได้รับการอนุมัติ",
    "portal.row.bank": "ธนาคาร",
    "portal.row.account": "บัญชี",
    "portal.row.routing": "Routing",
    "portal.row.memo": "บันทึก",
    "portal.confirmed.title": "คุณยืนยันการรับแล้ว",
    "portal.confirmed.body": "ยืนยันเมื่อ {when} ผู้ส่งสามารถเห็นข้อมูลนี้",
    "portal.disputed.title": "คุณแจ้งปัญหาไว้",
    "portal.disputed.body": "รายงานเมื่อ {when} ผู้ส่งได้รับแจ้งทาง Email แล้ว",
    "portal.actions.body": "ยอดเงินนี้เข้าบัญชีของคุณแล้วหรือยัง? การยืนยันจะแจ้งให้ผู้ส่งทราบ — ไม่มีการโอนเงินผ่าน Zcardon นี่เป็นเพียงการบันทึก",
    "portal.actions.confirm": "ใช่ ฉันได้รับแล้ว",
    "portal.actions.flag": "แจ้งปัญหา",
    "portal.dispute.placeholder": "ยอดเงินไม่ถูกต้อง ยังไม่ได้รับ หมายเลขบัญชีผิด…",
    "portal.dispute.submit": "ส่งรายงาน",
    "portal.dispute.too_short": "กรุณาระบุเหตุผลสั้นๆ (อย่างน้อย 4 ตัวอักษร)",
    "portal.toast.confirmed": "ขอบคุณ — ยืนยันใบเสร็จแล้ว",
    "portal.toast.confirm_failed": "ไม่สามารถยืนยันใบเสร็จได้",
    "portal.toast.reported": "รายงานแล้ว ผู้ส่งได้รับแจ้งแล้ว",
    "portal.toast.report_failed": "ไม่สามารถส่งรายงานข้อพิพาทได้",
    "portal.toast.pdf_failed": "ไม่สามารถดาวน์โหลดใบเสร็จได้",
    "portal.download_pdf": "ดาวน์โหลดใบเสร็จ PDF",
    "portal.privacy": "หน้านี้เป็นส่วนตัวสำหรับคุณ ผู้ที่มีลิงก์สามารถดูใบเสร็จนี้ได้ — อย่าแชร์สาธารณะ หากมีคำถาม:",
    "detail.back": "กลับแดชบอร์ด",
    "detail.eyebrow": "บัตรของคุณ",
    "detail.confirming_payment": "กำลังยืนยันการชำระเงิน…",
    "detail.active_ready": "การ์ดพร้อมใช้งานและแชร์ได้แล้ว",
    "detail.balance.label": "ยอดคงเหลือปัจจุบัน",
    "detail.toast.notfound": "ไม่พบการ์ด",
    "detail.toast.loaded": "โหลดเงินเข้าการ์ดแล้ว",
    "detail.toast.confirmed": "ยืนยันการชำระเงินแล้ว",
    "detail.toast.expired": "เซสชันการชำระเงินหมดอายุ",
    "detail.delivery.receiver_fallback": "ผู้รับของคุณ",
    "detail.delivery.received_title": "{name} เปิดการ์ดแล้ว",
    "detail.delivery.received_when": "ยืนยันการรับแล้ว · {when}",
    "detail.delivery.waiting_title_on": "คุณจะได้รับอีเมลทันทีที่มีการรับการ์ด",
    "detail.delivery.waiting_title_off": "การแจ้งเตือนการจัดส่งถูกปิดอยู่",
    "detail.delivery.waiting_body_on": "ทันทีที่ผู้รับสแกน QR บนการ์ดนี้ เราจะแจ้งคุณทางอีเมล — เพียงครั้งเดียว",
    "detail.delivery.waiting_body_off": "เปิดใช้งานเพื่อรับอีเมลครั้งเดียวในทันทีที่ผู้รับเปิดการ์ดเป็นครั้งแรก",
    "detail.delivery.toggle_on": "เปิด",
    "detail.delivery.toggle_off": "เปิดใช้งาน",
    "detail.delivery.toast_enabled": "คุณจะได้รับแจ้งเมื่อมีการเปิดการ์ด",
    "detail.delivery.toast_disabled": "ปิดการแจ้งเตือนการจัดส่งแล้ว",
    "detail.delivery.toast_failed": "ไม่สามารถอัปเดตการแจ้งเตือนการจัดส่งได้",
    "nudge.eyebrow": "แจ้งผู้รับของคุณ",
    "nudge.title": "ส่งข้อความแจ้ง {receiver}",
    "nudge.subtitle": "เราร่างข้อความสั้นๆ ให้ {receiver} ทราบว่าการ์ดพร้อมใช้งานแล้วและวิธีแตะใช้งาน",
    "nudge.receiver_fallback": "ผู้รับของคุณ",
    "nudge.sender_fallback": "เพื่อน",
    "nudge.template": "สวัสดี {receiver} — {sender} เพิ่งส่ง Zcardon virtual card มูลค่า {amount} ให้คุณ แตะเพื่อชำระเงินได้ที่นี่: {url}\\n\\nไม่ต้องติดตั้งแอป ลิงก์ใช้งานได้บนทุกโทรศัพท์",
    "nudge.email_subject": "{sender} ส่ง Zcardon card มูลค่า {amount} ให้คุณ",
    "nudge.action_copy": "คัดลอกข้อความ",
    "nudge.action_sms": "เปิด SMS",
    "nudge.action_email": "เปิดอีเมล",
    "nudge.toast_copied": "คัดลอกข้อความแล้ว",
    "nudge.toast_copy_failed": "ไม่สามารถคัดลอกได้ กรุณาเลือกและคัดลอกด้วยตนเอง",
    "ios_install.title": "ติดตั้ง Zcardon บน iPhone ของคุณ",
    "ios_install.step_1": "แตะ",
    "ios_install.share_label": "แชร์",
    "ios_install.step_2": " จากนั้นเลือก \\\"Add to Home Screen\\\" — Zcardon จะเปิดเป็นแอปแบบเต็มหน้าจอ",
    "smart_banner.title": "เปิดในแอป Zcardon",
    "smart_banner.subtitle": "ชำระเงินได้เร็วขึ้น แตะเพื่อจ่ายโดยไม่ต้องออกจากแอป",
    "smart_banner.cta": "เปิด",
    "smart_banner.dismiss": "ปิด",
    "social_proof.eyebrow": "สดๆ · 48 ชั่วโมงที่ผ่านมา",
    "social_proof.empty": "กำลังรอการ์ดใบถัดไป…",
    "social_proof.line_opened": "{name} เพิ่งเปิดการ์ดมูลค่า {amount}",
    "social_proof.line_issued": "{name} ได้รับการ์ดมูลค่า {amount} จากเพื่อน",
    "social_proof.line_deposit": "{name} ได้รับเงินโอนตรงมูลค่า {amount}",
    "social_proof.stat_funded": "การ์ดที่โหลดเงินวันนี้",
    "social_proof.stat_delivered": "การ์ดที่จัดส่งวันนี้",
    "social_proof.stat_recent": "กิจกรรมล่าสุด",
    "social_proof.ago_seconds": "{n} วินาทีที่แล้ว",
    "social_proof.ago_minutes": "{n} นาทีที่แล้ว",
    "social_proof.ago_hours": "{n} ชั่วโมงที่แล้ว",
    "flight.eyebrow": "การ์ดที่กำลังส่ง · 48 ชั่วโมงที่ผ่านมา",
    "flight.title": "Zcardon ไปถึงที่ไหนบ้าง",
    "flight.subtitle": "ตำแหน่งผู้รับจริง ปิดบังเป็นระดับภูมิภาค เราแสดงเฉพาะประเทศและรัฐในสหรัฐฯ — ไม่มีชื่อ ที่อยู่ หรือจำนวนเงิน",
    "flight.row_template": "เปิดการ์ดใน {label}",
    "flight.more_count": "+ {n} ภูมิภาคเพิ่มเติม",
    "flight.ago_seconds": "{n} วินาทีที่แล้ว",
    "flight.ago_minutes": "{n} นาทีที่แล้ว",
    "flight.ago_hours": "{n} ชั่วโมงที่แล้ว",
    "trust_strip.eyebrow": "ข้อมูลจริง · สดจากฐานข้อมูล",
    "trust_strip.title_a": "ไม่ใช่คำรับรอง",
    "trust_strip.title_b": "ข้อมูลสดจากฐานข้อมูล",
    "trust_strip.how_link": "วิธีที่เราเก็บข้อมูลนี้",
    "trust_strip.modal_eyebrow": "แหล่งข้อมูล",
    "trust_strip.modal_title": "ข้อมูลจริงทั้งหมด ปิดบังชื่อทั้งหมด",
    "trust_strip.modal_close": "ปิด",
    "trust_strip.modal_intro": "ตัวเลขทุกตัวบนแถบนี้มาจากฐานข้อมูลของเราเอง — ไม่ใช่ฟีดของบุคคลที่สาม ไม่มีการแต่งขึ้น นี่คือที่มาของแต่ละส่วนและวิธีที่เราปิดบังข้อมูลก่อนแสดงให้คุณเห็น",
    "trust_strip.src_carousel_title": "แคโรเซลกิจกรรมสด",
    "trust_strip.src_carousel_body": "ดึงข้อมูลจาก collections ของ cards และ direct_deposits ใน 48 ชั่วโมงที่ผ่านมา ชื่อแสดงเป็นชื่อจริง + อักษรย่อนามสกุล จำนวนเงินปัดเป็นทวีคูณของ $5 เพื่อป้องกันการระบุตัวตน รีเฟรชทุก 60 วินาที",
    "trust_strip.src_flight_title": "Zcardon ไปถึงที่ไหนบ้าง",
    "trust_strip.src_flight_body": "อ่านข้อมูลการสแกน QR จาก card_scans โดยระบุพิกัดตำแหน่งตอนสแกน ย่อให้เหลือระดับประเทศ (หรือรัฐในสหรัฐฯ) ไม่แสดงความแม่นยำระดับเมืองต่ำกว่ารัฐ ไม่แสดง IP ไม่แสดงรหัสการ์ด รีเฟรชทุก 90 วินาที",
    "trust_strip.src_badges_title": "ตัวเลขความน่าเชื่อถือ 30 วัน",
    "trust_strip.src_badges_body": "นับรวมทั้งหมดเท่านั้น — ไม่มีข้อมูลรายแถว แต่ละตัวชี้วัดจะแสดงก็ต่อเมื่อผ่านขนาดตัวอย่างขั้นต่ำ ดังนั้นอัตรา 100% จาก 2 การยืนยันจะไม่แสดง รีเฟรชทุกนาที",
    "trust_strip.modal_promise": "เราจะไม่เผยแพร่ชื่อ ที่อยู่ จำนวนเงินที่แน่นอน IP หรือหมายเลขการ์ดในวิดเจ็ตสาธารณะ สัญญา",
    "trust_badge.eyebrow": "ตัวเลขจริง · 30 วันที่ผ่านมา",
    "trust_badge.title": "ผู้คนใช้งานจริง",
    "trust_badge.subtitle": "ไม่ใช่คำรับรอง — ตัวเลขสดที่รีเฟรชจากฐานข้อมูลทุกนาที",
    "trust_badge.confirmed_label": "ผู้รับยืนยันการรับภายใน 24 ชั่วโมง",
    "trust_badge.confirmed_sub": "จาก {n} การตอบกลับใน 30 วันที่ผ่านมา",
    "trust_badge.thanks_label": "ข้อความขอบคุณที่ส่ง",
    "trust_badge.thanks_sub": "จากผู้รับใน 30 วันที่ผ่านมา",
    "trust_badge.cards_label": "การ์ดที่โหลดเงินแล้ว",
    "trust_badge.cards_sub": "30 วันที่ผ่านมา · นับแบบเรียลไทม์",
    "pulse.label": "LIVE PULSE",
    "pulse.recent_count": "+{n}",
    "pulse.tooltip_opened": "{name} เปิดบัตรมูลค่า {amount}",
    "pulse.tooltip_issued": "{name} ได้รับบัตรมูลค่า {amount}",
    "pulse.tooltip_deposit": "{name} ได้รับเงินฝาก {amount}",
    "pulse.ago_seconds": "{n} วินาทีที่แล้ว",
    "pulse.ago_minutes": "{n} นาทีที่แล้ว",
    "pulse.ago_hours": "{n} ชั่วโมงที่แล้ว",
    "note_cloud.eyebrow": "คนใช้มันเพื่ออะไร",
    "note_cloud.title": "เหตุผลจริง คนจริง",
    "note_cloud.subtitle": "รวบรวมจากโน้ตบัตร {n} รายการใน 90 วันที่ผ่านมา เราแสดงเฉพาะคำที่ปรากฏในโน้ตอย่างน้อย {min} รายการที่ต่างกัน — ข้อความของคุณยังเป็นของคุณ",
    "note_cloud.footnote": "ชื่อ จำนวนเงิน และข้อมูลติดต่อถูกกรองออกก่อนแสดงผล",
    "note_cloud.lang_eyebrow": "โน้ตเหล่านี้เขียนเป็นภาษาอะไร",
    "note_cloud.lang_other": "อื่นๆ",
    "download.button": "ดาวน์โหลดแอป",
    "download.close": "ปิด",
    "download.modal_eyebrow": "รับ Zcardon",
    "download.modal_title": "ติดตั้ง Zcardon บนโทรศัพท์ของคุณ",
    "download.footnote": "ยังไม่มีใน App Store — คุณกำลังติดตั้งเว็บแอปที่ปลอดภัยโดยตรง อัปเดตทันที",
    "download.installed_title": "ติดตั้ง Zcardon แล้ว",
    "download.installed_body": "มองหาไอคอนบนหน้าจอหลักของคุณ",
    "download.webview_warning": "เปิดหน้านี้ในเบราว์เซอร์ของโทรศัพท์ (Safari บน iPhone, Chrome บน Android) เพื่อติดตั้ง — เบราว์เซอร์ในแอปไม่สามารถเพิ่มไปยังหน้าจอหลักได้",
    "download.ios_step1_title": "บน Safari",
    "download.ios_step1_body": "หากคุณยังไม่ได้อยู่ใน Safari ให้คัดลอกลิงก์นี้แล้วเปิดที่นั่น (Chrome และเบราว์เซอร์ iOS อื่นๆ ไม่สามารถติดตั้งแอปได้)",
    "download.ios_step2_title": "แตะแชร์",
    "download.ios_step2_body": "ไอคอนแชร์อยู่ตรงกลางด้านล่างบน iPhone และด้านบนขวาบน iPad",
    "download.ios_step3_title": "เพิ่มไปยังหน้าจอหลัก",
    "download.ios_step3_body": "เลื่อนลงในเมนูแชร์แล้วเลือก \\\"เพิ่มไปยังหน้าจอหลัก\\\" Zcardon จะติดตั้งเป็นแอปแบบเต็มจอ",
    "download.android_ready_body": "Chrome ตรวจพบ Zcardon และพร้อมติดตั้งแล้ว แตะด้านล่าง",
    "download.android_install": "ติดตั้ง Zcardon",
    "download.android_installing": "กำลังติดตั้ง…",
    "download.android_step1_title": "เปิดเมนู Chrome",
    "download.android_step1_body": "แตะจุดสามจุดที่มุมบนขวาของ Chrome",
    "download.android_step2_title": "เพิ่มไปยังหน้าจอหลัก",
    "download.android_step2_body": "เลือก \\\"ติดตั้งแอป\\\" หรือ \\\"เพิ่มไปยังหน้าจอหลัก\\\" Zcardon จะติดตั้งเป็นแอปแบบเต็มจอ",
    "download.desktop_body": "สแกน QR code นี้ด้วยกล้องโทรศัพท์เพื่อเปิด Zcardon แล้วติดตั้งจากเบราว์เซอร์ของโทรศัพท์",
    "download.steps_hint": "ขั้นตอนเหล่านี้เกิดขึ้นในเบราว์เซอร์ของคุณ ไม่ใช่บนหน้านี้ แถวด้านล่างไม่สามารถแตะได้ — เป็นเพียงคำอธิบายสิ่งที่ต้องมองหา",
    "download.ios_arrow_label": "แตะสิ่งนี้ใน Safari",
    "download.android_arrow_label": "มองหาตรงนี้ใน Chrome",
    "install.eyebrow": "ติดตั้ง Zcardon",
    "install.title": "เพิ่ม Zcardon ไปยังหน้าจอหลักของคุณ",
    "install.subtitle": "แตะสองครั้งแล้วคุณจะมี Zcardon เป็นไอคอนแอปจริง — ไม่ต้องใช้ App Store",
    "install.back_home": "เปิด Zcardon ในเบราว์เซอร์",
    "install.continue": "ไปยัง Zcardon →",
    "install.copy": "คัดลอก",
    "install.copied": "คัดลอกแล้ว",
    "install.ios_step1_title": "เปิดหน้านี้ใน Safari",
    "install.ios_step1_body": "หากคุณสแกนจากเบราว์เซอร์อื่น ให้คัดลอก URL แล้วเปิดใน Safari ก่อน — Chrome บน iOS ไม่สามารถติดตั้งแอปได้",
    "install.ios_step2_title": "แตะปุ่มแชร์",
    "install.ios_step2_body": "เป็นรูปสี่เหลี่ยมมีลูกศรชี้ขึ้น — อยู่ด้านล่างของ Safari (หรือมุมบนขวาบน iPad)",
    "install.ios_step3_title": "แตะ \\\"เพิ่มไปยังหน้าจอหลัก\\\"",
    "install.ios_step3_body": "เลื่อนลงใน share sheet แล้วเลือก \\\"เพิ่มไปยังหน้าจอหลัก\\\" จากนั้นยืนยัน Zcardon จะปรากฏเป็นไอคอนแอป",
    "install.ios_arrow_label": "มองหาไอคอนนี้",
    "install.android_ready_title": "พร้อมติดตั้งแล้ว",
    "install.android_ready_body": "Chrome ตรวจพบว่า Zcardon สามารถติดตั้งได้ แตะด้านล่างแล้วเราจะปรากฏเป็นไอคอนแอปบนหน้าจอหลักของคุณ",
    "install.android_install": "ติดตั้ง Zcardon",
    "install.android_installing": "กำลังติดตั้ง…",
    "install.android_step1_title": "แตะเมนูของ Chrome",
    "install.android_step1_body": "เป็นจุดสามจุดที่มุมบนขวาของ Chrome",
    "install.android_step2_title": "แตะ \\\"ติดตั้งแอป\\\" หรือ \\\"เพิ่มไปยังหน้าจอหลัก\\\"",
    "install.android_step2_body": "ยืนยันเพื่อเพิ่มไอคอน Zcardon ในโทรศัพท์ของคุณ แตะได้ทุกเมื่อเพื่อเปิด",
    "install.android_hint": "หากคุณยังไม่เห็น \\\"ติดตั้งแอป\\\" ให้เรียกดูหน้าหลักสักครั้ง — Chrome จะปลดล็อกหลังจากเข้าชมสักครู่",
    "install.desktop_title": "สแกนด้วยโทรศัพท์ของคุณ",
    "install.desktop_body": "Zcardon เป็นแอปที่ออกแบบมาสำหรับมือถือ เปิด URL นี้บนโทรศัพท์เพื่อติดตั้ง:",
    "install.webview_title": "เปิดในเบราว์เซอร์ของคุณ",
    "install.webview_body": "เบราว์เซอร์ในแอป (Instagram, Facebook, TikTok) ไม่สามารถติดตั้งแอปได้ แตะ \\\"…\\\" หรือเมนูแชร์ แล้วเลือก \\\"เปิดใน Safari\\\" / \\\"เปิดใน Chrome\\\" หรือคัดลอกลิงก์นี้:",
    "install.already_title": "คุณอยู่ในแอปแล้ว",
    "install.already_body": "Zcardon ติดตั้งอยู่บนอุปกรณ์นี้แล้ว แตะไอคอนบนหน้าจอหลักเพื่อเปิดได้ทุกเมื่อ",
    "install.installed_title": "ติดตั้งแล้ว",
    "install.installed_body": "มองหาไอคอน Zcardon บนหน้าจอหลัก — นั่นคือแอปของคุณ",
    "install.steps_hint": "ขั้นตอนเหล่านี้เกิดขึ้นในเบราว์เซอร์ของคุณ ไม่ใช่บนหน้านี้ แถวด้านล่างไม่สามารถแตะได้ — เป็นเพียงคำอธิบายสิ่งที่ต้องมองหาในเมนูเบราว์เซอร์",
    "install.desktop_body_qr": "Zcardon ติดตั้งเป็นไอคอนแอปบน iPhone หรือ Android — ไม่ใช่บนเดสก์ท็อป ชี้กล้องโทรศัพท์ไปที่ QR code นี้:",
    "install.desktop_or": "หรือคัดลอกลิงก์",
    "install.android_arrow_label": "มองหาตรงนี้ใน Chrome",
    "install.ios_arrow_caption": "อยู่ที่ด้านล่างสุดของ Safari",
    "vietqr.disabled.title": "รับ VND ผ่าน VietQR",
    "vietqr.disabled.badge": "เฉพาะเวียดนาม",
    "vietqr.disabled.desc": "เชื่อมโยงบัญชีธนาคารเวียดนามและ QR บนบัตรนี้จะกลายเป็น VietQR จริง — แอปธนาคารเวียดนามใดๆ (Vietcombank, BIDV, MB, Techcombank, ACB, VietinBank…) สามารถสแกนและจ่าย VND ให้คุณโดยตรงผ่านเครือข่าย NAPAS ไม่ต้องได้รับการอนุมัติจาก Stripe Issuing",
    "vietqr.disabled.btn_enter": "กรอกข้อมูลธนาคาร",
    "vietqr.disabled.or": "หรือ",
    "vietqr.disabled.btn_send_link": "ส่งลิงก์ตั้งค่าให้ผู้รับ",
    "vietqr.disabled.btn_send_link_tip": "สร้างลิงก์สาธารณะอายุ 7 วันให้ผู้รับกรอกข้อมูลเอง",
    "vietqr.form.warn_title": "อ่านก่อนเปิดใช้งาน",
    "vietqr.form.warn_body_a": "การสแกนจะไม่นำไปยังหน้าติดตาม Zcardon อีกต่อไป เงินจะไหล",
    "vietqr.form.warn_body_b": "เข้าบัญชีธนาคารเวียดนามของคุณโดยตรง",
    "vietqr.form.warn_body_c": "ผ่านเครือข่ายระหว่างธนาคาร NAPAS — Zcardon ไม่เห็นเงินเลย VietQR ใช้งานได้เฉพาะภายในเวียดนามสำหรับการโอน VND เท่านั้น",
    "vietqr.form.bank": "ธนาคาร",
    "vietqr.form.account_number": "หมายเลขบัญชี",
    "vietqr.form.account_name": "ชื่อเจ้าของบัญชี (25 ตัวอักษร)",
    "vietqr.form.city": "เมือง (15 ตัวอักษร)",
    "vietqr.form.advanced": "ขั้นสูง — จำนวนเงินคงที่ / อ้างอิง (ไม่บังคับ)",
    "vietqr.form.amount_vnd": "จำนวนเงิน (VND)",
    "vietqr.form.amount_ph": "เว้นว่างสำหรับจำนวนเงินไม่คงที่",
    "vietqr.form.reference": "อ้างอิง (25 ตัวอักษร)",
    "vietqr.form.confirm": "ฉันเข้าใจว่าการสแกนบัตรนี้จะโอน VND ไปยังบัญชีธนาคารด้านบนโดยตรง Zcardon ไม่ประมวลผลหรือถือเงินจำนวนนี้",
    "vietqr.form.btn_attach": "เปิดใช้งาน VietQR",
    "vietqr.form.btn_attaching": "กำลังเปิดใช้งาน…",
    "vietqr.form.btn_cancel": "ยกเลิก",
    "vietqr.enabled.title": "VietQR เปิดใช้งานอยู่",
    "vietqr.enabled.desc": "เมื่อแอปธนาคารเวียดนามสแกนบัตรนี้ จะโอน VND ไปยังบัญชีด้านล่างโดยตรง — ข้าม Zcardon ทั้งหมด",
    "vietqr.enabled.bank_label": "ธนาคาร",
    "vietqr.enabled.account_label": "บัญชี",
    "vietqr.enabled.fixed_amount": "จำนวนเงินคงที่",
    "vietqr.enabled.reference": "อ้างอิง",
    "vietqr.enabled.btn_detach": "ยกเลิก VietQR",
    "vietqr.enabled.btn_detaching": "กำลังยกเลิก…",
    "vietqr.enabled.attached_at": "เปิดใช้งานเมื่อ",
    "vietqr.analytics.live_activity": "กิจกรรมสด",
    "vietqr.analytics.today": "วันนี้",
    "vietqr.analytics.days_live": "{n} วัน",
    "vietqr.analytics.phantom_views": "การดูโดยประมาณ",
    "vietqr.analytics.confirmed_pays": "การชำระเงินที่ยืนยัน",
    "vietqr.analytics.total_vnd": "VND รวม",
    "vietqr.analytics.recent_confirmed": "ยืนยันล่าสุด",
    "vietqr.analytics.phantom_explainer": "การดูโดยประมาณ ≈ การดาวน์โหลดภาพบัตรตั้งแต่เปิดใช้งาน การสแกน VietQR จริงไม่ผ่านเซิร์ฟเวอร์ของเรา ดังนั้นเราจึงประมาณการจากกิจกรรม \"แสดงบัตร\" ขอให้ผู้รับยืนยันการชำระเงินผ่านลิงก์ตั้งค่าของพวกเขาเพื่อข้อมูลที่แม่นยำ",
    "vietqr.toast.required": "ต้องระบุหมายเลขบัญชีและชื่อ",
    "vietqr.toast.confirm_first": "โปรดยืนยันว่าคุณเข้าใจว่า VietQR ส่ง VND ไปยังธนาคารของคุณโดยข้าม Zcardon",
    "vietqr.toast.attached_default": "เปิดใช้งาน VietQR แล้ว",
    "vietqr.toast.attach_failed": "ไม่สามารถเปิดใช้งาน VietQR",
    "vietqr.toast.detach_confirm": "ยกเลิก VietQR หรือไม่? QR บนบัตรจะกลับไปที่ลิงก์ติดตาม Zcardon",
    "vietqr.toast.detached": "ยกเลิก VietQR แล้ว — QR กลับไปที่ลิงก์ติดตาม Zcardon",
    "vietqr.toast.detach_failed": "ไม่สามารถยกเลิก",
    "vietqr.toast.link_copied": "คัดลอกลิงก์ตั้งค่าแล้ว · หมดอายุใน {h} ชั่วโมง",
    "vietqr.toast.link_generated": "สร้างลิงก์ตั้งค่าแล้ว · คัดลอกด้านล่าง",
    "vietqr.toast.link_failed": "ไม่สามารถสร้างลิงก์ตั้งค่า",

    "setpw.toast.too_short": "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
    "setpw.toast.no_match": "รหัสผ่านไม่ตรงกัน",
    "setpw.toast.success_signin_again": "ตั้งรหัสผ่านแล้ว — กรุณาลงชื่อเข้าใช้",
    "setpw.toast.welcome": "ยินดีต้อนรับสู่โปรแกรมตัวแทน Zcardon",
    "setpw.toast.expired": "ลิงก์หมดอายุหรือไม่ถูกต้อง",
    "setpw.verifying": "กำลังตรวจสอบคำเชิญของคุณ…",
    "setpw.invalid.title": "ลิงก์หมดอายุ",
    "setpw.invalid.desc": "ลิงก์ต้อนรับนี้หมดอายุหรือถูกใช้งานแล้ว กรุณาติดต่อผู้ดูแล Zcardon ของคุณเพื่อรับคำเชิญใหม่",
    "setpw.invalid.home_btn": "หน้าหลัก",
    "setpw.invalid.support_btn": "ติดต่อฝ่ายสนับสนุน",
    "setpw.fallback_name": "ตัวแทน",
    "setpw.eyebrow": "ต้อนรับตัวแทน",
    "setpw.heading": "ยินดีต้อนรับ, {name}",
    "setpw.subheading": "ตั้งรหัสผ่านเพื่อเปิดใช้บัญชีตัวแทนของคุณ คุณจะเข้าสู่ระบบทันที",
    "setpw.code_label": "รหัสตัวแทนของคุณ",
    "setpw.code_tag": "แชร์รหัสนี้เมื่อแนะนำลูกค้า",
    "setpw.form.title": "เลือกรหัสผ่าน",
    "setpw.form.new_label": "รหัสผ่านใหม่",
    "setpw.form.placeholder": "อย่างน้อย 8 ตัวอักษร",
    "setpw.form.confirm_label": "ยืนยันรหัสผ่าน",
    "setpw.form.btn_busy": "กำลังเปิดใช้งาน…",
    "setpw.form.btn_submit": "เปิดใช้งานบัญชีตัวแทน",
    "tx.heading": "รายการธุรกรรม",
    "tx.kind.direct_deposit": "ฝากเงินโดยตรง",
    "tx.kind.card_topup": "เติมเงินบัตร",
    "tx.kind.outbound_payout": "การจ่ายเงินออก",
    "tx.toast.load_failed": "โหลดธุรกรรมไม่สำเร็จ",
    "tx.summary.deposits": "ยอดฝาก",
    "tx.summary.topups": "ยอดเติมเงิน",
    "tx.summary.topups_sub": "บัตร",
    "tx.summary.payouts": "ยอดจ่าย",
    "tx.summary.payouts_sub": "ออก",
    "tx.filter.all": "ทั้งหมด",
    "tx.filter.deposits": "ฝากเงิน",
    "tx.filter.topups": "เติมเงิน",
    "tx.filter.payouts": "จ่ายเงิน",
    "tx.loading": "กำลังโหลดธุรกรรม…",
    "tx.empty.filtered": "ไม่มีธุรกรรมที่ตรงกับตัวกรองนี้",
    "tx.empty.none": "ยังไม่มีธุรกรรม",
    "tx.empty.cta": "เริ่มด้วยการฝากเงินโดยตรง →",
    "tx.open_title": "เปิด",
    "terms.load_error_default": "โหลดข้อกำหนดไม่สำเร็จ",
    "terms.toast.sign_and_check": "กรุณาลงชื่อและยืนยันเพื่อยอมรับข้อตกลง",
    "terms.toast.recorded": "บันทึกข้อตกลงแล้ว",
    "terms.toast.failed": "การส่งข้อมูลล้มเหลว",
    "terms.error.title": "ลิงก์ไม่ถูกต้อง",
    "terms.back_home": "กลับหน้าหลัก",
    "terms.loading": "กำลังโหลดข้อตกลง…",
    "terms.done.title": "ยอมรับข้อตกลงแล้ว",
    "terms.done.meta": "เวอร์ชัน {v} · {ts}",
    "terms.done.next": "คุณสามารถปิดหน้านี้ได้ บัตรของคุณจะพร้อมใช้งานทันทีที่ออกให้",
    "terms.eyebrow": "ข้อตกลงผู้ถือบัตร · v{v}",
    "terms.title_reaccept": "ยอมรับข้อตกลงผู้ถือบัตรอีกครั้ง",
    "terms.title_accept": "ยอมรับข้อตกลงผู้ถือบัตร",
    "terms.desc.sender_sent": "ส่งบัตร Zcardon ให้คุณ",
    "terms.desc.you_were_sent": "คุณได้รับบัตร Zcardon",
    "terms.desc.ending": "ลงท้ายด้วย",
    "terms.desc.read_before": "อ่านข้อตกลงด้านล่าง จากนั้นลงชื่อและยืนยันเพื่อเปิดใช้บัตร",
    "terms.already_accepted_note": "คุณยอมรับเวอร์ชันก่อนหน้าแล้ว ยอมรับอีกครั้งเพื่ออัปเดตเป็นเวอร์ชันล่าสุด",
    "terms.scroll_hint": "เลื่อนภายในกล่องเพื่ออ่านข้อตกลงฉบับเต็ม",
    "terms.view_json": "เปิดเป็น JSON",
    "terms.sign_label": "ลงชื่อด้วยชื่อทางกฎหมายเต็มของคุณ",
    "terms.sign_placeholder": "ตามที่ปรากฏในบัตรประชาชนของคุณ",
    "terms.consent": "ฉันได้อ่านและยอมรับข้อตกลงผู้ถือบัตร Zcardon v{v} แล้ว",
    "terms.btn_recording": "กำลังบันทึก…",
    "terms.btn_accept": "ยอมรับข้อตกลง",
    "hub.eyebrow": "ศูนย์รับของคุณ",
    "hub.heading.all_done": "คุณพร้อมแล้ว 🎉",
    "hub.heading.greet": "สวัสดี {name} 👋",
    "hub.fallback_name": "คุณ",
    "hub.desc.sender_sent": "ได้ส่งบัตร Zcardon ให้คุณ",
    "hub.desc.you_were_sent": "คุณได้รับบัตร Zcardon แล้ว",
    "hub.desc.ending": "ลงท้ายด้วย",
    "hub.desc.complete_steps": "ทำตามขั้นตอนด้านล่างเพื่อเปิดใช้งานทุกอย่าง",
    "hub.progress.label": "เสร็จแล้ว {done} จาก {total} ขั้นตอน",
    "hub.blocker.title_one": "เหลืออีก 1 ขั้นตอนที่จำเป็น",
    "hub.blocker.title_many": "เหลืออีก {n} ขั้นตอนที่จำเป็น",
    "hub.blocker.subtext": "บัตรของคุณจะยังไม่เปิดใช้งานอย่างสมบูรณ์จนกว่าจะเสร็จสิ้น",
    "hub.required_pill": "จำเป็น",
    "hub.done_pill": "เสร็จแล้ว",
    "hub.setup_btn": "ตั้งค่า",
    "hub.refresh": "รีเฟรช",
    "hub.loading": "กำลังโหลด…",
    "hub.error.title": "ปัญหาลิงก์",
    "hub.error.default": "ไม่สามารถโหลดศูนย์รับของคุณได้",
    "hub.back_home": "กลับหน้าหลัก",
    "hub.footer": "บุ๊กมาร์กหน้านี้ · ใช้งานได้ 90 วัน · เฉพาะผู้ที่มีลิงก์นี้เท่านั้นที่มองเห็นได้",
    "onboarding.eyebrow": "รับการโอนเงินของคุณ",
    "onboarding.title": "สวัสดี {name}",
    "onboarding.fallback_name": "คุณ",
    "onboarding.intro_via": "ผ่าน Zcardon",
    "onboarding.intro_ref_open": "(อ้างอิง",
    "onboarding.intro_ref_close": ")",
    "onboarding.country_intro": "เพิ่มบัญชีธนาคาร {country} ของคุณด้านล่างเพื่อรับเงิน ข้อมูลของคุณจะส่งตรงไปยัง Stripe (พาร์ทเนอร์ชำระเงินของเรา) — ผู้ส่งจะไม่เห็นข้อมูลเหล่านี้",
    "onboarding.invalid.title": "ลิงก์ไม่ถูกต้อง",
    "onboarding.invalid.hint": "กรุณาขอให้ผู้ส่งส่งลิงก์ใหม่",
    "onboarding.loading": "กำลังโหลด…",
    "onboarding.complete.title": "มีข้อมูลธนาคารอยู่แล้ว",
    "onboarding.complete.desc": "คุณได้ส่งข้อมูลบัญชีธนาคารแล้ว ผู้ส่งจะปล่อยเงินในเร็วๆ นี้",
    "onboarding.success.title": "ได้รับข้อมูลธนาคารแล้ว ✓",
    "onboarding.success.auto_paid": "ขอบคุณ — บัญชีธนาคารของคุณได้รับการยืนยันและเงินถูกปล่อยไปยังธนาคารของคุณแล้ว เงินจะเข้าบัญชีภายใน 1-2 วันทำการ",
    "onboarding.success.pending": "ขอบคุณ — บัญชีธนาคารของคุณได้รับการยืนยันแล้ว ผู้ส่งจะได้รับแจ้ง และเงินจะเข้าบัญชีของคุณภายใน 1-2 วันทำการ",
    "onboarding.success.ref": "อ้างอิง: {id}",
    "onboarding.beta.title": "ตัวอย่างเบต้า",
    "onboarding.beta.body": "หน้านี้เปิดตัวแล้ว แต่ระบบจ่ายเงินสดกำลังรอการอนุมัติจาก Stripe การส่งข้อมูลจะเกิดข้อผิดพลาดจนกว่าแพลตฟอร์มจะเปิดใช้งาน โปรดลองใหม่ภายหลัง หรือติดต่อฝ่ายสนับสนุนหากเร่งด่วน",
    "onboarding.select_placeholder": "เลือก…",
    "onboarding.submit_error_default": "การส่งล้มเหลว กรุณาลองใหม่",
    "onboarding.submit_busy": "กำลังส่ง…",
    "onboarding.submit": "ส่งข้อมูลธนาคาร →",
    "onboarding.secure_note": "ปลอดภัยผ่าน Stripe ลิงก์นี้หมดอายุใน 7 วัน",
    "onboarding.validate.required": "{field} จำเป็นต้องกรอก",
    "onboarding.validate.format": "รูปแบบ {field} ดูไม่ถูกต้อง (ที่คาดหวัง {pattern})",
    "onboarding.validate.no_schema": "ไม่มีสคีมาฟอร์มที่ใช้งานได้",
    "apply.toast.invalid_image": "กรุณาเลือกรูปภาพ JPEG, PNG หรือ WEBP",
    "apply.toast.too_large": "รูปภาพต้องมีขนาดไม่เกิน 6 MB",
    "apply.toast.upload_both": "กรุณาอัปโหลดทั้งสองด้านของใบขับขี่",
    "apply.toast.failed": "การส่งล้มเหลว",
    "apply.toast.submitted": "ส่งใบสมัครแล้ว",
    "apply.success.title": "ได้รับใบสมัครแล้ว",
    "apply.success.desc": "ขอบคุณ {name} เราจะตรวจสอบใบสมัครของคุณภายใน 2–3 วันทำการ และส่งอีเมลไปที่ {email} พร้อมขั้นตอนถัดไป",
    "apply.success.back_home": "กลับหน้าหลัก",
    "apply.success.contact_support": "ติดต่อฝ่ายสนับสนุน",
    "apply.eyebrow": "โปรแกรมตัวแทน",
    "apply.heading": "สมัครเป็นตัวแทน Zcardon",
    "apply.intro": "รับค่าคอมมิชชัน 0.5% ทุกครั้งที่ช่วยออกบัตร เติมเงิน หรือรับฝากเงินสดแทนลูกค้า เรารับสมัครตัวแทนที่ได้รับความไว้วางใจในชุมชนและยืนยันตัวตนได้",
    "apply.bullet1": "รับรหัสตัวแทนเฉพาะของคุณ · ติดตามได้ทุกธุรกรรม",
    "apply.bullet2": "แดชบอร์ดสรุปรายได้รายสัปดาห์/รายเดือน/รายปี",
    "apply.bullet3": "โอนเข้าบัญชีธนาคารโดยตรง — จ่ายทุกสัปดาห์",
    "apply.section.your_info": "ข้อมูลของคุณ",
    "apply.field.full_name": "ชื่อตามกฎหมาย",
    "apply.field.full_name_ph": "ตามที่ระบุในใบขับขี่",
    "apply.field.email": "อีเมล",
    "apply.field.phone": "เบอร์โทรศัพท์",
    "apply.field.phone_ph": "+1 555 555 0123",
    "apply.field.dl_number": "เลขที่ใบขับขี่",
    "apply.field.address": "ที่อยู่",
    "apply.field.address_ph": "ถนน เมือง รัฐ รหัสไปรษณีย์",
    "apply.field.why_join_label": "ทำไมถึงอยากเป็นตัวแทน? (ไม่บังคับ)",
    "apply.field.why_join_ph": "เล่าให้ฟังเกี่ยวกับความสัมพันธ์ในชุมชนหรือประสบการณ์ด้านการโอนเงิน",
    "apply.dl.section": "ยืนยันใบขับขี่",
    "apply.dl.helper": "อัปโหลดภาพถ่ายที่ชัดเจนและมีแสงสว่างทั้งสองด้าน JPEG, PNG หรือ WEBP · ขนาดสูงสุด 6 MB ต่อภาพ รูปภาพจะถูกเก็บอย่างเป็นส่วนตัวและเปิดดูโดยผู้ดูแล Zcardon เพื่อยืนยันเท่านั้น",
    "apply.dl.front": "ด้านหน้าใบขับขี่",
    "apply.dl.back": "ด้านหลังใบขับขี่",
    "apply.dl.tap_upload": "แตะเพื่ออัปโหลด",
    "apply.dl.file_meta": "JPEG · PNG · WEBP · ≤ 6 MB",
    "apply.cancel": "ยกเลิก",
    "apply.submit_busy": "กำลังส่ง…",
    "apply.submit": "ส่งใบสมัคร",
    "agent.toast.load_failed": "โหลดข้อมูลตัวแทนไม่สำเร็จ",
    "agent.toast.cust_created": "สร้างลูกค้า {name} แล้ว",
    "agent.toast.cust_failed": "สร้างลูกค้าไม่สำเร็จ",
    "agent.toast.card_issued": "ออกบัตรแล้ว · คุณได้รับ {amount}",
    "agent.toast.card_issue_failed": "ออกบัตรไม่สำเร็จ",
    "agent.toast.cash_accepted": "รับฝากเงินสดแล้ว · คุณได้รับ {amount}",
    "agent.toast.cash_failed": "บันทึกการฝากเงินไม่สำเร็จ",
    "agent.loading": "กำลังโหลด…",
    "agent.eyebrow": "พอร์ทัลตัวแทน",
    "agent.fallback_name": "ตัวแทน",
    "agent.code_line.code_label": "รหัสตัวแทน",
    "agent.code_line.commission_label": "ค่าคอมมิชชัน",
    "agent.earnings.label": "ยอดค้างรับ",
    "agent.earnings.sub": "{n} ธุรกรรมที่ยังไม่ได้รับเงิน",
    "agent.stat.week": "สัปดาห์นี้",
    "agent.stat.month": "เดือนนี้",
    "agent.stat.year": "ปีนี้",
    "agent.stat.txns": "{n} รายการ",
    "agent.stat.customers": "ลูกค้าของคุณ",
    "agent.stat.active": "ใช้งานอยู่",
    "agent.customers.title": "ลูกค้าของคุณ",
    "agent.customers.empty": "ยังไม่มีลูกค้า แตะ \"ลงทะเบียนลูกค้า\" เพื่อเพิ่มผู้ใช้คนแรก",
    "agent.customers.col_customer": "ลูกค้า",
    "agent.customers.col_phone": "เบอร์โทรศัพท์",
    "agent.customers.col_cards": "บัตร",
    "agent.customers.col_commissions": "ค่าคอมมิชชันที่ได้รับ",
    "agent.customers.col_joined": "วันที่เข้าร่วม",
    "agent.customers.col_actions": "การดำเนินการ",
    "agent.customers.txns": "{n} รายการ",
    "agent.commissions.title": "ค่าคอมมิชชันล่าสุด",
    "agent.commissions.empty": "ยังไม่มีค่าคอมมิชชัน ออกบัตรใบแรกเพื่อเริ่มรับเงิน",
    "agent.commissions.col_when": "เวลา",
    "agent.commissions.col_customer": "ลูกค้า",
    "agent.commissions.col_type": "ประเภท",
    "agent.commissions.col_txn": "รายการ",
    "agent.commissions.col_commission": "ค่าคอมมิชชัน",
    "agent.commissions.col_status": "สถานะ",
    "agent.commissions.paid": "จ่ายแล้ว",
    "agent.commissions.unpaid": "ยังไม่จ่าย",
    "agent.type.issuance": "การออกบัตร",
    "agent.type.topup": "เติมเงิน",
    "agent.type.cash_deposit": "ฝากเงินสด",
    "agent.register_btn": "ลงทะเบียนลูกค้า",
    "agent.dialog.new_cust.title": "ลูกค้าใหม่",
    "agent.dialog.new_cust.desc": "จะถูกเชื่อมกับรหัสตัวแทนของคุณ ทุกบัตรที่คุณออกในนามพวกเขาจะได้รับค่าคอมมิชชัน 0.5%",
    "agent.dialog.field.full_name": "ชื่อ-นามสกุล",
    "agent.dialog.field.email": "อีเมล",
    "agent.dialog.field.phone": "เบอร์โทรศัพท์",
    "agent.dialog.field.password": "รหัสผ่านเริ่มต้น (แจ้งลูกค้า)",
    "agent.dialog.cancel": "ยกเลิก",
    "agent.dialog.creating": "กำลังสร้าง…",
    "agent.dialog.create_customer": "สร้างลูกค้า",
    "agent.dialog.issue_card.title": "ออกบัตรให้ {name}",
    "agent.dialog.issue_card.desc": "ชำระจากเงินสดที่รับมา ลูกค้าชำระค่าธรรมเนียม 3.75% — คุณได้ 0.5%",
    "agent.dialog.field.receiver_name": "ชื่อผู้รับ",
    "agent.dialog.field.receiver_email": "อีเมลผู้รับ",
    "agent.dialog.field.receiver_phone": "เบอร์โทรผู้รับ",
    "agent.dialog.field.amount_usd": "จำนวนเงิน (USD)",
    "agent.dialog.field.note": "หมายเหตุ (ถ้ามี)",
    "agent.dialog.issuing": "กำลังออกบัตร…",
    "agent.dialog.issue_card_btn": "ออกบัตร",
    "agent.actions.issue_card": "ออกบัตร",
    "agent.dialog.cash_deposit.title": "ฝากเงินสดให้ {name}",
    "agent.dialog.cash_deposit.desc": "เงินสดที่รับจากลูกค้า · สร้างบัตรส่งถึงผู้รับ",
    "agent.dialog.field.receiver_name_opt": "ชื่อผู้รับ (ถ้ามี)",
    "agent.dialog.recording": "กำลังบันทึก…",
    "agent.dialog.record_deposit": "บันทึกการฝาก",
    "agent.actions.cash_deposit": "ฝากเงินสด",

  },
  // ─── END AUTO-GENERATED TRANSLATIONS ───
};
